/* eslint-disable */
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import useS3Upload from '../hooks/useS3Upload';
import S3AssetBrowser from '../components/S3AssetBrowser';
import { 
  Bot, 
  Calendar, 
  Clock, 
  Upload, 
  Image as ImageIcon, 
  Settings, 
  Save, 
  AlertCircle, 
  CheckCircle,
  Plus,
  Trash2,
  Edit2,
  Play,
  Pause,
  Target,
  MessageSquare,
  MessageCircle,
  Hash,
  FileText,
  CalendarDays,
  Share2,
  Zap,
  ArrowLeft,
  Eye,
  Timer,
  ClipboardList,
  ClipboardCheck,
  RefreshCw,
  Users,
  User,
  Mail,
  Gift,
  Heart,
  Stethoscope,
  Search,
  Type,
  Globe,
  X,
  DollarSign,
  Info,
  Folder
} from 'lucide-react';

// Global Placeholder Modal Component
const PlaceholderModal = ({ isOpen, onClose, onInsertPlaceholder, placeholderType = 'sms' }) => {
  const [globalPlaceholders, setGlobalPlaceholders] = React.useState([]);
  const [businessPlaceholders, setBusinessPlaceholders] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || (window.location.origin.includes("localhost") ? "http://localhost:8000" : window.location.origin + "/pandvah-backend");

  // Fetch placeholders when modal opens
  React.useEffect(() => {
    if (isOpen) {
      fetchPlaceholders();
    }
  }, [isOpen]);

  const fetchPlaceholders = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      
      // Fetch global placeholders
      const globalResponse = await fetch(`${API_BASE_URL}/api/global-placeholders`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // Fetch business placeholders
      const businessResponse = await fetch(`${API_BASE_URL}/api/business-placeholders`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (globalResponse.ok) {
        const globalData = await globalResponse.json();
        setGlobalPlaceholders(globalData);
      }

      if (businessResponse.ok) {
        const businessData = await businessResponse.json();
        setBusinessPlaceholders(businessData);
      }
    } catch (error) {
      console.error('Error fetching placeholders:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  // Combine and categorize placeholders
  const getPlaceholders = () => {
    const allPlaceholders = [];

    // Add global placeholders
    globalPlaceholders.forEach(placeholder => {
      allPlaceholders.push({
        code: placeholder.placeholder,
        category: 'Global',
        value: placeholder.value
      });
    });

    // Add business placeholders
    businessPlaceholders.forEach(placeholder => {
      allPlaceholders.push({
        code: placeholder.placeholder,
        category: 'Custom',
        value: placeholder.value
      });
    });

    return allPlaceholders;
  };

  const placeholders = getPlaceholders();
  const categories = [...new Set(placeholders.map(p => p.category))];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] flex flex-col">
        {/* Sticky Header */}
        <div className="px-6 py-4 border-b border-gray-200" style={{ backgroundColor: 'var(--theme-primary)' }}>
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-white">Available Placeholders</h3>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <p className="text-sm text-white opacity-90 mt-1">
            Click "Add" to insert a placeholder into your template
          </p>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto mb-4" style={{ borderColor: 'var(--theme-primary)' }}></div>
              <p className="text-gray-600">Loading placeholders...</p>
            </div>
          ) : placeholders.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-600">No placeholders available. Create some in Business Info or CMS Settings.</p>
            </div>
          ) : (
            categories.map(category => (
              <div key={category} className="mb-6">
                <h4 className="text-sm font-medium text-gray-700 mb-3 border-b pb-1">{category}</h4>
                <div className="space-y-2">
                  {placeholders
                    .filter(placeholder => placeholder.category === category)
                    .map(placeholder => (
                      <div key={placeholder.code} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100">
                        <div className="flex-1 space-y-1">
                          <div>
                            <code className="text-sm font-mono bg-white px-2 py-1 rounded border" style={{ color: 'var(--theme-primary)' }}>
                              {placeholder.code}
                            </code>
                          </div>
                          {placeholder.value && (
                            <div className="text-xs text-gray-600">
                              {placeholder.value}
                            </div>
                          )}
                        </div>
                        <button
                          onClick={() => onInsertPlaceholder(placeholder.code)}
                          className="ml-3 px-3 py-1 text-white text-sm rounded transition-colors"
                          style={{ backgroundColor: 'var(--theme-primary)' }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = 'rgb(35, 147, 180)'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = 'var(--theme-primary)'}
                        >
                          Insert
                        </button>
                      </div>
                    ))}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Sticky Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const AIAgentConfig = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, token, isAdmin, loading: authLoading } = useAuth();
  const { uploadFile, uploading, uploadProgress } = useS3Upload();
  const [activeTab, setActiveTab] = useState(() => {
    const agentType = searchParams.get('agent_type');
    if (agentType === 'email') {
      return 'email-scheduled'; // Default to scheduled for email agents
    } else if (agentType === 'sms_agent') {
      return 'sms-scheduled'; // Default to scheduled for SMS agents
    } else if (agentType === 'time_sheet') {
      return 'timesheet-recurring'; // Default to recurring for timesheet agents
    } else if (agentType === 'paystub_agent') {
      return 'paystub-recurring'; // Default to recurring for paystub agents
    }
    return 'social-media-recurring'; // Default for social media agents
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [agents, setAgents] = useState([]);
  const [pageLoading, setPageLoading] = useState(true);
  
  // Validation Modal State
  const [validationModal, setValidationModal] = useState({
    isOpen: false,
    title: '',
    message: ''
  });
  
  // Placeholder Modal State
  const [placeholderModal, setPlaceholderModal] = useState({
    isOpen: false,
    targetField: null,
    updateFunction: null,
    targetElement: null // Track the actual DOM element for cursor position
  });

  // Recurring Mode State
  const [recurringMode, setRecurringMode] = useState({
    agentName: '',
    topic: '',
    customTopic: '',
    imageOption: 'ai_generate', // 'ai_generate', 'upload', 'reference', 'none'
    uploadedImages: [],
    // AI Image Format Selection - format for each platform
    platformImageFormats: {}, // e.g., { instagram: 'post', facebook: 'cover_photo', twitter: 'landscape' }
    scheduleType: 'all_days', // 'all_days' or 'selected_days'
    frequency: '24', // hours (only used when scheduleType is 'all_days')
    socialPlatforms: {
      facebook: false,
      instagram: false,
      twitter: false,
      tiktok: false,
      whatsapp: false
    },
    postTime: '09:00',
    imageText: '[BUSINESS_NAME] [PHONE_NUMBER]',
    enableCustomImageText: false, // Checkbox to enable custom image text
    customImageBannerText: '[BUSINESS_NAME] [PHONE_NUMBER]', // Custom text banner for image
    wordCount: '100',
    daysOfWeek: {
      monday: false,
      tuesday: false,
      wednesday: false,
      thursday: false,
      friday: false,
      saturday: false,
      sunday: false
    },
    postDestination: 'in_review', // 'auto_post', 'in_review', or 'ready_to_publish'
    enableSocialLink: false, // Whether to include link in social media post
    socialLink: 'https://petsandvetsanimalhospital.com' // Default website link
  });

  // Timesheet-specific state for recurring mode
  const [timesheetRecurringMode, setTimesheetRecurringMode] = useState({
    agentName: '',
    selectedEmployees: [], // Array of employee IDs
    runEveryPayPeriod: 'weekly', // Changed from frequency to runEveryPayPeriod
    daysAfterPeriodEnd: 1, // New field: days after pay period ends to run the agent
    customStartDate: '',
    customEndDate: '',
    includeSummary: true,
    includeBillingRates: true,
    initialStatus: 'in_review', // 'in_review', 'ready_to_publish', 'published'
    emailRecipients: [], // Array of email addresses
    autoEmail: false,
    scheduleTime: '09:00',
    daysOfWeek: {
      monday: true,
      tuesday: false,
      wednesday: false,
      thursday: false,
      friday: false,
      saturday: false,
      sunday: false
    }
  });

  // Timesheet-specific state for adhoc mode
  const [timesheetAdhocMode, setTimesheetAdhocMode] = useState({
    agentName: '',
    selectedEmployees: [], // Array of employee IDs
    reportPeriod: 'current_week',
    customStartDate: '',
    customEndDate: '',
    includeSummary: true,
    includeBillingRates: true,
    initialStatus: 'in_review',
    emailRecipients: [], // Array of email addresses
    autoEmail: false,
    isRegeneratingExisting: false // Track if regenerating existing timesheet
  });

  // Paystub-specific state for recurring mode
  const [paystubRecurringMode, setPaystubRecurringMode] = useState({
    agentName: '',
    selectedEmployees: [], // Array of employee IDs
    daysAfterTimesheetPublished: 0, // 0=immediately, 1=1 day, 2=2 days after timesheet is published
    workflowMode: 'in_review', // Renamed from initialStatus
    paystubTemplate: 'standard',
    includeDeductions: true,
    includeOvertime: true,
    paystubFormat: 'pdf',
    emailRecipients: [], // Array of email addresses
    autoEmail: false,
    scheduleTime: '09:00',
    daysOfWeek: {
      monday: false,
      tuesday: false,
      wednesday: false,
      thursday: false,
      friday: false,
      saturday: false,
      sunday: false
    }
  });

  // Paystub-specific state for adhoc mode
  const [paystubAdhocMode, setPaystubAdhocMode] = useState({
    agentName: '',
    selectedEmployees: [], // Array of employee IDs
    selectedTimesheetId: '', // Dropdown to select from published timesheets
    workflowMode: 'in_review', // Renamed from initialStatus
    paystubTemplate: 'standard',
    includeDeductions: true,
    includeOvertime: true,
    paystubFormat: 'pdf',
    emailRecipients: [], // Array of email addresses
    autoEmail: false
  });

  // Available employees for selection
  const [availableEmployees, setAvailableEmployees] = useState([]);
  const [newEmailRecipient, setNewEmailRecipient] = useState('');
  
  // Template management state
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [smsTemplates, setSmsTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  
  // Full-screen email editor state
  const [showFullScreenEditor, setShowFullScreenEditor] = useState(false);
  const [fullScreenEditorContent, setFullScreenEditorContent] = useState('');
  const [fullScreenEditorTitle, setFullScreenEditorTitle] = useState('');
  const [fullScreenEditorCallback, setFullScreenEditorCallback] = useState(null);
  const [fullScreenEditorPersonalized, setFullScreenEditorPersonalized] = useState(true);
  
  // Pay period settings
  const [defaultPayPeriods, setDefaultPayPeriods] = useState([]);
  const [pastPayPeriods, setPastPayPeriods] = useState([]);
  const [selectedPayPeriod, setSelectedPayPeriod] = useState(null);
  const [existingTimesheetReports, setExistingTimesheetReports] = useState([]);

  // Email Agent - Scheduled Mode State
  const [emailScheduledMode, setEmailScheduledMode] = useState({
    agentName: '',
    selectedHolidays: [], // Array of holiday IDs
    imageOption: 'ai_generate', // 'ai_generate', 'upload', 's3_browse', 'none'
    imageFormat: 'landscape', // 'portrait', 'landscape', 'square'
    uploadedImages: [],
    enableCustomImageText: false,
    customImageBannerText: '[BUSINESS_NAME] [PHONE_NUMBER]',
    useWebResearch: false,
    postTime: '09:00',
    postDestination: 'in_review', // 'auto_send', 'in_review', or 'ready_to_publish'
    useCustomerDatabase: true, // Default to true for customer database integration
    emailContentTemplate: `Dear [CUSTOMER_NAME],

We hope you and [PET_NAME] are doing well! As we celebrate this special holiday season, we wanted to reach out with some exciting news and offers from our veterinary clinic.

Our team is here to provide the best care for [PET_NAME] with:
• Comprehensive health checkups and vaccinations
• Emergency services available 24/7
• Professional grooming and wellness services
• Specialized treatments tailored to your pet's needs

Don't forget to schedule [PET_NAME]'s regular checkup to keep them healthy and happy throughout the year.

Visit our clinic or book your appointment online at our website. We look forward to seeing you and [PET_NAME] soon!

Best regards,
The Veterinary Care Team`,
    emailType: 'bulk', // 'single' or 'bulk'
    selectedCustomer: null, // For single email mode
    customerName: '' // For displaying selected customer name
  });

  // Email Agent - Write Email Mode State  
  const [emailWriteMode, setEmailWriteMode] = useState({
    agentName: '',
    emailSubject: '',
    emailContent: `Dear CUSTOMER_NAME,

I hope this message finds you and PET_NAME in great health and spirits!

We wanted to reach out to share some important information and updates from our veterinary clinic that we believe will be valuable for you and PET_NAME.

Our dedicated team continues to provide comprehensive care including:
• Regular health checkups and preventive care
• Vaccination services to keep PET_NAME protected
• Professional grooming and wellness treatments
• Emergency services available when you need us most

We believe that preventive care is the key to keeping PET_NAME healthy and happy for years to come. If it's been a while since PET_NAME's last visit, now would be a great time to schedule a checkup.

Please don't hesitate to contact us if you have any questions about PET_NAME's health or our services. We're always here to help!

Warm regards,
The Veterinary Care Team`,
    emailContentBulk: `Dear [CUSTOMER_NAME],

I hope this message finds you and [PET_NAME] in great health and spirits!

We wanted to reach out to share some important information and updates from our veterinary clinic that we believe will be valuable for you and [PET_NAME].

Our dedicated team continues to provide comprehensive care including:
• Regular health checkups and preventive care
• Vaccination services to keep [PET_NAME] protected
• Professional grooming and wellness treatments
• Emergency services available when you need us most

We believe that preventive care is the key to keeping [PET_NAME] healthy and happy for years to come. If it's been a while since [PET_NAME]'s last visit, now would be a great time to schedule a checkup.

Please don't hesitate to contact us if you have any questions about [PET_NAME]'s health or our services. We're always here to help!

Warm regards,
The Veterinary Care Team`,
    imageOption: 'ai_generate', // 'ai_generate', 'upload', 's3_browse', 'none'
    imageFormat: 'landscape', // 'portrait', 'landscape', 'square'
    uploadedImages: [],
    enableCustomImageText: false,
    customImageBannerText: '[BUSINESS_NAME] [PHONE_NUMBER]',
    useChatGPTFormatting: true, // Replace web research with ChatGPT formatting
    emailType: 'bulk', // 'single' or 'bulk'
    selectedCustomer: null, // For single email mode
    customerName: '', // For displaying selected customer name
    isScheduled: false, // Whether to schedule the email
    postDate: '',
    postTime: '09:00',
    postDestination: 'in_review'
  });

  // Email Agent - Recurring Mode State (schedule-based, similar to scheduled mode)
  const [emailRecurringMode, setEmailRecurringMode] = useState({
    agentName: '',
    topic: '', // Required for recurring email agents  
    customTopic: '', // For custom topic selection
    // Schedule Selection (replacing holiday selection)
    scheduleType: 'weekly', // 'weekly' or 'monthly'
    // Weekly schedule fields
    daysOfWeek: {
      monday: false,
      tuesday: false,
      wednesday: false,
      thursday: false,
      friday: false,
      saturday: false,
      sunday: false
    },
    // Monthly schedule - simplified to 1st of every month
    monthlySchedule: '1st', // Always 1st day of month for monthly schedules
    // All other fields matching scheduled mode
    emailContentTemplate: `Dear [CUSTOMER_NAME],

I hope this message finds you and [PET_NAME] in great health and spirits!

We wanted to reach out with our regular update and check-in about [PET_NAME]'s care and our services.

Our dedicated team continues to provide comprehensive care including:
• Regular health checkups and preventive care
• Vaccination services to keep [PET_NAME] protected
• Professional grooming and wellness treatments
• Emergency services available when you need us most

We believe that preventive care is the key to keeping [PET_NAME] healthy and happy for years to come. If it's been a while since [PET_NAME]'s last visit, now would be a great time to schedule a checkup.

Please don't hesitate to contact us if you have any questions about [PET_NAME]'s health or our services. We're always here to help!

Warm regards,
The Veterinary Care Team`,
    postTime: '09:00',
    imageOption: 'ai_generate', // 'ai_generate', 'upload', 's3_browse', 'none'
    imageFormat: 'landscape', // 'portrait', 'landscape', 'square'
    uploadedImages: [],
    enableCustomImageText: false,
    customImageBannerText: '[BUSINESS_NAME] [PHONE_NUMBER]',
    useChatGPTFormatting: true, // Default to true for ChatGPT formatting
    postDestination: 'in_review', // 'in_review', 'ready_to_publish', 'auto_send'
    emailType: 'bulk', // 'single' or 'bulk'
    selectedCustomer: null, // For single email mode
    customerName: '' // For displaying selected customer name
  });

  // Email-specific data
  const [availableHolidays, setAvailableHolidays] = useState([]);
  const [availableCustomers, setAvailableCustomers] = useState([]);
  
  // Customer search functionality
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [searchedCustomers, setSearchedCustomers] = useState([]);
  const [isSearchingCustomers, setIsSearchingCustomers] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // SMS Agent - Scheduled Mode State (Holiday-based SMS)
  const [smsScheduledMode, setSmsScheduledMode] = useState({
    agentName: '',
    selectedHolidays: [], // Array of holiday IDs
    smsContentTemplate: `Hi [CUSTOMER_NAME]! Hope [PET_NAME] is doing well. Special holiday offer - 20% off checkups this month. Call us at (555) 123-4567 to book!`,
    smsLink: 'https://petsandvetsanimalhospital.com', // Default website link
    enableSmsLink: false, // Whether to include SMS link in message
    postTime: '09:00',
    postDestination: 'in_review', // 'auto_send', 'in_review', or 'ready_to_publish'
    useCustomerDatabase: true, // Default to true for customer database integration
    smsType: 'bulk', // 'single' or 'bulk'
    selectedCustomer: null, // For single SMS mode
    smsCharacterLimit: 160,
    // Image Options
    imageOption: 'none', // 'ai_generate', 'upload', 's3_browse', 'none'
    imageFormat: 'landscape', // 'portrait', 'landscape', 'square'
    uploadedImages: [],
    enableCustomImageText: false,
    customImageBannerText: ''
  });

  // SMS Agent - Write SMS Mode State  
  const [smsWriteMode, setSmsWriteMode] = useState({
    agentName: '',
    smsSubject: '', // This will be renamed to ChatGPT Content
    smsContent: `Hi [CUSTOMER_NAME]! Hope [PET_NAME] is well. Don't forget your pet's checkup. Call (555) 123-4567 to schedule. Thanks!`,
    smsTemplate: '', // Selected SMS template that will contain [CHATGPT_CONTENT] placeholder
    smsLink: 'https://petsandvetsanimalhospital.com', // Default website link
    enableSmsLink: false, // Whether to include SMS link in message
    useSMSChatGPTFormatting: true,
    smsType: 'bulk', // 'single' or 'bulk'
    selectedCustomer: null, // For single SMS mode
    postDate: '',
    postTime: '09:00',
    postDestination: 'in_review',
    smsCharacterLimit: 160,
    enableScheduling: false,
    // Image Options
    imageOption: 'none', // 'ai_generate', 'upload', 's3_browse', 'none'
    imageFormat: 'landscape', // 'portrait', 'landscape', 'square'
    uploadedImages: [],
    enableCustomImageText: false,
    customImageBannerText: ''
  });

  // SMS Agent - Recurring Mode State
  const [smsRecurringMode, setSmsRecurringMode] = useState({
    agentName: '',
    topic: '', // Required for recurring SMS agents  
    customTopic: '', // For custom topic selection
    // Schedule Selection
    scheduleType: 'weekly', // 'weekly' or 'monthly'
    // Weekly schedule fields
    daysOfWeek: {
      monday: false,
      tuesday: false,
      wednesday: false,
      thursday: false,
      friday: false,
      saturday: false,
      sunday: false
    },
    // Monthly schedule - simplified to 1st of every month
    monthlySchedule: '1st', // Always 1st day of month for monthly schedules
    smsContentTemplate: `Hi [CUSTOMER_NAME]! Regular reminder about [PET_NAME]'s care. Visit us for checkups, vaccines & more. Call (555) 123-4567`,
    smsLink: 'https://petsandvetsanimalhospital.com', // Default website link
    enableSmsLink: false, // Whether to include SMS link in message
    postTime: '09:00',
    postDestination: 'in_review',
    smsType: 'bulk',
    selectedCustomer: null, // For single SMS mode
    smsCharacterLimit: 160,
    // Image Options
    imageOption: 'none', // 'ai_generate', 'upload', 's3_browse', 'none'
    imageFormat: 'landscape', // 'portrait', 'landscape', 'square'
    uploadedImages: [],
    enableCustomImageText: false,
    customImageBannerText: ''
  });

  // Adhoc Mode State
  const [adhocMode, setAdhocMode] = useState({
    agentName: '',
    topic: '',
    customTopic: '',
    wordCount: '100',
    postDate: '',
    postTime: '09:00',
    enableScheduling: false, // Checkbox to enable scheduling
    imageOption: 'ai_generate', // 'ai_generate', 'upload', 'reference', 'none'
    uploadedImages: [],
    // AI Image Format Selection - format for each platform
    platformImageFormats: {}, // e.g., { instagram: 'post', facebook: 'cover_photo', twitter: 'landscape' }
    socialPlatforms: {
      facebook: false,
      instagram: false,
      twitter: false,
      tiktok: false,
      whatsapp: false
    },
    imageText: '[BUSINESS_NAME] [PHONE_NUMBER]',
    enableCustomImageText: false, // Checkbox to enable custom image text
    customImageBannerText: '[BUSINESS_NAME] [PHONE_NUMBER]', // Custom text banner for image
    useWebResearch: false, // Checkbox for web research
    useChatGPTFormatting: true, // Option to use/not use ChatGPT formatting
    postDestination: 'in_review', // 'auto_post', 'in_review', or 'ready_to_publish'
    enableSocialLink: false, // Whether to include link in social media post
    socialLink: 'https://petsandvetsanimalhospital.com' // Default website link
  });

  // Write Your Post Mode State
  const [writePostMode, setWritePostMode] = useState({
    agentName: '',
    postTitle: '', // Post title field
    postContent: '', // Big text area for post details (replacing topic field)
    wordCount: '100',
    postDate: '',
    postTime: '09:00',
    imageText: '[BUSINESS_NAME] [PHONE_NUMBER]',
    customImageBannerText: '[BUSINESS_NAME] [PHONE_NUMBER]', // Custom text banner for image
    imageOption: 'ai_generate', // 'ai_generate', 'upload', 'reference', 'none'
    uploadedImages: [],
    // AI Image Format Selection - format for each platform
    platformImageFormats: {}, // e.g., { instagram: 'post', facebook: 'cover_photo', twitter: 'landscape' }
    socialPlatforms: {
      facebook: false,
      instagram: false,
      twitter: false,
      tiktok: false,
      whatsapp: false
    },
    enableCustomImageText: false, // Checkbox to enable custom image text
    enableScheduling: false, // Checkbox to enable scheduling
    useWebResearch: false, // Checkbox for web research
    useChatGPTFormatting: true, // Option to use/not use ChatGPT formatting
    postDestination: 'in_review', // 'auto_post', 'in_review', or 'ready_to_publish'
    enableSocialLink: false, // Whether to include link in social media post
    socialLink: 'https://petsandvetsanimalhospital.com' // Default website link
  });

  // Marketing Agent State
  const [marketingMode, setMarketingMode] = useState({
    agentName: '',
    contentType: 'topic', // 'topic', 'holidays', 'custom_campaign'
    selectedTopic: '',
    selectedHolidays: [], // Array of holiday IDs
    customCampaign: `🎉 Special Offer Alert! 🎉

We're excited to announce a limited-time promotion for your furry friends! 

🐾 20% OFF all pet checkups this month
🐾 FREE nail trimming with any visit
🐾 Complimentary health consultation

Your pet's health is our priority. Book now to ensure [PET_NAME] gets the best care possible.

📞 Call us at (555) 123-4567
🌐 Visit: [LINK]

*Offer valid until end of month. Terms and conditions apply.

#PetHealth #VeterinaryCare #SpecialOffer`,
    
    // Channel Selection
    channels: [], // ['social_media', 'email', 'sms']
    activeTab: 'social_media', // 'social_media', 'email', 'sms' - for tab display
    
    // Social Media Settings
    socialPlatforms: {
      facebook: false,
      instagram: false,
      twitter: false,
      tiktok: false,
      whatsapp: false
    },
    
    // Email Settings
    emailPersonalized: true,
    emailIncludeImage: false,
    emailTemplate: `Subject: Special Offer for [CUSTOMER_NAME] and [PET_NAME]!

Dear [CUSTOMER_NAME],

We hope you and [PET_NAME] are doing well! 

We're excited to share a special offer just for you:

🎉 20% OFF all pet checkups this month
🎉 FREE nail trimming with any visit  
🎉 Complimentary health consultation

Your pet's health is our top priority, and we'd love to see [PET_NAME] for a wellness check.

To schedule your appointment:
📞 Call us at (555) 123-4567
🌐 Visit our website: [LINK]

Thank you for trusting us with [PET_NAME]'s care!

Best regards,
Your Veterinary Team`,
    
    // SMS Settings  
    smsPersonalized: true,
    smsIncludeImage: false,
    smsTemplate: `Hi [CUSTOMER_NAME]! 🐾 Special offer for [PET_NAME]: 20% off checkups this month + FREE nail trim! Call (555) 123-4567 or visit [LINK] to book. Limited time only!`,
    
    // Campaign Settings
    marketingLink: 'https://petsandvetsanimalhospital.com',
    enableScheduling: false, // Checkbox to enable/disable scheduling
    scheduledDate: '',
    postTime: '09:00',
    workflowMode: 'in_review', // 'in_review', 'ready_to_publish', 'auto_publish'
    
    // Image Options
    imageOption: 'ai_generate', // 'ai_generate', 'upload', 'reference', 'none'
    uploadedImages: [],
    // AI Image Format Selection - format for each platform
    platformImageFormats: {}, // e.g., { instagram: 'post', facebook: 'cover_photo', twitter: 'landscape' }
    imageText: 'Happy pets getting veterinary care, professional clinic setting',
    enableCustomImageText: false, // Checkbox to enable custom image text
    customImageBannerText: '[BUSINESS_NAME] [PHONE_NUMBER]', // Separate field for image banner text
    wordCount: '100',
    
    // ChatGPT Formatting Options (only for custom_campaign)
    useChatGPTFormatting: false, // For main campaign content
    emailUseChatGPT: false, // For email content formatting
    smsUseChatGPT: false // For SMS content formatting
  });

  const [isFormattingContent, setIsFormattingContent] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isRunMode, setIsRunMode] = useState(false);
  const [editingAgentMode, setEditingAgentMode] = useState(null);
  const [editingAgentData, setEditingAgentData] = useState(null);
  const [showSaveModal, setShowSaveModal] = useState(false);
  
  // S3 Asset Browser States
  const [showAssetBrowser, setShowAssetBrowser] = useState(false);
  const [currentImageMode, setCurrentImageMode] = useState(''); // To track which form is using the asset browser

  // Social Media Platform Configuration (same as AIChatbot)
  const socialMediaPlatforms = {
    instagram: {
      name: 'Instagram',
      icon: '📷',
      formats: {
        post: 'Post',
        profile_picture: 'Profile Picture',
        landscape: 'Landscape',
        square: 'Square',
        story_reels: 'Story/Reels',
        cover_photo: 'Cover Photo'
      }
    },
    facebook: {
      name: 'Facebook',
      icon: '📘',
      formats: {
        post: 'Post',
        profile_picture: 'Profile Picture',
        landscape: 'Landscape',
        square: 'Square',
        story_reels: 'Stories and Reels',
        cover_photo: 'Cover Photo'
      }
    },
    tiktok: {
      name: 'TikTok',
      icon: '🎵',
      formats: {
        post: 'Post',
        profile_picture: 'Profile Picture',
        landscape: 'Landscape',
        square: 'Square',
        story_reels: 'Stories'
      }
    },
    twitter: {
      name: 'X (Twitter)',
      icon: '🐦',
      formats: {
        post: 'Post',
        profile_picture: 'Profile Picture',
        landscape: 'Landscape',
        square: 'Square',
        cover_photo: 'Cover Photo'
      }
    },
    whatsapp: {
      name: 'WhatsApp',
      icon: '💬',
      formats: {
        post: 'Post'
      }
    }
  };

  // Social Media Image Settings State
  const [socialMediaImageSettings, setSocialMediaImageSettings] = useState({
    instagram: {
      post: { width: 1080, height: 1080, include_signature: true, include_text: true },
      profile_picture: { width: 320, height: 320, include_signature: false, include_text: false },
      landscape: { width: 1080, height: 566, include_signature: true, include_text: true },
      square: { width: 1080, height: 1080, include_signature: true, include_text: true },
      story_reels: { width: 1080, height: 1920, include_signature: true, include_text: true },
      cover_photo: { width: 820, height: 312, include_signature: false, include_text: false }
    },
    facebook: {
      post: { width: 1080, height: 1080, include_signature: true, include_text: true },
      profile_picture: { width: 500, height: 500, include_signature: false, include_text: false },
      landscape: { width: 1080, height: 566, include_signature: true, include_text: true },
      square: { width: 1080, height: 1080, include_signature: true, include_text: true },
      story_reels: { width: 1080, height: 1920, include_signature: true, include_text: true },
      cover_photo: { width: 851, height: 315, include_signature: false, include_text: false }
    },
    tiktok: {
      post: { width: 1080, height: 1920, include_signature: true, include_text: true },
      profile_picture: { width: 20, height: 20, include_signature: false, include_text: false },
      landscape: { width: 1920, height: 1080, include_signature: true, include_text: true },
      square: { width: 640, height: 640, include_signature: true, include_text: true },
      story_reels: { width: 1080, height: 1920, include_signature: true, include_text: true }
    },
    twitter: {
      post: { width: 1080, height: 1080, include_signature: true, include_text: true },
      profile_picture: { width: 400, height: 400, include_signature: false, include_text: false },
      landscape: { width: 1280, height: 720, include_signature: true, include_text: true },
      square: { width: 720, height: 720, include_signature: true, include_text: true },
      cover_photo: { width: 1500, height: 500, include_signature: false, include_text: false }
    },
    whatsapp: {
      post: { width: 1080, height: 1080, include_signature: true, include_text: true }
    },
    email: {
      portrait: { width: 1080, height: 1920, include_signature: true, include_text: true },
      landscape: { width: 1280, height: 720, include_signature: true, include_text: true },
      square: { width: 600, height: 600, include_signature: true, include_text: true }
    },
    sms: {
      portrait: { width: 1080, height: 1920, include_signature: true, include_text: true },
      landscape: { width: 1280, height: 720, include_signature: true, include_text: true },
      square: { width: 600, height: 600, include_signature: true, include_text: true }
    }
  });

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || (window.location.origin.includes("localhost") ? "http://localhost:8000" : window.location.origin + "/pandvah-backend");

  // Check for edit/run mode and pre-populate form
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const mode = urlParams.get('mode');
    const agentId = urlParams.get('id');

    if ((mode === 'edit' || mode === 'run') && agentId) {
      // Get agent data from localStorage - check both edit and run storage
      const editingAgent = mode === 'edit' 
        ? localStorage.getItem('editingAgent')
        : localStorage.getItem('runningAgent');
      console.log('Raw localStorage data:', editingAgent);
      
      if (editingAgent) {
        try {
          const agentData = JSON.parse(editingAgent);
          console.log('Parsed agent data:', agentData);
          
          if (mode === 'edit') {
            setIsEditMode(true);
          } else if (mode === 'run') {
            setIsRunMode(true);
          }
          setEditingAgentMode(agentData.mode);
          setEditingAgentData(agentData);
          
          // Set the active tab based on agent mode and agent type
          // Handle legacy "auto" mode by mapping it to "recurring"
          const tabMode = agentData.mode === 'auto' ? 'recurring' : agentData.mode;
          
          // Determine the correct tab ID based on agent type and mode
          let correctTabId;
          if (agentData.agent_type === 'time_sheet') {
            correctTabId = tabMode === 'adhoc' ? 'timesheet-adhoc' : 'timesheet-recurring';
          } else if (agentData.agent_type === 'paystub_agent') {
            correctTabId = tabMode === 'paystub_adhoc' ? 'paystub-adhoc' : 'paystub-recurring';
          } else if (agentData.agent_type === 'email') {
            if (tabMode === 'write') {
              correctTabId = 'email-write';
            } else if (tabMode === 'recurring' && agentData.selected_holidays?.length > 0) {
              correctTabId = 'email-scheduled'; // Holiday-based emails use scheduled tab
            } else {
              correctTabId = 'email-recurring'; // Topic-based recurring emails
            }
          } else if (agentData.agent_type === 'sms_agent') {
            if (tabMode === 'write') {
              correctTabId = 'sms-write';
            } else if (tabMode === 'recurring' && agentData.selected_holidays?.length > 0) {
              correctTabId = 'sms-scheduled'; // Holiday-based SMS use scheduled tab
            } else {
              correctTabId = 'sms-recurring'; // Topic-based recurring SMS
            }
          } else if (agentData.agent_type === 'marketing_agent') {
            // Marketing agents don't use tabs, they have a single form
            correctTabId = null;
          } else {
            // Social media agents
            correctTabId = `social-media-${tabMode}`;
          }
          
          if (correctTabId) {
            setActiveTab(correctTabId);
          }
          
          // Pre-populate form fields based on agent type and mode
          if (agentData.agent_type === 'marketing_agent') {
            // Marketing Agent - populate marketingMode state
            setMarketingMode({
              agentName: agentData.agent_name || agentData.name || '',
              contentType: agentData.marketing_content_type || 'topic',
              selectedTopic: agentData.topic || '',
              customTopic: agentData.custom_topic || '',
              customCampaign: agentData.marketing_custom_campaign || '',
              selectedHolidays: agentData.selected_holidays || [],
              channels: agentData.marketing_channels || [],
              socialPlatforms: {
                facebook: Array.isArray(agentData.marketing_social_platforms)
                  ? agentData.marketing_social_platforms.includes('facebook')
                  : agentData.marketing_social_platforms?.facebook || false,
                instagram: Array.isArray(agentData.marketing_social_platforms)
                  ? agentData.marketing_social_platforms.includes('instagram')
                  : agentData.marketing_social_platforms?.instagram || false,
                twitter: Array.isArray(agentData.marketing_social_platforms)
                  ? agentData.marketing_social_platforms.includes('x') || agentData.marketing_social_platforms.includes('twitter')
                  : agentData.marketing_social_platforms?.twitter || false,
                tiktok: Array.isArray(agentData.marketing_social_platforms)
                  ? agentData.marketing_social_platforms.includes('tiktok')
                  : agentData.marketing_social_platforms?.tiktok || false,
                whatsapp: Array.isArray(agentData.marketing_social_platforms)
                  ? agentData.marketing_social_platforms.includes('whatsapp')
                  : agentData.marketing_social_platforms?.whatsapp || false
              },
              emailPersonalized: agentData.marketing_email_personalized !== undefined ? agentData.marketing_email_personalized : true,
              emailIncludeImage: agentData.marketing_email_include_image || false,
              emailTemplate: agentData.email_content_template || '',
              smsPersonalized: agentData.marketing_sms_personalized !== undefined ? agentData.marketing_sms_personalized : true,
              smsIncludeImage: agentData.marketing_sms_include_image || false,
              smsTemplate: agentData.sms_template || '',
              enableScheduling: !!(agentData.post_date), // Enable if there's a scheduled date
              scheduledDate: agentData.post_date || '',
              postTime: agentData.post_time || '09:00',
              marketingLink: agentData.marketing_link || '',
              imageOption: agentData.image_option || 'ai_generate',
              uploadedImages: [],
              platformImageFormats: agentData.platform_image_formats || {},
              imageText: agentData.image_text || '',
              enableCustomImageText: agentData.enable_custom_image_text || false,
              customImageBannerText: agentData.custom_image_banner_text || '[BUSINESS_NAME] [PHONE_NUMBER]',
              wordCount: agentData.word_count?.toString() || '100',
              workflowMode: agentData.marketing_workflow_mode || 'in_review',
              // ChatGPT Formatting Options
              useChatGPTFormatting: agentData.use_chatgpt_formatting || false,
              emailUseChatGPT: agentData.marketing_email_use_chatgpt || false,
              smsUseChatGPT: agentData.marketing_sms_use_chatgpt || false
            });
          } else if (agentData.mode === 'auto' || agentData.mode === 'recurring' || agentData.mode === 'paystub_recurring') {
            setRecurringMode({
              agentName: agentData.name || agentData.agent_name || '',
              topic: agentData.topic || '',
              customTopic: agentData.custom_topic || '',
              imageOption: agentData.image_option || 'ai_generate',
              uploadedImages: [],
              platformImageFormats: agentData.platform_image_formats || {},
              frequency: agentData.frequency?.toString() || '24',
              socialPlatforms: {
                facebook: Array.isArray(agentData.social_platforms) 
                  ? agentData.social_platforms.includes('facebook')
                  : agentData.social_platforms?.facebook || false,
                instagram: Array.isArray(agentData.social_platforms)
                  ? agentData.social_platforms.includes('instagram')
                  : agentData.social_platforms?.instagram || false,
                twitter: Array.isArray(agentData.social_platforms)
                  ? agentData.social_platforms.includes('x') || agentData.social_platforms.includes('twitter')
                  : agentData.social_platforms?.twitter || false,
                tiktok: Array.isArray(agentData.social_platforms)
                  ? agentData.social_platforms.includes('tiktok')
                  : agentData.social_platforms?.tiktok || false,
                whatsapp: Array.isArray(agentData.social_platforms)
                  ? agentData.social_platforms.includes('whatsapp')
                  : agentData.social_platforms?.whatsapp || false
              },
              postTime: agentData.post_time || '09:00',
              imageText: agentData.image_text || '',
              enableCustomImageText: agentData.enable_custom_image_text || false,
              customImageBannerText: agentData.custom_image_banner_text || '[BUSINESS_NAME] [PHONE_NUMBER]',
              imageFormat: agentData.image_format || 'landscape',
              wordCount: agentData.word_count?.toString() || '150',
              scheduleType: agentData.schedule_type || (
                // Smart default based on existing data
                agentData.days_of_week && Object.values(agentData.days_of_week).some(day => day) && 
                !Object.values(agentData.days_of_week).every(day => day) 
                  ? 'selected_days' 
                  : 'all_days'
              ),
              daysOfWeek: agentData.days_of_week || { monday: true, tuesday: true, wednesday: true, thursday: true, friday: true, saturday: true, sunday: true },
              postDestination: agentData.auto_post ? 'auto_post' : (agentData.post_destination || 'in_review'),
              enableSocialLink: agentData.enable_social_link || false,
              socialLink: agentData.social_link || 'https://petsandvetsanimalhospital.com'
            });
            
            // Set the selectedCustomer display state for editing mode
            if (agentData.selected_sms_customer && availableCustomers) {
              const customer = availableCustomers.find(c => c.id === agentData.selected_sms_customer);
              if (customer) {
                setSelectedCustomer(customer);
              }
            }
          } else if (agentData.mode === 'adhoc' || agentData.mode === 'paystub_adhoc') {
            setAdhocMode({
              agentName: agentData.name || agentData.agent_name || '',
              topic: agentData.topic || '',
              customTopic: agentData.custom_topic || '',
              imageOption: agentData.image_option || 'ai_generate',
              uploadedImages: [],
              platformImageFormats: agentData.platform_image_formats || {},
              socialPlatforms: {
                facebook: Array.isArray(agentData.social_platforms) 
                  ? agentData.social_platforms.includes('facebook')
                  : agentData.social_platforms?.facebook || false,
                instagram: Array.isArray(agentData.social_platforms)
                  ? agentData.social_platforms.includes('instagram')
                  : agentData.social_platforms?.instagram || false,
                twitter: Array.isArray(agentData.social_platforms)
                  ? agentData.social_platforms.includes('x') || agentData.social_platforms.includes('twitter')
                  : agentData.social_platforms?.twitter || false,
                tiktok: Array.isArray(agentData.social_platforms)
                  ? agentData.social_platforms.includes('tiktok')
                  : agentData.social_platforms?.tiktok || false,
                whatsapp: Array.isArray(agentData.social_platforms)
                  ? agentData.social_platforms.includes('whatsapp')
                  : agentData.social_platforms?.whatsapp || false
              },
              wordCount: agentData.word_count?.toString() || '150',
              postDate: agentData.post_date || '',
              postTime: agentData.post_time || '09:00',
              imageText: agentData.image_text || '',
              enableScheduling: agentData.enable_scheduling || (agentData.post_date ? true : false),
              enableCustomImageText: agentData.enable_custom_image_text || false,
              customImageBannerText: agentData.custom_image_banner_text || '[BUSINESS_NAME] [PHONE_NUMBER]',
              useWebResearch: agentData.use_web_research || false,
              useChatGPTFormatting: agentData.use_chatgpt_formatting !== undefined ? agentData.use_chatgpt_formatting : true,
              postDestination: agentData.post_destination || 'in_review',
              enableSocialLink: agentData.enable_social_link || false,
              socialLink: agentData.social_link || 'https://petsandvetsanimalhospital.com'
            });
          } else if (agentData.mode === 'write') {
            setWritePostMode({
              agentName: agentData.name || agentData.agent_name || '',
              postTitle: agentData.post_title || '',
              postContent: agentData.post_content || '',
              wordCount: agentData.word_count?.toString() || '100',
              postDate: agentData.post_date || '',
              postTime: agentData.post_time || '09:00',
              imageText: agentData.image_text || '',
              imageOption: agentData.image_option || 'ai_generate',
              uploadedImages: [],
              platformImageFormats: agentData.platform_image_formats || {},
              socialPlatforms: {
                facebook: Array.isArray(agentData.social_platforms) 
                  ? agentData.social_platforms.includes('facebook')
                  : agentData.social_platforms?.facebook || false,
                instagram: Array.isArray(agentData.social_platforms)
                  ? agentData.social_platforms.includes('instagram')
                  : agentData.social_platforms?.instagram || false,
                twitter: Array.isArray(agentData.social_platforms)
                  ? agentData.social_platforms.includes('x') || agentData.social_platforms.includes('twitter')
                  : agentData.social_platforms?.twitter || false,
                tiktok: Array.isArray(agentData.social_platforms)
                  ? agentData.social_platforms.includes('tiktok')
                  : agentData.social_platforms?.tiktok || false,
                whatsapp: Array.isArray(agentData.social_platforms)
                  ? agentData.social_platforms.includes('whatsapp')
                  : agentData.social_platforms?.whatsapp || false
              },
              enableCustomImageText: agentData.enable_custom_image_text || false,
              customImageBannerText: agentData.custom_image_banner_text || '[BUSINESS_NAME] [PHONE_NUMBER]',
              enableScheduling: agentData.enable_scheduling || false,
              useWebResearch: agentData.use_web_research || false,
              useChatGPTFormatting: agentData.use_chatgpt_formatting !== undefined ? agentData.use_chatgpt_formatting : true,
              postDestination: agentData.post_destination || 'in_review',
              enableSocialLink: agentData.enable_social_link || false,
              socialLink: agentData.social_link || 'https://petsandvetsanimalhospital.com'
            });
          }
          
          // Handle timesheet agent data prepopulation
          if (agentData.agent_type === 'time_sheet') {
            if (agentData.mode === 'recurring') {
              setTimesheetRecurringMode({
                agentName: agentData.name || agentData.agent_name || '',
                selectedEmployees: agentData.selected_employees || [],
                runEveryPayPeriod: agentData.run_every_pay_period || 'weekly',
                daysAfterPeriodEnd: agentData.days_after_period_end || 1,
                scheduleTime: agentData.schedule_time || '09:00',
                includeBillingRates: agentData.include_billing_rates !== undefined ? agentData.include_billing_rates : true,
                emailRecipients: agentData.email_recipients || [],
                initialStatus: agentData.initial_status || agentData.post_destination || 'in_review',
                daysOfWeek: agentData.days_of_week || { monday: true, tuesday: true, wednesday: true, thursday: true, friday: true, saturday: true, sunday: true }
              });
            } else if (agentData.mode === 'adhoc' || agentData.mode === 'paystub_adhoc') {
              setTimesheetAdhocMode({
                agentName: agentData.name || agentData.agent_name || '',
                selectedEmployees: agentData.selected_employees || [],
                reportPeriod: agentData.report_period || 'current_week',
                customStartDate: agentData.custom_start_date || '',
                customEndDate: agentData.custom_end_date || '',
                includeBillingRates: agentData.include_billing_rates !== undefined ? agentData.include_billing_rates : true,
                emailRecipients: agentData.email_recipients || [],
                initialStatus: agentData.initial_status || agentData.post_destination || 'in_review'
              });
            }
          }
          
          // Handle paystub agent data prepopulation
          if (agentData.agent_type === 'paystub_agent') {
            if (agentData.mode === 'paystub_recurring') {
              console.log('Pre-populating paystub recurring agent:', agentData);
              console.log('days_after_timesheet_published from backend:', agentData.days_after_timesheet_published);
              
              setPaystubRecurringMode({
                agentName: agentData.name || agentData.agent_name || '',
                selectedEmployees: agentData.selected_employees || [],
                daysAfterTimesheetPublished: agentData.days_after_timesheet_published !== undefined ? agentData.days_after_timesheet_published : 0,
                workflowMode: agentData.workflow_mode || 'in_review',
                paystubTemplate: agentData.paystub_template || 'standard',
                paystubFormat: agentData.paystub_format || 'pdf'
              });
              
              console.log('Set daysAfterTimesheetPublished to:', agentData.days_after_timesheet_published !== undefined ? agentData.days_after_timesheet_published : 0);
            } else if (agentData.mode === 'paystub_adhoc') {
              setPaystubAdhocMode({
                agentName: agentData.name || agentData.agent_name || '',
                selectedEmployees: agentData.selected_employees || [],
                reportPeriod: agentData.report_period || 'current_week',
                customStartDate: agentData.custom_start_date || '',
                customEndDate: agentData.custom_end_date || '',
                selectedTimesheetId: agentData.selected_timesheet_id || '',
                workflowMode: agentData.workflow_mode || 'in_review',
                paystubTemplate: agentData.paystub_template || 'standard',
                paystubFormat: agentData.paystub_format || 'pdf'
              });
            }
          }
          
          // Handle email agent data prepopulation
          if (agentData.agent_type === 'email') {
            if (agentData.mode === 'recurring' && agentData.selected_holidays && agentData.selected_holidays.length > 0) {
              // This is a scheduled email agent (holiday-based)
              console.log('Email scheduled agent data for edit mode:', {
                use_chatgpt_formatting: agentData.use_chatgpt_formatting,
                email_content_template: agentData.email_content_template,
                email_template: agentData.email_template
              });
              
              console.log('Email Scheduled Mode - Debug image format fields:', {
                image_format: agentData.image_format,
                platform_image_formats: agentData.platform_image_formats,
                platform_image_formats_email: agentData.platform_image_formats?.email,
                agentData_keys: Object.keys(agentData)
              });
              
              setEmailScheduledMode({
                agentName: agentData.name || agentData.agent_name || '',
                selectedHolidays: agentData.selected_holidays || [],
                imageOption: agentData.image_option || 'ai_generate',
                imageFormat: agentData.platform_image_formats?.email || agentData.image_format || '',
                uploadedImages: [],
                imageText: agentData.image_text || '',
                enableCustomImageText: agentData.enable_custom_image_text || false,
                customImageBannerText: agentData.custom_image_banner_text || '[BUSINESS_NAME] [PHONE_NUMBER]',
                // wordCount field removed from email agents
                postTime: agentData.post_time || '09:00',
                useChatGPTFormatting: agentData.use_chatgpt_formatting !== undefined ? agentData.use_chatgpt_formatting : true,
                postDestination: agentData.post_destination || 'in_review',
                emailContentTemplate: agentData.email_content_template || agentData.email_template || '',
                emailType: agentData.email_type || 'bulk',
                selectedCustomer: agentData.selected_customer || null,
                customerName: '' // Will be set by the customer lookup logic below
              });
            } else if (agentData.mode === 'recurring') {
              // This is a recurring email agent (topic-based)
              const isCustomTopic = !['Trending Pet Health News', 'Trending Pet Food News', 'Pet Care Tips', 'Veterinary Updates', 'Pet Safety Alerts', 'Seasonal Pet Care', 'Pet Training Tips', 'Pet Nutrition Advice'].includes(agentData.topic);
              setEmailRecurringMode({
                agentName: agentData.name || agentData.agent_name || '',
                topic: isCustomTopic ? 'Custom' : (agentData.topic || ''),
                customTopic: isCustomTopic ? agentData.topic : '',
                // wordCount field removed from email agents
                scheduleType: agentData.schedule_type || 'weekly',
                daysOfWeek: agentData.days_of_week || { monday: false, tuesday: false, wednesday: false, thursday: false, friday: false, saturday: false, sunday: false },
                monthlySchedule: agentData.monthly_schedule || '1st',
                emailContentTemplate: agentData.email_content_template || `Dear [CUSTOMER_NAME],

Hope this message finds you and [PET_NAME] doing well!

[Your business information and content will be inserted here]

Best regards,
[Your Business Name]`,
                postTime: agentData.post_time || '09:00',
                imageOption: agentData.image_option || 'ai_generate',
                imageFormat: agentData.platform_image_formats?.email || agentData.image_format || '',
                uploadedImages: [],
                enableCustomImageText: agentData.enable_custom_image_text || false,
                customImageBannerText: agentData.custom_image_banner_text || '[BUSINESS_NAME] [PHONE_NUMBER]',
                useChatGPTFormatting: agentData.use_chatgpt_formatting !== undefined ? agentData.use_chatgpt_formatting : true,
                postDestination: agentData.post_destination || 'in_review',
                emailType: agentData.email_type || 'bulk',
                selectedCustomer: agentData.selected_customer || null,
                customerName: '' // Will be set by the customer lookup logic below
              });
            } else if (agentData.mode === 'write') {
              // This is a write mode email agent
              setEmailWriteMode({
                agentName: agentData.name || agentData.agent_name || '',
                emailContent: agentData.email_content || agentData.post_content || '',
                emailSubject: agentData.email_subject || agentData.post_title || '',
                imageOption: agentData.image_option || 'ai_generate',
                imageFormat: agentData.platform_image_formats?.email || '',
                uploadedImages: agentData.uploaded_images || [],
                enableCustomImageText: agentData.enable_custom_image_text || false,
                customImageBannerText: agentData.custom_image_banner_text || '',
                // wordCount field removed from email agents
                useChatGPTFormatting: agentData.use_chatgpt_formatting !== undefined ? agentData.use_chatgpt_formatting : true,
                postDestination: agentData.post_destination || 'in_review',
                postDate: agentData.post_date || '',
                postTime: agentData.post_time || '',
                isScheduled: agentData.is_scheduled || (agentData.post_date && agentData.post_time),
                emailType: agentData.email_type || 'bulk',
                selectedCustomer: agentData.selected_customer || null,
                customerName: '' // Will be set by the customer lookup logic below
              });

              // Set the selectedCustomer display state for editing mode (same logic as SMS)
              console.log('Email Write Edit - agentData.selected_customer:', agentData.selected_customer);
              console.log('Email Write Edit - availableCustomers length:', availableCustomers?.length);
              if (agentData.selected_customer && availableCustomers) {
                const customerId = agentData.selected_customer;
                console.log('Email Write Edit - Looking for customer ID:', customerId);
                const customer = availableCustomers.find(c => c.id === customerId);
                console.log('Email Write Edit - Found customer:', customer);
                if (customer) {
                  setSelectedCustomer(customer);
                  console.log('Email Write Edit - Set selectedCustomer to:', getCustomerFullName(customer));
                  // Also update the emailWriteMode customerName field
                  setEmailWriteMode(prev => ({ 
                    ...prev, 
                    customerName: getCustomerFullName(customer) 
                  }));
                }
              } else {
                console.log('Email Write Edit - No customer to pre-populate or availableCustomers not loaded yet');
              }
            } else if (agentData.mode === 'recurring' && agentData.selected_holidays && agentData.selected_holidays.length > 0) {
              // Email Scheduled mode customer pre-population
              if (agentData.selected_customer && availableCustomers) {
                const customer = availableCustomers.find(c => c.id === agentData.selected_customer);
                if (customer) {
                  setSelectedCustomer(customer);
                  setEmailScheduledMode(prev => ({ 
                    ...prev, 
                    customerName: getCustomerFullName(customer) 
                  }));
                }
              }
            } else if (agentData.mode === 'recurring') {
              // Email Recurring mode customer pre-population  
              if (agentData.selected_customer && availableCustomers) {
                const customer = availableCustomers.find(c => c.id === agentData.selected_customer);
                if (customer) {
                  setSelectedCustomer(customer);
                  setEmailRecurringMode(prev => ({ 
                    ...prev, 
                    customerName: getCustomerFullName(customer) 
                  }));
                }
              }
            }
          }

          // Handle SMS agent data prepopulation
          if (agentData.agent_type === 'sms_agent') {
            if (agentData.mode === 'recurring' && agentData.selected_holidays && agentData.selected_holidays.length > 0) {
              // This is a scheduled SMS agent (holiday-based)
              setSmsScheduledMode({
                agentName: agentData.name || agentData.agent_name || '',
                selectedHolidays: agentData.selected_holidays || [],
                smsContentTemplate: agentData.sms_template || agentData.sms_content_template || '',
                smsLink: agentData.sms_link || 'https://petsandvetsanimalhospital.com',
                enableSmsLink: agentData.enable_sms_link || false,
                postTime: agentData.post_time || '09:00',
                postDestination: agentData.post_destination || 'in_review',
                useCustomerDatabase: agentData.use_customer_database !== undefined ? agentData.use_customer_database : true,
                smsType: agentData.sms_type || 'bulk',
                selectedCustomer: agentData.selected_sms_customer || null,
                smsCharacterLimit: agentData.sms_character_limit || 160,
                // Missing image-related fields
                imageOption: agentData.image_option || 'none',
                imageFormat: agentData.platform_image_formats?.sms || '',
                enableCustomImageText: agentData.enable_custom_image_text || false,
                customImageBannerText: agentData.custom_image_banner_text || '[BUSINESS_NAME] [PHONE_NUMBER]',
                uploadedImages: agentData.uploaded_images || []
              });
            } else if (agentData.mode === 'recurring') {
              // This is a recurring SMS agent (topic-based)
              const isCustomTopic = !['Pet vaccination reminders', 'Appointment reminders', 'Health checkup alerts', 'Special offers', 'Holiday greetings', 'Pet care tips', 'Emergency updates'].includes(agentData.topic);
              setSmsRecurringMode({
                agentName: agentData.name || agentData.agent_name || '',
                topic: isCustomTopic ? 'Custom' : (agentData.topic || ''),
                customTopic: isCustomTopic ? agentData.topic : '',
                scheduleType: agentData.schedule_type || 'weekly',
                daysOfWeek: agentData.days_of_week || { monday: false, tuesday: false, wednesday: false, thursday: false, friday: false, saturday: false, sunday: false },
                monthlySchedule: agentData.monthly_schedule || '1st',
                smsContentTemplate: agentData.sms_template || agentData.sms_content_template || '',
                smsLink: agentData.sms_link || 'https://petsandvetsanimalhospital.com',
                enableSmsLink: agentData.enable_sms_link || false,
                postTime: agentData.post_time || '09:00',
                postDestination: agentData.post_destination || 'in_review',
                smsType: agentData.sms_type || 'bulk',
                selectedCustomer: agentData.selected_sms_customer || null,
                smsCharacterLimit: agentData.sms_character_limit || 160,
                // Missing image-related fields
                imageOption: agentData.image_option || 'none',
                imageFormat: agentData.platform_image_formats?.sms || '',
                enableCustomImageText: agentData.enable_custom_image_text || false,
                customImageBannerText: agentData.custom_image_banner_text || '[BUSINESS_NAME] [PHONE_NUMBER]',
                uploadedImages: agentData.uploaded_images || []
              });
            } else if (agentData.mode === 'write') {
              // This is a write mode SMS agent
              setSmsWriteMode({
                agentName: agentData.name || agentData.agent_name || '',
                smsContent: agentData.sms_content || agentData.post_content || '',
                smsSubject: agentData.sms_subject || agentData.post_title || '', // This is now ChatGPT Content
                smsTemplate: agentData.sms_template || '', // Load selected SMS template
                smsLink: agentData.sms_link || 'https://petsandvetsanimalhospital.com',
                enableSmsLink: agentData.enable_sms_link || false,
                useSMSChatGPTFormatting: agentData.use_sms_chatgpt_formatting !== undefined ? agentData.use_sms_chatgpt_formatting : true,
                postDestination: agentData.post_destination || 'in_review',
                postDate: agentData.post_date || '',
                postTime: agentData.post_time || '',
                smsType: agentData.sms_type || 'bulk',
                selectedCustomer: agentData.selected_sms_customer || null,
                smsCharacterLimit: agentData.sms_character_limit || 160,
                // Missing image-related fields
                imageOption: agentData.image_option || 'none',
                imageFormat: agentData.platform_image_formats?.sms || '',
                enableCustomImageText: agentData.enable_custom_image_text || false,
                customImageBannerText: agentData.custom_image_banner_text || '[BUSINESS_NAME] [PHONE_NUMBER]',
                uploadedImages: agentData.uploaded_images || []
              });
              
              // Set the selectedCustomer display state for editing mode
              if (agentData.selected_sms_customer && availableCustomers) {
                const customer = availableCustomers.find(c => c.id === agentData.selected_sms_customer);
                if (customer) {
                  setSelectedCustomer(customer);
                }
              }
            }
          }
          
          setMessage({ type: 'info', text: `Editing agent: ${agentData.name || agentData.agent_name || 'Unknown Agent'}` });
        } catch (error) {
          console.error('Error parsing agent data:', error);
          setMessage({ type: 'error', text: `Error loading agent data: ${error.message}` });
        }
      } else {
        console.log('No editingAgent found in localStorage');
        setMessage({ type: 'error', text: 'No agent data found for editing. Please try again from the dashboard.' });
      }
    }
  }, []);

  const topicOptions = [
    'Trending Pet Health News',
    'Trending Pet Food News', 
    'Pet Care Tips',
    'Veterinary Updates',
    'Pet Safety Alerts',
    'Seasonal Pet Care',
    'Pet Training Tips',
    'Pet Nutrition Advice',
    'Custom'
  ];

  const wordCountOptions = [
    { value: '50', label: '<50 words' },
    { value: '75', label: '<75 words' },
    { value: '100', label: '<100 words' },
    { value: '150', label: '<150 words' },
    { value: '200', label: '<200 words' }
  ];

  const frequencyOptions = [
    { value: '1', label: 'Every 1 hour' },
    { value: '2', label: 'Every 2 hours' },
    { value: '4', label: 'Every 4 hours' },
    { value: '6', label: 'Every 6 hours' },
    { value: '8', label: 'Every 8 hours' },
    { value: '12', label: 'Every 12 hours' },
    { value: '24', label: 'Every 24 hours' }
  ];

  // Get tabs based on agent type
  const getTabs = () => {
    const agentType = searchParams.get('agent_type');
    
    if (agentType === 'time_sheet') {
      // Timesheet AI Agent tabs
      return [
        { 
          id: 'timesheet-recurring', 
          label: 'Recurring Mode', 
          icon: Timer, 
          color: 'var(--theme-primary)',
          description: 'Generate timesheets automatically on a schedule'
        },
        { 
          id: 'timesheet-adhoc', 
          label: 'Adhoc Mode', 
          icon: ClipboardList, 
          color: '#f59e0b',
          description: 'Generate timesheets on-demand when needed'
        }
      ];
    }
    
    if (agentType === 'paystub_agent') {
      // Paystub AI Agent tabs
      return [
        { 
          id: 'paystub-recurring', 
          label: 'Recurring Mode', 
          icon: Timer, 
          color: '#10b981',
          description: 'Generate paystubs automatically when timesheets are published'
        },
        { 
          id: 'paystub-adhoc', 
          label: 'Adhoc Mode', 
          icon: ClipboardList, 
          color: '#f59e0b',
          description: 'Generate paystubs on-demand for specific timesheets'
        }
      ];
    } else if (agentType === 'marketing_agent') {
      // Marketing Agent - no tabs needed, just the campaign form
      return [];
    } else if (agentType === 'email') {
      // Email Agent tabs with specific IDs to avoid conflicts
      return [
        { 
          id: 'email-scheduled', 
          label: 'Holiday Mode', 
          icon: Calendar, 
          color: '#10b981',
          description: 'Send emails automatically on holidays with personalized content'
        },
        { 
          id: 'email-recurring', 
          label: 'Recurring Mode', 
          icon: RefreshCw, 
          color: 'var(--theme-primary)',
          description: 'Send emails regularly on selected days and frequency'
        },
        { 
          id: 'email-write', 
          label: 'Write Your Email', 
          icon: Edit2, 
          color: 'var(--theme-primary)',
          description: 'Create and send custom emails with subject and content'
        }
      ];
    } else if (agentType === 'sms_agent') {
      // SMS Agent tabs
      return [
        { 
          id: 'sms-scheduled', 
          label: 'Holiday Mode', 
          icon: Calendar, 
          color: '#10b981',
          description: 'Send SMS automatically on holidays with personalized content'
        },
        { 
          id: 'sms-recurring', 
          label: 'Recurring Mode', 
          icon: RefreshCw, 
          color: 'var(--theme-primary)',
          description: 'Send SMS regularly on selected days and frequency'
        },
        { 
          id: 'sms-write', 
          label: 'Write Your SMS', 
          icon: MessageSquare, 
          color: 'var(--theme-primary)',
          description: 'Create and send custom SMS messages with content'
        }
      ];
    } else {
      // Social Media Agent tabs with specific IDs to avoid conflicts
      return [
        { 
          id: 'social-media-recurring', 
          label: 'Recurring Mode', 
          icon: RefreshCw, 
          color: 'var(--theme-primary)',
          description: 'Automatically generate and post content on a schedule'
        },
        { 
          id: 'social-media-adhoc', 
          label: 'Adhoc Mode', 
          icon: Zap, 
          color: '#f59e0b',
          description: 'Generate content on-demand with custom scheduling'
        },
        { 
          id: 'social-media-write', 
          label: 'Write Your Post', 
          icon: FileText, 
          color: 'var(--theme-primary)',
          description: 'Write your own content and let AI format it for different platforms'
        }
      ];
    }
  };

  const tabs = getTabs();

  // Placeholder Modal Functions
  const openPlaceholderModal = (targetField, updateFunction, targetElement = null) => {
    setPlaceholderModal({
      isOpen: true,
      targetField,
      updateFunction,
      targetElement: targetElement || document.activeElement
    });
  };

  const closePlaceholderModal = () => {
    setPlaceholderModal({
      isOpen: false,
      targetField: null,
      updateFunction: null,
      targetElement: null
    });
  };

  const insertPlaceholder = (placeholderCode) => {
    if (placeholderModal.updateFunction && placeholderModal.targetField) {
      // Special handling for full-screen editor
      if (placeholderModal.targetField === 'fullScreenEditor') {
        const element = placeholderModal.targetElement;
        if (element && element.tagName === 'TEXTAREA') {
          const currentValue = element.value;
          const cursorPosition = element.selectionStart || currentValue.length;
          
          // Insert at cursor position
          const newValue = currentValue.slice(0, cursorPosition) + 
                           placeholderCode + 
                           currentValue.slice(cursorPosition);
          
          // Update the full-screen editor content directly
          setFullScreenEditorContent(newValue);
          
          // Set cursor position after the inserted placeholder
          setTimeout(() => {
            if (element) {
              element.focus();
              element.setSelectionRange(cursorPosition + placeholderCode.length, cursorPosition + placeholderCode.length);
            }
          }, 0);
        } else {
          // Fallback for full-screen editor without valid element
          setFullScreenEditorContent(prev => prev + placeholderCode);
        }
      } else {
        // Regular form field handling
        if (placeholderModal.targetElement && 
            (placeholderModal.targetElement.tagName === 'TEXTAREA' || 
             placeholderModal.targetElement.tagName === 'INPUT')) {
          
          const element = placeholderModal.targetElement;
          const currentValue = element.value;
          const cursorPosition = element.selectionStart || currentValue.length;
          
          // Insert at cursor position
          const newValue = currentValue.slice(0, cursorPosition) + 
                           placeholderCode + 
                           currentValue.slice(cursorPosition);
          
          // Update the state with the new value
          placeholderModal.updateFunction(prev => ({
            ...prev,
            [placeholderModal.targetField]: newValue
          }));
          
          // Set cursor position after the inserted placeholder
          setTimeout(() => {
            if (element) {
              element.focus();
              element.setSelectionRange(cursorPosition + placeholderCode.length, cursorPosition + placeholderCode.length);
            }
          }, 0);
          
        } else {
          // Fallback: append to the end if no valid target element
          placeholderModal.updateFunction(prev => ({
            ...prev,
            [placeholderModal.targetField]: prev[placeholderModal.targetField] + placeholderCode
          }));
        }
      }
    }
    closePlaceholderModal();
  };

  // Auth check
  useEffect(() => {
    if (!authLoading && !user) {
      window.location.href = '/login';
    } else if (!authLoading) {
      setPageLoading(false);
      fetchAgents();
      
      // Fetch employees and pay periods if this is a timesheet or paystub agent
      const agentType = searchParams.get('agent_type');
      if (agentType === 'time_sheet') {
        fetchAvailableEmployees();
        fetchDefaultPayPeriods();
      } else if (agentType === 'paystub_agent') {
        fetchAvailableEmployees(); // Paystub agents also need employee data
        fetchDefaultPayPeriods(); // Paystub agents also need pay period data for configuration display
      } else if (agentType === 'email') {
        fetchAvailableHolidays();
        fetchAvailableCustomers();
      } else if (agentType === 'sms_agent') {
        fetchAvailableHolidays(); // SMS agents also use holidays for scheduled mode
        fetchAvailableCustomers(); // SMS agents also use customer database
      } else if (agentType === 'marketing_agent') {
        fetchAvailableHolidays(); // Marketing agents use holidays for holiday-based campaigns
        fetchAvailableCustomers(); // Marketing agents use customer database for personalization
      }
      
      // Fetch templates for all agent types that use email/SMS
      if (['email', 'sms_agent', 'marketing_agent'].includes(agentType)) {
        fetchTemplates();
      }
    }
  }, [user, authLoading, searchParams]);

  // Fetch all pay period settings and existing timesheet reports
  useEffect(() => {
    const fetchTimesheetData = async () => {
      const agentType = searchParams.get('agent_type');
      if (agentType === 'time_sheet' || agentType === 'paystub_agent') {
        try {
          // Fetch existing timesheet reports for adhoc mode (both timesheet and paystub agents need this)
          await fetchExistingTimesheetReports();
        } catch (error) {
          console.error('Error fetching timesheet data:', error);
        }
      }
    };

    if (!authLoading && user && token) {
      fetchTimesheetData();
    }
  }, [user, token, authLoading, searchParams]);

  // Handle agent type changes and set correct default tab
  useEffect(() => {
    const agentType = searchParams.get('agent_type');
    const agentId = searchParams.get('id'); // Fixed: use 'id' not 'agent_id'
    const mode = searchParams.get('mode');
    
    // Only set default tab if we're not editing/running an existing agent
    if (!agentId && mode !== 'edit' && mode !== 'run') {
      if (agentType === 'email') {
        setActiveTab('email-scheduled');
      } else if (agentType === 'sms_agent') {
        setActiveTab('sms-scheduled');
      } else if (agentType === 'time_sheet') {
        setActiveTab('timesheet-recurring');
      } else if (agentType === 'paystub_agent') {
        setActiveTab('paystub-recurring');
      } else {
        setActiveTab('social-media-recurring');
      }
    }
  }, [searchParams]);

  const fetchAgents = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/ai-agents`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setAgents(data);
      }
    } catch (error) {
      console.error('Error fetching agents:', error);
    }
  };

  const generateTimeOptions = () => {
    const times = [];
    for (let hour = 1; hour <= 12; hour++) {
      times.push({ value: `${hour.toString().padStart(2, '0')}:00`, label: `${hour}:00 AM` });
      if (hour !== 12) {
        times.push({ value: `${hour.toString().padStart(2, '0')}:30`, label: `${hour}:30 AM` });
      }
    }
    for (let hour = 13; hour <= 24; hour++) {
      const displayHour = hour > 12 ? hour - 12 : hour;
      const ampm = hour >= 12 ? 'PM' : 'AM';
      times.push({ 
        value: `${hour.toString().padStart(2, '0')}:00`, 
        label: `${displayHour}:00 ${ampm}` 
      });
      if (hour !== 24) {
        times.push({ 
          value: `${hour.toString().padStart(2, '0')}:30`, 
          label: `${displayHour}:30 ${ampm}` 
        });
      }
    }
    return times;
  };

  const timeOptions = generateTimeOptions();

  const handleRecurringModeChange = (field, value) => {
    setRecurringMode(prev => {
      const updated = {
        ...prev,
        [field]: value
      };
      
      // Auto-uncheck enableCustomImageText when imageOption is set to 'none'
      if (field === 'imageOption' && value === 'none') {
        updated.enableCustomImageText = false;
      }
      
      // Ensure daysOfWeek is properly initialized when switching to selected_days
      if (field === 'scheduleType' && value === 'selected_days') {
        if (!updated.daysOfWeek || typeof updated.daysOfWeek !== 'object') {
          updated.daysOfWeek = {
            monday: false,
            tuesday: false,
            wednesday: false,
            thursday: false,
            friday: false,
            saturday: false,
            sunday: false
          };
        }
      }
      
      return updated;
    });
  };

  const handleAdhocModeChange = (field, value) => {
    setAdhocMode(prev => {
      const updated = {
        ...prev,
        [field]: value
      };
      
      // Auto-uncheck enableCustomImageText when imageOption is set to 'none'
      if (field === 'imageOption' && value === 'none') {
        updated.enableCustomImageText = false;
      }
      
      return updated;
    });
  };

  // Email Agent handlers
  const handleEmailScheduledModeChange = (field, value) => {
    setEmailScheduledMode(prev => {
      const updated = {
        ...prev,
        [field]: value
      };
      
      // Auto-uncheck enableCustomImageText when imageOption is set to 'none'
      if (field === 'imageOption' && value === 'none') {
        updated.enableCustomImageText = false;
      }
      
      return updated;
    });
  };

  const handleEmailWriteModeChange = (field, value) => {
    setEmailWriteMode(prev => {
      const updated = {
        ...prev,
        [field]: value
      };
      
      // Auto-uncheck enableCustomImageText when imageOption is set to 'none'
      if (field === 'imageOption' && value === 'none') {
        updated.enableCustomImageText = false;
      }
      
      return updated;
    });
  };

  const handleEmailRecurringModeChange = (field, value) => {
    setEmailRecurringMode(prev => {
      const updated = {
        ...prev,
        [field]: value
      };
      
      // Auto-uncheck enableCustomImageText when imageOption is set to 'none'
      if (field === 'imageOption' && value === 'none') {
        updated.enableCustomImageText = false;
      }
      
      return updated;
    });
  };

  // Toggle holiday selection for scheduled email mode
  const toggleHolidaySelection = (holidayId) => {
    setEmailScheduledMode(prev => ({
      ...prev,
      selectedHolidays: prev.selectedHolidays.includes(holidayId)
        ? prev.selectedHolidays.filter(id => id !== holidayId)
        : [...prev.selectedHolidays, holidayId]
    }));
  };

  // Get category icon for holidays
  const getCategoryIcon = (category) => {
    switch (category) {
      case 'pet': return Heart;
      case 'veterinary': return Stethoscope;
      case 'family': return Gift;
      default: return Calendar;
    }
  };

  const handleWritePostModeChange = (field, value) => {
    setWritePostMode(prev => {
      const updated = {
        ...prev,
        [field]: value
      };
      
      // Auto-uncheck enableCustomImageText when imageOption is set to 'none'
      if (field === 'imageOption' && value === 'none') {
        updated.enableCustomImageText = false;
      }
      
      return updated;
    });
  };

  const handleSocialPlatformChange = (platform, checked, mode = 'recurring') => {
    console.log(`Social platform change: ${platform} = ${checked}, mode = ${mode}`);
    
    let stateUpdater;
    if (mode === 'recurring') stateUpdater = setRecurringMode;
    else if (mode === 'adhoc') stateUpdater = setAdhocMode;
    else if (mode === 'write') stateUpdater = setWritePostMode;
    
    stateUpdater(prev => {
      const newState = {
        ...prev,
        socialPlatforms: {
          ...prev.socialPlatforms,
          [platform]: checked
        }
      };
      console.log(`Updated ${mode} socialPlatforms:`, newState.socialPlatforms);
      return newState;
    });
  };

  const handleDayOfWeekChange = (day, checked) => {
    setRecurringMode(prev => ({
      ...prev,
      daysOfWeek: {
        ...prev.daysOfWeek,
        [day]: checked
      }
    }));
  };

  // Timesheet-specific handlers
  const handleTimesheetRecurringModeChange = (field, value) => {
    setTimesheetRecurringMode(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleTimesheetAdhocModeChange = (field, value) => {
    setTimesheetAdhocMode(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Paystub-specific handlers
  const handlePaystubRecurringModeChange = (field, value) => {
    setPaystubRecurringMode(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePaystubAdhocModeChange = (field, value) => {
    setPaystubAdhocMode(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleTimesheetDayOfWeekChange = (day, checked) => {
    setTimesheetRecurringMode(prev => ({
      ...prev,
      daysOfWeek: {
        ...prev.daysOfWeek,
        [day]: checked
      }
    }));
  };

  const handleEmployeeToggle = (employeeId, mode = 'recurring') => {
    let stateUpdater;
    
    // Support timesheet and paystub modes
    if (mode === 'recurring') {
      stateUpdater = setTimesheetRecurringMode;
    } else if (mode === 'adhoc') {
      stateUpdater = setTimesheetAdhocMode;
    } else if (mode === 'paystub_recurring') {
      stateUpdater = setPaystubRecurringMode;
    } else if (mode === 'paystub_adhoc') {
      stateUpdater = setPaystubAdhocMode;
    }
    
    if (stateUpdater) {
      stateUpdater(prev => ({
        ...prev,
        selectedEmployees: prev.selectedEmployees.includes(employeeId)
          ? prev.selectedEmployees.filter(id => id !== employeeId)
          : [...prev.selectedEmployees, employeeId]
      }));
    }
  };

  const addEmailRecipient = (mode = 'recurring') => {
    if (newEmailRecipient && newEmailRecipient.includes('@')) {
      const stateUpdater = mode === 'recurring' ? setTimesheetRecurringMode : setTimesheetAdhocMode;
      stateUpdater(prev => ({
        ...prev,
        emailRecipients: [...prev.emailRecipients, newEmailRecipient.trim()]
      }));
      setNewEmailRecipient('');
    }
  };

  const removeEmailRecipient = (index, mode = 'recurring') => {
    const stateUpdater = mode === 'recurring' ? setTimesheetRecurringMode : setTimesheetAdhocMode;
    stateUpdater(prev => ({
      ...prev,
      emailRecipients: prev.emailRecipients.filter((_, i) => i !== index)
    }));
  };

  // Fetch available employees for timesheet agents
  const fetchAvailableEmployees = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/employees`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const employees = await response.json();
        setAvailableEmployees(employees);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  // Fetch default pay period settings
  const fetchDefaultPayPeriods = async () => {
    try {
      // Get all pay period settings
      const response = await fetch(`${API_BASE_URL}/api/pay-period-settings`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const allSettings = await response.json();
        
        // Create a Map to ensure only one setting per period_type
        const periodTypeMap = new Map();
        
        // First pass: prioritize default settings
        allSettings.forEach(setting => {
          if (setting.is_default) {
            periodTypeMap.set(setting.period_type, setting);
          }
        });
        
        // Second pass: add non-defaults only if period_type not already exists
        allSettings.forEach(setting => {
          if (!periodTypeMap.has(setting.period_type)) {
            periodTypeMap.set(setting.period_type, setting);
          }
        });
        
        // Convert Map values to array and ensure unique period types
        const uniqueSettings = Array.from(periodTypeMap.values());
        
        setDefaultPayPeriods(uniqueSettings);
        
        // Find the default setting among unique settings
        const defaultSetting = uniqueSettings.find(setting => setting.is_default);
        
        if (defaultSetting) {
          setSelectedPayPeriod(defaultSetting);
          setTimesheetRecurringMode(prev => ({
            ...prev,
            runEveryPayPeriod: defaultSetting.period_type
          }));
        } else if (uniqueSettings.length > 0) {
          // If no default found, use the first unique setting
          setSelectedPayPeriod(uniqueSettings[0]);
          setTimesheetRecurringMode(prev => ({
            ...prev,
            runEveryPayPeriod: uniqueSettings[0].period_type
          }));
        }
      }
    } catch (error) {
      console.error('Error fetching default pay periods:', error);
    }
  };

  // Fetch existing timesheet reports for adhoc mode
  const fetchExistingTimesheetReports = async () => {
    try {
      console.log('Fetching published posts including timesheets...');
      
      // Use the same endpoint as AIPublishedPosts page - this gets actual published content
      const params = new URLSearchParams({
        limit: '50' // Get more posts to find timesheets
      });
      
      const response = await fetch(`${API_BASE_URL}/api/ai-posts/published?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('Published posts data:', data);
        
        const publishedPosts = data.posts || data;
        console.log('All published posts:', publishedPosts);
        
        // Filter for timesheet posts only
        const timesheetPosts = publishedPosts.filter(post => {
          // Check if it's a timesheet post by looking at platforms or post type
          const isTimesheetPost = post.platforms?.includes('timesheet') || 
                                post.post_type === 'timesheet' ||
                                post.agent_type === 'time_sheet' ||
                                (post.agent_name && post.agent_name.toLowerCase().includes('timesheet'));
          
          console.log('Post:', post.id, 'Agent:', post.agent_name, 'Platforms:', post.platforms, 'IsTimesheet:', isTimesheetPost);
          return isTimesheetPost;
        });
        
        console.log('Filtered timesheet posts:', timesheetPosts);
        
        // Sort by created_at or published_at (newest first) and limit to latest 10
        const sortedTimesheets = timesheetPosts
          .sort((a, b) => {
            const dateA = new Date(a.published_at || a.created_at || a.scheduled_time);
            const dateB = new Date(b.published_at || b.created_at || b.scheduled_time);
            return dateB - dateA; // Descending order (newest first)
          })
          .slice(0, 10); // Limit to latest 10
        
        console.log('Latest 10 timesheet posts (sorted by date):', sortedTimesheets);
        
        // Transform to the format expected by the dropdown
        const formattedReports = sortedTimesheets.map((post, index) => {
          // Extract date range from timesheet_data if available
          let startDate, endDate;
          
          if (post.timesheet_data) {
            startDate = post.timesheet_data.start_date;
            endDate = post.timesheet_data.end_date;
          } else {
            // Fallback: use created/published date
            const postDate = new Date(post.published_at || post.created_at);
            startDate = postDate.toISOString().split('T')[0];
            endDate = startDate;
          }
          
          return {
            id: `timesheet_${post.id}`,
            label: `${post.agent_name || 'Timesheet'} - ${formatDateRange(
              startDate, 
              endDate
            )}`,
            start_date: startDate,
            end_date: endDate,
            period_type: post.timesheet_data?.report_period || 'custom',
            report_id: post.id,
            agent_name: post.agent_name || 'Timesheet Report',
            status: 'published',
            published_at: post.published_at || post.created_at
          };
        });
        
        console.log('Final formatted timesheet reports for dropdown (latest 10):', formattedReports);
        setExistingTimesheetReports(formattedReports);
      } else {
        console.error('Failed to fetch published posts:', response.status, response.statusText);
        const errorText = await response.text();
        console.error('Error response body:', errorText);
      }
    } catch (error) {
      console.error('Error fetching published timesheet posts:', error);
    }
  };

  // Fetch available holidays for email agents
  const fetchAvailableHolidays = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/holidays/enabled`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const holidays = await response.json();
        setAvailableHolidays(holidays);
      }
    } catch (error) {
      console.error('Error fetching holidays:', error);
    }
  };

  // useEffect to sync selectedCustomer display state when availableCustomers loads
  useEffect(() => {
    if (availableCustomers.length > 0 && !selectedCustomer) {
      let customerId = null;
      
      // Check which mode has a selected customer
      if (smsScheduledMode.selectedCustomer) {
        customerId = smsScheduledMode.selectedCustomer;
      } else if (smsRecurringMode.selectedCustomer) {
        customerId = smsRecurringMode.selectedCustomer;
      } else if (smsWriteMode.selectedCustomer) {
        customerId = smsWriteMode.selectedCustomer;
      } else if (emailWriteMode.selectedCustomer) {
        customerId = emailWriteMode.selectedCustomer;
      } else if (emailScheduledMode.selectedCustomer) {
        customerId = emailScheduledMode.selectedCustomer;
      } else if (emailRecurringMode.selectedCustomer) {
        customerId = emailRecurringMode.selectedCustomer;
      }
      
      if (customerId) {
        const customer = availableCustomers.find(c => c.id === customerId);
        if (customer) {
          setSelectedCustomer(customer);
        }
      }
    }
  }, [availableCustomers, smsScheduledMode.selectedCustomer, smsRecurringMode.selectedCustomer, smsWriteMode.selectedCustomer, emailWriteMode.selectedCustomer, emailScheduledMode.selectedCustomer, emailRecurringMode.selectedCustomer, selectedCustomer]);

  // useEffect to handle Email Write mode customer pre-population when availableCustomers loads
  useEffect(() => {
    console.log('Email Write useEffect - availableCustomers:', availableCustomers?.length);
    console.log('Email Write useEffect - emailWriteMode.selectedCustomer:', emailWriteMode.selectedCustomer);
    console.log('Email Write useEffect - current selectedCustomer:', selectedCustomer);
    if (availableCustomers && emailWriteMode.selectedCustomer && !selectedCustomer) {
      const customer = availableCustomers.find(c => c.id === emailWriteMode.selectedCustomer);
      console.log('Email Write useEffect - Found customer:', customer);
      if (customer) {
        setSelectedCustomer(customer);
        console.log('Email Write useEffect - Set selectedCustomer to:', getCustomerFullName(customer));
        setEmailWriteMode(prev => ({ 
          ...prev, 
          customerName: getCustomerFullName(customer) 
        }));
      }
    }
  }, [availableCustomers, emailWriteMode.selectedCustomer, selectedCustomer]);

  // useEffect to handle Email Scheduled mode customer pre-population when availableCustomers loads
  useEffect(() => {
    if (availableCustomers && emailScheduledMode.selectedCustomer && !selectedCustomer) {
      const customer = availableCustomers.find(c => c.id === emailScheduledMode.selectedCustomer);
      if (customer) {
        setSelectedCustomer(customer);
        setEmailScheduledMode(prev => ({ 
          ...prev, 
          customerName: getCustomerFullName(customer) 
        }));
      }
    }
  }, [availableCustomers, emailScheduledMode.selectedCustomer, selectedCustomer]);

  // useEffect to handle Email Recurring mode customer pre-population when availableCustomers loads
  useEffect(() => {
    if (availableCustomers && emailRecurringMode.selectedCustomer && !selectedCustomer) {
      const customer = availableCustomers.find(c => c.id === emailRecurringMode.selectedCustomer);
      if (customer) {
        setSelectedCustomer(customer);
        setEmailRecurringMode(prev => ({ 
          ...prev, 
          customerName: getCustomerFullName(customer) 
        }));
      }
    }
  }, [availableCustomers, emailRecurringMode.selectedCustomer, selectedCustomer]);

  // Fetch available customers for email agents
  const fetchAvailableCustomers = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/customers`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        // The API returns paginated results with a customers array
        const customers = data.customers || [];
        setAvailableCustomers(customers);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  // Helper function to check if template has customer personalization placeholders
  const hasCustomerPlaceholders = (content) => {
    const customerPlaceholders = ['[CUSTOMER_NAME]', '[PET_NAME]', '[PET_NAMES]'];
    return customerPlaceholders.some(placeholder => content.includes(placeholder));
  };

  // Helper function to identify ChatGPT campaign templates
  const isCampaignTemplate = (template) => {
    return template.name.includes('Campaign') && template.name.includes('ChatGPT');
  };

  // Filter templates based on personalization setting, campaign type, and ChatGPT requirement
  const filterTemplatesByPersonalization = (templates, isPersonalized, showCampaignTemplates = false, requireChatGPT = false) => {
    console.log('Filtering templates:', { 
      totalTemplates: templates.length, 
      isPersonalized, 
      showCampaignTemplates, 
      requireChatGPT 
    });
    
    let filteredTemplates = templates;
    
    // Filter by ChatGPT requirement if specified (for Holiday and Recurring modes)
    if (requireChatGPT) {
      console.log('Filtering by ChatGPT requirement...');
      const beforeCount = filteredTemplates.length;
      filteredTemplates = filteredTemplates.filter(template => {
        const isEnabled = template.chatgpt_enabled === true;
        console.log(`Template "${template.name}": chatgpt_enabled = ${template.chatgpt_enabled} (${typeof template.chatgpt_enabled}), included: ${isEnabled}`);
        return isEnabled;
      });
      console.log(`ChatGPT filtering: ${beforeCount} -> ${filteredTemplates.length} templates`);
    }
    
    // First filter for campaign templates if needed
    if (showCampaignTemplates) {
      // Show only campaign templates for Topic/Holiday content types
      filteredTemplates = filteredTemplates.filter(template => isCampaignTemplate(template));
    } else {
      // Exclude campaign templates for other content types (custom campaigns)
      filteredTemplates = filteredTemplates.filter(template => !isCampaignTemplate(template));
    }
    
    // Then filter by personalization
    if (isPersonalized) {
      // Show all filtered templates when personalization is enabled
      return filteredTemplates;
    } else {
      // Show only templates without customer placeholders when personalization is disabled
      return filteredTemplates.filter(template => !hasCustomerPlaceholders(template.content));
    }
  };

  // Fetch email and SMS templates
  const fetchTemplates = async () => {
    setLoadingTemplates(true);
    try {
      // Fetch email templates
      const emailResponse = await fetch(`${API_BASE_URL}/api/templates?template_type=email`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (emailResponse.ok) {
        const emailData = await emailResponse.json();
        setEmailTemplates(emailData);
      }

      // Fetch SMS templates
      const smsResponse = await fetch(`${API_BASE_URL}/api/templates?template_type=sms`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (smsResponse.ok) {
        const smsData = await smsResponse.json();
        setSmsTemplates(smsData);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setLoadingTemplates(false);
    }
  };

  // Helper function to get customer's full name
  const getCustomerFullName = (customer) => {
    if (customer.first_name && customer.last_name) {
      return `${customer.first_name} ${customer.last_name}`;
    } else if (customer.first_name) {
      return customer.first_name;
    } else if (customer.last_name) {
      return customer.last_name;
    } else if (customer.name) {
      return customer.name; // Fallback to legacy name field
    }
    return 'Unnamed Customer';
  };

  // Customer search function with debouncing
  const searchCustomers = async (searchTerm) => {
    if (searchTerm.length < 3) {
      setSearchedCustomers([]);
      return;
    }

    setIsSearchingCustomers(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/customers?search=${encodeURIComponent(searchTerm)}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Customer search API response:', data);
        // The API returns paginated results with a customers array
        const customers = data.customers || [];
        console.log('Extracted customers:', customers);
        // No need to filter again since the backend already filtered by search term
        setSearchedCustomers(customers);
      } else {
        console.error('Customer search API error:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Error searching customers:', error);
      setSearchedCustomers([]);
    } finally {
      setIsSearchingCustomers(false);
    }
  };

  // Debounced search effect
  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      if (customerSearchTerm) {
        searchCustomers(customerSearchTerm);
      }
    }, 500); // 500ms delay

    return () => clearTimeout(delayedSearch);
  }, [customerSearchTerm]);

  // Helper functions
  const getDayNumber = (dayName) => {
    const days = {
      'sunday': 0, 'monday': 1, 'tuesday': 2, 'wednesday': 3,
      'thursday': 4, 'friday': 5, 'saturday': 6
    };
    return days[dayName.toLowerCase()] || 0;
  };

  const formatDateRange = (startDate, endDate) => {
    // Helper function to format date from string without timezone conversion
    const formatDateFromString = (dateStr) => {
      const [year, month, day] = dateStr.split('-');
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                         'Jul', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'];
      const monthName = monthNames[parseInt(month) - 1];
      const dayNum = parseInt(day);
      return `${monthName} ${dayNum}`;
    };

    // Handle both Date objects and date strings
    let startDateStr, endDateStr;
    
    if (typeof startDate === 'string') {
      startDateStr = startDate;
    } else {
      startDateStr = startDate.toISOString().split('T')[0];
    }
    
    if (typeof endDate === 'string') {
      endDateStr = endDate;
    } else {
      endDateStr = endDate.toISOString().split('T')[0];
    }
    
    const start = formatDateFromString(startDateStr);
    const end = formatDateFromString(endDateStr);
    return `${start} - ${end}`;
  };

  // Auto-calculate dates based on report period
  const calculateDateRange = (period) => {
    const today = new Date();
    let startDate = '';
    let endDate = '';

    switch (period) {
      case 'current_week':
        const currentMonday = new Date(today);
        currentMonday.setDate(today.getDate() - today.getDay() + 1);
        const currentSunday = new Date(currentMonday);
        currentSunday.setDate(currentMonday.getDate() + 6);
        startDate = currentMonday.toISOString().split('T')[0];
        endDate = currentSunday.toISOString().split('T')[0];
        break;
      case 'last_week':
        const lastMonday = new Date(today);
        lastMonday.setDate(today.getDate() - today.getDay() - 6);
        const lastSunday = new Date(lastMonday);
        lastSunday.setDate(lastMonday.getDate() + 6);
        startDate = lastMonday.toISOString().split('T')[0];
        endDate = lastSunday.toISOString().split('T')[0];
        break;
      case 'current_month':
        const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
        const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        startDate = firstDay.toISOString().split('T')[0];
        endDate = lastDay.toISOString().split('T')[0];
        break;
      case 'last_month':
        const firstDayLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const lastDayLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
        startDate = firstDayLastMonth.toISOString().split('T')[0];
        endDate = lastDayLastMonth.toISOString().split('T')[0];
        break;
      default:
        break;
    }

    return { startDate, endDate };
  };

  // Update dates when report period changes
  const handleReportPeriodChange = (period, mode = 'recurring') => {
    const { startDate, endDate } = calculateDateRange(period);
    const stateUpdater = mode === 'recurring' ? setTimesheetRecurringMode : setTimesheetAdhocMode;
    
    stateUpdater(prev => ({
      ...prev,
      reportPeriod: period,
      customStartDate: period === 'custom' ? prev.customStartDate : startDate,
      customEndDate: period === 'custom' ? prev.customEndDate : endDate
    }));
  };

  const handleImageUpload = async (event, mode = 'recurring') => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    let stateUpdater;
    if (mode === 'recurring') stateUpdater = setRecurringMode;
    else if (mode === 'adhoc') stateUpdater = setAdhocMode;
    else if (mode === 'write') stateUpdater = setWritePostMode;
    else if (mode === 'email_scheduled') stateUpdater = setEmailScheduledMode;
    else if (mode === 'email_recurring') stateUpdater = setEmailRecurringMode;
    else if (mode === 'email_write') stateUpdater = setEmailWriteMode;
    else if (mode === 'marketing') stateUpdater = setMarketingMode;
    
    try {
      const fileArray = Array.from(files);
      const uploadPromises = fileArray.map(async (file) => {
        const result = await uploadFile(file, { 
          folderType: 'images',
          path: 'AI Agent Uploads'
        });
        return {
          url: result.url,
          key: result.key,
          name: file.name,
          s3Key: result.key
        };
      });

      const uploadedFiles = await Promise.all(uploadPromises);

      stateUpdater(prev => ({
        ...prev,
        uploadedImages: [...prev.uploadedImages, ...uploadedFiles]
      }));
    } catch (error) {
      console.error('Error uploading images:', error);
      setMessage({ type: 'error', text: 'Failed to upload images. Please try again.' });
    }
  };

  const removeUploadedImage = (index, mode = 'recurring') => {
    let stateUpdater;
    if (mode === 'recurring') stateUpdater = setRecurringMode;
    else if (mode === 'adhoc') stateUpdater = setAdhocMode;
    else if (mode === 'write') stateUpdater = setWritePostMode;
    else if (mode === 'email_scheduled') stateUpdater = setEmailScheduledMode;
    else if (mode === 'email_recurring') stateUpdater = setEmailRecurringMode;
    else if (mode === 'email_write') stateUpdater = setEmailWriteMode;
    else if (mode === 'marketing') stateUpdater = setMarketingMode;
    
    stateUpdater(prev => ({
      ...prev,
      uploadedImages: prev.uploadedImages.filter((_, i) => i !== index)
    }));
  };

  // Handle asset selection from S3AssetBrowser
  const handleAssetSelect = (asset) => {
    const newAsset = {
      url: asset.url,
      key: asset.key,
      name: asset.key.split('/').pop(),
      s3Key: asset.key
    };

    let stateUpdater;
    if (currentImageMode === 'recurring') stateUpdater = setRecurringMode;
    else if (currentImageMode === 'adhoc') stateUpdater = setAdhocMode;
    else if (currentImageMode === 'write') stateUpdater = setWritePostMode;
    else if (currentImageMode === 'email_scheduled') stateUpdater = setEmailScheduledMode;
    else if (currentImageMode === 'email_recurring') stateUpdater = setEmailRecurringMode;
    else if (currentImageMode === 'email_write') stateUpdater = setEmailWriteMode;
    else if (currentImageMode === 'marketing') stateUpdater = setMarketingMode;

    stateUpdater(prev => ({
      ...prev,
      uploadedImages: [...prev.uploadedImages, newAsset]
    }));

    setShowAssetBrowser(false);
    setCurrentImageMode('');
  };

  // Open asset browser for specific mode
  const openAssetBrowser = (mode) => {
    setCurrentImageMode(mode);
    setShowAssetBrowser(true);
  };

  // Template Dropdown Component
  const TemplateDropdown = ({ 
    templates, 
    selectedValue, 
    onSelect, 
    placeholder = "Select a template...", 
    templateType = "email",
    isPersonalized = true, // New prop to control filtering
    showCampaignTemplates = false, // New prop to show ChatGPT campaign templates
    requireChatGPT = false // New prop to filter only ChatGPT-enabled templates
  }) => {
    const handleTemplateSelect = (template) => {
      onSelect(template.content);
    };

    // Filter templates based on personalization setting, campaign type, and ChatGPT requirement
    const filteredTemplates = filterTemplatesByPersonalization(templates, isPersonalized, showCampaignTemplates, requireChatGPT);

    return (
      <div className="mb-3">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          📄 Select Template
          {showCampaignTemplates && (
            <span className="text-xs text-theme-blue ml-2">(ChatGPT Campaign Templates)</span>
          )}
          {!isPersonalized && !showCampaignTemplates && (
            <span className="text-xs text-gray-500 ml-2">(Non-personalized templates only)</span>
          )}
        </label>
        <div className="relative">
          <select
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 bg-white text-sm"
            onChange={(e) => {
              if (e.target.value) {
                const template = filteredTemplates.find(t => t.id === e.target.value);
                if (template) {
                  handleTemplateSelect(template);
                }
              }
            }}
            value={(() => {
              // Find the template that matches the selectedValue content
              const matchingTemplate = filteredTemplates.find(t => t.content === selectedValue);
              return matchingTemplate ? matchingTemplate.id : "";
            })()}
          >
            <option value="" disabled>{placeholder}</option>
            {filteredTemplates.map(template => (
              <option key={template.id} value={template.id}>
                {template.name}
                {template.description && ` - ${template.description.substring(0, 50)}${template.description.length > 50 ? '...' : ''}`}
              </option>
            ))}
          </select>
        </div>
        {filteredTemplates.length === 0 && (
          <p className="text-xs text-gray-500 mt-1">
            {showCampaignTemplates ? 
              `No ChatGPT campaign ${templateType} templates available.` :
              `No ${isPersonalized ? '' : 'non-personalized '}${templateType} templates available. 
               ${!isPersonalized && ' Create templates without [CUSTOMER_NAME] or [PET_NAME] placeholders in'} ${templateType === 'email' ? 'Email' : 'SMS'} Configuration.`
            }
          </p>
        )}
      </div>
    );
  };

  // Email Editor Button Component
  const FullScreenEditorButton = ({ content, title, onSave, className = "", isPersonalized = true }) => {
    return (
      <button
        type="button"
        onClick={() => openFullScreenEditor(content, title, onSave, isPersonalized)}
        className={`inline-flex items-center px-3 py-1 text-xs font-medium rounded text-white transition-colors hover:opacity-90 ${className}`}
        style={{ 
          backgroundColor: 'var(--theme-primary)', 
          borderColor: 'var(--theme-primary)',
          '--tw-ring-color': 'var(--theme-primary)'
        }}
      >
        <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
        </svg>
        Email Editor
      </button>
    );
  };

  // Full-screen email editor functions
  const openFullScreenEditor = (content, title, onSave, isPersonalized = true) => {
    setFullScreenEditorContent(content);
    setFullScreenEditorTitle(title);
    setFullScreenEditorCallback(() => onSave); // Wrap in arrow function to store the callback
    setFullScreenEditorPersonalized(isPersonalized);
    setShowFullScreenEditor(true);
    // Prevent body scroll when modal is open
    document.body.style.overflow = 'hidden';
  };

  const closeFullScreenEditor = () => {
    setShowFullScreenEditor(false);
    setFullScreenEditorContent('');
    setFullScreenEditorTitle('');
    setFullScreenEditorCallback(null);
    setFullScreenEditorPersonalized(true);
    // Restore body scroll
    document.body.style.overflow = 'unset';
  };

  const saveFullScreenEditor = () => {
    if (fullScreenEditorCallback) {
      fullScreenEditorCallback(fullScreenEditorContent);
    }
    closeFullScreenEditor();
  };

  // Handle keyboard events for full-screen editor
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && showFullScreenEditor) {
        closeFullScreenEditor();
      }
      // Ctrl+S or Cmd+S to save
      if ((event.ctrlKey || event.metaKey) && event.key === 's' && showFullScreenEditor) {
        event.preventDefault();
        saveFullScreenEditor();
      }
    };

    if (showFullScreenEditor) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showFullScreenEditor, fullScreenEditorContent, fullScreenEditorCallback]);

  // Helper functions to check if forms are valid (for button state)
  const isTimesheetAdhocModeValid = () => {
    return timesheetAdhocMode.agentName.trim() && 
           (timesheetAdhocMode.reportPeriod !== 'custom' || 
            (timesheetAdhocMode.customStartDate && timesheetAdhocMode.customEndDate)) &&
           (!timesheetAdhocMode.autoEmail || timesheetAdhocMode.emailRecipients.length > 0);
  };

  const isTimesheetRecurringModeValid = () => {
    return timesheetRecurringMode.agentName.trim() && 
           (!timesheetRecurringMode.autoEmail || timesheetRecurringMode.emailRecipients.length > 0);
  };

  // Validation functions for paystub agents
  const isPaystubRecurringModeValid = () => {
    return paystubRecurringMode.agentName.trim() && 
           (!paystubRecurringMode.autoEmail || paystubRecurringMode.emailRecipients.length > 0);
  };

  const isPaystubAdhocModeValid = () => {
    return paystubAdhocMode.agentName.trim() && 
           paystubAdhocMode.selectedTimesheetId &&
           (!paystubAdhocMode.autoEmail || paystubAdhocMode.emailRecipients.length > 0);
  };

  // Validation functions for timesheet agents
  const validateTimesheetAdhocMode = () => {
    const errors = [];
    
    // Required fields
    if (!timesheetAdhocMode.agentName.trim()) {
      errors.push("Agent Name is required");
    }
    
    // Custom date range validation
    if (timesheetAdhocMode.reportPeriod === 'custom') {
      if (!timesheetAdhocMode.customStartDate) {
        errors.push("Custom Start Date is required when using custom report period");
      }
      if (!timesheetAdhocMode.customEndDate) {
        errors.push("Custom End Date is required when using custom report period");
      }
      if (timesheetAdhocMode.customStartDate && timesheetAdhocMode.customEndDate) {
        const startDate = new Date(timesheetAdhocMode.customStartDate);
        const endDate = new Date(timesheetAdhocMode.customEndDate);
        if (startDate > endDate) {
          errors.push("Start Date must be before End Date");
        }
      }
    }
    
    // Email validation
    if (timesheetAdhocMode.autoEmail && timesheetAdhocMode.emailRecipients.length === 0) {
      errors.push("At least one email recipient is required when auto-email is enabled");
    }
    
    // Validate email format
    for (const email of timesheetAdhocMode.emailRecipients) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        errors.push(`Invalid email format: ${email}`);
      }
    }
    
    return errors;
  };

  const validateTimesheetRecurringMode = () => {
    const errors = [];
    
    // Required fields
    if (!timesheetRecurringMode.agentName.trim()) {
      errors.push("Agent Name is required");
    }
    
    // Email validation
    if (timesheetRecurringMode.autoEmail && timesheetRecurringMode.emailRecipients.length === 0) {
      errors.push("At least one email recipient is required when auto-email is enabled");
    }
    
    // Validate email format
    for (const email of timesheetRecurringMode.emailRecipients) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        errors.push(`Invalid email format: ${email}`);
      }
    }
    
    return errors;
  };

  // Handle save for all agent types
  const handleSave = async (runAfterSave = false) => {
    if (runAfterSave) {
      await saveAndRunAgent();
    } else {
      await saveAgent();
    }
  };

  const saveAgent = async () => {
    setLoading(true);
    
    // Show loading modal for both create and edit modes
    setShowSaveModal(true);
    setMessage({ type: '', text: '' });

    try {
      let agentData;
      const agentType = searchParams.get('agent_type');
      
      if (agentType === 'time_sheet') {
        // Handle timesheet agents - validate before proceeding
        if (activeTab === 'timesheet-recurring') {
          // Validate timesheet recurring mode
          const validationErrors = validateTimesheetRecurringMode();
          if (validationErrors.length > 0) {
            setMessage({ 
              type: 'error', 
              text: `Please fix the following issues: ${validationErrors.join(', ')}` 
            });
            setLoading(false);
            setShowSaveModal(false);
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
          }
          agentData = {
            agent_type: 'time_sheet',
            mode: 'recurring',
            agent_name: timesheetRecurringMode.agentName,
            // Timesheet specific fields
            selected_employees: timesheetRecurringMode.selectedEmployees,
            run_every_pay_period: timesheetRecurringMode.runEveryPayPeriod,
            days_after_period_end: timesheetRecurringMode.daysAfterPeriodEnd,
            custom_start_date: timesheetRecurringMode.customStartDate,
            custom_end_date: timesheetRecurringMode.customEndDate,
            include_billing_rates: timesheetRecurringMode.includeBillingRates,
            initial_status: timesheetRecurringMode.initialStatus,
            email_recipients: timesheetRecurringMode.emailRecipients,
            frequency: timesheetRecurringMode.runEveryPayPeriod, // Keep backward compatibility
            schedule_time: timesheetRecurringMode.scheduleTime,
            days_of_week: timesheetRecurringMode.daysOfWeek,
            // Required fields for AI agents (defaults for timesheet agents)
            topic: 'Timesheet Report',
            image_option: 'ai_generate',
            social_platforms: {},
            word_count: '100',
            post_destination: timesheetRecurringMode.initialStatus
          };
        } else if (activeTab === 'timesheet-adhoc') {
          // Validate timesheet adhoc mode
          const validationErrors = validateTimesheetAdhocMode();
          if (validationErrors.length > 0) {
            setMessage({ 
              type: 'error', 
              text: `Please fix the following issues: ${validationErrors.join(', ')}` 
            });
            setLoading(false);
            setShowSaveModal(false);
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
          }
          agentData = {
            agent_type: 'time_sheet',
            mode: 'adhoc',
            agent_name: timesheetAdhocMode.agentName,
            // Timesheet specific fields
            selected_employees: timesheetAdhocMode.selectedEmployees,
            report_period: timesheetAdhocMode.reportPeriod,
            custom_start_date: timesheetAdhocMode.customStartDate,
            custom_end_date: timesheetAdhocMode.customEndDate,
            include_billing_rates: timesheetAdhocMode.includeBillingRates,
            initial_status: timesheetAdhocMode.initialStatus,
            email_recipients: timesheetAdhocMode.emailRecipients,
            // Required fields for AI agents (defaults for timesheet agents)
            topic: 'Timesheet Report',
            image_option: 'ai_generate',
            social_platforms: {},
            word_count: '100',
            post_destination: timesheetAdhocMode.initialStatus
          };
        }
      } else if (agentType === 'paystub_agent') {
        // Handle paystub agents
        if (activeTab === 'paystub-recurring') {
          // Validate paystub recurring mode
          if (!paystubRecurringMode.agentName.trim()) {
            setMessage({ 
              type: 'error', 
              text: 'Please enter an agent name' 
            });
            setLoading(false);
            setShowSaveModal(false);
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
          }
          
          agentData = {
            agent_type: 'paystub_agent',
            mode: 'paystub_recurring',
            agent_name: paystubRecurringMode.agentName,
            // Paystub specific fields
            selected_employees: paystubRecurringMode.selectedEmployees,
            run_every_pay_period: selectedPayPeriod ? selectedPayPeriod.period_type : 'bi_weekly', // Use configured pay period
            days_after_timesheet_published: paystubRecurringMode.daysAfterTimesheetPublished,
            workflow_mode: paystubRecurringMode.workflowMode,
            paystub_template: paystubRecurringMode.paystubTemplate,
            include_deductions: paystubRecurringMode.includeDeductions,
            include_overtime: paystubRecurringMode.includeOvertime,
            paystub_format: paystubRecurringMode.paystubFormat,
            email_recipients: paystubRecurringMode.emailRecipients,
            auto_email: paystubRecurringMode.autoEmail,
            schedule_time: paystubRecurringMode.scheduleTime,
            days_of_week: paystubRecurringMode.daysOfWeek,
            // Required fields for AI agents (defaults for paystub agents)
            topic: 'Paystub Generation',
            image_option: 'ai_generate',
            social_platforms: {},
            word_count: '100',
            post_destination: paystubRecurringMode.workflowMode
          };
          
          console.log('Paystub Recurring Agent Data being sent:', agentData);
          console.log('daysAfterTimesheetPublished value:', paystubRecurringMode.daysAfterTimesheetPublished);
        } else if (activeTab === 'paystub-adhoc') {
          // Validate paystub adhoc mode
          if (!paystubAdhocMode.agentName.trim()) {
            setMessage({ 
              type: 'error', 
              text: 'Please enter an agent name' 
            });
            setLoading(false);
            setShowSaveModal(false);
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
          }
          
          if (!paystubAdhocMode.selectedTimesheetId) {
            setMessage({ 
              type: 'error', 
              text: 'Please select a timesheet' 
            });
            setLoading(false);
            setShowSaveModal(false);
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
          }
          
          agentData = {
            agent_type: 'paystub_agent',
            mode: 'paystub_adhoc',
            agent_name: paystubAdhocMode.agentName,
            // Paystub specific fields
            selected_employees: paystubAdhocMode.selectedEmployees,
            selected_timesheet_id: paystubAdhocMode.selectedTimesheetId,
            workflow_mode: paystubAdhocMode.workflowMode,
            paystub_template: paystubAdhocMode.paystubTemplate,
            include_deductions: paystubAdhocMode.includeDeductions,
            include_overtime: paystubAdhocMode.includeOvertime,
            paystub_format: paystubAdhocMode.paystubFormat,
            email_recipients: paystubAdhocMode.emailRecipients,
            auto_email: paystubAdhocMode.autoEmail,
            // Required fields for AI agents (defaults for paystub agents)
            topic: 'Paystub Generation',
            image_option: 'ai_generate',
            social_platforms: {},
            word_count: '100',
            post_destination: paystubAdhocMode.workflowMode
          };
        }
      } else if (agentType === 'email') {
        // Handle email agents
        
        // Validate ChatGPT formatting requirement for email write mode
        if (activeTab === 'email-write' && emailWriteMode.useChatGPTFormatting) {
          if (!emailWriteMode.emailContent.includes('CHATGTP_CONTENT')) {
            setValidationModal({
              isOpen: true,
              title: 'ChatGPT Placeholder Required',
              message: 'Please add CHATGTP_CONTENT placeholder in Email Body as you have enabled ChatGPT formatting for email.'
            });
            setLoading(false);
            setShowSaveModal(false);
            return;
          }
        }
        
        // Validate CHATGTP_CONTENT requirement for email scheduled mode (Holiday Mode)
        if (activeTab === 'email-scheduled') {
          if (!emailScheduledMode.emailContentTemplate.includes('CHATGTP_CONTENT')) {
            setValidationModal({
              isOpen: true,
              title: 'ChatGPT Placeholder Required',
              message: 'Please add CHATGTP_CONTENT placeholder in Email Content as it is mandatory for Holiday email agents.'
            });
            setLoading(false);
            setShowSaveModal(false);
            return;
          }
        }
        
        // Validate CHATGTP_CONTENT requirement for email recurring mode
        if (activeTab === 'email-recurring') {
          if (!emailRecurringMode.emailContentTemplate.includes('CHATGTP_CONTENT')) {
            setValidationModal({
              isOpen: true,
              title: 'ChatGPT Placeholder Required',
              message: 'Please add CHATGTP_CONTENT placeholder in Email Content as it is mandatory for Recurring email agents.'
            });
            setLoading(false);
            setShowSaveModal(false);
            return;
          }
        }
        
        if (activeTab === 'email-scheduled') {
          console.log('Email scheduled mode data being saved:', {
            emailContentTemplate: emailScheduledMode.emailContentTemplate,
            imageFormat: emailScheduledMode.imageFormat,
            imageOption: emailScheduledMode.imageOption
          });
          
          agentData = {
            agent_type: 'email',
            mode: 'recurring', // Scheduled emails are recurring based on holidays
            agent_name: emailScheduledMode.agentName,
            // No topic required for scheduled email agents - holidays define the context
            selected_holidays: emailScheduledMode.selectedHolidays,
            image_option: emailScheduledMode.imageOption,
            platform_image_formats: emailScheduledMode.imageFormat ? { email: emailScheduledMode.imageFormat } : {},
            uploaded_images: emailScheduledMode.uploadedImages,
            enable_custom_image_text: emailScheduledMode.enableCustomImageText,
            custom_image_banner_text: emailScheduledMode.customImageBannerText,
            word_count: '100', // Default word count
            use_chatgpt_formatting: true, // Default to true for email agents
            post_time: emailScheduledMode.postTime,
            post_destination: emailScheduledMode.postDestination,
            auto_post: emailScheduledMode.postDestination === 'auto_send',
            email_content_template: emailScheduledMode.emailContentTemplate, // Add the template
            use_customer_database: true, // Always true for scheduled mode
            email_type: emailScheduledMode.emailType, // Add email type (bulk/single)
            selected_customer: emailScheduledMode.selectedCustomer, // Add selected customer for single emails
          };
        } else if (activeTab === 'email-write') {
          agentData = {
            agent_type: 'email',
            mode: 'write',
            agent_name: emailWriteMode.agentName,
            email_subject: emailWriteMode.emailSubject,
            email_content: emailWriteMode.emailContent,
            image_option: emailWriteMode.imageOption,
            platform_image_formats: emailWriteMode.imageFormat ? { email: emailWriteMode.imageFormat } : {},
            uploaded_images: emailWriteMode.uploadedImages,
            enable_custom_image_text: emailWriteMode.enableCustomImageText,
            custom_image_banner_text: emailWriteMode.customImageBannerText,
            is_scheduled: emailWriteMode.isScheduled,
            word_count: '100', // Default word count
            use_chatgpt_formatting: emailWriteMode.useChatGPTFormatting, // Updated field name
            post_date: emailWriteMode.postDate,
            post_time: emailWriteMode.postTime,
            post_destination: emailWriteMode.postDestination,
            auto_post: emailWriteMode.postDestination === 'auto_post',
            email_type: emailWriteMode.emailType, // Add email type (bulk/single)
            selected_customer: emailWriteMode.selectedCustomer, // Add selected customer for single emails
          };
        } else if (activeTab === 'email-recurring') {
          agentData = {
            agent_type: 'email',
            mode: 'recurring',
            agent_name: emailRecurringMode.agentName,
            topic: emailRecurringMode.topic === 'Custom' ? emailRecurringMode.customTopic : emailRecurringMode.topic, // Use custom topic if selected
            word_count: '100', // Default word count
            // Schedule fields (replacing holiday fields)
            schedule_type: emailRecurringMode.scheduleType,
            days_of_week: emailRecurringMode.daysOfWeek, // For weekly
            monthly_schedule: emailRecurringMode.monthlySchedule, // For monthly  
            email_content_template: emailRecurringMode.emailContentTemplate,
            post_time: emailRecurringMode.postTime,
            image_option: emailRecurringMode.imageOption,
            platform_image_formats: emailRecurringMode.imageFormat ? { email: emailRecurringMode.imageFormat } : {},
            uploaded_images: emailRecurringMode.uploadedImages,
            enable_custom_image_text: emailRecurringMode.enableCustomImageText,
            custom_image_banner_text: emailRecurringMode.customImageBannerText,
            use_chatgpt_formatting: emailRecurringMode.useChatGPTFormatting,
            post_destination: emailRecurringMode.postDestination,
            auto_post: emailRecurringMode.postDestination === 'auto_send',
            use_customer_database: true, // Always use customer database for recurring emails
            email_type: emailRecurringMode.emailType, // Add email type (bulk/single)
            selected_customer: emailRecurringMode.selectedCustomer, // Add selected customer for single emails
          };
        }
      } else if (agentType === 'sms_agent') {
        // Handle SMS agents
        
        if (activeTab === 'sms-scheduled') {
          agentData = {
            agent_type: 'sms_agent',
            mode: 'recurring', // Scheduled SMS are recurring based on holidays
            agent_name: smsScheduledMode.agentName,
            selected_holidays: smsScheduledMode.selectedHolidays,
            sms_template: smsScheduledMode.smsContentTemplate,
            sms_link: smsScheduledMode.smsLink,
            enable_sms_link: smsScheduledMode.enableSmsLink,
            use_sms_chatgpt_formatting: true,
            post_time: smsScheduledMode.postTime,
            post_destination: smsScheduledMode.postDestination,
            auto_post: smsScheduledMode.postDestination === 'auto_send',
            use_customer_database: smsScheduledMode.useCustomerDatabase,
            sms_type: smsScheduledMode.smsType,
            selected_sms_customer: smsScheduledMode.selectedCustomer,
            sms_character_limit: smsScheduledMode.smsCharacterLimit,
            image_option: smsScheduledMode.imageOption,
            // Fix: Use platform_image_formats instead of image_format for SMS
            platform_image_formats: smsScheduledMode.imageFormat ? { sms: smsScheduledMode.imageFormat } : {},
            uploaded_images: smsScheduledMode.uploadedImages,
            enable_custom_image_text: smsScheduledMode.enableCustomImageText,
            custom_image_banner_text: smsScheduledMode.customImageBannerText
          };
        } else if (activeTab === 'sms-write') {
          // SMS Write Agent validation
          if (!smsWriteMode.agentName) {
            setMessage({ type: 'error', text: 'Agent name is required for SMS Write agents.' });
            setLoading(false);
            return;
          }
          if (!smsWriteMode.smsTemplate) {
            setMessage({ type: 'error', text: 'SMS content is required for SMS Write agents.' });
            setLoading(false);
            return;
          }
          if (!smsWriteMode.smsSubject) {
            setMessage({ type: 'error', text: 'SMS ChatGPT Content is required for SMS Write agents.' });
            setLoading(false);
            return;
          }
          
          // Validate ChatGPT formatting requirement for SMS write mode
          if (smsWriteMode.useSMSChatGPTFormatting && !smsWriteMode.smsTemplate.includes('CHATGTP_CONTENT')) {
            setValidationModal({
              isOpen: true,
              title: 'ChatGPT Placeholder Required',
              message: 'Please add CHATGTP_CONTENT placeholder in SMS Content as you have enabled ChatGPT formatting for SMS.'
            });
            setLoading(false);
            setShowSaveModal(false);
            return;
          }
          
          agentData = {
            agent_type: 'sms_agent',
            mode: 'write',
            agent_name: smsWriteMode.agentName,
            sms_subject: smsWriteMode.smsSubject, // This is now ChatGPT Content that will replace [CHATGPT_CONTENT]
            sms_content: smsWriteMode.smsSubject, // Send the same content as sms_content for backend processing
            sms_template: smsWriteMode.smsTemplate, // Selected SMS template
            sms_link: smsWriteMode.smsLink,
            enable_sms_link: smsWriteMode.enableSmsLink,
            use_sms_chatgpt_formatting: smsWriteMode.useSMSChatGPTFormatting,
            post_date: smsWriteMode.postDate,
            post_time: smsWriteMode.postTime,
            post_destination: smsWriteMode.postDestination,
            auto_post: smsWriteMode.postDestination === 'auto_post',
            sms_type: smsWriteMode.smsType,
            selected_sms_customer: smsWriteMode.selectedCustomer,
            sms_character_limit: smsWriteMode.smsCharacterLimit,
            image_option: smsWriteMode.imageOption,
            // Fix: Use platform_image_formats instead of image_format for SMS
            platform_image_formats: smsWriteMode.imageFormat ? { sms: smsWriteMode.imageFormat } : {},
            uploaded_images: smsWriteMode.uploadedImages,
            enable_custom_image_text: smsWriteMode.enableCustomImageText,
            custom_image_banner_text: smsWriteMode.customImageBannerText
          };
        } else if (activeTab === 'sms-recurring') {
          agentData = {
            agent_type: 'sms_agent',
            mode: 'recurring',
            agent_name: smsRecurringMode.agentName,
            topic: smsRecurringMode.topic === 'Custom' ? smsRecurringMode.customTopic : smsRecurringMode.topic,
            schedule_type: smsRecurringMode.scheduleType,
            days_of_week: smsRecurringMode.daysOfWeek,
            monthly_schedule: smsRecurringMode.monthlySchedule,
            sms_template: smsRecurringMode.smsContentTemplate,
            sms_link: smsRecurringMode.smsLink,
            enable_sms_link: smsRecurringMode.enableSmsLink,
            post_time: smsRecurringMode.postTime,
            post_destination: smsRecurringMode.postDestination,
            auto_post: smsRecurringMode.postDestination === 'auto_send',
            use_sms_chatgpt_formatting: true,
            sms_type: smsRecurringMode.smsType,
            selected_sms_customer: smsRecurringMode.selectedCustomer,
            sms_character_limit: smsRecurringMode.smsCharacterLimit,
            use_customer_database: true,
            image_option: smsRecurringMode.imageOption,
            // Fix: Use platform_image_formats instead of image_format for SMS
            platform_image_formats: smsRecurringMode.imageFormat ? { sms: smsRecurringMode.imageFormat } : {},
            uploaded_images: smsRecurringMode.uploadedImages,
            enable_custom_image_text: smsRecurringMode.enableCustomImageText,
            custom_image_banner_text: smsRecurringMode.customImageBannerText
          };
        }
      } else if (agentType === 'marketing_agent') {
        // Marketing Agent validation and save
        
        // Validate CHATGTP_CONTENT placeholder for Marketing Agent when ChatGPT formatting is enabled
        if (marketingMode.contentType === 'custom_campaign') {
          // Check Email content if Email channel is selected and ChatGPT formatting is enabled
          if (marketingMode.channels.includes('email') && marketingMode.emailUseChatGPT) {
            if (!marketingMode.emailTemplate.includes('CHATGTP_CONTENT')) {
              setValidationModal({
                isOpen: true,
                title: 'ChatGPT Placeholder Required',
                message: 'Please add CHATGTP_CONTENT placeholder in Email Content as you have enabled ChatGPT formatting for email.'
              });
              setLoading(false);
              setShowSaveModal(false);
              return;
            }
          }
          
          // Check SMS content if SMS channel is selected and ChatGPT formatting is enabled
          if (marketingMode.channels.includes('sms') && marketingMode.smsUseChatGPT) {
            if (!marketingMode.smsTemplate.includes('CHATGTP_CONTENT')) {
              setValidationModal({
                isOpen: true,
                title: 'ChatGPT Placeholder Required',
                message: 'Please add CHATGTP_CONTENT placeholder in SMS Content as you have enabled ChatGPT formatting for SMS.'
              });
              setLoading(false);
              setShowSaveModal(false);
              return;
            }
          }
        }
        
        // Handle Marketing Agent
        agentData = {
          agent_type: 'marketing_agent',
          mode: 'adhoc', // Marketing agents are typically adhoc/one-time campaigns
          agent_name: marketingMode.agentName,
          
          // Marketing-specific fields
          marketing_content_type: marketingMode.contentType, // 'topic', 'holidays', 'custom_campaign'
          marketing_channels: marketingMode.channels, // Array: ['social_media', 'email', 'sms']
          
          // Content Type specific data
          ...(marketingMode.contentType === 'topic' && {
            topic: marketingMode.selectedTopic === 'Custom' ? marketingMode.customTopic : marketingMode.selectedTopic
          }),
          ...(marketingMode.contentType === 'holidays' && {
            selected_holidays: marketingMode.selectedHolidays
          }),
          ...(marketingMode.contentType === 'custom_campaign' && {
            marketing_custom_campaign: marketingMode.customCampaign,
            use_chatgpt_formatting: marketingMode.useChatGPTFormatting
          }),
          
          // Social Media Settings (if social_media channel selected)
          ...(marketingMode.channels.includes('social_media') && {
            marketing_social_platforms: marketingMode.socialPlatforms
          }),
          
          // Email Settings (only when email channel is selected)
          ...(marketingMode.channels.includes('email') && {
            marketing_email_personalized: marketingMode.emailPersonalized,
            marketing_email_include_image: marketingMode.emailIncludeImage,
            email_content_template: marketingMode.emailTemplate,
            ...(marketingMode.contentType === 'custom_campaign' && {
              marketing_email_use_chatgpt: marketingMode.emailUseChatGPT
            })
          }),
          
          // SMS Settings (only when sms channel is selected)
          ...(marketingMode.channels.includes('sms') && {
            marketing_sms_personalized: marketingMode.smsPersonalized,
            marketing_sms_include_image: marketingMode.smsIncludeImage,
            sms_template: marketingMode.smsTemplate,
            ...(marketingMode.contentType === 'custom_campaign' && {
              marketing_sms_use_chatgpt: marketingMode.smsUseChatGPT
            })
          }),
          
          // Campaign Settings
          marketing_link: marketingMode.marketingLink,
          ...(marketingMode.enableScheduling && marketingMode.scheduledDate && {
            post_date: marketingMode.scheduledDate,
            post_time: marketingMode.postTime
          }),
          marketing_workflow_mode: marketingMode.workflowMode, // 'in_review', 'ready_to_publish', 'auto_publish'
          
          // Image Options
          image_option: marketingMode.imageOption,
          uploaded_images: marketingMode.uploadedImages,
          // AI Image Format Selection - format for each platform
          platform_image_formats: marketingMode.platformImageFormats,
          image_text: marketingMode.imageText,
          enable_custom_image_text: marketingMode.enableCustomImageText,
          custom_image_banner_text: marketingMode.customImageBannerText,
          word_count: marketingMode.wordCount,
          
          // Standard fields
          post_destination: marketingMode.workflowMode,
          auto_post: marketingMode.workflowMode === 'auto_publish'
        };
        
        // API payload constructed successfully
      } else {
        // Handle social media agents (existing logic)
        if (activeTab === 'social-media-recurring') {
          // For editing existing agents, preserve the original mode (auto/recurring)
          const modeToSend = isEditMode && editingAgentData?.mode === 'auto' ? 'auto' : 'recurring';
          agentData = { ...recurringMode, mode: modeToSend };
        } else if (activeTab === 'social-media-adhoc') {
          agentData = { ...adhocMode, mode: 'adhoc' };
        } else if (activeTab === 'social-media-write') {
          agentData = { ...writePostMode, mode: 'write' };
        }

        // Convert camelCase to snake_case for backend (social media agents only)
        const socialMediaFields = {
          social_platforms: agentData.socialPlatforms,
          post_title: agentData.postTitle,
          post_content: agentData.postContent,
          use_web_research: agentData.useWebResearch,
          use_chatgpt_formatting: agentData.useChatGPTFormatting,
          image_option: agentData.imageOption,
          uploaded_images: agentData.uploadedImages,
          // AI Image Format Selection
          platform_image_formats: agentData.platformImageFormats,
          // Image text and banner fields
          image_text: agentData.imageText,
          enable_custom_image_text: agentData.enableCustomImageText,
          custom_image_banner_text: agentData.customImageBannerText,
          enable_social_link: agentData.enableSocialLink,
          social_link: agentData.socialLink,
          post_time: agentData.postTime,
          enable_scheduling: agentData.enableScheduling,
          word_count: agentData.wordCount,
          days_of_week: agentData.daysOfWeek,
          schedule_type: agentData.scheduleType,
          auto_post: agentData.postDestination === 'auto_post',
          post_destination: agentData.postDestination,
          post_date: agentData.postDate,
          agent_name: agentData.agentName,
          custom_topic: agentData.customTopic
        };

        agentData = { ...agentData, ...socialMediaFields };
        
        // Remove camelCase fields
        delete agentData.socialPlatforms;
        delete agentData.postTitle;
        delete agentData.postContent;
        delete agentData.useWebResearch;
        delete agentData.imageOption;
        delete agentData.uploadedImages;
        delete agentData.postTime;
        delete agentData.imageText;
        delete agentData.wordCount;
        delete agentData.daysOfWeek;
        delete agentData.scheduleType;
        delete agentData.autoPost;
        delete agentData.postDestination;
        // Keep post_date for adhoc mode scheduling - don't delete postDate field
        delete agentData.agentName;
        delete agentData.customTopic;
        delete agentData.customImageBannerText;
        delete agentData.enableCustomImageText;
      }

      // Debug: Log the data being sent
      console.log('Sending agent data:', agentData);
      console.log('Agent type:', agentType);
      
      // Marketing Agent data ready for backend
      
      // Debug Email Write mode customer data
      if (activeTab === 'email-write') {
        console.log('EMAIL WRITE DEBUG - selected_customer being sent:', agentData.selected_customer);
        console.log('EMAIL WRITE DEBUG - emailWriteMode.selectedCustomer:', emailWriteMode.selectedCustomer);
        console.log('EMAIL WRITE DEBUG - email_type:', agentData.email_type);
      }
      
      // Specific debug for email agents
      if (agentType === 'email' && agentData.use_chatgpt_formatting !== undefined) {
        console.log('EMAIL AGENT DEBUG - Fields being sent:');
        console.log('- use_chatgpt_formatting:', agentData.use_chatgpt_formatting);
        console.log('- email_content_template:', agentData.email_content_template);
      }

      // Determine if we're in edit mode and get agent ID
      const urlParams = new URLSearchParams(window.location.search);
      const agentId = urlParams.get('id');
      const isEditing = isEditMode && agentId;

      // Use unified ai-agents endpoints for all agent types (timesheet, social media, etc.)
      const url = isEditing 
        ? `${API_BASE_URL}/api/ai-agents/${agentId}`
        : `${API_BASE_URL}/api/ai-agents`;
      const method = isEditing ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method: method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(agentData)
      });

      if (response.ok) {
        const agentTypeText = agentType === 'time_sheet' ? 'Timesheet' : 'Social Media';
        const modeText = activeTab === 'recurring' ? 'Recurring' : activeTab === 'adhoc' ? 'Adhoc' : 'Custom Post';
        const actionText = isEditMode ? 'updated' : 'configured';
        setMessage({ 
          type: 'success', 
          text: `${agentTypeText} ${modeText} agent ${actionText} successfully!` 
        });
        fetchAgents();
        
        // Redirect to appropriate dashboard after successful save
        if (isEditMode) {
          // Brief delay to show success message, then redirect
          setTimeout(() => {
            navigate('/ai-agents-dashboard');
            // Keep modal visible during redirect - it will be hidden when component unmounts
          }, 1500);
        } else {
          // For new agents, show success message briefly then redirect to dashboard
          // Add longer delay to ensure database write completes before dashboard loads
          setTimeout(() => {
            navigate('/ai-agents-dashboard');
          }, 2500);
        }
        
        // Reset form only when creating new agents (not when editing)
        // Note: Form reset removed since we now redirect to dashboard
        // Users won't see the form after successful creation
      } else {
        const errorData = await response.json();
        let errorText = 'Failed to save agent configuration';
        
        if (errorData.detail) {
          if (Array.isArray(errorData.detail)) {
            // Handle FastAPI validation errors
            errorText = errorData.detail.map(err => {
              const field = err.loc ? err.loc[err.loc.length - 1] : 'field';
              return `${field}: ${err.msg || 'Invalid value'}`;
            }).join(', ');
          } else if (typeof errorData.detail === 'string') {
            errorText = errorData.detail;
          }
        }
        
        setMessage({ type: 'error', text: errorText });
        
        // Hide loading modal on error
        setShowSaveModal(false);
        
        // Scroll to top to show error message
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (error) {
      console.error('Error saving agent:', error);
      setMessage({ type: 'error', text: 'Network error. Please try again.' });
      
      // Hide loading modal on error
      setShowSaveModal(false);
      
      // Scroll to top to show error message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setLoading(false);
    }
  };

  const saveAndRunAgent = async () => {
    if (!editingAgentData) return;
    
    setLoading(true);
    setShowSaveModal(true);
    setMessage({ type: '', text: '' });

    try {
      // First, save/update the agent configuration
      let agentData;
      if (activeTab === 'social-media-recurring') {
        // For editing existing agents, preserve the original mode (auto/recurring)
        const modeToSend = isEditMode && editingAgentData?.mode === 'auto' ? 'auto' : 'recurring';
        agentData = { ...recurringMode, mode: modeToSend };
      } else if (activeTab === 'social-media-adhoc') {
        agentData = { ...adhocMode, mode: 'adhoc' };
      } else if (activeTab === 'social-media-write') {
        agentData = { ...writePostMode, mode: 'write' };
      }

      // Convert camelCase to snake_case for backend
      const backendData = {
        ...agentData,
        social_platforms: agentData.socialPlatforms,
        post_title: agentData.postTitle,
        post_content: agentData.postContent,
        use_web_research: agentData.useWebResearch,
        image_option: agentData.imageOption,
        uploaded_images: agentData.uploadedImages,
        post_time: agentData.postTime,
        image_text: agentData.imageText,
        enable_custom_image_text: agentData.enableCustomImageText,
        custom_image_banner_text: agentData.customImageBannerText,
        enable_social_link: agentData.enableSocialLink,
        social_link: agentData.socialLink,
        word_count: agentData.wordCount,
        days_of_week: agentData.daysOfWeek,
        schedule_type: agentData.scheduleType,
        auto_post: agentData.postDestination === 'auto_post',
        post_destination: agentData.postDestination,
        post_date: agentData.postDate,
        agent_name: agentData.agentName,
        custom_topic: agentData.customTopic
      };
      
      // Remove camelCase fields
      delete backendData.socialPlatforms;
      delete backendData.postTitle;
      delete backendData.postContent;
      delete backendData.useWebResearch;
      delete backendData.imageOption;
      delete backendData.uploadedImages;
      delete backendData.postTime;
      delete backendData.imageText;
      delete backendData.wordCount;
      delete backendData.daysOfWeek;
      delete backendData.scheduleType;
      delete backendData.autoPost;
      delete backendData.postDestination;
      delete backendData.postDate;
      delete backendData.agentName;
      delete backendData.customTopic;

      // Update the agent configuration
      const updateResponse = await fetch(`${API_BASE_URL}/api/ai-agents/${editingAgentData.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(backendData)
      });

      if (!updateResponse.ok) {
        throw new Error('Failed to update agent configuration');
      }

      // Then execute the agent with the updated configuration
      const executeResponse = await fetch(`${API_BASE_URL}/api/ai-agents/${editingAgentData.id}/execute`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });

      if (executeResponse.ok) {
        setMessage({ 
          type: 'success', 
          text: 'Agent updated and executed successfully! Check the dashboard for post status.'
        });
        
        // Redirect to dashboard after successful update and run
        setTimeout(() => {
          navigate('/ai-agents-dashboard');
        }, 2000);
      } else {
        const errorData = await executeResponse.json();
        setMessage({ 
          type: 'error', 
          text: `Agent updated but execution failed: ${errorData.detail || 'Unknown error'}` 
        });
      }
    } catch (error) {
      console.error('Error updating and running agent:', error);
      setMessage({ type: 'error', text: 'Failed to update and run agent. Please try again.' });
    } finally {
      setLoading(false);
      setShowSaveModal(false);
    }
  };

  if (authLoading || pageLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-theme-blue"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        
        {/* Sticky Header */}
        <div className="sticky top-24 z-40 bg-white rounded-xl shadow-lg overflow-hidden mb-6">
          {/* Header */}
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                {/* Icon based on agent type */}
                {searchParams.get('agent_type') === 'time_sheet' ? (
                  <Timer className="h-8 w-8 text-theme-blue" />
                ) : (
                  <Bot className="h-8 w-8 text-theme-blue" />
                )}
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    {(() => {
                      const agentType = searchParams.get('agent_type');
                      if (isRunMode) {
                        return agentType === 'time_sheet' ? 'Run Timesheet AI Agent' : 
                               agentType === 'sms_agent' ? 'Run SMS AI Agent' :
                               agentType === 'marketing_agent' ? 'Run Marketing AI Agent' :
                               agentType === 'paystub_agent' ? 'Run Paystub AI Agent' : 'Run Social Media AI Agent';
                      } else if (isEditMode) {
                        return agentType === 'time_sheet' ? 'Edit Timesheet AI Agent' : 
                               agentType === 'email' ? 'Edit Email AI Agent' :
                               agentType === 'sms_agent' ? 'Edit SMS AI Agent' :
                               agentType === 'marketing_agent' ? 'Edit Marketing AI Agent' :
                               agentType === 'paystub_agent' ? 'Edit Paystub AI Agent' : 'Edit Social Media AI Agent';
                      } else {
                        return agentType === 'time_sheet' ? 'Create New Timesheet Agent' : 
                               agentType === 'email' ? 'Create New Email Agent' :
                               agentType === 'sms_agent' ? 'Create New SMS Agent' :
                               agentType === 'marketing_agent' ? 'Create New Marketing Agent' :
                               agentType === 'paystub_agent' ? 'Create New Paystub Agent' : 'Create New Social Media Agent';
                      }
                    })()}
                  </h2>
                  <p className="text-sm text-gray-600">
                    {(() => {
                      const agentType = searchParams.get('agent_type');
                      if (isRunMode) {
                        return agentType === 'time_sheet' 
                          ? 'Make changes and run your timesheet agent' 
                          : agentType === 'email'
                          ? 'Make changes and run your email agent'
                          : agentType === 'sms_agent'
                          ? 'Make changes and run your SMS agent'
                          : agentType === 'marketing_agent'
                          ? 'Make changes and run your marketing agent'
                          : 'Make changes and run your social media agent';
                      } else if (isEditMode) {
                        return agentType === 'time_sheet'
                          ? 'Modify your timesheet agent settings'
                          : agentType === 'email'
                          ? 'Modify your email agent settings'
                          : agentType === 'sms_agent'
                          ? 'Modify your SMS agent settings'
                          : agentType === 'marketing_agent'
                          ? 'Modify your marketing agent settings'
                          : 'Modify your social media agent settings';
                      } else {
                        return agentType === 'time_sheet'
                          ? 'Configure automated timesheet generation with AI-powered reporting'
                          : agentType === 'email'
                          ? 'Configure AI agents for automated email campaigns and customer communication'
                          : agentType === 'sms_agent'
                          ? 'Configure AI agents for automated SMS campaigns and customer communication'
                          : agentType === 'marketing_agent'
                          ? 'Create comprehensive marketing campaigns across multiple channels including social media, email, and SMS'
                          : 'Configure AI agents for automated social media posting';
                      }
                    })()}
                  </p>
                </div>
              </div>
              {/* Back button - always show */}
              <button
                onClick={() => navigate('/ai-agents-dashboard')}
                className="flex items-center px-4 py-2 text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {/* Content Area */}
          <div className="p-6">
            {/* Message Display - Hide editing messages in edit mode */}
            {message.text && !isEditMode && (
              <div className={`mb-6 rounded-md p-4 ${
                message.type === 'error' 
                  ? 'bg-gray-50 border border-gray-200' 
                  : 'bg-white border border-gray-200'
              }`}>
                <div className="flex">
                  <div className="flex-shrink-0">
                    {message.type === 'error' ? (
                      <AlertCircle className="h-5 w-5 text-red-400" />
                    ) : (
                      <CheckCircle className="h-5 w-5 text-green-400" />
                    )}
                  </div>
                  <div className="ml-3">
                    <p className={`text-sm font-medium ${
                      message.type === 'error' ? 'text-red-800' : 'text-green-800'
                    }`}>
                      {message.text}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Content Area */}
            <>
            {/* Tab Navigation - Only show if tabs exist */}
            {tabs.length > 0 && (
            <div className="border border-gray-200 rounded-lg mb-4 sm:mb-6">
              <div className="border-b border-gray-200">
                <nav className="flex flex-col sm:flex-row sm:space-x-8 px-3 sm:px-6" aria-label="Tabs">
                  {tabs.map((tab) => {
                    const Icon = tab.icon;
                    const isActive = activeTab === tab.id;
                    // In edit mode, disable tabs that don't match the agent being edited
                    const isDisabled = isEditMode && editingAgentMode && tab.id !== activeTab;
                    
                    return (
                      <button
                        key={tab.id}
                        onClick={() => !isDisabled && setActiveTab(tab.id)}
                        disabled={isDisabled}
                        className={`w-full sm:w-auto py-3 sm:py-4 px-3 sm:px-1 border-b-2 sm:border-l-0 font-medium text-sm transition-colors border-transparent ${
                          isDisabled 
                            ? 'text-gray-300 cursor-not-allowed opacity-50' 
                            : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                        style={{
                          borderBottomColor: isActive ? 'var(--theme-primary)' : 'transparent',
                          color: isActive ? 'var(--theme-primary)' : isDisabled ? '#d1d5db' : undefined,
                          backgroundColor: isActive ? '#f0fdff' : 'transparent'
                        }}
                      >
                        <div className="flex items-center justify-center sm:justify-start space-x-2">
                          <Icon className={`h-4 w-4 ${isDisabled ? 'opacity-50' : ''}`} 
                                style={{ color: isActive ? tab.color : isDisabled ? '#d1d5db' : undefined }} />
                          <span>{tab.label}</span>
                          {isDisabled && (
                            <span className="text-xs text-gray-400 ml-1">(disabled)</span>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </nav>
              </div>

              {/* Tab Content */}
              <div className="p-3 sm:p-6">
                {/* Recurring Mode Tab - Now handles different agent types with specific IDs */}
                {(activeTab === 'recurring' || activeTab === 'timesheet-recurring' || activeTab === 'paystub-recurring' || activeTab === 'social-media-recurring') && (
                  <div className="space-y-6">
                    {/* Check if this is a timesheet agent */}
                    {searchParams.get('agent_type') === 'time_sheet' ? (
                      // Timesheet Recurring Mode Form
                      <div className="space-y-6">
                        {/* Agent Configuration Section */}
                        <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-6">
                          {/* Agent Name - Full Row */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              <Timer className="h-4 w-4 inline mr-2" />
                              Agent Name *
                            </label>
                            <input
                              type="text"
                              value={timesheetRecurringMode.agentName}
                              onChange={(e) => handleTimesheetRecurringModeChange('agentName', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 text-sm"
                              placeholder="e.g., Weekly Payroll Report Agent"
                              required
                            />
                          </div>

                          {/* Scheduling Configuration */}
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {/* Run Every PayPeriod - Based on All Pay Period Settings */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                <Clock className="h-4 w-4 inline mr-2" />
                                Run Every PayPeriod *
                              </label>
                              <select
                                value={timesheetRecurringMode.runEveryPayPeriod}
                                onChange={(e) => {
                                  handleTimesheetRecurringModeChange('runEveryPayPeriod', e.target.value);
                                  // Update selected pay period when changed
                                  const selectedSetting = defaultPayPeriods.find(p => p.period_type === e.target.value);
                                  if (selectedSetting) {
                                    setSelectedPayPeriod(selectedSetting);
                                  }
                                }}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 bg-gray-100 cursor-not-allowed text-sm"
                                disabled
                                required
                              >
                                {defaultPayPeriods.map(period => (
                                  <option key={period.id} value={period.period_type} className="text-sm">
                                    {period.period_type === 'weekly' ? 'Weekly' : 
                                     period.period_type === 'bi_weekly' ? 'Bi-Weekly' : 'Monthly'}
                                  </option>
                                ))}
                              </select>
                              <p className="text-xs text-gray-500 mt-1">
                                Uses the default pay period configured in Timesheet Configuration. Agent will run after each completed pay period.
                              </p>
                            </div>

                            {/* Days After Period End */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                <Calendar className="h-4 w-4 inline mr-2" />
                                Days After Period End *
                              </label>
                              <select
                                value={timesheetRecurringMode.daysAfterPeriodEnd}
                                onChange={(e) => handleTimesheetRecurringModeChange('daysAfterPeriodEnd', parseInt(e.target.value))}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 text-sm"
                                required
                              >
                                {Array.from({length: 7}, (_, i) => i + 1).map(day => (
                                  <option key={day} value={day} className="text-sm">
                                    {day} day{day > 1 ? 's' : ''} after
                                  </option>
                                ))}
                              </select>
                              <p className="text-xs text-gray-500 mt-1">
                                {selectedPayPeriod && (
                                  <>
                                    If pay period ends on a {selectedPayPeriod.weekly_config?.end_day || 
                                      selectedPayPeriod.bi_weekly_config?.end_day || 'day'}, 
                                    agent runs {timesheetRecurringMode.daysAfterPeriodEnd} day{timesheetRecurringMode.daysAfterPeriodEnd > 1 ? 's' : ''} later
                                  </>
                                )}
                              </p>
                            </div>

                            {/* Schedule Time */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                <Clock className="h-4 w-4 inline mr-2" />
                                Schedule Time
                              </label>
                              <input
                                type="time"
                                value={timesheetRecurringMode.scheduleTime}
                                onChange={(e) => handleTimesheetRecurringModeChange('scheduleTime', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 text-sm"
                              />
                            </div>
                          </div>
                        </div>

                        {/* Pay Period Configuration Display */}
                        {selectedPayPeriod && (
                          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                            <h4 className="font-medium text-blue-900 mb-2 flex items-center">
                              <Calendar className="h-5 w-5 mr-2" />
                              Selected Pay Period Configuration
                            </h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                              <div>
                                <span className="font-medium text-blue-800">Period Type:</span>
                                <span className="ml-2 text-blue-900">
                                  {selectedPayPeriod.period_type === 'weekly' ? 'Weekly' : 
                                   selectedPayPeriod.period_type === 'bi_weekly' ? 'Bi-Weekly' : 'Monthly'}
                                </span>
                              </div>
                              {selectedPayPeriod.weekly_config && (
                                <div>
                                  <span className="font-medium text-blue-800">Period:</span>
                                  <span className="ml-2 text-blue-900">
                                    {selectedPayPeriod.weekly_config.start_day.charAt(0).toUpperCase() + selectedPayPeriod.weekly_config.start_day.slice(1)} to {' '}
                                    {selectedPayPeriod.weekly_config.end_day.charAt(0).toUpperCase() + selectedPayPeriod.weekly_config.end_day.slice(1)}
                                  </span>
                                </div>
                              )}
                              {selectedPayPeriod.bi_weekly_config && (
                                <div>
                                  <span className="font-medium text-blue-800">Period:</span>
                                  <span className="ml-2 text-blue-900">
                                    {selectedPayPeriod.bi_weekly_config.start_day.charAt(0).toUpperCase() + selectedPayPeriod.bi_weekly_config.start_day.slice(1)} to {' '}
                                    {selectedPayPeriod.bi_weekly_config.end_day.charAt(0).toUpperCase() + selectedPayPeriod.bi_weekly_config.end_day.slice(1)} (Every 2 weeks)
                                  </span>
                                </div>
                              )}
                              {selectedPayPeriod.monthly_config && (
                                <div>
                                  <span className="font-medium text-blue-800">Period:</span>
                                  <span className="ml-2 text-blue-900">
                                    {selectedPayPeriod.monthly_config.start_day === 1 ? '1st' : `${selectedPayPeriod.monthly_config.start_day}th`} to {' '}
                                    {selectedPayPeriod.monthly_config.end_day === -1 ? 'Last Day' : 
                                     selectedPayPeriod.monthly_config.end_day === 1 ? '1st' : `${selectedPayPeriod.monthly_config.end_day}th`}
                                  </span>
                                </div>
                              )}
                            </div>
                            <div className="mt-3 p-2 bg-gray-100 rounded text-sm">
                              <span className="font-semibold text-blue-900">Agent Schedule:</span>
                              <span className="text-blue-800 ml-1">
                                Will run {timesheetRecurringMode.daysAfterPeriodEnd} day{timesheetRecurringMode.daysAfterPeriodEnd > 1 ? 's' : ''} after each pay period ends at {timesheetRecurringMode.scheduleTime}
                              </span>
                            </div>
                          </div>
                        )}

                        {/* Employee Selection Section */}
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                          <label className="block text-sm font-medium text-gray-700 mb-3">
                            <Users className="h-4 w-4 inline mr-2" />
                            Employee Selection
                          </label>
                          <p className="text-sm font-medium text-gray-600 mb-3">
                            Select specific employees for this report (leave none selected to include all employees)
                          </p>
                          <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-md p-3 bg-white">
                            {availableEmployees.length > 0 ? (
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                {availableEmployees.map((employee) => (
                                  <label key={employee.id} className="flex items-center p-2 hover:bg-gray-100 rounded cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={timesheetRecurringMode.selectedEmployees.includes(employee.id)}
                                      onChange={() => handleEmployeeToggle(employee.id, 'recurring')}
                                      className="h-4 w-4 border-gray-300 rounded"
                                      style={{
                                        accentColor: 'var(--theme-primary)',
                                        filter: 'brightness(1) contrast(1)',
                                        colorScheme: 'light'
                                      }}
                                    />
                                    <span className="ml-2 text-sm font-medium text-gray-700">
                                      {employee.full_name}
                                    </span>
                                  </label>
                                ))}
                              </div>
                            ) : (
                              <p className="text-sm font-medium text-gray-500">No employees found</p>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 mt-2">
                            Selected: {timesheetRecurringMode.selectedEmployees.length > 0 ? timesheetRecurringMode.selectedEmployees.length : 'All'} employees
                          </p>
                        </div>

                        {/* Report Generation Section */}
                        <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-4">
                          <label className="block text-sm font-medium text-gray-700 mb-3">
                            <FileText className="h-4 w-4 inline mr-2" />
                            Report Generation
                          </label>
                          
                          {/* Report Features */}
                          <div>
                            <label className="flex items-center">
                              <input
                                type="checkbox"
                                checked={timesheetRecurringMode.includeBillingRates}
                                onChange={(e) => handleTimesheetRecurringModeChange('includeBillingRates', e.target.checked)}
                                className="h-4 w-4 border-gray-300 rounded"
                                style={{
                                  accentColor: 'var(--theme-primary)',
                                  filter: 'brightness(1) contrast(1)',
                                  colorScheme: 'light'
                                }}
                              />
                              <span className="ml-2 text-sm font-medium text-gray-700">Include Billing Rates</span>
                            </label>
                          </div>

                          {/* Email Recipients */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              <Mail className="h-4 w-4 inline mr-2" />
                              Email Recipients
                            </label>
                            <div className="flex space-x-2 mb-2">
                              <input
                                type="email"
                                value={newEmailRecipient}
                                onChange={(e) => setNewEmailRecipient(e.target.value)}
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 text-sm"
                                placeholder="Enter email address"
                              />
                              <button
                                type="button"
                                onClick={() => addEmailRecipient('recurring')}
                                className="px-4 py-2 text-white rounded-md transition-colors text-sm font-medium"
                                style={{
                                  backgroundColor: 'var(--theme-primary)'
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.backgroundColor = 'var(--theme-primary-hover)';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.backgroundColor = 'var(--theme-primary)';
                                }}
                              >
                                Add
                              </button>
                            </div>
                            
                            {/* Email Recipients List */}
                            {timesheetRecurringMode.emailRecipients.length > 0 && (
                              <div className="border border-gray-200 rounded-md p-3 max-h-32 overflow-y-auto">
                                {timesheetRecurringMode.emailRecipients.map((email, index) => (
                                  <div key={index} className="flex items-center justify-between py-1">
                                    <span className="text-sm font-medium text-gray-700">{email}</span>
                                    <button
                                      type="button"
                                      onClick={() => removeEmailRecipient(index, 'recurring')}
                                      className="text-red-600 hover:text-red-700 text-sm font-medium"
                                    >
                                      Remove
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Content Review Workflow */}
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                          <label className="block text-sm font-medium text-gray-700 mb-4">
                            <Eye className="h-4 w-4 inline mr-2" />
                            Workflow Mode
                          </label>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
                              <input
                                type="radio"
                                name="timesheetInitialStatus"
                                value="in_review"
                                checked={timesheetRecurringMode.initialStatus === 'in_review'}
                                onChange={(e) => handleTimesheetRecurringModeChange('initialStatus', e.target.value)}
                                className="mr-3"
                              />
                              <div>
                                <div className="font-medium text-sm">In Review</div>
                                <div className="text-xs text-gray-500">Reports go to "In Review" for manager approval</div>
                              </div>
                            </label>
                            <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
                              <input
                                type="radio"
                                name="timesheetInitialStatus"
                                value="ready_to_publish"
                                checked={timesheetRecurringMode.initialStatus === 'ready_to_publish'}
                                onChange={(e) => handleTimesheetRecurringModeChange('initialStatus', e.target.value)}
                                className="mr-3"
                              />
                              <div>
                                <div className="font-medium text-sm">Ready to Publish</div>
                                <div className="text-xs text-gray-500">Reports go directly to "Ready to Publish"</div>
                              </div>
                            </label>
                            <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
                              <input
                                type="radio"
                                name="timesheetInitialStatus"
                                value="published"
                                checked={timesheetRecurringMode.initialStatus === 'published'}
                                onChange={(e) => handleTimesheetRecurringModeChange('initialStatus', e.target.value)}
                                className="mr-3"
                              />
                              <div>
                                <div className="font-medium text-sm">Auto-Publish</div>
                                <div className="text-xs text-gray-500">Reports are automatically published and emailed</div>
                              </div>
                            </label>
                          </div>
                        </div>

                        {/* Save Button for Timesheet Recurring Mode */}
                        <div className="flex justify-end space-x-3 pt-4">
                          <button
                            onClick={isRunMode ? saveAndRunAgent : saveAgent}
                            disabled={loading || (!isEditMode && !isRunMode && !isTimesheetRecurringModeValid())}
                            className="text-white px-6 py-2 rounded-lg transition-colors disabled:opacity-50 flex items-center font-medium"
                            style={{ 
                              backgroundColor: loading ? '#94a3b8' : isRunMode ? '#10b981' : 'var(--theme-primary)'
                            }}
                            onMouseEnter={(e) => {
                              if (!loading) e.target.style.backgroundColor = isRunMode ? '#059669' : 'var(--theme-primary-hover)';
                            }}
                            onMouseLeave={(e) => {
                              if (!loading) e.target.style.backgroundColor = isRunMode ? '#10b981' : 'var(--theme-primary)';
                            }}
                          >
                            {isRunMode ? <Play className="h-4 w-4 mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                            {loading ? (isRunMode ? 'Updating & Running...' : 'Saving...') : 
                             isRunMode ? 'Update & Run Agent' : 
                             isEditMode ? 'Update Agent Configuration' : 'Save Agent'}
                          </button>
                        </div>
                      </div>
                    ) : searchParams.get('agent_type') === 'paystub_agent' ? (
                      // Paystub Recurring Mode Form
                      <div className="space-y-6">
                        {/* Agent Configuration Section */}
                        <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-6">
                          {/* Agent Name - Full Row */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              <DollarSign className="h-4 w-4 inline mr-2" />
                              Agent Name *
                            </label>
                            <input
                              type="text"
                              value={paystubRecurringMode.agentName}
                              onChange={(e) => setPaystubRecurringMode({...paystubRecurringMode, agentName: e.target.value})}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                              placeholder="Enter agent name"
                              required
                            />
                          </div>

                          {/* Pay Period Configuration */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Runs Every Pay Period */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                <Calendar className="h-4 w-4 inline mr-2" />
                                Runs Every Pay Period *
                              </label>
                              <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600 text-sm">
                                {selectedPayPeriod ? (
                                  selectedPayPeriod.period_type === 'weekly' ? 'Weekly' : 
                                  selectedPayPeriod.period_type === 'bi_weekly' ? 'Biweekly' : 'Monthly'
                                ) : 'Biweekly'}
                              </div>
                              <p className="text-xs text-gray-500 mt-1">
                                Uses the default pay period configured in Timesheet Configuration. Agent will run after each completed pay period.
                              </p>
                            </div>

                            {/* Paystub Will be Generated */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                <Clock className="h-4 w-4 inline mr-2" />
                                Paystub Will be Generated *
                              </label>
                              <select
                                value={paystubRecurringMode.daysAfterTimesheetPublished}
                                onChange={(e) => setPaystubRecurringMode({...paystubRecurringMode, daysAfterTimesheetPublished: parseInt(e.target.value)})}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                                required
                              >
                                <option value={0} className="text-sm">Immediately after Timesheet is Published</option>
                                <option value={1} className="text-sm">1 Day after Timesheet is Published</option>
                                <option value={2} className="text-sm">2 Days after Timesheet is Published</option>
                              </select>
                              <p className="text-xs text-gray-500 mt-1">
                                When timesheet is published, paystub agent will be triggered based on selected timing
                              </p>
                            </div>
                          </div>

                          {/* Pay Period Configuration Display */}
                          {selectedPayPeriod && (
                            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                              <h4 className="text-sm font-medium text-blue-900 mb-2 flex items-center">
                                <Calendar className="h-5 w-5 mr-2" />
                                Selected Pay Period Configuration
                              </h4>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                <div>
                                  <span className="font-medium text-blue-800">Period Type:</span>
                                  <span className="ml-2 text-blue-900">
                                    {selectedPayPeriod.period_type === 'weekly' ? 'Weekly' : 
                                     selectedPayPeriod.period_type === 'bi_weekly' ? 'Biweekly' : 'Monthly'}
                                  </span>
                                </div>
                                {selectedPayPeriod.weekly_config && (
                                  <div>
                                    <span className="font-medium text-blue-800">Period:</span>
                                    <span className="ml-2 text-blue-900">
                                      {selectedPayPeriod.weekly_config.start_day.charAt(0).toUpperCase() + selectedPayPeriod.weekly_config.start_day.slice(1)} to {' '}
                                      {selectedPayPeriod.weekly_config.end_day.charAt(0).toUpperCase() + selectedPayPeriod.weekly_config.end_day.slice(1)}
                                    </span>
                                  </div>
                                )}
                                {selectedPayPeriod.bi_weekly_config && (
                                  <div>
                                    <span className="font-medium text-blue-800">Period:</span>
                                    <span className="ml-2 text-blue-900">
                                      {selectedPayPeriod.bi_weekly_config.start_day.charAt(0).toUpperCase() + selectedPayPeriod.bi_weekly_config.start_day.slice(1)} to {' '}
                                      {selectedPayPeriod.bi_weekly_config.end_day.charAt(0).toUpperCase() + selectedPayPeriod.bi_weekly_config.end_day.slice(1)} (Every 2 weeks)
                                    </span>
                                  </div>
                                )}
                                {selectedPayPeriod.monthly_config && (
                                  <div>
                                    <span className="font-medium text-blue-800">Period:</span>
                                    <span className="ml-2 text-blue-900">
                                      {selectedPayPeriod.monthly_config.start_day === 1 ? '1st' : `${selectedPayPeriod.monthly_config.start_day}th`} to {' '}
                                      {selectedPayPeriod.monthly_config.end_day === -1 ? 'Last Day' : 
                                       selectedPayPeriod.monthly_config.end_day === 1 ? '1st' : `${selectedPayPeriod.monthly_config.end_day}th`}
                                    </span>
                                  </div>
                                )}
                              </div>
                              <div className="mt-3 p-2 bg-gray-100 rounded text-sm">
                                <p>
                                  <strong>Agent Schedule:</strong> Will run {paystubRecurringMode.daysAfterTimesheetPublished} day{paystubRecurringMode.daysAfterTimesheetPublished !== 1 ? 's' : ''} after each timesheet is published at {paystubRecurringMode.scheduleTime}
                                </p>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Employee Selection Section */}
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                          <label className="block text-sm font-medium text-gray-700 mb-3">
                            <Users className="h-4 w-4 inline mr-2" />
                            Employee Selection
                          </label>
                          <p className="text-sm font-medium text-gray-600 mb-3">
                            Select specific employees for paystub generation (leave none selected to include all employees)
                          </p>
                          <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-md p-3 bg-white">
                            {availableEmployees.length > 0 ? (
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                {availableEmployees.map((employee) => (
                                  <label key={employee.id} className="flex items-center p-2 hover:bg-gray-100 rounded cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={paystubRecurringMode.selectedEmployees.includes(employee.id)}
                                      onChange={() => handleEmployeeToggle(employee.id, 'paystub_recurring')}
                                      className="h-4 w-4 border-gray-300 rounded"
                                      style={{
                                        accentColor: '#10b981',
                                        filter: 'brightness(1) contrast(1)',
                                        colorScheme: 'light'
                                      }}
                                    />
                                    <span className="ml-2 text-sm font-medium text-gray-700">
                                      {employee.full_name}
                                    </span>
                                  </label>
                                ))}
                              </div>
                            ) : (
                              <p className="text-sm font-medium text-gray-500 text-center py-4">No employees available</p>
                            )}
                          </div>
                          {paystubRecurringMode.selectedEmployees.length > 0 && (
                            <p className="text-xs text-green-600 mt-2">
                              {paystubRecurringMode.selectedEmployees.length} employee{paystubRecurringMode.selectedEmployees.length !== 1 ? 's' : ''} selected
                            </p>
                          )}
                        </div>

                        {/* Content Review Workflow */}
                        <div className="bg-white border border-gray-200 rounded-lg p-6">
                          <label className="block text-sm font-medium text-gray-700 mb-4">
                            <Settings className="h-4 w-4 inline mr-2" />
                            Workflow Mode
                          </label>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <label className="flex items-start p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 border">
                              <input
                                type="radio"
                                name="paystubRecurringWorkflowMode"
                                value="in_review"
                                checked={paystubRecurringMode.workflowMode === 'in_review'}
                                onChange={(e) => setPaystubRecurringMode({...paystubRecurringMode, workflowMode: e.target.value})}
                                className="mr-3"
                              />
                              <div>
                                <div className="font-medium text-sm">In Review</div>
                                <div className="text-xs text-gray-500">Paystubs go to "In Review" for manager approval</div>
                              </div>
                            </label>
                            <label className="flex items-start p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 border">
                              <input
                                type="radio"
                                name="paystubRecurringWorkflowMode"
                                value="ready_to_publish"
                                checked={paystubRecurringMode.workflowMode === 'ready_to_publish'}
                                onChange={(e) => setPaystubRecurringMode({...paystubRecurringMode, workflowMode: e.target.value})}
                                className="mr-3"
                              />
                              <div>
                                <div className="font-medium text-sm">Ready to Publish</div>
                                <div className="text-xs text-gray-500">Paystubs are ready for immediate publication</div>
                              </div>
                            </label>
                            <label className="flex items-start p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 border">
                              <input
                                type="radio"
                                name="paystubRecurringWorkflowMode"
                                value="published"
                                checked={paystubRecurringMode.workflowMode === 'published'}
                                onChange={(e) => setPaystubRecurringMode({...paystubRecurringMode, workflowMode: e.target.value})}
                                className="mr-3"
                              />
                              <div>
                                <div className="font-medium text-sm">Published</div>
                                <div className="text-xs text-gray-500">Paystubs are automatically published</div>
                              </div>
                            </label>
                          </div>
                        </div>

                        {/* Save/Run Button */}
                        <div className="flex justify-end pt-4">
                          <button
                            onClick={() => handleSave(isRunMode)}
                            disabled={loading}
                            className="px-6 py-2 text-white font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors flex items-center"
                            style={{
                              backgroundColor: isRunMode ? '#10b981' : 'var(--theme-primary)',
                              borderColor: isRunMode ? '#10b981' : 'var(--theme-primary)'
                            }}
                            onMouseEnter={(e) => {
                              if (!loading) e.target.style.backgroundColor = isRunMode ? '#059669' : 'var(--theme-primary-hover)';
                            }}
                            onMouseLeave={(e) => {
                              if (!loading) e.target.style.backgroundColor = isRunMode ? '#10b981' : 'var(--theme-primary)';
                            }}
                          >
                            {isRunMode ? <Play className="h-4 w-4 mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                            {loading ? (isRunMode ? 'Updating & Running...' : 'Saving...') : 
                             isRunMode ? 'Update & Run Agent' : 
                             isEditMode ? 'Update Agent Configuration' : 'Save Agent'}
                          </button>
                        </div>
                      </div>
                    ) : (
                     
                      // Social Media Recurring Mode Form
                      <div className="space-y-6">
                        {/* Agent Name & Schedule Section - Top Section */}
                        <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-6">
                          {/* Agent Name - Full Row */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              <User className="h-4 w-4 inline mr-2" />
                              Agent Name *
                            </label>
                            <input
                              type="text"
                              value={recurringMode.agentName}
                              onChange={(e) => handleRecurringModeChange('agentName', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 text-sm"
                              placeholder="e.g., Weekly Social Media Agent"
                              required
                            />
                          </div>

                          {/* Scheduling Configuration - Separate Row */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Schedule Selection */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Schedule Selection *
                              </label>
                              <select
                                value={recurringMode.scheduleType}
                                onChange={(e) => handleRecurringModeChange('scheduleType', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 text-sm"
                                required
                              >
                                <option value="all_days">All Days</option>
                                <option value="selected_days">Selected Days</option>
                              </select>
                            </div>

                            {/* Post Time - Show for both All Days and Selected Days */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                <Clock className="h-4 w-4 inline mr-2" />
                                Post Time *
                              </label>
                              <select
                                value={recurringMode.postTime}
                                onChange={(e) => handleRecurringModeChange('postTime', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 text-sm"
                                required
                              >
                                {timeOptions.map(option => (
                                  <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                              </select>
                            </div>
                          </div>

                          {/* Selected Days Configuration */}
                          {recurringMode.scheduleType === 'selected_days' && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-4">
                                <Calendar className="h-4 w-4 inline mr-2" />
                                Select Days *
                              </label>
                              <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
                                {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(day => (
                                  <label key={day} className="flex items-center space-x-2 p-3 bg-gray-50 rounded-lg border hover:bg-gray-100 cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={recurringMode.daysOfWeek?.[day] || false}
                                      onChange={(e) => {
                                        handleRecurringModeChange('daysOfWeek', {
                                          ...(recurringMode.daysOfWeek || {}),
                                          [day]: e.target.checked
                                        });
                                      }}
                                      className="h-4 w-4 text-theme-blue border-gray-300 rounded focus:ring-blue-500"
                                    />
                                    <span className="text-sm font-medium text-gray-700 capitalize">
                                      {day}
                                    </span>
                                  </label>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Topic & Word Count Section */}
                        <div className="space-y-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
                          {/* Topic and Word Count - Side by side */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Topic Selection */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                <Target className="h-4 w-4 inline mr-2" />
                                Select Topic *
                              </label>
                              <select
                                value={recurringMode.topic}
                                onChange={(e) => handleRecurringModeChange('topic', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 text-sm"
                                required
                              >
                                <option value="">Select a topic...</option>
                                {topicOptions.map(topic => (
                                  <option key={topic} value={topic}>{topic}</option>
                                ))}
                              </select>
                              {recurringMode.topic === 'Custom' && (
                                <input
                                  type="text"
                                  value={recurringMode.customTopic}
                                  onChange={(e) => handleRecurringModeChange('customTopic', e.target.value)}
                                  className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 text-sm"
                                  placeholder="Enter custom topic..."
                                />
                              )}
                            </div>

                            {/* Word Count */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                <Hash className="h-4 w-4 inline mr-2" />
                                Word Count *
                              </label>
                              <select
                                value={recurringMode.wordCount}
                                onChange={(e) => handleRecurringModeChange('wordCount', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 text-sm"
                                                    required
                                                  >
                                                    {wordCountOptions.map(option => (
                                                      <option key={option.value} value={option.value}>{option.label}</option>
                                                    ))}
                                                  </select>
                                                </div>
                          
                                              </div>
                        </div>

                        {/* Duplicate Schedule Section Removed - Schedule configuration moved to Agent Name section above */}

                        {/* Social Media Platforms & Image Format Selection - MOVED TO TOP */}
                        <div className="space-y-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                          {/* Social Platforms */}
                          <div className="space-y-3">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              <Share2 className="h-4 w-4 inline mr-2" />
                              Select Social Media Platforms *
                            </label>
                            {/* All 5 platforms in one row with checkboxes on the left */}
                            <div className="grid grid-cols-5 gap-2">
                              {Object.entries(recurringMode.socialPlatforms).map(([platform, selected]) => (
                                <label
                                  key={platform}
                                  className={`flex items-center p-2 rounded-lg border cursor-pointer transition-colors ${
                                    selected ? 'bg-gray-50 border-gray-300' : 'bg-white border-gray-200 hover:bg-gray-50'
                                  }`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={selected}
                                    onChange={(e) => handleSocialPlatformChange(platform, e.target.checked, 'recurring')}
                                    className="h-3 w-3 text-gray-600 border-gray-300 rounded focus:ring-gray-500 mr-2"
                                  />
                                  <span className="capitalize text-sm font-medium">{platform}</span>
                                </label>
                              ))}
                            </div>
                            {!Object.values(recurringMode.socialPlatforms).some(platform => platform) && (
                              <p className="text-sm text-red-600">Please select at least one platform.</p>
                            )}
                          </div>

                          {/* Image Format Selection moved to appear below image options */}
                        </div>

                        {/* Image Options & Upload Section - Combined with border and background */}
                        <div className="space-y-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                          <label className="block text-sm font-medium text-gray-700">
                            <ImageIcon className="h-4 w-4 inline mr-2" />
                            Image Options
                          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
              <input
                type="radio"
                name="recurringImageOption"
                value="ai_generate"
                checked={recurringMode.imageOption === 'ai_generate'}
                onChange={(e) => handleRecurringModeChange('imageOption', e.target.value)}
                className="mr-3"
              />
              <div>
                <div className="font-medium text-sm">AI Generate</div>
                <div className="text-xs text-gray-500">Create unique images using AI</div>
              </div>
            </label>
            <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
              <input
                type="radio"
                name="recurringImageOption"
                value="upload"
                checked={recurringMode.imageOption === 'upload'}
                onChange={(e) => handleRecurringModeChange('imageOption', e.target.value)}
                className="mr-3"
              />
              <div>
                <div className="font-medium text-sm">Upload Image</div>
                <div className="text-xs text-gray-500">Use your own images</div>
              </div>
            </label>
            <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
              <input
                type="radio"
                name="recurringImageOption"
                value="reference"
                checked={recurringMode.imageOption === 'reference'}
                onChange={(e) => handleRecurringModeChange('imageOption', e.target.value)}
                className="mr-3"
              />
              <div>
                <div className="font-medium text-sm">Select Existing Image</div>
                <div className="text-xs text-gray-500">Select from uploaded images</div>
              </div>
            </label>
            <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
              <input
                type="radio"
                name="recurringImageOption"
                value="none"
                checked={recurringMode.imageOption === 'none'}
                onChange={(e) => handleRecurringModeChange('imageOption', e.target.value)}
                className="mr-3"
              />
              <div>
                <div className="font-medium text-sm">No Images</div>
                <div className="text-xs text-gray-500">Text-only posts</div>
              </div>
            </label>
          </div>

          {/* Select Image Format for Each Platform - MOVED TO APPEAR DIRECTLY BELOW IMAGE OPTIONS */}
          {Object.values(recurringMode.socialPlatforms).some(platform => platform) && recurringMode.imageOption !== 'none' && (
            <div className="space-y-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                <ImageIcon className="h-4 w-4 inline mr-2" />
                Select Image Format for Each Platform
              </label>
              
              {/* Format Selection for Each Enabled Platform - Dynamic grid */}
              <div className={`grid gap-2 ${Object.entries(recurringMode.socialPlatforms).filter(([platform, enabled]) => enabled).length === 1 ? 'grid-cols-1' : Object.entries(recurringMode.socialPlatforms).filter(([platform, enabled]) => enabled).length === 2 ? 'grid-cols-2' : Object.entries(recurringMode.socialPlatforms).filter(([platform, enabled]) => enabled).length === 3 ? 'grid-cols-3' : Object.entries(recurringMode.socialPlatforms).filter(([platform, enabled]) => enabled).length === 4 ? 'grid-cols-4' : 'grid-cols-5'}`}>
                {Object.entries(recurringMode.socialPlatforms)
                  .filter(([platform, enabled]) => enabled)
                  .map(([platform, enabled]) => (
                    <div key={platform} className="flex flex-col">
                      <label className="text-sm font-medium text-gray-600 mb-2 text-left capitalize">
                        {platform}
                      </label>
                      <select
                        value={recurringMode.platformImageFormats[platform] || ''}
                        onChange={(e) => {
                          const newFormats = { ...(recurringMode.platformImageFormats || {}) };
                          if (e.target.value) {
                            newFormats[platform] = e.target.value;
                          } else {
                            delete newFormats[platform];
                          }
                          handleRecurringModeChange('platformImageFormats', newFormats);
                        }}
                        className="w-full px-2 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                      >
                        <option value="">Select...</option>
                        {Object.entries(socialMediaPlatforms[platform === 'twitter' ? 'twitter' : platform]?.formats || {}).map(([formatKey, formatName]) => (
                          <option key={formatKey} value={formatKey}>{formatName}</option>
                        ))}
                      </select>
                      
                      {/* Show format dimensions */}
                      {recurringMode.platformImageFormats[platform] && socialMediaImageSettings[platform === 'twitter' ? 'twitter' : platform]?.[recurringMode.platformImageFormats[platform]] && (
                        <div className="text-xs text-gray-600 mt-1 text-left">
                          {socialMediaImageSettings[platform === 'twitter' ? 'twitter' : platform][recurringMode.platformImageFormats[platform]].width} × {socialMediaImageSettings[platform === 'twitter' ? 'twitter' : platform][recurringMode.platformImageFormats[platform]].height}
                        </div>
                      )}
                    </div>
                  ))
                }
              </div>
            </div>
          )}

          {/* Image Upload Area - Integrated into Image Options */}
          {recurringMode.imageOption === 'upload' && (
            <div className="mt-4">
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center bg-gray-50">
                <Users className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                <p className="text-sm text-gray-600 mb-2">Upload social media images</p>
                <p className="text-xs text-gray-500 mb-4">Recommended: High resolution images for best social media results</p>
                <div className="flex justify-center">
                  <label className="inline-flex items-center px-4 py-2 text-white rounded-md hover:opacity-90 transition-colors cursor-pointer" style={{ backgroundColor: 'var(--theme-primary)' }}>
                    <Upload className="h-4 w-4 mr-2" />
                    Upload Image
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e, 'recurring')}
                      className="hidden"
                    />
                  </label>
                </div>
              </div>
            </div>
          )}
          
          {recurringMode.imageOption === 'reference' && (
            <div className="mt-4">
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center bg-gray-50">
                <ImageIcon className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                <p className="text-sm text-gray-600 mb-2">Select reference images</p>
                <p className="text-xs text-gray-500 mb-4">Choose images from your asset library to use as style reference</p>
                <div className="flex justify-center">
                  <button
                    type="button"
                    onClick={() => openAssetBrowser('recurring')}
                    className="inline-flex items-center px-4 py-2 text-white rounded-md hover:opacity-90 transition-colors" style={{ backgroundColor: 'var(--theme-primary)' }}
                  >
                    <Folder className="h-4 w-4 mr-2" />
                    Browse Assets
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Uploaded Images Preview */}
          {(recurringMode.imageOption === 'upload' || recurringMode.imageOption === 'reference') && recurringMode.uploadedImages.length > 0 && (
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Selected Images ({recurringMode.imageOption === 'reference' ? 'Reference' : 'Upload'})
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {recurringMode.uploadedImages.map((image, index) => (
                  <div key={index} className="relative">
                    <img 
                      src={image.url} 
                      alt={image.name}
                      className="w-full h-24 object-cover rounded-lg border"
                    />
                    <button
                      onClick={() => removeUploadedImage(index, 'recurring')}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        {/* Add Custom Text Banner to Image - MOVED HERE below Image Options */}
        {(recurringMode.imageOption === 'ai_generate' || recurringMode.imageOption === 'upload' || recurringMode.imageOption === 'reference') && (
          <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
            <label className="flex items-start">
              <input
                type="checkbox"
                checked={recurringMode.enableCustomImageText}
                onChange={(e) => handleRecurringModeChange('enableCustomImageText', e.target.checked)}
                className="h-4 w-4 text-theme-blue border-gray-300 rounded focus:ring-gray-500 mr-3"
              />
              <span className="block text-sm font-medium text-gray-700">
                <FileText className="h-4 w-4 inline mr-2" />
                Add Custom Text Banner to Image
              </span>
            </label>
            <p className="text-xs text-gray-500 mt-2 ml-7">
              Add custom text overlay on the generated image (e.g., your clinic name, promotional text)
            </p>
            
            {recurringMode.enableCustomImageText && (
              <div className="mt-3">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Custom Banner Text
                  </label>
                  <button
                    type="button"
                    onClick={() => openPlaceholderModal('customImageBannerText', handleRecurringModeChange)}
                    className="px-3 py-1 text-xs font-medium text-white rounded transition-colors"
                    style={{ backgroundColor: 'var(--theme-primary)' }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = 'rgb(35, 147, 180)'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'var(--theme-primary)'}
                  >
                    Available Placeholders
                  </button>
                </div>
                <input
                  type="text"
                  value={recurringMode.customImageBannerText || '[BUSINESS_NAME] [PHONE_NUMBER]'}
                  onChange={(e) => handleRecurringModeChange('customImageBannerText', e.target.value)}
                  className="w-full px-3 py-2 text-sm font-medium border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                  placeholder="[BUSINESS_NAME] [PHONE_NUMBER]"
                />
              </div>
            )}
          </div>
        )}

        {/* Add Link Section - Social Media */}
        <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <label className="flex items-start">
            <input
              type="checkbox"
              checked={recurringMode.enableSocialLink}
              onChange={(e) => handleRecurringModeChange('enableSocialLink', e.target.checked)}
              className="h-4 w-4 text-theme-blue border-gray-300 rounded focus:ring-gray-500 mr-3"
            />
            <span className="block text-sm font-medium text-gray-700">
              <Globe className="h-4 w-4 inline mr-2" />
              Add Link
            </span>
          </label>
          <p className="text-xs text-gray-500 mt-2 ml-7">
            Add a link to your social media post (e.g., website, promotion, product page)
          </p>
          
          {recurringMode.enableSocialLink && (
            <div className="mt-3">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Link URL
              </label>
              <input
                type="text"
                value={recurringMode.socialLink || ''}
                onChange={(e) => handleRecurringModeChange('socialLink', e.target.value)}
                className="w-full px-3 py-2 text-sm font-medium border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                placeholder="https://example.com"
              />
            </div>
          )}
        </div>

        {/* Social Media Platforms moved above Image Options */}

        {/* Content Review Workflow - Styled like Image Options blocks */}
        <div className="space-y-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Content Review Workflow
          </label>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
              <input
                type="radio"
                name="recurringPostDestination"
                value="in_review"
                checked={recurringMode.postDestination === 'in_review'}
                onChange={(e) => handleRecurringModeChange('postDestination', e.target.value)}
                className="mr-3"
              />
              <div>
                <div className="font-medium text-sm">In Review First</div>
                <div className="text-xs text-gray-500">Generated posts go to "In Review" for manager approval</div>
              </div>
            </label>
            <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
              <input
                type="radio"
                name="recurringPostDestination"
                value="ready_to_publish"
                checked={recurringMode.postDestination === 'ready_to_publish'}
                onChange={(e) => handleRecurringModeChange('postDestination', e.target.value)}
                className="mr-3"
              />
              <div>
                <div className="font-medium text-sm">Ready to Publish</div>
                <div className="text-xs text-gray-500">Generated posts go directly to "Ready to Publish"</div>
              </div>
            </label>
            <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
              <input
                type="radio"
                name="recurringPostDestination"
                value="auto_post"
                checked={recurringMode.postDestination === 'auto_post'}
                onChange={(e) => handleRecurringModeChange('postDestination', e.target.value)}
                className="mr-3"
              />
              <div>
                <div className="font-medium text-sm">Auto Post</div>
                <div className="text-xs text-gray-500">Posts are automatically published without manual review</div>
              </div>
            </label>
          </div>
        </div>
                          
                                              {/* Save/Run Button */}
                                              <div className="flex justify-end space-x-3">
                                                <button
                                                  onClick={isRunMode ? saveAndRunAgent : saveAgent}
                                                  disabled={loading}
                                                  className="text-white px-6 py-2 rounded-lg transition-colors disabled:opacity-50 flex items-center font-medium"
                                                  style={{ 
                                                    backgroundColor: loading ? '#94a3b8' : isRunMode ? '#10b981' : 'var(--theme-primary)'
                                                  }}
                                                  onMouseEnter={(e) => {
                                                    if (!loading) e.target.style.backgroundColor = isRunMode ? '#059669' : 'var(--theme-primary-hover)';
                                                  }}
                                                  onMouseLeave={(e) => {
                                                    if (!loading) e.target.style.backgroundColor = isRunMode ? '#10b981' : 'var(--theme-primary)';
                                                  }}
                                                >
                                                  {isRunMode ? <Play className="h-4 w-4 mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                                                  {loading ? (isRunMode ? 'Updating & Running...' : 'Saving...') : 
                                                   isRunMode ? 'Update & Run Agent' : 
                                                   isEditMode ? 'Update Agent Configuration' : 'Save Agent'}
                                                </button>
               

              
                    </div>
                      </div>
                    )}
                  </div>
                )}

            {/* Adhoc Mode Tab */}
            {(activeTab === 'adhoc' || activeTab === 'timesheet-adhoc' || activeTab === 'paystub-adhoc' || activeTab === 'social-media-adhoc') && (
              <div className="space-y-6">
                {/* Check if this is a timesheet agent */}
                    {searchParams.get('agent_type') === 'time_sheet' ? (
                      // Timesheet Adhoc Mode Form
                      <div className="space-y-6">
                        {/* Agent Configuration Section */}
                        <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-6">
                          {/* Agent Name - Full Row */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              <Timer className="h-4 w-4 inline mr-2" />
                              Agent Name *
                            </label>
                            <input
                              type="text"
                              value={timesheetAdhocMode.agentName}
                              onChange={(e) => handleTimesheetAdhocModeChange('agentName', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 text-sm"
                              placeholder="e.g., Monthly Report Agent"
                              required
                            />
                          </div>

                          {/* Report Period - Regenerate Existing Timesheet Reports + Custom */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              <Calendar className="h-4 w-4 inline mr-2" />
                              Report Period *
                            </label>
                            <p className="text-sm font-medium text-gray-600 mb-3">
                              Select from existing timesheet reports or choose a custom date range
                            </p>
                            
                            {/* Regenerate Existing Timesheet Reports */}
                            <div className="space-y-2">
                              <h4 className="text-sm font-medium text-gray-700">Regenerate Existing Timesheet Reports</h4>
                              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md mb-3">
                                <p className="text-sm font-medium text-yellow-800">
                                  ⚠️ Regenerating will replace the published timesheet for that period with a new timesheet calculated based on latest employee hours. This may delete existing paystubs for the selected employees.
                                </p>
                              </div>
                              <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-md p-3 bg-gray-50">
                                {existingTimesheetReports.filter(report => report.status === 'published').slice(0, 3).length > 0 ? (
                                  <div className="space-y-2">
                                    {existingTimesheetReports.filter(report => report.status === 'published').slice(0, 3).map((report) => (
                                      <label key={report.id} className="flex items-center p-2 hover:bg-gray-100 rounded cursor-pointer">
                                        <input
                                          type="radio"
                                          name="adhocReportPeriod"
                                          value={report.id}
                                          checked={timesheetAdhocMode.reportPeriod === report.id}
                                          onChange={(e) => {
                                            handleTimesheetAdhocModeChange('reportPeriod', e.target.value);
                                            handleTimesheetAdhocModeChange('customStartDate', report.start_date);
                                            handleTimesheetAdhocModeChange('customEndDate', report.end_date);
                                            handleTimesheetAdhocModeChange('isRegeneratingExisting', true);
                                          }}
                                          className="mr-3"
                                        />
                                        <div className="flex-1">
                                          <div className="text-sm font-medium text-gray-900">{report.label}</div>
                                          <div className="text-xs text-gray-500 flex items-center space-x-2">
                                            <span>{report.start_date} to {report.end_date}</span>
                                            <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800">
                                              Published
                                            </span>
                                          </div>
                                        </div>
                                      </label>
                                    ))}
                                  </div>
                                ) : (
                                  <p className="text-sm font-medium text-gray-500">No published timesheet reports found</p>
                                )}
                              </div>
                            </div>

                            {/* Custom Period Option */}
                            <div className="border-t border-gray-200 pt-3">
                              <label className="flex items-center p-2 hover:bg-gray-50 rounded cursor-pointer">
                                <input
                                  type="radio"
                                  name="adhocReportPeriod"
                                  value="custom"
                                  checked={timesheetAdhocMode.reportPeriod === 'custom'}
                                  onChange={(e) => {
                                    handleTimesheetAdhocModeChange('reportPeriod', e.target.value);
                                    handleTimesheetAdhocModeChange('isRegeneratingExisting', false);
                                  }}
                                  className="mr-3"
                                />
                                <div>
                                  <div className="text-sm font-medium text-gray-900">Custom Date Range</div>
                                  <div className="text-xs text-gray-500">Select your own start and end dates</div>
                                </div>
                              </label>

                              {/* Custom Date Range Inputs */}
                              {timesheetAdhocMode.reportPeriod === 'custom' && (
                                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-4 ml-6">
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      Start Date *
                                    </label>
                                    <input
                                      type="date"
                                      value={timesheetAdhocMode.customStartDate}
                                      onChange={(e) => handleTimesheetAdhocModeChange('customStartDate', e.target.value)}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 text-sm"
                                      required
                                    />
                                  </div>

                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      End Date *
                                    </label>
                                    <input
                                      type="date"
                                      value={timesheetAdhocMode.customEndDate}
                                      onChange={(e) => handleTimesheetAdhocModeChange('customEndDate', e.target.value)}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 text-sm"
                                      required
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Employee Selection Section */}
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                          <label className="block text-sm font-medium text-gray-700 mb-3">
                            <Users className="h-4 w-4 inline mr-2" />
                            Employee Selection
                          </label>
                          <p className="text-sm font-medium text-gray-600 mb-3">
                            Select specific employees for this report (leave none selected to include all employees)
                          </p>
                          <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-md p-3 bg-white">
                            {availableEmployees.length > 0 ? (
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                {availableEmployees.map((employee) => (
                                  <label key={employee.id} className="flex items-center p-2 hover:bg-gray-100 rounded cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={timesheetAdhocMode.selectedEmployees.includes(employee.id)}
                                      onChange={() => handleEmployeeToggle(employee.id, 'adhoc')}
                                      className="h-4 w-4 border-gray-300 rounded"
                                      style={{
                                        accentColor: 'var(--theme-primary)',
                                        filter: 'brightness(1) contrast(1)',
                                        colorScheme: 'light'
                                      }}
                                    />
                                    <span className="ml-2 text-sm font-medium text-gray-700">
                                      {employee.full_name}
                                    </span>
                                  </label>
                                ))}
                              </div>
                            ) : (
                              <p className="text-sm font-medium text-gray-500">No employees found</p>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 mt-2">
                            Selected: {timesheetAdhocMode.selectedEmployees.length > 0 ? timesheetAdhocMode.selectedEmployees.length : 'All'} employees
                          </p>
                        </div>

                        {/* Report Generation Section */}
                        <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-4">
                          <label className="block text-sm font-medium text-gray-700 mb-3">
                            <FileText className="h-4 w-4 inline mr-2" />
                            Report Generation
                          </label>
                          
                          {/* Report Features */}
                          <div>
                            <label className="flex items-center">
                              <input
                                type="checkbox"
                                checked={timesheetAdhocMode.includeBillingRates}
                                onChange={(e) => handleTimesheetAdhocModeChange('includeBillingRates', e.target.checked)}
                                className="h-4 w-4 border-gray-300 rounded"
                                style={{
                                  accentColor: 'var(--theme-primary)',
                                  filter: 'brightness(1) contrast(1)',
                                  colorScheme: 'light'
                                }}
                              />
                              <span className="ml-2 text-sm font-medium text-gray-700">Include Billing Rates</span>
                            </label>
                          </div>

                          {/* Email Recipients */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              <Mail className="h-4 w-4 inline mr-2" />
                              Email Recipients
                            </label>
                            <div className="flex space-x-2 mb-2">
                              <input
                                type="email"
                                value={newEmailRecipient}
                                onChange={(e) => setNewEmailRecipient(e.target.value)}
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 text-sm"
                                placeholder="Enter email address"
                              />
                              <button
                                type="button"
                                onClick={() => addEmailRecipient('adhoc')}
                                className="px-4 py-2 text-white rounded-md transition-colors text-sm font-medium"
                                style={{
                                  backgroundColor: 'var(--theme-primary)'
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.backgroundColor = 'var(--theme-primary-hover)';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.backgroundColor = 'var(--theme-primary)';
                                }}
                              >
                                Add
                              </button>
                            </div>
                            
                            {/* Email Recipients List */}
                            {timesheetAdhocMode.emailRecipients.length > 0 && (
                              <div className="border border-gray-200 rounded-md p-3 max-h-32 overflow-y-auto">
                                {timesheetAdhocMode.emailRecipients.map((email, index) => (
                                  <div key={index} className="flex items-center justify-between py-1">
                                    <span className="text-sm font-medium text-gray-700">{email}</span>
                                    <button
                                      type="button"
                                      onClick={() => removeEmailRecipient(index, 'adhoc')}
                                      className="text-red-600 hover:text-red-700 text-sm font-medium"
                                    >
                                      Remove
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Content Review Workflow */}
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                          <label className="block text-sm font-medium text-gray-700 mb-4">
                            <Eye className="h-4 w-4 inline mr-2" />
                            Workflow Mode
                          </label>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
                              <input
                                type="radio"
                                name="timesheetAdhocInitialStatus"
                                value="in_review"
                                checked={timesheetAdhocMode.initialStatus === 'in_review'}
                                onChange={(e) => handleTimesheetAdhocModeChange('initialStatus', e.target.value)}
                                className="mr-3"
                              />
                              <div>
                                <div className="font-medium text-sm">In Review</div>
                                <div className="text-xs text-gray-500">Reports go to "In Review" for manager approval</div>
                              </div>
                            </label>
                            <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
                              <input
                                type="radio"
                                name="timesheetAdhocInitialStatus"
                                value="ready_to_publish"
                                checked={timesheetAdhocMode.initialStatus === 'ready_to_publish'}
                                onChange={(e) => handleTimesheetAdhocModeChange('initialStatus', e.target.value)}
                                className="mr-3"
                              />
                              <div>
                                <div className="font-medium text-sm">Ready to Publish</div>
                                <div className="text-xs text-gray-500">Reports go directly to "Ready to Publish"</div>
                              </div>
                            </label>
                            <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
                              <input
                                type="radio"
                                name="timesheetAdhocInitialStatus"
                                value="published"
                                checked={timesheetAdhocMode.initialStatus === 'published'}
                                onChange={(e) => handleTimesheetAdhocModeChange('initialStatus', e.target.value)}
                                className="mr-3"
                              />
                              <div>
                                <div className="font-medium text-sm">Auto-Publish</div>
                                <div className="text-xs text-gray-500">Reports are automatically published and emailed</div>
                              </div>
                            </label>
                          </div>
                        </div>

                        {/* Save Button for Timesheet Adhoc Mode */}
                        <div className="flex justify-end space-x-3 pt-4">
                          <button
                            onClick={isRunMode ? saveAndRunAgent : saveAgent}
                            disabled={loading || (!isEditMode && !isRunMode && !isTimesheetAdhocModeValid())}
                            className="text-white px-6 py-2 rounded-lg transition-colors disabled:opacity-50 flex items-center font-medium"
                            style={{ 
                              backgroundColor: loading ? '#94a3b8' : isRunMode ? '#10b981' : 'var(--theme-primary)'
                            }}
                            onMouseEnter={(e) => {
                              if (!loading) e.target.style.backgroundColor = isRunMode ? '#059669' : 'var(--theme-primary-hover)';
                            }}
                            onMouseLeave={(e) => {
                              if (!loading) e.target.style.backgroundColor = isRunMode ? '#10b981' : 'var(--theme-primary)';
                            }}
                          >
                            {isRunMode ? <Play className="h-4 w-4 mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                            {loading ? (isRunMode ? 'Updating & Running...' : 'Saving...') : 
                             isRunMode ? 'Update & Run Agent' : 
                             isEditMode ? 'Update Agent Configuration' : 'Save Agent'}
                          </button>
                        </div>
                      </div>
                    ) : searchParams.get('agent_type') === 'paystub_agent' ? (
                      // Paystub Adhoc Mode Form
                      <div className="space-y-6">
                        {/* Agent Configuration Section */}
                        <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-6">
                          {/* Agent Name - Full Row */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              <DollarSign className="h-4 w-4 inline mr-2" />
                              Agent Name *
                            </label>
                            <input
                              type="text"
                              value={paystubAdhocMode.agentName}
                              onChange={(e) => setPaystubAdhocMode({...paystubAdhocMode, agentName: e.target.value})}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                              placeholder="Enter agent name"
                              required
                            />
                          </div>

                          {/* Select Timesheet */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              <FileText className="h-4 w-4 inline mr-2" />
                              Select Timesheet *
                            </label>
                            <p className="text-sm font-medium text-gray-600 mb-3">
                              Choose from published timesheets from the past 3 months
                            </p>
                            
                            {/* Warning Message */}
                            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md mb-3">
                              <p className="text-sm font-medium text-yellow-800">
                                ⚠️ If you execute this agent for an already published timesheet from the agent dashboard, it will replace existing paystubs for the selected employees based on that timesheet hours for that period.
                              </p>
                            </div>
                            
                            {/* Timesheet List */}
                            <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-md p-3 bg-gray-50">
                              {existingTimesheetReports && existingTimesheetReports.length > 0 ? (
                                <div className="space-y-2">
                                  {existingTimesheetReports.map((report) => (
                                    <label key={report.id} className="flex items-center p-2 hover:bg-gray-100 rounded cursor-pointer">
                                      <input
                                        type="radio"
                                        name="paystubTimesheetSelection"
                                        value={report.report_id}
                                        checked={paystubAdhocMode.selectedTimesheetId === report.report_id}
                                        onChange={(e) => setPaystubAdhocMode({...paystubAdhocMode, selectedTimesheetId: e.target.value})}
                                        className="mr-3"
                                      />
                                      <div className="flex-1">
                                        <div className="text-sm font-medium text-gray-900">{report.label}</div>
                                        <div className="text-xs text-gray-500 flex items-center space-x-2">
                                          <span>{report.start_date} to {report.end_date}</span>
                                          <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800">
                                            Published
                                          </span>
                                        </div>
                                      </div>
                                    </label>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-sm font-medium text-gray-500">No published timesheets available</p>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Employee Selection Section */}
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                          <label className="block text-sm font-medium text-gray-700 mb-3">
                            <Users className="h-4 w-4 inline mr-2" />
                            Employee Selection
                          </label>
                          <p className="text-sm font-medium text-gray-600 mb-3">
                            Select specific employees for paystub generation (leave none selected to include all employees from selected timesheet)
                          </p>
                          <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-md p-3 bg-white">
                            {availableEmployees.length > 0 ? (
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                {availableEmployees.map((employee) => (
                                  <label key={employee.id} className="flex items-center p-2 hover:bg-gray-100 rounded cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={paystubAdhocMode.selectedEmployees.includes(employee.id)}
                                      onChange={() => handleEmployeeToggle(employee.id, 'paystub_adhoc')}
                                      className="h-4 w-4 border-gray-300 rounded"
                                      style={{
                                        accentColor: '#10b981',
                                        filter: 'brightness(1) contrast(1)',
                                        colorScheme: 'light'
                                      }}
                                    />
                                    <span className="ml-2 text-sm font-medium text-gray-700">
                                      {employee.full_name}
                                    </span>
                                  </label>
                                ))}
                              </div>
                            ) : (
                              <p className="text-sm font-medium text-gray-500 text-center py-4">No employees available</p>
                            )}
                          </div>
                          {paystubAdhocMode.selectedEmployees.length > 0 && (
                            <p className="text-xs text-green-600 mt-2">
                              {paystubAdhocMode.selectedEmployees.length} employee{paystubAdhocMode.selectedEmployees.length !== 1 ? 's' : ''} selected
                            </p>
                          )}
                        </div>

                        {/* Content Review Workflow */}
                        <div className="bg-white border border-gray-200 rounded-lg p-6">
                          <label className="block text-sm font-medium text-gray-700 mb-4">
                            <Settings className="h-4 w-4 inline mr-2" />
                            Workflow Mode
                          </label>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <label className="flex items-start p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 border">
                              <input
                                type="radio"
                                name="paystubAdhocWorkflowMode"
                                value="in_review"
                                checked={paystubAdhocMode.workflowMode === 'in_review'}
                                onChange={(e) => setPaystubAdhocMode({...paystubAdhocMode, workflowMode: e.target.value})}
                                className="mr-3"
                              />
                              <div>
                                <div className="font-medium text-sm">In Review</div>
                                <div className="text-xs text-gray-500">Paystubs go to "In Review" for manager approval</div>
                              </div>
                            </label>
                            <label className="flex items-start p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 border">
                              <input
                                type="radio"
                                name="paystubAdhocWorkflowMode"
                                value="ready_to_publish"
                                checked={paystubAdhocMode.workflowMode === 'ready_to_publish'}
                                onChange={(e) => setPaystubAdhocMode({...paystubAdhocMode, workflowMode: e.target.value})}
                                className="mr-3"
                              />
                              <div>
                                <div className="font-medium text-sm">Ready to Publish</div>
                                <div className="text-xs text-gray-500">Paystubs are ready for immediate publication</div>
                              </div>
                            </label>
                            <label className="flex items-start p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 border">
                              <input
                                type="radio"
                                name="paystubAdhocWorkflowMode"
                                value="published"
                                checked={paystubAdhocMode.workflowMode === 'published'}
                                onChange={(e) => setPaystubAdhocMode({...paystubAdhocMode, workflowMode: e.target.value})}
                                className="mr-3"
                              />
                              <div>
                                <div className="font-medium text-sm">Published</div>
                                <div className="text-xs text-gray-500">Paystubs are automatically published</div>
                              </div>
                            </label>
                          </div>
                        </div>

                        {/* Save Button */}
                        <div className="flex justify-end pt-4">
                          <button
                            onClick={() => handleSave(isRunMode)}
                            disabled={loading}
                            className="px-6 py-2 text-white font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors flex items-center"
                            style={{
                              backgroundColor: isRunMode ? '#10b981' : 'var(--theme-primary)',
                              borderColor: isRunMode ? '#10b981' : 'var(--theme-primary)'
                            }}
                            onMouseEnter={(e) => {
                              if (!loading) e.target.style.backgroundColor = isRunMode ? '#059669' : 'var(--theme-primary-hover)';
                            }}
                            onMouseLeave={(e) => {
                              if (!loading) e.target.style.backgroundColor = isRunMode ? '#10b981' : 'var(--theme-primary)';
                            }}
                          >
                            {isRunMode ? <Play className="h-4 w-4 mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                            {loading ? (isRunMode ? 'Updating & Running...' : 'Saving...') : 
                             isRunMode ? 'Update & Run Agent' : 
                             isEditMode ? 'Update Agent Configuration' : 'Save Agent'}
                          </button>
                        </div>
                      </div>
                    ) : (
                      // Social Media Adhoc Mode Form (existing)
                      <div className="space-y-6">
                        {/* Agent Name & Schedule Section - Top Section */}
                        <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-6">
                          {/* Agent Name - Full Row */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              <Bot className="h-4 w-4 inline mr-2" />
                              Agent Name *
                            </label>
                            <input
                              type="text"
                              value={adhocMode.agentName}
                              onChange={(e) => handleAdhocModeChange('agentName', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 text-sm"
                              placeholder="Enter agent name..."
                              required
                            />
                          </div>

                          {/* Schedule Post - Separate Row */}
                          <div>
                            <div className="flex items-center mb-4">
                              <input
                                type="checkbox"
                                id="adhocEnableScheduling"
                                checked={adhocMode.enableScheduling}
                                onChange={(e) => handleAdhocModeChange('enableScheduling', e.target.checked)}
                                className="h-4 w-4 text-gray-600 border-gray-300 rounded focus:ring-gray-500 mr-3"
                              />
                              <label htmlFor="adhocEnableScheduling" className="text-sm font-medium text-gray-700">
                                <Calendar className="h-4 w-4 inline mr-2" />
                                Schedule Post
                              </label>
                            </div>
                            
                            {!adhocMode.enableScheduling && (
                              <p className="text-sm text-orange-600">💡 If not scheduled, you'll need to run this post manually from the dashboard.</p>
                            )}
                            
                            {adhocMode.enableScheduling && (
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-2">
                                    <Calendar className="h-4 w-4 inline mr-2" />
                                    Post Date *
                                  </label>
                                  <input
                                    type="date"
                                    value={adhocMode.postDate}
                                    onChange={(e) => handleAdhocModeChange('postDate', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 text-sm"
                                    min={new Date().toISOString().split('T')[0]}
                                    required
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-2">
                                    <Clock className="h-4 w-4 inline mr-2" />
                                    Post Time *
                                  </label>
                                  <select
                                    value={adhocMode.postTime}
                                    onChange={(e) => handleAdhocModeChange('postTime', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 text-sm"
                                    required
                                  >
                                    {timeOptions.map(option => (
                                      <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                  </select>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Topic & Word Count Section */}
                        <div className="space-y-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
                          {/* Topic and Word Count - Side by side */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Topic Selection */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            <Target className="h-4 w-4 inline mr-2" />
                            Select Topic *
                          </label>
                          <select
                            value={adhocMode.topic}
                            onChange={(e) => handleAdhocModeChange('topic', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 text-sm"
                            required
                          >
                            <option value="">Select a topic...</option>
                            {topicOptions.map(topic => (
                              <option key={topic} value={topic}>{topic}</option>
                            ))}
                          </select>
                          {adhocMode.topic === 'Custom' && (
                            <input
                              type="text"
                              value={adhocMode.customTopic}
                              onChange={(e) => handleAdhocModeChange('customTopic', e.target.value)}
                              className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 text-sm"
                              placeholder="Enter custom topic..."
                            />
                          )}
                        </div>

                        {/* Word Count */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            <Hash className="h-4 w-4 inline mr-2" />
                            Word Count *
                          </label>
                          <select
                            value={adhocMode.wordCount}
                            onChange={(e) => handleAdhocModeChange('wordCount', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 text-sm"
                            required
                          >
                            {wordCountOptions.map(option => (
                              <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>

                    {/* Schedule moved to top section */}

                    {/* Social Media Platforms & Image Format Selection - MOVED TO TOP */}
                    <div className="space-y-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                      {/* Social Platforms */}
                      <div className="space-y-3">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <Share2 className="h-4 w-4 inline mr-2" />
                          Select Social Media Platforms *
                        </label>
                        {/* All 5 platforms in one row with checkboxes on the left */}
                        <div className="grid grid-cols-5 gap-2">
                          {Object.entries(adhocMode.socialPlatforms).map(([platform, selected]) => (
                            <label
                              key={platform}
                              className={`flex items-center p-2 rounded-lg border cursor-pointer transition-colors ${
                                selected ? 'bg-gray-50 border-gray-300' : 'bg-white border-gray-200 hover:bg-gray-50'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={selected}
                                onChange={(e) => handleSocialPlatformChange(platform, e.target.checked, 'adhoc')}
                                className="h-3 w-3 text-gray-600 border-gray-300 rounded focus:ring-gray-500 mr-2"
                              />
                              <span className="capitalize font-medium text-sm">{platform === 'twitter' ? 'X' : platform}</span>
                            </label>
                          ))}
                        </div>
                        {!Object.values(adhocMode.socialPlatforms).some(platform => platform) && (
                          <p className="text-sm text-red-600">Please select at least one platform.</p>
                        )}
                      </div>

                      {/* Image Format Selection moved to appear below image options */}
                    </div>

                    {/* Image Options & Upload Section - Combined with border and background */}
                    <div className="space-y-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                      <label className="block text-sm font-medium text-gray-700">
                        <ImageIcon className="h-4 w-4 inline mr-2" />
                        Image Options
                      </label>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
                          <input
                            type="radio"
                            name="adhocImageOption"
                            value="ai_generate"
                            checked={adhocMode.imageOption === 'ai_generate'}
                            onChange={(e) => handleAdhocModeChange('imageOption', e.target.value)}
                            className="mr-3"
                          />
                          <div>
                            <div className="font-medium text-sm">AI Generate</div>
                            <div className="text-xs text-gray-500">Create unique images using AI</div>
                          </div>
                        </label>
                        <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
                          <input
                            type="radio"
                            name="adhocImageOption"
                            value="upload"
                            checked={adhocMode.imageOption === 'upload'}
                            onChange={(e) => handleAdhocModeChange('imageOption', e.target.value)}
                            className="mr-3"
                          />
                          <div>
                            <div className="font-medium text-sm">Upload Image</div>
                            <div className="text-xs text-gray-500">Use your own images</div>
                          </div>
                        </label>
                        <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
                          <input
                            type="radio"
                            name="adhocImageOption"
                            value="reference"
                            checked={adhocMode.imageOption === 'reference'}
                            onChange={(e) => handleAdhocModeChange('imageOption', e.target.value)}
                            className="mr-3"
                          />
                          <div>
                            <div className="font-medium text-sm">Select Existing Image</div>
                            <div className="text-xs text-gray-500">Select from uploaded images</div>
                          </div>
                        </label>
                        <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
                          <input
                            type="radio"
                            name="adhocImageOption"
                            value="none"
                            checked={adhocMode.imageOption === 'none'}
                            onChange={(e) => handleAdhocModeChange('imageOption', e.target.value)}
                            className="mr-3"
                          />
                          <div>
                            <div className="font-medium text-sm">No Images</div>
                            <div className="text-xs text-gray-500">Text-only posts</div>
                          </div>
                        </label>
                      </div>

                      {/* Select Image Format for Each Platform - MOVED TO APPEAR DIRECTLY BELOW IMAGE OPTIONS */}
                      {Object.values(adhocMode.socialPlatforms).some(platform => platform) && adhocMode.imageOption !== 'none' && (
                        <div className="space-y-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                          <label className="block text-sm font-medium text-gray-700 mb-3">
                            <ImageIcon className="h-4 w-4 inline mr-2" />
                            Select Image Format for Each Platform
                          </label>
                          
                          {/* Format Selection for Each Enabled Platform - Dynamic grid */}
                          <div className={`grid gap-2 ${Object.entries(adhocMode.socialPlatforms).filter(([platform, enabled]) => enabled).length === 1 ? 'grid-cols-1' : Object.entries(adhocMode.socialPlatforms).filter(([platform, enabled]) => enabled).length === 2 ? 'grid-cols-2' : Object.entries(adhocMode.socialPlatforms).filter(([platform, enabled]) => enabled).length === 3 ? 'grid-cols-3' : Object.entries(adhocMode.socialPlatforms).filter(([platform, enabled]) => enabled).length === 4 ? 'grid-cols-4' : 'grid-cols-5'}`}>
                            {Object.entries(adhocMode.socialPlatforms)
                              .filter(([platform, enabled]) => enabled)
                              .map(([platform, enabled]) => (
                                <div key={platform} className="flex flex-col">
                                  <label className="text-sm font-medium text-gray-600 mb-2 text-left capitalize">
                                    {platform === 'twitter' ? 'X' : platform}
                                  </label>
                                  <select
                                    value={adhocMode.platformImageFormats[platform] || ''}
                                    onChange={(e) => {
                                      const newFormats = { ...(adhocMode.platformImageFormats || {}) };
                                      if (e.target.value) {
                                        newFormats[platform] = e.target.value;
                                      } else {
                                        delete newFormats[platform];
                                      }
                                      handleAdhocModeChange('platformImageFormats', newFormats);
                                    }}
                                    className="w-full px-2 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                                  >
                                    <option value="">Select...</option>
                                    {Object.entries(socialMediaPlatforms[platform === 'twitter' ? 'twitter' : platform]?.formats || {}).map(([formatKey, formatName]) => (
                                      <option key={formatKey} value={formatKey}>{formatName}</option>
                                    ))}
                                  </select>
                                  
                                  {/* Show format dimensions */}
                                  {adhocMode.platformImageFormats[platform] && socialMediaImageSettings[platform === 'twitter' ? 'twitter' : platform]?.[adhocMode.platformImageFormats[platform]] && (
                                    <div className="text-xs text-gray-600 mt-1 text-left">
                                      {socialMediaImageSettings[platform === 'twitter' ? 'twitter' : platform][adhocMode.platformImageFormats[platform]].width} × {socialMediaImageSettings[platform === 'twitter' ? 'twitter' : platform][adhocMode.platformImageFormats[platform]].height}
                                    </div>
                                  )}
                                </div>
                              ))
                            }
                          </div>
                        </div>
                      )}

                      {/* Image Upload Area - Integrated into Image Options */}
                      {adhocMode.imageOption === 'upload' && (
                        <div className="mt-4">
                          <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center bg-gray-50">
                            <Users className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                            <p className="text-sm text-gray-600 mb-2">Upload social media images</p>
                            <p className="text-xs text-gray-500 mb-4">Recommended: High resolution images for best social media results</p>
                            <div className="flex justify-center">
                              <label className="inline-flex items-center px-4 py-2 text-white rounded-md hover:opacity-90 transition-colors cursor-pointer" style={{ backgroundColor: 'var(--theme-primary)' }}>
                                <Upload className="h-4 w-4 mr-2" />
                                Upload Image
                                <input
                                  type="file"
                                  multiple
                                  accept="image/*"
                                  onChange={(e) => handleImageUpload(e, 'adhoc')}
                                  className="hidden"
                                />
                              </label>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {adhocMode.imageOption === 'reference' && (
                        <div className="mt-4">
                          <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center bg-gray-50">
                            <ImageIcon className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                            <p className="text-sm text-gray-600 mb-2">Select reference images</p>
                            <p className="text-xs text-gray-500 mb-4">Choose images from your asset library to use as style reference</p>
                            <div className="flex justify-center">
                              <button
                                type="button"
                                onClick={() => openAssetBrowser('adhoc')}
                                className="inline-flex items-center px-4 py-2 text-white rounded-md hover:opacity-90 transition-colors" style={{ backgroundColor: 'var(--theme-primary)' }}
                              >
                                <Folder className="h-4 w-4 mr-2" />
                                Browse Assets
                              </button>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Uploaded Images Preview */}
                      {(adhocMode.imageOption === 'upload' || adhocMode.imageOption === 'reference') && adhocMode.uploadedImages.length > 0 && (
                        <div className="mt-4">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Selected Images ({adhocMode.imageOption === 'reference' ? 'Reference' : 'Upload'})
                          </label>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            {adhocMode.uploadedImages.map((image, index) => (
                              <div key={index} className="relative">
                                <img 
                                  src={image.url} 
                                  alt={image.name}
                                  className="w-full h-24 object-cover rounded-lg border"
                                />
                                <button
                                  onClick={() => removeUploadedImage(index, 'adhoc')}
                                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                                >
                                  <Trash2 className="h-3 w-3" />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Add Custom Text Banner to Image - MOVED HERE below Image Options */}
                    {(adhocMode.imageOption === 'ai_generate' || adhocMode.imageOption === 'upload' || adhocMode.imageOption === 'reference') && (
                      <div className="p-4 bg-white border border-gray-200 rounded-lg">
                        <label className="flex items-start">
                          <input
                            type="checkbox"
                            checked={adhocMode.enableCustomImageText}
                            onChange={(e) => handleAdhocModeChange('enableCustomImageText', e.target.checked)}
                            className="h-4 w-4 text-theme-blue border-gray-300 rounded focus:ring-gray-500 mr-3"
                          />
                          <span className="block text-sm font-medium text-gray-700">
                            <FileText className="h-4 w-4 inline mr-2" />
                            Add Custom Text Banner to Image
                          </span>
                        </label>
                        <p className="text-xs text-gray-500 mt-2 ml-7">
                          Add custom text overlay on the generated image (e.g., your clinic name, promotional text)
                        </p>
                        
                        {adhocMode.enableCustomImageText && (
                          <div className="mt-3">
                            <div className="flex items-center justify-between mb-2">
                              <label className="block text-sm font-medium text-gray-700">
                                Custom Banner Text
                              </label>
                              <button
                                type="button"
                                onClick={() => openPlaceholderModal('customImageBannerText', handleAdhocModeChange)}
                                className="px-3 py-1 text-xs font-medium text-white rounded transition-colors"
                                style={{ backgroundColor: 'var(--theme-primary)' }}
                                onMouseEnter={(e) => e.target.style.backgroundColor = 'rgb(35, 147, 180)'}
                                onMouseLeave={(e) => e.target.style.backgroundColor = 'var(--theme-primary)'}
                              >
                                Available Placeholders
                              </button>
                            </div>
                            <input
                              type="text"
                              value={adhocMode.customImageBannerText || '[BUSINESS_NAME] [PHONE_NUMBER]'}
                              onChange={(e) => handleAdhocModeChange('customImageBannerText', e.target.value)}
                              className="w-full px-3 py-2 text-sm font-medium border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                              placeholder="[BUSINESS_NAME] [PHONE_NUMBER]"
                            />
                          </div>
                        )}
                      </div>
                    )}

                    {/* Add Link Section - Social Media */}
                    <div className="p-4 bg-white border border-gray-200 rounded-lg">
                      <label className="flex items-start">
                        <input
                          type="checkbox"
                          checked={adhocMode.enableSocialLink}
                          onChange={(e) => handleAdhocModeChange('enableSocialLink', e.target.checked)}
                          className="h-4 w-4 text-theme-blue border-gray-300 rounded focus:ring-gray-500 mr-3"
                        />
                        <span className="block text-sm font-medium text-gray-700">
                          <Globe className="h-4 w-4 inline mr-2" />
                          Add Link
                        </span>
                      </label>
                      <p className="text-xs text-gray-500 mt-2 ml-7">
                        Add a link to your social media post (e.g., website, promotion, product page)
                      </p>
                      
                      {adhocMode.enableSocialLink && (
                        <div className="mt-3">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Link URL
                          </label>
                          <input
                            type="text"
                            value={adhocMode.socialLink || ''}
                            onChange={(e) => handleAdhocModeChange('socialLink', e.target.value)}
                            className="w-full px-3 py-2 text-sm font-medium border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                            placeholder="https://example.com"
                          />
                        </div>
                      )}
                    </div>

                    {/* Social Media Platforms moved above Image Options */}

                    {/* Content Review Workflow - Styled like Image Options blocks */}
                    <div className="space-y-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Content Review Workflow
                      </label>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
                          <input
                            type="radio"
                            name="adhocPostDestination"
                            value="in_review"
                            checked={adhocMode.postDestination === 'in_review'}
                            onChange={(e) => handleAdhocModeChange('postDestination', e.target.value)}
                            className="mr-3"
                          />
                          <div>
                            <div className="font-medium text-sm">In Review First</div>
                            <div className="text-xs text-gray-500">Generated posts go to "In Review" for manager approval</div>
                          </div>
                        </label>
                        <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
                          <input
                            type="radio"
                            name="adhocPostDestination"
                            value="ready_to_publish"
                            checked={adhocMode.postDestination === 'ready_to_publish'}
                            onChange={(e) => handleAdhocModeChange('postDestination', e.target.value)}
                            className="mr-3"
                          />
                          <div>
                            <div className="font-medium text-sm">Ready to Publish</div>
                            <div className="text-xs text-gray-500">Generated posts go directly to "Ready to Publish"</div>
                          </div>
                        </label>
                        <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
                          <input
                            type="radio"
                            name="adhocPostDestination"
                            value="auto_post"
                            checked={adhocMode.postDestination === 'auto_post'}
                            onChange={(e) => handleAdhocModeChange('postDestination', e.target.value)}
                            className="mr-3"
                          />
                          <div>
                            <div className="font-medium text-sm">Auto Post</div>
                            <div className="text-xs text-gray-500">Posts are automatically published without manual review</div>
                          </div>
                        </label>
                      </div>
                    </div>

                    {/* Save/Run Button */}
                    <div className="flex justify-end space-x-3">
                      <button
                        onClick={isRunMode ? saveAndRunAgent : saveAgent}
                        disabled={loading || (!isEditMode && !isRunMode && (!adhocMode.agentName || !adhocMode.topic || Object.values(adhocMode.socialPlatforms).every(v => !v)))}
                        className="text-white px-6 py-2 rounded-lg transition-colors disabled:opacity-50 flex items-center font-medium"
                        style={{ 
                          backgroundColor: loading ? '#94a3b8' : 
                                          isRunMode ? '#10b981' : 'var(--theme-primary)'
                        }}
                        onMouseEnter={(e) => {
                          if (!loading && ((isEditMode || isRunMode) || (adhocMode.agentName && adhocMode.topic && Object.values(adhocMode.socialPlatforms).some(v => v)))) {
                            e.target.style.backgroundColor = isRunMode ? '#059669' : 'rgb(35, 158, 196)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!loading) {
                            e.target.style.backgroundColor = isRunMode ? '#10b981' : 'var(--theme-primary)';
                          }
                        }}
                      >
                        {isRunMode ? <Play className="h-4 w-4 mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                        {loading ? (isRunMode ? 'Updating & Running...' : 'Saving...') : 
                         (isRunMode ? 'Update & Run Agent' : 
                          isEditMode ? 'Update Agent Configuration' : 'Save Agent')}
                      </button>
                    </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Write Your Post Tab (Social Media Agents) */}
                {activeTab === 'social-media-write' && (
                  <div className="space-y-6">
                    {/* Agent Name & Schedule Section - Top Section */}
                    <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-6">
                      {/* Agent Name - Full Row */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <User className="h-4 w-4 inline mr-2" />
                          Agent Name *
                        </label>
                        <input
                          type="text"
                          value={writePostMode.agentName}
                          onChange={(e) => setWritePostMode(prev => ({ ...prev, agentName: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 text-sm"
                          placeholder="e.g., My Custom Post Agent"
                          required
                        />
                      </div>

                      {/* Schedule Post - Separate Row */}
                      <div>
                          <div className="flex items-center mb-4">
                            <input
                              type="checkbox"
                              id="writePostEnableScheduling"
                              checked={writePostMode.enableScheduling}
                              onChange={(e) => setWritePostMode(prev => ({ ...prev, enableScheduling: e.target.checked }))}
                              className="h-4 w-4 text-gray-600 border-gray-300 rounded focus:ring-gray-500 mr-3"
                            />
                            <label htmlFor="writePostEnableScheduling" className="text-sm font-medium text-gray-700">
                              <Calendar className="h-4 w-4 inline mr-2" />
                              Schedule Post
                            </label>
                          </div>
                          
                          {!writePostMode.enableScheduling && (
                            <p className="text-sm text-orange-600">💡 If not scheduled, you'll need to run this post manually from the dashboard.</p>
                          )}
                          
                          {writePostMode.enableScheduling && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  <Calendar className="h-4 w-4 inline mr-2" />
                                  Post Date *
                                </label>
                                <input
                                  type="date"
                                  value={writePostMode.postDate}
                                  onChange={(e) => setWritePostMode(prev => ({ ...prev, postDate: e.target.value }))}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 text-sm"
                                  required
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  <Clock className="h-4 w-4 inline mr-2" />
                                  Post Time *
                                </label>
                                <select
                                  value={writePostMode.postTime}
                                  onChange={(e) => setWritePostMode(prev => ({ ...prev, postTime: e.target.value }))}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 text-sm"
                                  required
                                >
                                  {timeOptions.map(option => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                  ))}
                                </select>
                              </div>
                            </div>
                          )}
                      </div>
                    </div>

                    {/* Post Title & Content Section */}
                    <div className="space-y-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
                      {/* Post Title */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <FileText className="h-4 w-4 inline mr-2" />
                          Post Title
                        </label>
                        <input
                          type="text"
                          value={writePostMode.postTitle}
                          onChange={(e) => setWritePostMode(prev => ({ ...prev, postTitle: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 text-sm"
                          placeholder="Enter a catchy title for your post (optional - AI can generate if left empty)"
                        />
                      </div>

                      {/* Post Content - Big Text Area */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <FileText className="h-4 w-4 inline mr-2" />
                          Post Content *
                        </label>
                        <textarea
                          value={writePostMode.postContent}
                          onChange={(e) => setWritePostMode(prev => ({ ...prev, postContent: e.target.value }))}
                          rows={8}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 resize-y text-sm"
                          required
                        />
                      </div>
                    </div>

                    {/* ChatGPT Formatting Option with Word Count - MOVED HERE below post content */}
                    <div className="space-y-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="useChatGPTFormatting"
                          checked={writePostMode.useChatGPTFormatting}
                          onChange={(e) => setWritePostMode(prev => ({ ...prev, useChatGPTFormatting: e.target.checked }))}
                          className="h-4 w-4 text-theme-blue border-gray-300 rounded focus:ring-gray-500"
                        />
                        <label htmlFor="useChatGPTFormatting" className="ml-2 text-sm text-gray-700">
                          <span className="font-medium">Use ChatGPT for Content Formatting</span>
                          <p className="text-xs text-gray-500 mt-1">Let ChatGPT format and optimize your content for each social media platform</p>
                        </label>
                      </div>
                      
                      {/* Word Count - Only show if ChatGPT formatting is enabled */}
                      {writePostMode.useChatGPTFormatting && (
                        <div className="flex items-center">
                          <label className="text-sm font-medium text-gray-700 mr-3">
                            <Hash className="h-4 w-4 inline mr-2" />
                            Word Count *
                          </label>
                          <select
                            value={writePostMode.wordCount}
                            onChange={(e) => setWritePostMode(prev => ({ ...prev, wordCount: e.target.value }))}
                            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 text-sm"
                            style={{ width: '300px' }}
                            required={writePostMode.useChatGPTFormatting}
                          >
                            {wordCountOptions.map(option => (
                              <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>

                    {/* Social Media Platforms Section */}
                    <div className="space-y-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                      {/* Social Platforms */}
                      <div className="space-y-3">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <Globe className="h-4 w-4 inline mr-2" />
                          Select Social Media Platforms *
                        </label>
                        {/* All 5 platforms in one row with checkboxes on the left */}
                        <div className="grid grid-cols-5 gap-2">
                          {Object.entries(writePostMode.socialPlatforms).map(([platform, selected]) => (
                            <label
                              key={platform}
                              className={`flex items-center p-2 rounded-lg border cursor-pointer transition-colors ${
                                selected ? 'bg-gray-50 border-gray-300' : 'bg-white border-gray-200 hover:bg-gray-50'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={selected}
                                onChange={(e) => setWritePostMode(prev => ({
                                  ...prev,
                                  socialPlatforms: {
                                    ...prev.socialPlatforms,
                                    [platform]: e.target.checked
                                  },
                                  platformImageFormats: {
                                    ...prev.platformImageFormats,
                                    ...(e.target.checked ? {} : { [platform]: undefined })
                                  }
                                }))}
                                className="h-3 w-3 text-gray-600 border-gray-300 rounded focus:ring-gray-500 mr-2"
                              />
                              <span className="capitalize font-medium text-sm">{platform === 'twitter' ? 'X' : platform}</span>
                            </label>
                          ))}
                        </div>
                        {!Object.values(writePostMode.socialPlatforms).some(platform => platform) && (
                          <p className="font-medium text-sm text-red-600">Please select at least one platform.</p>
                        )}
                      </div>
                    </div>

                    {/* Schedule Post moved to top section */}

                    {/* Image Options & Upload Section - Combined with border and background */}
                    <div className="space-y-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                      <label className="block text-sm font-medium text-gray-700">
                        <ImageIcon className="h-4 w-4 inline mr-2" />
                        Image Options
                      </label>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
                          <input
                            type="radio"
                            name="writeImageOption"
                            value="ai_generate"
                            checked={writePostMode.imageOption === 'ai_generate'}
                            onChange={(e) => setWritePostMode(prev => ({ ...prev, imageOption: e.target.value }))}
                            className="mr-3"
                          />
                          <div>
                            <div className="font-medium text-sm">AI Generate</div>
                            <div className="text-xs text-gray-500">Create unique images using AI</div>
                          </div>
                        </label>
                        <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
                          <input
                            type="radio"
                            name="writeImageOption"
                            value="upload"
                            checked={writePostMode.imageOption === 'upload'}
                            onChange={(e) => setWritePostMode(prev => ({ ...prev, imageOption: e.target.value }))}
                            className="mr-3"
                          />
                          <div>
                            <div className="font-medium text-sm">Upload Images</div>
                            <div className="text-xs text-gray-500">Use your own images</div>
                          </div>
                        </label>
                        <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
                          <input
                            type="radio"
                            name="writeImageOption"
                            value="s3_browse"
                            checked={writePostMode.imageOption === 's3_browse'}
                            onChange={(e) => setWritePostMode(prev => ({ ...prev, imageOption: e.target.value }))}
                            className="mr-3"
                          />
                          <div>
                            <div className="font-medium text-sm">Select Existing Image</div>
                            <div className="text-xs text-gray-500">Select from uploaded images</div>
                          </div>
                        </label>
                        <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
                          <input
                            type="radio"
                            name="writeImageOption"
                            value="none"
                            checked={writePostMode.imageOption === 'none'}
                            onChange={(e) => setWritePostMode(prev => ({ ...prev, imageOption: e.target.value }))}
                            className="mr-3"
                          />
                          <div>
                            <div className="font-medium text-sm">No Images</div>
                            <div className="text-xs text-gray-500">Text-only posts</div>
                          </div>
                        </label>
                      </div>

                      {/* Select Image Format for Each Platform - MOVED TO APPEAR DIRECTLY BELOW IMAGE OPTIONS */}
                      {writePostMode.imageOption && writePostMode.imageOption !== 'none' && Object.values(writePostMode.socialPlatforms).some(platform => platform) && (
                        <div className="mt-4">
                          <label className="block text-sm font-medium text-gray-700 mb-3">
                            <ImageIcon className="h-4 w-4 inline mr-2" />
                            Select Image Format for Each Platform
                          </label>
                          
                          {/* Format Selection for Each Enabled Platform - Dynamic grid */}
                          <div className={`grid gap-2 ${Object.entries(writePostMode.socialPlatforms).filter(([platform, enabled]) => enabled).length === 1 ? 'grid-cols-1' : Object.entries(writePostMode.socialPlatforms).filter(([platform, enabled]) => enabled).length === 2 ? 'grid-cols-2' : Object.entries(writePostMode.socialPlatforms).filter(([platform, enabled]) => enabled).length === 3 ? 'grid-cols-3' : Object.entries(writePostMode.socialPlatforms).filter(([platform, enabled]) => enabled).length === 4 ? 'grid-cols-4' : 'grid-cols-5'}`}>
                            {Object.entries(writePostMode.socialPlatforms)
                              .filter(([platform, enabled]) => enabled)
                              .map(([platform, enabled]) => (
                                <div key={platform} className="flex flex-col">
                                  <label className="text-sm font-medium text-gray-600 mb-2 text-left capitalize">
                                    {platform}
                                  </label>
                                  <select
                                    value={writePostMode.platformImageFormats[platform] || ''}
                                    onChange={(e) => {
                                      const newFormats = { ...(writePostMode.platformImageFormats || {}) };
                                      if (e.target.value) {
                                        newFormats[platform] = e.target.value;
                                      } else {
                                        delete newFormats[platform];
                                      }
                                      setWritePostMode(prev => ({ ...prev, platformImageFormats: newFormats }));
                                    }}
                                    className="w-full px-2 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                                  >
                                    <option value="">Select...</option>
                                    {Object.entries(socialMediaPlatforms[platform === 'twitter' ? 'twitter' : platform]?.formats || {}).map(([formatKey, formatName]) => (
                                      <option key={formatKey} value={formatKey}>{formatName}</option>
                                    ))}
                                  </select>
                                  
                                  {/* Show format dimensions */}
                                  {writePostMode.platformImageFormats[platform] && socialMediaImageSettings[platform === 'twitter' ? 'twitter' : platform]?.[writePostMode.platformImageFormats[platform]] && (
                                    <div className="text-xs text-gray-600 mt-1 text-left">
                                      {socialMediaImageSettings[platform === 'twitter' ? 'twitter' : platform][writePostMode.platformImageFormats[platform]].width} × {socialMediaImageSettings[platform === 'twitter' ? 'twitter' : platform][writePostMode.platformImageFormats[platform]].height}
                                    </div>
                                  )}
                                </div>
                              ))
                            }
                          </div>
                        </div>
                      )}

                      {/* Image Upload Area - Match adhoc form styling */}
                      {writePostMode.imageOption === 'upload' && (
                        <div className="mt-4">
                          <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center bg-gray-50">
                            <Users className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                            <p className="text-sm text-gray-600 mb-2">Upload social media images</p>
                            <p className="text-xs text-gray-500 mb-4">Recommended: High resolution images for best social media results</p>
                            <div className="flex justify-center">
                              <label className="inline-flex items-center px-4 py-2 text-white rounded-md hover:opacity-90 transition-colors cursor-pointer" style={{ backgroundColor: 'var(--theme-primary)' }}>
                                <Upload className="h-4 w-4 mr-2" />
                                Upload Image
                                <input
                                  type="file"
                                  multiple
                                  accept="image/*"
                                  onChange={(e) => handleImageUpload(e, 'write')}
                                  className="hidden"
                                />
                              </label>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {writePostMode.imageOption === 's3_browse' && (
                        <div className="mt-4">
                          <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center bg-gray-50">
                            <ImageIcon className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                            <p className="text-sm text-gray-600 mb-2">Select reference images</p>
                            <p className="text-xs text-gray-500 mb-4">Choose images from your asset library to use as style reference</p>
                            <div className="flex justify-center">
                              <button
                                type="button"
                                onClick={() => openAssetBrowser('write')}
                                className="inline-flex items-center px-4 py-2 text-white rounded-md hover:opacity-90 transition-colors" style={{ backgroundColor: 'var(--theme-primary)' }}
                              >
                                <Folder className="h-4 w-4 mr-2" />
                                Browse Assets
                              </button>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Uploaded Images Preview */}
                      {(writePostMode.imageOption === 'upload' || writePostMode.imageOption === 's3_browse') && writePostMode.uploadedImages.length > 0 && (
                        <div className="mt-4">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Selected Images ({writePostMode.imageOption === 's3_browse' ? 'Reference' : 'Upload'})
                          </label>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            {writePostMode.uploadedImages.map((image, index) => (
                              <div key={index} className="relative">
                                <img 
                                  src={image.url || image.preview} 
                                  alt={image.name || `Upload ${index + 1}`}
                                  className="w-full h-24 object-cover rounded-lg border"
                                />
                                <button
                                  onClick={() => removeUploadedImage(index, 'write')}
                                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                                >
                                  <Trash2 className="h-3 w-3" />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Add Custom Text Banner to Image - MOVED HERE below Image Options */}
                    {(writePostMode.imageOption === 'ai_generate' || writePostMode.imageOption === 'upload' || writePostMode.imageOption === 's3_browse') && (
                      <div className="p-4 bg-white border border-gray-200 rounded-lg">
                        <label className="flex items-start">
                          <input
                            type="checkbox"
                            checked={writePostMode.enableCustomImageText}
                            onChange={(e) => setWritePostMode(prev => ({ ...prev, enableCustomImageText: e.target.checked }))}
                            className="h-4 w-4 text-theme-blue border-gray-300 rounded focus:ring-gray-500 mr-3"
                          />
                          <span className="block text-sm font-medium text-gray-700">
                            <FileText className="h-4 w-4 inline mr-2" />
                            Add Custom Text Banner to Image
                          </span>
                        </label>
                        <p className="text-xs text-gray-500 mt-2 ml-7">
                          Add custom text overlay on the generated image (e.g., your clinic name, promotional text)
                        </p>
                        
                        {writePostMode.enableCustomImageText && (
                          <div className="mt-3">
                            <div className="flex items-center justify-between mb-2">
                              <label className="block text-sm font-medium text-gray-700">
                                Custom Banner Text
                              </label>
                              <button
                                type="button"
                                onClick={() => openPlaceholderModal('customImageBannerText', (field, value) => setWritePostMode(prev => ({ ...prev, [field]: value })))}
                                className="px-3 py-1 text-xs font-medium text-white rounded transition-colors"
                                style={{ backgroundColor: 'var(--theme-primary)' }}
                                onMouseEnter={(e) => e.target.style.backgroundColor = 'rgb(35, 147, 180)'}
                                onMouseLeave={(e) => e.target.style.backgroundColor = 'var(--theme-primary)'}
                              >
                                Available Placeholders
                              </button>
                            </div>
                            <input
                              type="text"
                              value={writePostMode.customImageBannerText || '[BUSINESS_NAME] [PHONE_NUMBER]'}
                              onChange={(e) => setWritePostMode(prev => ({ ...prev, customImageBannerText: e.target.value }))}
                              className="w-full px-3 py-2 text-sm font-medium border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                              placeholder="[BUSINESS_NAME] [PHONE_NUMBER]"
                            />
                          </div>
                        )}
                      </div>
                    )}

                    {/* Add Link Section - Social Media */}
                    <div className="p-4 bg-white border border-gray-200 rounded-lg">
                      <label className="flex items-start">
                        <input
                          type="checkbox"
                          checked={writePostMode.enableSocialLink}
                          onChange={(e) => setWritePostMode(prev => ({ ...prev, enableSocialLink: e.target.checked }))}
                          className="h-4 w-4 text-theme-blue border-gray-300 rounded focus:ring-gray-500 mr-3"
                        />
                        <span className="block text-sm font-medium text-gray-700">
                          <Globe className="h-4 w-4 inline mr-2" />
                          Add Link
                        </span>
                      </label>
                      <p className="text-xs text-gray-500 mt-2 ml-7">
                        Add a link to your social media post (e.g., website, promotion, product page)
                      </p>
                      
                      {writePostMode.enableSocialLink && (
                        <div className="mt-3">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Link URL
                          </label>
                          <input
                            type="text"
                            value={writePostMode.socialLink || ''}
                            onChange={(e) => setWritePostMode(prev => ({ ...prev, socialLink: e.target.value }))}
                            className="w-full px-3 py-2 text-sm font-medium border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                            placeholder="https://example.com"
                          />
                        </div>
                      )}
                    </div>

                    {/* Select Image Format for Each Platform section moved to appear directly below image options */}

                    {/* Content Review Workflow - Styled like Image Options blocks */}
                    <div className="space-y-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Content Review Workflow
                      </label>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
                          <input
                            type="radio"
                            name="writePostDestination"
                            value="in_review"
                            checked={writePostMode.postDestination === 'in_review'}
                            onChange={(e) => setWritePostMode(prev => ({ ...prev, postDestination: e.target.value }))}
                            className="mr-3"
                          />
                          <div>
                            <div className="font-medium text-sm">In Review First</div>
                            <div className="text-xs text-gray-500">Generated posts go to "In Review" for manager approval</div>
                          </div>
                        </label>
                        <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
                          <input
                            type="radio"
                            name="writePostDestination"
                            value="ready_to_publish"
                            checked={writePostMode.postDestination === 'ready_to_publish'}
                            onChange={(e) => setWritePostMode(prev => ({ ...prev, postDestination: e.target.value }))}
                            className="mr-3"
                          />
                          <div>
                            <div className="font-medium text-sm">Ready to Publish</div>
                            <div className="text-xs text-gray-500">Generated posts go directly to "Ready to Publish"</div>
                          </div>
                        </label>
                        <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
                          <input
                            type="radio"
                            name="writePostDestination"
                            value="auto_post"
                            checked={writePostMode.postDestination === 'auto_post'}
                            onChange={(e) => setWritePostMode(prev => ({ ...prev, postDestination: e.target.value }))}
                            className="mr-3"
                          />
                          <div>
                            <div className="font-medium text-sm">Auto Post</div>
                            <div className="text-xs text-gray-500">Posts are automatically published without manual review</div>
                          </div>
                        </label>
                      </div>
                    </div>

                    {/* Save/Update Button */}
                    <div className="flex justify-end pt-6 border-t">
                      <button
                        onClick={isRunMode ? saveAndRunAgent : saveAgent}
                        disabled={loading || (!isEditMode && !isRunMode && (!writePostMode.agentName || !writePostMode.postContent || !Object.values(writePostMode.socialPlatforms).some(platform => platform)))}
                        className="px-6 py-2 bg-theme-blue text-white rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center"
                        style={{ 
                          backgroundColor: loading ? '#94a3b8' : 
                                          isRunMode ? '#10b981' : 'var(--theme-primary)'
                        }}
                        onMouseEnter={(e) => {
                          if (!loading && (isEditMode || isRunMode || (writePostMode.agentName && writePostMode.postContent && Object.values(writePostMode.socialPlatforms).some(platform => platform)))) {
                            e.target.style.backgroundColor = isRunMode ? '#059669' : '#1e88e5';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!loading) {
                            e.target.style.backgroundColor = isRunMode ? '#10b981' : 'var(--theme-primary)';
                          }
                        }}
                      >
                        {isRunMode ? <Play className="h-4 w-4 mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                        {loading ? (isRunMode ? 'Updating & Running...' : 'Saving...') : 
                         isRunMode ? 'Update & Run Agent' : 
                         isEditMode ? 'Update Agent Configuration' : 'Save Agent'}
                      </button>
                    </div>
                  </div>
                )}

                {/* Holiday Mode Tab (Email Agents) */}
                {activeTab === 'email-scheduled' && (
                  <div className="space-y-6">
                    {searchParams.get('agent_type') === 'email' ? (
                      // Email Holiday Mode Form
                      <div className="space-y-6">
                        {/* Agent Name & Send Time Section - Top Section */}
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Agent Name */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                <Mail className="h-4 w-4 inline mr-2" />
                                Agent Name *
                              </label>
                              <input
                                type="text"
                                value={emailScheduledMode.agentName}
                                onChange={(e) => handleEmailScheduledModeChange('agentName', e.target.value)}
                                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-theme-blue bg-white text-sm font-medium"
                                placeholder="Holiday Email Campaign"
                                required
                              />
                            </div>

                            {/* Send Time */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                <Clock className="h-4 w-4 inline mr-2" />
                                Send Time *
                              </label>
                              <select
                                value={emailScheduledMode.postTime}
                                onChange={(e) => handleEmailScheduledModeChange('postTime', e.target.value)}
                                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-theme-blue bg-white text-sm font-medium"
                                required
                              >
                                {timeOptions.map(option => (
                                  <option key={option.value} value={option.value} className="text-sm font-medium">
                                    {option.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                          <p className="text-xs text-gray-500 mt-4">
                            Emails will be sent automatically on each selected holiday at selected time
                          </p>
                        </div>

                        {/* Select Holidays Section */}
                        <div className="bg-white border border-gray-200 rounded-lg p-6">
                          <div className="flex justify-between items-center mb-4">
                            <label className="text-sm font-medium text-gray-700">
                              <Calendar className="h-4 w-4 inline mr-2" />
                              Select Holidays *
                            </label>
                            
                            {availableHolidays.length > 0 && (
                              <label className="flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={emailScheduledMode.selectedHolidays.length === availableHolidays.length && availableHolidays.length > 0}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      // Select all holidays
                                      const allHolidayIds = availableHolidays.map(holiday => holiday.id);
                                      handleEmailScheduledModeChange('selectedHolidays', allHolidayIds);
                                    } else {
                                      // Deselect all holidays
                                      handleEmailScheduledModeChange('selectedHolidays', []);
                                    }
                                  }}
                                  className="h-4 w-4 text-theme-blue border-gray-300 rounded focus:ring-blue-500 mr-2"
                                />
                                <span className="text-xs text-gray-600">Select All</span>
                              </label>
                            )}
                          </div>
                          
                          {availableHolidays.length > 0 ? (
                            <>
                              <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-lg p-4 bg-gray-50">
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                {availableHolidays.map((holiday) => {
                                  const IconComponent = getCategoryIcon(holiday.category);
                                  return (
                                    <label key={holiday.id} className="flex items-center p-3 bg-white rounded-lg hover:bg-gray-100 cursor-pointer border">
                                      <input
                                        type="checkbox"
                                        checked={emailScheduledMode.selectedHolidays.includes(holiday.id)}
                                        onChange={(e) => {
                                          const selected = [...emailScheduledMode.selectedHolidays];
                                          if (e.target.checked) {
                                            selected.push(holiday.id);
                                          } else {
                                            selected.splice(selected.indexOf(holiday.id), 1);
                                          }
                                          handleEmailScheduledModeChange('selectedHolidays', selected);
                                        }}
                                        className="h-4 w-4 text-theme-blue border-gray-300 rounded focus:ring-gray-500 mr-3"
                                      />
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center mb-1">
                                          <IconComponent className="h-4 w-4 mr-2 text-gray-500 flex-shrink-0" />
                                          <span className="text-sm font-medium text-gray-900 truncate">{holiday.name}</span>
                                        </div>
                                        <span className="text-xs text-gray-500 block">
                                          {new Date(holiday.date).toLocaleDateString('en-US', { 
                                            weekday: 'short', 
                                            month: 'short', 
                                            day: 'numeric',
                                            year: 'numeric'
                                          })}
                                        </span>
                                      </div>
                                    </label>
                                  );
                                })}
                              </div>
                            </div>
                            </>
                          ) : (
                            <div className="text-center py-4 text-gray-500 bg-gray-50 rounded-lg">
                              <Calendar className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                              <p className="text-sm font-medium">Loading holidays...</p>
                            </div>
                          )}
                          <p className="text-xs text-gray-500 mt-2">
                            Selected: {emailScheduledMode.selectedHolidays.length} holiday{emailScheduledMode.selectedHolidays.length !== 1 ? 's' : ''}
                          </p>
                        </div>

                        {/* Email Recipients section moved below - now appears before Content Review Workflow */}

                        {/* Customer Database Integration section removed */}

                        {/* Email Template Configuration Section */}
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 space-y-4">
                          {/* Select Template */}
                          <div>
                            <TemplateDropdown
                              templates={emailTemplates}
                              selectedValue={emailScheduledMode.emailContentTemplate}
                              onSelect={(templateContent) => handleEmailScheduledModeChange('emailContentTemplate', templateContent)}
                              placeholder="Select an email template..."
                              templateType="email"
                              isPersonalized={true}
                              requireChatGPT={true}
                            />
                          </div>

                          <div className="flex items-center justify-between mb-2">
                            <label className="block text-sm font-medium text-gray-700">
                              <FileText className="h-4 w-4 inline mr-2" />
                              Email Content *
                            </label>
                            <div className="flex space-x-2">
                              <button
                                type="button"
                                onClick={() => openPlaceholderModal('emailContentTemplate', setEmailScheduledMode)}
                                className="px-3 py-1 text-xs font-medium text-white rounded transition-colors"
                                style={{ backgroundColor: 'var(--theme-primary)' }}
                                onMouseEnter={(e) => e.target.style.backgroundColor = 'rgb(35, 147, 180)'}
                                onMouseLeave={(e) => e.target.style.backgroundColor = 'var(--theme-primary)'}
                              >
                                Available Placeholders
                              </button>
                              <FullScreenEditorButton
                                content={emailScheduledMode.emailContentTemplate}
                                title="Holiday Email Agent - Email Content"
                                onSave={(content) => handleEmailScheduledModeChange('emailContentTemplate', content)}
                                className="text-xs"
                                isPersonalized={true}
                              />
                            </div>
                          </div>
                          <textarea
                            value={emailScheduledMode.emailContentTemplate}
                            onChange={(e) => handleEmailScheduledModeChange('emailContentTemplate', e.target.value)}
                            rows={6}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-theme-blue resize-none bg-white text-sm font-medium"
                            placeholder="Enter your email template content. Use placeholders like [CUSTOMER_NAME], [PET_NAME], and [BUSINESS_NAME] for personalization."
                          />
                          <p className="text-xs text-gray-500">
                            Use [CUSTOMER_NAME] and [PET_NAME] placeholders for personalized emails. CHATGTP_CONTENT placeholder is mandatory as ChatGPT replaces it with AI-rendered content.
                          </p>
                        </div>

                        {/* Image Options Section */}
                        <div className="space-y-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                          <label className="block text-sm font-medium text-gray-700">
                            <ImageIcon className="h-4 w-4 inline mr-2" />
                            Image Options
                          </label>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
                              <input
                                type="radio"
                                name="emailScheduledImageOption"
                                value="ai_generate"
                                checked={emailScheduledMode.imageOption === 'ai_generate'}
                                onChange={(e) => handleEmailScheduledModeChange('imageOption', e.target.value)}
                                className="mr-3"
                              />
                              <div>
                                <div className="font-medium text-sm">AI Generate</div>
                                <div className="text-xs text-gray-500">Create unique images using AI</div>
                              </div>
                            </label>
                            <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
                              <input
                                type="radio"
                                name="emailScheduledImageOption"
                                value="upload"
                                checked={emailScheduledMode.imageOption === 'upload'}
                                onChange={(e) => handleEmailScheduledModeChange('imageOption', e.target.value)}
                                className="mr-3"
                              />
                              <div>
                                <div className="font-medium text-sm">Upload Image</div>
                                <div className="text-xs text-gray-500">Use your own images</div>
                              </div>
                            </label>
                            <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
                              <input
                                type="radio"
                                name="emailScheduledImageOption"
                                value="s3_browse"
                                checked={emailScheduledMode.imageOption === 's3_browse'}
                                onChange={(e) => handleEmailScheduledModeChange('imageOption', e.target.value)}
                                className="mr-3"
                              />
                              <div>
                                <div className="font-medium text-sm">Select Existing Image</div>
                                <div className="text-xs text-gray-500">Select from uploaded images</div>
                              </div>
                            </label>
                            <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
                              <input
                                type="radio"
                                name="emailScheduledImageOption"
                                value="none"
                                checked={emailScheduledMode.imageOption === 'none'}
                                onChange={(e) => handleEmailScheduledModeChange('imageOption', e.target.value)}
                                className="mr-3"
                              />
                              <div>
                                <div className="font-medium text-sm">No Images</div>
                                <div className="text-xs text-gray-500">Text-only emails</div>
                              </div>
                            </label>
                          </div>

                          {/* Image Upload Area */}
                          {emailScheduledMode.imageOption === 'upload' && (
                            <div className="mt-4">
                              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center bg-gray-50">
                                <Upload className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                                <p className="text-sm text-gray-600 mb-2">Upload email images</p>
                                <p className="text-xs text-gray-500 mb-4">Recommended: High resolution images for best results</p>
                                <div className="flex justify-center">
                                  <label className="inline-flex items-center px-4 py-2 text-white rounded-md hover:opacity-90 transition-colors cursor-pointer" style={{ backgroundColor: 'var(--theme-primary)' }}>
                                    <Upload className="h-4 w-4 mr-2" />
                                    Upload Image
                                    <input
                                      type="file"
                                      multiple
                                      accept="image/*"
                                      onChange={(e) => handleImageUpload(e, 'emailScheduled')}
                                      className="hidden"
                                    />
                                  </label>
                                </div>
                              </div>
                            </div>
                          )}
                          
                          {emailScheduledMode.imageOption === 's3_browse' && (
                            <div className="mt-4">
                              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center bg-gray-50">
                                <ImageIcon className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                                <p className="text-sm text-gray-600 mb-2">Select existing images</p>
                                <p className="text-xs text-gray-500 mb-4">Choose images from your asset library</p>
                                <div className="flex justify-center">
                                  <button
                                    type="button"
                                    onClick={() => openAssetBrowser('emailScheduled')}
                                    className="inline-flex items-center px-4 py-2 text-white rounded-md hover:opacity-90 transition-colors" style={{ backgroundColor: 'var(--theme-primary)' }}
                                  >
                                    <Folder className="h-4 w-4 mr-2" />
                                    Browse Images
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Uploaded Images Preview */}
                          {(emailScheduledMode.imageOption === 'upload' || emailScheduledMode.imageOption === 's3_browse') && emailScheduledMode.uploadedImages && emailScheduledMode.uploadedImages.length > 0 && (
                            <div className="mt-4">
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Selected Images ({emailScheduledMode.imageOption === 's3_browse' ? 'S3 Browse' : 'Upload'})
                              </label>
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                {emailScheduledMode.uploadedImages.map((image, index) => (
                                  <div key={index} className="relative">
                                    <img 
                                      src={image.url} 
                                      alt={image.name}
                                      className="w-full h-24 object-cover rounded-lg border"
                                    />
                                    <button
                                      onClick={() => removeUploadedImage(index, 'emailScheduled')}
                                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                                    >
                                      <Trash2 className="h-3 w-3" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Select Image Format for Email */}
                        {(emailScheduledMode.imageOption === 'ai_generate' || emailScheduledMode.imageOption === 'upload' || emailScheduledMode.imageOption === 's3_browse') && (
                          <div className="space-y-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                            <label className="block font-medium text-sm text-gray-700">
                              <ImageIcon className="h-4 w-4 inline mr-2" />
                              Select Image Format *
                            </label>
                            
                            <div className="flex flex-col">
                              <label className="font-medium text-sm text-gray-600 mb-2 text-left">
                                📧 Email
                              </label>
                              <select
                                value={emailScheduledMode.imageFormat || ''}
                                onChange={(e) => handleEmailScheduledModeChange('imageFormat', e.target.value)}
                                className="w-full px-3 py-2 font-medium text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                              >
                                <option value="">Select...</option>
                                <option value="portrait">Portrait</option>
                                <option value="landscape">Landscape</option>
                                <option value="square">Square</option>
                              </select>
                              
                              {/* Show format dimensions */}
                              {emailScheduledMode.imageFormat && socialMediaImageSettings.email?.[emailScheduledMode.imageFormat] && (
                                <div className="text-xs text-gray-600 mt-1 text-left">
                                  {socialMediaImageSettings.email[emailScheduledMode.imageFormat].width} × {socialMediaImageSettings.email[emailScheduledMode.imageFormat].height}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        {/* Add Custom Text Banner to Image */}
                        {(emailScheduledMode.imageOption === 'ai_generate' || emailScheduledMode.imageOption === 'upload' || emailScheduledMode.imageOption === 's3_browse') && (
                          <div className="p-4 bg-white border border-gray-200 rounded-lg">
                            <label className="flex items-start">
                              <input
                                type="checkbox"
                                checked={emailScheduledMode.enableCustomImageText}
                                onChange={(e) => handleEmailScheduledModeChange('enableCustomImageText', e.target.checked)}
                                className="h-4 w-4 text-theme-blue border-gray-300 rounded focus:ring-gray-500 mr-3"
                              />
                              <span className="block text-sm font-medium text-gray-700">
                                <FileText className="h-4 w-4 inline mr-2" />
                                Add Custom Text Banner to Image
                              </span>
                            </label>
                            <p className="text-xs text-gray-500 mt-2 ml-7">
                              Add custom text overlay on the generated image (e.g., your clinic name, promotional text)
                            </p>
                            
                            {emailScheduledMode.enableCustomImageText && (
                              <div className="mt-3">
                                <div className="flex items-center justify-between mb-2">
                                  <label className="block text-sm font-medium text-gray-700">
                                    Custom Banner Text
                                  </label>
                                  <button
                                    type="button"
                                    onClick={() => openPlaceholderModal('customImageBannerText', setEmailScheduledMode)}
                                    className="px-3 py-1 text-xs font-medium text-white rounded transition-colors"
                                    style={{ backgroundColor: 'var(--theme-primary)' }}
                                    onMouseEnter={(e) => e.target.style.backgroundColor = 'rgb(35, 147, 180)'}
                                    onMouseLeave={(e) => e.target.style.backgroundColor = 'var(--theme-primary)'}
                                  >
                                    Available Placeholders
                                  </button>
                                </div>
                                <input
                                  type="text"
                                  value={emailScheduledMode.customImageBannerText || '[BUSINESS_NAME] [PHONE_NUMBER]'}
                                  onChange={(e) => handleEmailScheduledModeChange('customImageBannerText', e.target.value)}
                                  className="w-full px-3 py-2 text-sm font-medium border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                                  placeholder="[BUSINESS_NAME] [PHONE_NUMBER]"
                                />
                              </div>
                            )}
                          </div>
                        )}

                        {/* Email Recipients Section - moved above Content Review Workflow */}
                        <div className="space-y-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                          <label className="block text-sm font-medium text-gray-700">
                            <Users className="h-4 w-4 inline mr-2" />
                            Email Recipients *
                          </label>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
                              <input
                                type="radio"
                                name="emailScheduledType"
                                value="bulk"
                                checked={emailScheduledMode.emailType === 'bulk'}
                                onChange={(e) => {
                                  handleEmailScheduledModeChange('emailType', e.target.value);
                                  // Clear selected customer if switching to bulk
                                  if (e.target.value === 'bulk') {
                                    handleEmailScheduledModeChange('selectedCustomer', null);
                                    handleEmailScheduledModeChange('customerName', '');
                                    setSelectedCustomer(null);
                                  }
                                }}
                                className="mr-3"
                              />
                              <div>
                                <div className="font-medium text-sm">All Customers</div>
                                <div className="text-xs text-gray-500">Send to all opted-in customers in your database</div>
                              </div>
                            </label>
                            <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
                              <input
                                type="radio"
                                name="emailScheduledType"
                                value="single"
                                checked={emailScheduledMode.emailType === 'single'}
                                onChange={(e) => {
                                  handleEmailScheduledModeChange('emailType', e.target.value);
                                }}
                                className="mr-3"
                              />
                              <div>
                                <div className="font-medium text-sm">Single Customer</div>
                                <div className="text-xs text-gray-500">Send to one specific customer</div>
                              </div>
                            </label>
                          </div>
                        </div>

                        {/* Customer Search (only for single email) */}
                        {emailScheduledMode.emailType === 'single' && (
                          <div className="bg-white border border-gray-200 rounded-lg p-6">
                            <label className="block text-sm font-medium text-gray-700 mb-4">
                              <Search className="h-4 w-4 inline mr-2" />
                              Search Customer *
                            </label>
                            <div className="relative">
                              <input
                                type="text"
                                value={customerSearchTerm}
                                onChange={(e) => setCustomerSearchTerm(e.target.value)}
                                placeholder="Type at least 3 characters to search by customer name, email, or pet name..."
                                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-theme-blue pl-10 text-sm font-medium"
                              />
                              <Search className="h-5 w-5 text-gray-400 absolute left-3 top-3.5" />
                              {isSearchingCustomers && (
                                <div className="absolute right-3 top-3.5">
                                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-theme-blue"></div>
                                </div>
                              )}
                            </div>

                            {/* Search Results */}
                            {searchedCustomers.length > 0 && customerSearchTerm.length >= 3 && (
                              <div className="mt-4 border border-gray-200 rounded-lg bg-white shadow-sm">
                                <div className="px-3 py-2 bg-gray-50 border-b text-xs font-medium text-gray-700">Search Results</div>
                                <div className="max-h-60 overflow-y-auto">
                                  {searchedCustomers.map((customer, index) => (
                                    <div
                                      key={index}
                                      onClick={() => {
                                        handleEmailScheduledModeChange('selectedCustomer', customer.id);
                                        handleEmailScheduledModeChange('customerName', getCustomerFullName(customer));
                                        setSelectedCustomer(customer);
                                        setCustomerSearchTerm('');
                                        setSearchedCustomers([]);
                                      }}
                                      className="p-3 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                                    >
                                      <div className="flex justify-between items-start">
                                        <div className="flex-1">
                                          <div className="text-sm font-medium text-gray-900">{getCustomerFullName(customer)}</div>
                                          <div className="text-xs text-gray-500">{customer.email || 'No email'}</div>
                                          {customer.pets && customer.pets.length > 0 && (
                                            <div className="text-xs text-theme-blue mt-1">
                                              Pets: {customer.pets.map(pet => pet.name).join(', ')}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Selected Customer Display */}
                            {selectedCustomer && emailScheduledMode.selectedCustomer && (
                              <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                                <div className="text-sm font-medium text-green-900">Selected Customer:</div>
                                <div className="text-sm text-green-800 mt-1">
                                  {getCustomerFullName(selectedCustomer)}
                                  <button
                                    type="button"
                                    onClick={() => {
                                      handleEmailScheduledModeChange('selectedCustomer', null);
                                      handleEmailScheduledModeChange('customerName', '');
                                      setSelectedCustomer(null);
                                    }}
                                    className="ml-2 text-red-600 hover:text-red-800 text-xs"
                                  >
                                    Remove
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Content Review Workflow - Styled like Image Options blocks */}
                        <div className="space-y-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Content Review Workflow
                          </label>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
                              <input
                                type="radio"
                                name="emailScheduledPostDestination"
                                value="in_review"
                                checked={emailScheduledMode.postDestination === 'in_review'}
                                onChange={(e) => handleEmailScheduledModeChange('postDestination', e.target.value)}
                                className="mr-3"
                              />
                              <div>
                                <div className="font-medium text-sm">In Review First</div>
                                <div className="text-xs text-gray-500">Generated emails go to "In Review" for manager approval</div>
                              </div>
                            </label>
                            <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
                              <input
                                type="radio"
                                name="emailScheduledPostDestination"
                                value="ready_to_publish"
                                checked={emailScheduledMode.postDestination === 'ready_to_publish'}
                                onChange={(e) => handleEmailScheduledModeChange('postDestination', e.target.value)}
                                className="mr-3"
                              />
                              <div>
                                <div className="font-medium text-sm">Ready to Publish</div>
                                <div className="text-xs text-gray-500">Generated emails go directly to "Ready to Publish"</div>
                              </div>
                            </label>
                            <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
                              <input
                                type="radio"
                                name="emailScheduledPostDestination"
                                value="auto_send"
                                checked={emailScheduledMode.postDestination === 'auto_send'}
                                onChange={(e) => handleEmailScheduledModeChange('postDestination', e.target.value)}
                                className="mr-3"
                              />
                              <div>
                                <div className="font-medium text-sm">Send Automatically</div>
                                <div className="text-xs text-gray-500">Emails are automatically sent without manual review</div>
                              </div>
                            </label>
                          </div>
                        </div>

                        {/* Save Button */}
                        <div className="flex justify-end space-x-3">
                          <button
                            onClick={isRunMode ? saveAndRunAgent : saveAgent}
                            disabled={loading || (!isEditMode && !isRunMode && (!emailScheduledMode.agentName || emailScheduledMode.selectedHolidays.length === 0))}
                            className="text-white px-6 py-2 rounded-lg transition-colors disabled:opacity-50 flex items-center font-medium"
                            style={{ 
                              backgroundColor: loading ? '#94a3b8' : 
                                              isRunMode ? '#10b981' : 
                                              (isEditMode || isRunMode) ? 'var(--theme-primary)' : 'var(--theme-primary)'
                            }}
                            onMouseEnter={(e) => {
                              if (!loading && ((isEditMode || isRunMode) || (emailScheduledMode.agentName && emailScheduledMode.selectedHolidays.length > 0))) {
                                e.target.style.backgroundColor = isRunMode ? '#059669' : (isEditMode || isRunMode) ? 'var(--theme-primary-hover)' : 'var(--theme-primary-hover)';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!loading) {
                                e.target.style.backgroundColor = isRunMode ? '#10b981' : (isEditMode || isRunMode) ? 'var(--theme-primary)' : 'var(--theme-primary)';
                              }
                            }}
                          >
                            {isRunMode ? <Play className="h-4 w-4 mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                            {loading ? (isRunMode ? 'Updating & Running...' : 'Saving...') : 
                             isRunMode ? 'Update & Run Agent' : 
                             isEditMode ? 'Update Agent Configuration' : 'Save Agent'}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <h3 className="text-lg font-medium text-gray-900 mb-2">Scheduled Mode</h3>
                        <p className="text-gray-600">Scheduled mode is only available for Email agents.</p>
                      </div>
                    )}
                  </div>
                )}

                                {/* Write Your Email Tab */}
                {activeTab === 'email-recurring' && (
                  <div className="space-y-6">
                    {searchParams.get('agent_type') === 'email' ? (
                      // Email Recurring Mode Form
                      <div className="space-y-6">
                        {/* Agent Configuration Section - Top Section */}
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                          {/* Agent Name & Topic Row */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                            {/* Agent Name */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                <Mail className="h-4 w-4 inline mr-2" />
                                Agent Name *
                              </label>
                              <input
                                type="text"
                                value={emailRecurringMode.agentName}
                                onChange={(e) => handleEmailRecurringModeChange('agentName', e.target.value)}
                                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-theme-blue bg-white text-sm font-medium"
                                placeholder="e.g., Weekly Pet Care Email Agent"
                                required
                              />
                            </div>

                            {/* Topic Selection */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                <Target className="h-4 w-4 inline mr-2" />
                                Select Topic *
                              </label>
                              <select
                                value={emailRecurringMode.topic}
                                onChange={(e) => handleEmailRecurringModeChange('topic', e.target.value)}
                                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-theme-blue bg-white text-sm font-medium"
                                required
                              >
                                <option value="">Select a topic...</option>
                                {topicOptions.map(topic => (
                                  <option key={topic} value={topic} className="text-sm font-medium">{topic}</option>
                                ))}
                              </select>
                              {emailRecurringMode.topic === 'Custom' && (
                                <input
                                  type="text"
                                  value={emailRecurringMode.customTopic}
                                  onChange={(e) => handleEmailRecurringModeChange('customTopic', e.target.value)}
                                  className="w-full mt-2 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-theme-blue bg-white text-sm font-medium"
                                  placeholder="Enter custom topic..."
                                />
                              )}
                            </div>
                          </div>

                          {/* Schedule & Send Time Row */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Schedule Selection */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                <Calendar className="h-4 w-4 inline mr-2" />
                                Select Schedule *
                              </label>
                              
                              {/* Schedule Type Selection */}
                              <div className="space-y-3">
                                <label className="flex items-start">
                                  <input
                                    type="radio"
                                    name="scheduleType"
                                    value="weekly"
                                    checked={emailRecurringMode.scheduleType === 'weekly'}
                                    onChange={(e) => handleEmailRecurringModeChange('scheduleType', e.target.value)}
                                    className="h-4 w-4 text-theme-blue border-gray-300 focus:ring-gray-500"
                                  />
                                  <div className="ml-3">
                                    <span className="text-sm font-medium text-gray-900">Weekly Schedule</span>
                                    <p className="text-xs text-gray-500">Send emails on selected days of the week</p>
                                  </div>
                                </label>
                                <label className="flex items-start">
                                  <input
                                    type="radio"
                                    name="scheduleType"
                                    value="monthly"
                                    checked={emailRecurringMode.scheduleType === 'monthly'}
                                    onChange={(e) => handleEmailRecurringModeChange('scheduleType', e.target.value)}
                                    className="h-4 w-4 text-theme-blue border-gray-300 focus:ring-gray-500"
                                  />
                                  <div className="ml-3">
                                    <span className="text-sm font-medium text-gray-900">Monthly Schedule</span>
                                    <p className="text-xs text-gray-500">1st of every month this agent will run</p>
                                  </div>
                                </label>
                              </div>
                            </div>

                            {/* Send Time */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                <Clock className="h-4 w-4 inline mr-2" />
                                Send Time *
                              </label>
                              <select
                                value={emailRecurringMode.postTime}
                                onChange={(e) => handleEmailRecurringModeChange('postTime', e.target.value)}
                                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-theme-blue bg-white text-sm font-medium"
                                required
                              >
                                {timeOptions.map(option => (
                                  <option key={option.value} value={option.value} className="text-sm font-medium">{option.label}</option>
                                ))}
                              </select>
                            </div>
                          </div>

                          {/* Weekly Schedule - Days of Week Selection */}
                          {emailRecurringMode.scheduleType === 'weekly' && (
                            <div className="mt-6 pt-6 border-t border-gray-200">
                              <label className="block text-sm font-medium text-gray-700 mb-4">
                                <Calendar className="h-4 w-4 inline mr-2" />
                                Select Days of Week *
                              </label>
                              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                {Object.entries(emailRecurringMode.daysOfWeek).map(([day, selected]) => (
                                  <label key={day} className="flex items-center p-3 bg-white rounded-lg hover:bg-gray-50 cursor-pointer border">
                                    <input
                                      type="checkbox"
                                      checked={selected}
                                      onChange={(e) => handleEmailRecurringModeChange('daysOfWeek', {
                                        ...emailRecurringMode.daysOfWeek,
                                        [day]: e.target.checked
                                      })}
                                      className="h-4 w-4 text-theme-blue border-gray-300 rounded focus:ring-gray-500 mr-3"
                                    />
                                    <span className="text-sm font-medium text-gray-900 capitalize">{day}</span>
                                  </label>
                                ))}
                              </div>
                              {!Object.values(emailRecurringMode.daysOfWeek).some(day => day) && (
                                <p className="text-sm text-red-600 mt-2">Please select at least one day.</p>
                              )}
                            </div>
                          )}

                          {/* Monthly Schedule - Fixed to 1st of every month */}
                          {emailRecurringMode.scheduleType === 'monthly' && (
                            <div className="mt-6">
                              <div className="flex items-center space-x-2 p-4 bg-white border border-gray-200 rounded-lg">
                                <Calendar className="h-5 w-5 text-theme-blue" />
                                <div>
                                  <p className="text-sm font-medium text-gray-900">Monthly Schedule</p>
                                  <p className="text-sm text-gray-500">This agent will run on the <strong>1st of every month</strong> (January 1st, February 1st, March 1st, etc.)</p>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                        
                        {/* Customer Database Integration section removed */}

                        {/* Email Template Section */}
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                          {/* Select Template */}
                          <div className="mb-4">
                            <TemplateDropdown
                              templates={emailTemplates}
                              selectedValue={emailRecurringMode.emailContentTemplate}
                              onSelect={(templateContent) => handleEmailRecurringModeChange('emailContentTemplate', templateContent)}
                              placeholder="Select an email template..."
                              templateType="email"
                              isPersonalized={true}
                              requireChatGPT={true}
                            />
                          </div>

                          {/* Email Content */}
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <label className="block text-sm font-medium text-gray-700">
                                <FileText className="h-4 w-4 inline mr-2" />
                                Email Content
                              </label>
                              <div className="flex items-center space-x-2">
                                <button
                                  type="button"
                                  onClick={() => openPlaceholderModal('emailContentTemplate', setEmailRecurringMode)}
                                  className="px-3 py-1 text-xs font-medium text-white rounded transition-colors"
                                  style={{ backgroundColor: 'var(--theme-primary)' }}
                                  onMouseEnter={(e) => e.target.style.backgroundColor = 'rgb(35, 147, 180)'}
                                  onMouseLeave={(e) => e.target.style.backgroundColor = 'var(--theme-primary)'}
                                >
                                  Available Placeholders
                                </button>
                                <FullScreenEditorButton
                                  content={emailRecurringMode.emailContentTemplate}
                                  title="Recurring Email Agent - Email Content"
                                  onSave={(content) => handleEmailRecurringModeChange('emailContentTemplate', content)}
                                  className="text-xs"
                                  isPersonalized={true}
                                />
                              </div>
                            </div>
                            <textarea
                              value={emailRecurringMode.emailContentTemplate}
                              onChange={(e) => handleEmailRecurringModeChange('emailContentTemplate', e.target.value)}
                              rows={6}
                              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-theme-blue bg-white text-sm font-medium"
                              placeholder="Enter generic business information and content that should be included in all recurring emails. ChatGPT will use this information along with customer name, pet details, and your email topic to generate personalized emails.

Example:
- Visit our clinic for special offers
- Don't forget your pet's annual checkup
- We offer 24/7 emergency services
- Book appointments online at [website]"
                            />
                            <p className="text-xs text-gray-500 mt-2">
                              Use [CUSTOMER_NAME] and [PET_NAME] placeholders for personalized emails. CHATGTP_CONTENT placeholder is mandatory as ChatGPT replaces it with AI-rendered content.
                            </p>
                          </div>
                        </div>

                        {/* Email Recipients section moved below - now appears before Content Review Workflow */}
                                                
                                                {/* Image Options Section */}
                        <div className="space-y-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                          <label className="block text-sm font-medium text-gray-700">
                            <ImageIcon className="h-4 w-4 inline mr-2" />
                            Image Options
                          </label>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
                              <input
                                type="radio"
                                name="emailRecurringImageOption"
                                value="ai_generate"
                                checked={emailRecurringMode.imageOption === 'ai_generate'}
                                onChange={(e) => handleEmailRecurringModeChange('imageOption', e.target.value)}
                                className="mr-3"
                              />
                              <div>
                                <div className="font-medium text-sm">AI Generate</div>
                                <div className="text-xs text-gray-500">Create unique images using AI</div>
                              </div>
                            </label>
                            <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
                              <input
                                type="radio"
                                name="emailRecurringImageOption"
                                value="upload"
                                checked={emailRecurringMode.imageOption === 'upload'}
                                onChange={(e) => handleEmailRecurringModeChange('imageOption', e.target.value)}
                                className="mr-3"
                              />
                              <div>
                                <div className="font-medium text-sm">Upload Image</div>
                                <div className="text-xs text-gray-500">Use your own images</div>
                              </div>
                            </label>
                            <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
                              <input
                                type="radio"
                                name="emailRecurringImageOption"
                                value="s3_browse"
                                checked={emailRecurringMode.imageOption === 's3_browse'}
                                onChange={(e) => handleEmailRecurringModeChange('imageOption', e.target.value)}
                                className="mr-3"
                              />
                              <div>
                                <div className="font-medium text-sm">Select Existing Image</div>
                                <div className="text-xs text-gray-500">Select from uploaded images</div>
                              </div>
                            </label>
                            <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
                              <input
                                type="radio"
                                name="emailRecurringImageOption"
                                value="none"
                                checked={emailRecurringMode.imageOption === 'none'}
                                onChange={(e) => handleEmailRecurringModeChange('imageOption', e.target.value)}
                                className="mr-3"
                              />
                              <div>
                                <div className="font-medium text-sm">No Images</div>
                                <div className="text-xs text-gray-500">Text-only emails</div>
                              </div>
                            </label>
                          </div>

                          {/* Image Upload Area */}
                          {emailRecurringMode.imageOption === 'upload' && (
                            <div className="mt-4">
                              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center bg-gray-50">
                                <Upload className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                                <p className="text-sm text-gray-600 mb-2">Upload email images</p>
                                <p className="text-xs text-gray-500 mb-4">Recommended: High resolution images for best results</p>
                                <div className="flex justify-center">
                                  <label className="inline-flex items-center px-4 py-2 text-white rounded-md hover:opacity-90 transition-colors cursor-pointer" style={{ backgroundColor: 'var(--theme-primary)' }}>
                                    <Upload className="h-4 w-4 mr-2" />
                                    Upload Image
                                    <input
                                      type="file"
                                      multiple
                                      accept="image/*"
                                      onChange={(e) => handleImageUpload(e, 'emailRecurring')}
                                      className="hidden"
                                    />
                                  </label>
                                </div>
                              </div>
                            </div>
                          )}
                          
                          {emailRecurringMode.imageOption === 's3_browse' && (
                            <div className="mt-4">
                              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center bg-gray-50">
                                <ImageIcon className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                                <p className="text-sm text-gray-600 mb-2">Select existing images</p>
                                <p className="text-xs text-gray-500 mb-4">Choose images from your asset library</p>
                                <div className="flex justify-center">
                                  <button
                                    type="button"
                                    onClick={() => openAssetBrowser('emailRecurring')}
                                    className="inline-flex items-center px-4 py-2 text-white rounded-md hover:opacity-90 transition-colors" style={{ backgroundColor: 'var(--theme-primary)' }}
                                  >
                                    <Folder className="h-4 w-4 mr-2" />
                                    Browse Images
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Uploaded Images Preview */}
                          {(emailRecurringMode.imageOption === 'upload' || emailRecurringMode.imageOption === 's3_browse') && emailRecurringMode.uploadedImages && emailRecurringMode.uploadedImages.length > 0 && (
                            <div className="mt-4">
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Selected Images ({emailRecurringMode.imageOption === 's3_browse' ? 'S3 Browse' : 'Upload'})
                              </label>
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                {emailRecurringMode.uploadedImages.map((image, index) => (
                                  <div key={index} className="relative">
                                    <img 
                                      src={image.url} 
                                      alt={image.name}
                                      className="w-full h-24 object-cover rounded-lg border"
                                    />
                                    <button
                                      onClick={() => removeUploadedImage(index, 'emailRecurring')}
                                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                                    >
                                      <Trash2 className="h-3 w-3" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Select Image Format for Email */}
                        {(emailRecurringMode.imageOption === 'ai_generate' || emailRecurringMode.imageOption === 'upload' || emailRecurringMode.imageOption === 's3_browse') && (
                          <div className="space-y-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                            <label className="block font-medium text-sm text-gray-700">
                              <ImageIcon className="h-4 w-4 inline mr-2" />
                              Select Image Format *
                            </label>
                            
                            <div className="flex flex-col">
                              <label className="font-medium text-sm text-gray-600 mb-2 text-left">
                                📧 Email
                              </label>
                              <select
                                value={emailRecurringMode.imageFormat || ''}
                                onChange={(e) => handleEmailRecurringModeChange('imageFormat', e.target.value)}
                                className="w-full px-3 py-2 font-medium text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                              >
                                <option value="">Select...</option>
                                <option value="portrait">Portrait</option>
                                <option value="landscape">Landscape</option>
                                <option value="square">Square</option>
                              </select>
                              
                              {/* Show format dimensions */}
                              {emailRecurringMode.imageFormat && socialMediaImageSettings.email?.[emailRecurringMode.imageFormat] && (
                                <div className="text-xs text-gray-600 mt-1 text-left">
                                  {socialMediaImageSettings.email[emailRecurringMode.imageFormat].width} × {socialMediaImageSettings.email[emailRecurringMode.imageFormat].height}
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Add Custom Text Banner to Image */}
                        {(emailRecurringMode.imageOption === 'ai_generate' || emailRecurringMode.imageOption === 'upload' || emailRecurringMode.imageOption === 's3_browse') && (
                          <div className="p-4 bg-white border border-gray-200 rounded-lg">
                            <label className="flex items-start">
                              <input
                                type="checkbox"
                                checked={emailRecurringMode.enableCustomImageText}
                                onChange={(e) => handleEmailRecurringModeChange('enableCustomImageText', e.target.checked)}
                                className="h-4 w-4 text-theme-blue border-gray-300 rounded focus:ring-gray-500 mr-3"
                              />
                              <span className="block text-sm font-medium text-gray-700">
                                <FileText className="h-4 w-4 inline mr-2" />
                                Add Custom Text Banner to Image
                              </span>
                            </label>
                            <p className="text-xs text-gray-500 mt-2 ml-7">
                              Add custom text overlay on the generated image (e.g., your clinic name, promotional text)
                            </p>
                            
                            {emailRecurringMode.enableCustomImageText && (
                              <div className="mt-3">
                                <div className="flex items-center justify-between mb-2">
                                  <label className="block text-sm font-medium text-gray-700">
                                    Custom Banner Text
                                  </label>
                                  <button
                                    type="button"
                                    onClick={() => openPlaceholderModal('customImageBannerText', setEmailRecurringMode)}
                                    className="px-3 py-1 text-xs font-medium text-white rounded transition-colors"
                                    style={{ backgroundColor: 'var(--theme-primary)' }}
                                    onMouseEnter={(e) => e.target.style.backgroundColor = 'rgb(35, 147, 180)'}
                                    onMouseLeave={(e) => e.target.style.backgroundColor = 'var(--theme-primary)'}
                                  >
                                    Available Placeholders
                                  </button>
                                </div>
                                <input
                                  type="text"
                                  value={emailRecurringMode.customImageBannerText || '[BUSINESS_NAME] [PHONE_NUMBER]'}
                                  onChange={(e) => handleEmailRecurringModeChange('customImageBannerText', e.target.value)}
                                  className="w-full px-3 py-2 text-sm font-medium border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                                  placeholder="[BUSINESS_NAME] [PHONE_NUMBER]"
                                />
                              </div>
                            )}
                          </div>
                        )}

                        {/* Email Recipients Section - moved above Content Review Workflow */}
                        <div className="space-y-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                          <label className="block text-sm font-medium text-gray-700">
                            <Users className="h-4 w-4 inline mr-2" />
                            Email Recipients *
                          </label>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
                              <input
                                type="radio"
                                name="emailRecurringType"
                                value="bulk"
                                checked={emailRecurringMode.emailType === 'bulk'}
                                onChange={(e) => {
                                  handleEmailRecurringModeChange('emailType', e.target.value);
                                  // Clear selected customer if switching to bulk
                                  if (e.target.value === 'bulk') {
                                    handleEmailRecurringModeChange('selectedCustomer', null);
                                    handleEmailRecurringModeChange('customerName', '');
                                    setSelectedCustomer(null);
                                  }
                                }}
                                className="mr-3"
                              />
                              <div>
                                <div className="font-medium text-sm">All Customers</div>
                                <div className="text-xs text-gray-500">Send to all opted-in customers in your database</div>
                              </div>
                            </label>
                            <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
                              <input
                                type="radio"
                                name="emailRecurringType"
                                value="single"
                                checked={emailRecurringMode.emailType === 'single'}
                                onChange={(e) => {
                                  handleEmailRecurringModeChange('emailType', e.target.value);
                                }}
                                className="mr-3"
                              />
                              <div>
                                <div className="font-medium text-sm">Single Customer</div>
                                <div className="text-xs text-gray-500">Send to one specific customer</div>
                              </div>
                            </label>
                          </div>
                        </div>

                        {/* Customer Search (only for single email) */}
                        {emailRecurringMode.emailType === 'single' && (
                          <div className="bg-white border border-gray-200 rounded-lg p-6">
                            <label className="block text-sm font-medium text-gray-700 mb-4">
                              <Search className="h-4 w-4 inline mr-2" />
                              Search Customer *
                            </label>
                            <div className="relative">
                              <input
                                type="text"
                                value={customerSearchTerm}
                                onChange={(e) => setCustomerSearchTerm(e.target.value)}
                                placeholder="Type at least 3 characters to search by customer name, email, or pet name..."
                                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-theme-blue pl-10 text-sm font-medium"
                              />
                              <Search className="h-5 w-5 text-gray-400 absolute left-3 top-3.5" />
                              {isSearchingCustomers && (
                                <div className="absolute right-3 top-3.5">
                                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-theme-blue"></div>
                                </div>
                              )}
                            </div>

                            {/* Search Results */}
                            {searchedCustomers.length > 0 && customerSearchTerm.length >= 3 && (
                              <div className="mt-4 border border-gray-200 rounded-lg bg-white shadow-sm">
                                <div className="px-3 py-2 bg-gray-50 border-b text-xs font-medium text-gray-700">Search Results</div>
                                <div className="max-h-60 overflow-y-auto">
                                  {searchedCustomers.map((customer, index) => (
                                    <div
                                      key={index}
                                      onClick={() => {
                                        handleEmailRecurringModeChange('selectedCustomer', customer.id);
                                        handleEmailRecurringModeChange('customerName', getCustomerFullName(customer));
                                        setSelectedCustomer(customer);
                                        setCustomerSearchTerm('');
                                        setSearchedCustomers([]);
                                      }}
                                      className="p-3 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                                    >
                                      <div className="flex justify-between items-start">
                                        <div className="flex-1">
                                          <div className="text-sm font-medium text-gray-900">{getCustomerFullName(customer)}</div>
                                          <div className="text-xs text-gray-500">{customer.email || 'No email'}</div>
                                          {customer.pets && customer.pets.length > 0 && (
                                            <div className="text-xs text-theme-blue mt-1">
                                              Pets: {customer.pets.map(pet => pet.name).join(', ')}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Selected Customer Display */}
                            {selectedCustomer && emailRecurringMode.selectedCustomer && (
                              <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                                <div className="text-sm font-medium text-green-900">Selected Customer:</div>
                                <div className="text-sm text-green-800 mt-1">
                                  {getCustomerFullName(selectedCustomer)}
                                  <button
                                    type="button"
                                    onClick={() => {
                                      handleEmailRecurringModeChange('selectedCustomer', null);
                                      handleEmailRecurringModeChange('customerName', '');
                                      setSelectedCustomer(null);
                                    }}
                                    className="ml-2 text-red-600 hover:text-red-800 text-xs"
                                  >
                                    Remove
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Content Review Workflow - Styled like Image Options blocks */}
                        <div className="space-y-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Content Review Workflow
                          </label>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
                              <input
                                type="radio"
                                name="emailRecurringPostDestination"
                                value="in_review"
                                checked={emailRecurringMode.postDestination === 'in_review'}
                                onChange={(e) => handleEmailRecurringModeChange('postDestination', e.target.value)}
                                className="mr-3"
                              />
                              <div>
                                <div className="font-medium text-sm">In Review First</div>
                                <div className="text-xs text-gray-500">Generated emails go to "In Review" for manager approval</div>
                              </div>
                            </label>
                            <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
                              <input
                                type="radio"
                                name="emailRecurringPostDestination"
                                value="ready_to_publish"
                                checked={emailRecurringMode.postDestination === 'ready_to_publish'}
                                onChange={(e) => handleEmailRecurringModeChange('postDestination', e.target.value)}
                                className="mr-3"
                              />
                              <div>
                                <div className="font-medium text-sm">Ready to Publish</div>
                                <div className="text-xs text-gray-500">Generated emails go directly to "Ready to Publish"</div>
                              </div>
                            </label>
                            <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
                              <input
                                type="radio"
                                name="emailRecurringPostDestination"
                                value="auto_send"
                                checked={emailRecurringMode.postDestination === 'auto_send'}
                                onChange={(e) => handleEmailRecurringModeChange('postDestination', e.target.value)}
                                className="mr-3"
                              />
                              <div>
                                <div className="font-medium text-sm">Send Automatically</div>
                                <div className="text-xs text-gray-500">Emails are automatically sent without manual review</div>
                              </div>
                            </label>
                          </div>
                        </div>

                                       {/* Save Button (from scheduled mode) */}
                                                <div className="flex justify-end space-x-3">
                                                  <button
                                                    onClick={isRunMode ? saveAndRunAgent : saveAgent}
                                                    disabled={(() => {
                                                      const isDisabled = loading || (!isEditMode && !isRunMode && (
                                                        !emailRecurringMode.agentName || 
                                                        !emailRecurringMode.topic || 
                                                        (emailRecurringMode.topic === 'Custom' && !emailRecurringMode.customTopic) ||
                                                        (emailRecurringMode.scheduleType === 'weekly' && 
                                                         !Object.values(emailRecurringMode.daysOfWeek).some(day => day)) ||
                                                        (emailRecurringMode.scheduleType === 'monthly' && 
                                                         !emailRecurringMode.monthlySchedule)
                                                      ));
                                                      
                                                      // Debug logging to help identify missing fields
                                                      if (!isEditMode && !isRunMode && isDisabled) {
                                                        console.log('🔍 Email Recurring Mode Validation Debug:');
                                                        console.log('  agentName:', emailRecurringMode.agentName);
                                                        console.log('  topic:', emailRecurringMode.topic);
                                                        console.log('  customTopic:', emailRecurringMode.customTopic);
                                                        console.log('  scheduleType:', emailRecurringMode.scheduleType);
                                                        if (emailRecurringMode.scheduleType === 'weekly') {
                                                          console.log('  daysOfWeek:', emailRecurringMode.daysOfWeek);
                                                          console.log('  anyDaySelected:', Object.values(emailRecurringMode.daysOfWeek).some(day => day));
                                                        }
                                                        if (emailRecurringMode.scheduleType === 'monthly') {
                                                          console.log('  monthlySchedule:', emailRecurringMode.monthlySchedule);
                                                        }
                                                      }
                                                      
                                                      return isDisabled;
                                                    })()}
                                                    className="text-white px-6 py-2 rounded-lg transition-colors disabled:opacity-50 flex items-center font-medium"
                                                    style={{ 
                                                      backgroundColor: loading ? '#94a3b8' : 
                                                                      isRunMode ? '#10b981' : 'var(--theme-primary)'
                                                    }}
                                                    onMouseEnter={(e) => {
                                                      if (!loading && ((isEditMode || isRunMode) || (emailRecurringMode.agentName && emailRecurringMode.topic))) {
                                                        e.target.style.backgroundColor = isRunMode ? '#059669' : '#1e88e5';
                                                      }
                                                    }}
                                                    onMouseLeave={(e) => {
                                                      if (!loading) {
                                                        e.target.style.backgroundColor = isRunMode ? '#10b981' : 'var(--theme-primary)';
                                                      }
                                                    }}
                                                  >
                                                    {isRunMode ? <Play className="h-4 w-4 mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                                                    {loading ? (isRunMode ? 'Updating & Running...' : 'Saving...') : 
                                                     isRunMode ? 'Update & Run Agent' : 
                                                     isEditMode ? 'Update Agent Configuration' : 'Save Agent'}
                                                  </button>
                                                </div>
                        
                                                
                        
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <h3 className="text-lg font-medium text-gray-900 mb-2">Social Media Write Mode</h3>
                        <p className="text-gray-600">Social Media Write Mode form will be available here.</p>
                        <p className="text-sm text-gray-500 mt-2">Currently under development.</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Write Your Email Tab */}
                {activeTab === 'email-write' && (
                  <div className="space-y-6">
                    {searchParams.get('agent_type') === 'email' ? (
                      // Email Write Mode Form
                      <div className="space-y-6">
                        {/* Agent Configuration Section */}
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Agent Name */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                <Mail className="h-4 w-4 inline mr-2" />
                                Agent Name *
                              </label>
                              <input
                                type="text"
                                value={emailWriteMode.agentName}
                                onChange={(e) => handleEmailWriteModeChange('agentName', e.target.value)}
                                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-theme-blue bg-white text-sm font-medium"
                                placeholder="e.g., Custom Email Agent"
                                required
                              />
                            </div>

                            {/* Schedule Email Option */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                <Clock className="h-4 w-4 inline mr-2" />
                                Schedule Email
                              </label>
                              <div className="space-y-2">
                                <label className="flex items-start">
                                  <input
                                    type="checkbox"
                                    checked={emailWriteMode.isScheduled}
                                    onChange={(e) => handleEmailWriteModeChange('isScheduled', e.target.checked)}
                                    className="h-4 w-4 text-theme-blue border-gray-300 rounded focus:ring-gray-500 mr-3 mt-1"
                                  />
                                  <span className="text-sm font-medium text-gray-700">Schedule this email for a specific date and time</span>
                                </label>
                                {!emailWriteMode.isScheduled && (
                                  <p className="text-xs text-gray-500 mt-2 ml-7">
                                    If unchecked, you can manually run this agent anytime from the dashboard
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Schedule Details (only if scheduled) */}
                          {emailWriteMode.isScheduled && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6 pt-6 border-t border-gray-200">
                              {/* Send Date */}
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  <Calendar className="h-4 w-4 inline mr-2" />
                                  Send Date *
                                </label>
                                <input
                                  type="date"
                                  value={emailWriteMode.postDate}
                                  onChange={(e) => handleEmailWriteModeChange('postDate', e.target.value)}
                                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-theme-blue bg-white text-sm font-medium"
                                  required
                                />
                              </div>

                              {/* Send Time */}
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  <Clock className="h-4 w-4 inline mr-2" />
                                  Send Time *
                                </label>
                                <select
                                  value={emailWriteMode.postTime}
                                  onChange={(e) => handleEmailWriteModeChange('postTime', e.target.value)}
                                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-theme-blue bg-white text-sm font-medium"
                                  required
                                >
                                  {timeOptions.map(option => (
                                    <option key={option.value} value={option.value} className="text-sm font-medium">{option.label}</option>
                                  ))}
                                </select>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Email Recipients Section */}
                        <div className="space-y-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                          <label className="block text-sm font-medium text-gray-700">
                            <Users className="h-4 w-4 inline mr-2" />
                            Email Recipients *
                          </label>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
                              <input
                                type="radio"
                                name="emailType"
                                value="bulk"
                                checked={emailWriteMode.emailType === 'bulk'}
                                onChange={(e) => {
                                  handleEmailWriteModeChange('emailType', e.target.value);
                                  // Update email content based on type
                                  if (e.target.value === 'bulk') {
                                    handleEmailWriteModeChange('emailContent', emailWriteMode.emailContentBulk);
                                  } else {
                                    handleEmailWriteModeChange('emailContent', emailWriteMode.emailContent.replace(/\\[CUSTOMER_NAME\\]/g, 'CUSTOMER_NAME').replace(/\\[PET_NAME\\]/g, 'PET_NAME'));
                                  }
                                }}
                                className="mr-3"
                              />
                              <div>
                                <div className="font-medium text-sm">All Customers</div>
                                <div className="text-xs text-gray-500">Send to all opted-in customers in your database</div>
                              </div>
                            </label>
                            <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
                              <input
                                type="radio"
                                name="emailType"
                                value="single"
                                checked={emailWriteMode.emailType === 'single'}
                                onChange={(e) => {
                                  handleEmailWriteModeChange('emailType', e.target.value);
                                  // Update email content for single email
                                  if (e.target.value === 'single') {
                                    handleEmailWriteModeChange('emailContent', emailWriteMode.emailContent.replace(/\\[CUSTOMER_NAME\\]/g, 'CUSTOMER_NAME').replace(/\\[PET_NAME\\]/g, 'PET_NAME'));
                                  }
                                }}
                                className="mr-3"
                              />
                              <div>
                                <div className="font-medium text-sm">Single Customer</div>
                                <div className="text-xs text-gray-500">Send to one specific customer</div>
                              </div>
                            </label>
                          </div>
                        </div>

                        {/* Customer Search (only for single email) */}
                        {emailWriteMode.emailType === 'single' && (
                          <div className="bg-white border border-gray-200 rounded-lg p-6">
                            <label className="block text-sm font-medium text-gray-700 mb-4">
                              <Search className="h-4 w-4 inline mr-2" />
                              Search Customer *
                            </label>
                            <div className="relative">
                              <input
                                type="text"
                                value={customerSearchTerm}
                                onChange={(e) => setCustomerSearchTerm(e.target.value)}
                                placeholder="Type at least 3 characters to search by customer name, email, or pet name..."
                                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-theme-blue pl-10 text-sm font-medium"
                              />
                              <Search className="h-5 w-5 text-gray-400 absolute left-3 top-3.5" />
                              {isSearchingCustomers && (
                                <div className="absolute right-3 top-3.5">
                                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-theme-blue"></div>
                                </div>
                              )}
                            </div>
                            
                            {/* Search Results */}
                            {customerSearchTerm.length >= 3 && !isSearchingCustomers && (
                              <div className="border border-gray-300 rounded-lg mt-2 max-h-40 overflow-y-auto">
                                {searchedCustomers.length > 0 ? (
                                  searchedCustomers.map((customer) => (
                                    <div
                                      key={customer.id}
                                      onClick={() => {
                                        setSelectedCustomer(customer);
                                        setCustomerSearchTerm(''); // Clear search input
                                        setSearchedCustomers([]);
                                        setEmailWriteMode(prev => ({ ...prev, selectedCustomer: customer.id, customerName: getCustomerFullName(customer) }));
                                      }}
                                      className="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                                    >
                                      <div className="text-sm font-medium text-gray-900">{getCustomerFullName(customer)}</div>
                                      <div className="text-xs text-gray-600">{customer.email}</div>
                                      {customer.pets && customer.pets.length > 0 && (
                                        <div className="text-xs text-gray-500">
                                          Pets: {customer.pets.map(pet => pet.name).join(', ')}
                                        </div>
                                      )}
                                    </div>
                                  ))
                                ) : (
                                  <div className="p-3 text-xs text-gray-500 text-center">
                                    No customers found matching "{customerSearchTerm}"
                                  </div>
                                )}
                              </div>
                            )}
                            
                            {/* Selected Customer Display */}
                            {selectedCustomer && (
                              <div className="bg-green-50 border border-green-200 rounded-lg p-3 mt-4">
                                <div className="flex items-center justify-between">
                                  <div>
                                    <div className="text-sm font-medium text-green-900">Selected Customer:</div>
                                    <div className="text-sm font-medium text-green-700">{getCustomerFullName(selectedCustomer)}</div>
                                    <div className="text-xs text-green-600">{selectedCustomer.email}</div>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setSelectedCustomer(null);
                                      setCustomerSearchTerm('');
                                      setEmailWriteMode(prev => ({ ...prev, selectedCustomer: null, customerName: '' }));
                                    }}
                                    className="text-green-600 hover:text-green-800"
                                  >
                                    <X className="h-5 w-5" />
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Email Content Section */}
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                          <h3 className="text-sm font-medium text-gray-700 mb-4">
                            <FileText className="h-4 w-4 inline mr-2" />
                            Email Content
                          </h3>

                          {/* Select Template & Use ChatGPT Email Formatting Row */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
                            <div>
                              <TemplateDropdown
                                templates={emailTemplates}
                                selectedValue={emailWriteMode.emailContent}
                                onSelect={(templateContent) => handleEmailWriteModeChange('emailContent', templateContent)}
                                placeholder="Select an email template..."
                                templateType="email"
                                isPersonalized={emailWriteMode.emailType === 'bulk'}
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Email Formatting Options
                              </label>
                              <div className="flex items-center">
                                <label className="flex items-start p-3 bg-white rounded-lg border cursor-pointer hover:bg-gray-50 w-full">
                                  <input
                                    type="checkbox"
                                    checked={emailWriteMode.useChatGPTFormatting}
                                    onChange={(e) => handleEmailWriteModeChange('useChatGPTFormatting', e.target.checked)}
                                    className="h-4 w-4 text-theme-blue border-gray-300 rounded focus:ring-gray-500 mr-3 mt-1"
                                  />
                                  <div>
                                    <span className="text-sm font-medium text-gray-900">Use ChatGPT for Email Formatting</span>
                                    <p className="text-xs text-gray-500">Enhance Email content with AI-powered formatting and optimization</p>
                                  </div>
                                </label>
                              </div>
                            </div>
                          </div>

                          {/* Email Subject */}
                          <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              <Mail className="h-4 w-4 inline mr-2" />
                              Email Subject *
                            </label>
                            <input
                              type="text"
                              value={emailWriteMode.emailSubject}
                              onChange={(e) => handleEmailWriteModeChange('emailSubject', e.target.value)}
                              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-theme-blue bg-white text-sm font-medium"
                              placeholder="e.g., Special Offer for Your Pet's Health"
                              required
                            />
                          </div>

                          {/* Email Content */}
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <label className="block text-sm font-medium text-gray-700">
                                <FileText className="h-4 w-4 inline mr-2" />
                                Email Body *
                              </label>
                              <div className="flex items-center space-x-2">
                                <button
                                  type="button"
                                  onClick={() => openPlaceholderModal('emailContent', setEmailWriteMode)}
                                  className="px-3 py-1 text-xs font-medium text-white rounded transition-colors"
                                  style={{ backgroundColor: 'var(--theme-primary)' }}
                                  onMouseEnter={(e) => e.target.style.backgroundColor = 'rgb(35, 147, 180)'}
                                  onMouseLeave={(e) => e.target.style.backgroundColor = 'var(--theme-primary)'}
                                >
                                  Available Placeholders
                                </button>
                                <FullScreenEditorButton
                                  content={emailWriteMode.emailContent}
                                  title="Write Email Agent - Email Content"
                                  onSave={(content) => handleEmailWriteModeChange('emailContent', content)}
                                  className="text-xs"
                                  isPersonalized={emailWriteMode.emailType === 'bulk'}
                                />
                              </div>
                            </div>
                            <textarea
                              value={emailWriteMode.emailContent}
                              onChange={(e) => handleEmailWriteModeChange('emailContent', e.target.value)}
                              rows={6}
                              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-theme-blue bg-white text-sm font-medium"
                              placeholder={emailWriteMode.emailType === 'bulk' 
                                ? "Write your email content here. You can use [CUSTOMER_NAME] and [PET_NAME] for personalization..." 
                                : "Write your email content here. Replace CUSTOMER_NAME and PET_NAME with actual names..."
                              }
                              required
                            />
                            <p className="text-xs text-gray-500 mt-2">
                              {emailWriteMode.useChatGPTFormatting 
                                ? "Use [CUSTOMER_NAME] and [PET_NAME] placeholders for personalized emails. CHATGTP_CONTENT placeholder is mandatory as ChatGPT replaces it with AI-rendered content."
                                : "Use [CUSTOMER_NAME] and [PET_NAME] placeholders for personalized emails."
                              }
                            </p>
                          </div>
                        </div>

                        {/* Image Options Section */}
                        <div className="space-y-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                          <label className="block text-sm font-medium text-gray-700">
                            <ImageIcon className="h-4 w-4 inline mr-2" />
                            Image Options
                          </label>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
                              <input
                                type="radio"
                                name="emailWriteImageOption"
                                value="ai_generate"
                                checked={emailWriteMode.imageOption === 'ai_generate'}
                                onChange={(e) => handleEmailWriteModeChange('imageOption', e.target.value)}
                                className="mr-3"
                              />
                              <div>
                                <div className="font-medium text-sm">AI Generate</div>
                                <div className="text-xs text-gray-500">Create unique images using AI</div>
                              </div>
                            </label>
                            <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
                              <input
                                type="radio"
                                name="emailWriteImageOption"
                                value="upload"
                                checked={emailWriteMode.imageOption === 'upload'}
                                onChange={(e) => handleEmailWriteModeChange('imageOption', e.target.value)}
                                className="mr-3"
                              />
                              <div>
                                <div className="font-medium text-sm">Upload Image</div>
                                <div className="text-xs text-gray-500">Use your own images</div>
                              </div>
                            </label>
                            <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
                              <input
                                type="radio"
                                name="emailWriteImageOption"
                                value="s3_browse"
                                checked={emailWriteMode.imageOption === 's3_browse'}
                                onChange={(e) => handleEmailWriteModeChange('imageOption', e.target.value)}
                                className="mr-3"
                              />
                              <div>
                                <div className="font-medium text-sm">Select Existing Image</div>
                                <div className="text-xs text-gray-500">Select from uploaded images</div>
                              </div>
                            </label>
                            <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
                              <input
                                type="radio"
                                name="emailWriteImageOption"
                                value="none"
                                checked={emailWriteMode.imageOption === 'none'}
                                onChange={(e) => handleEmailWriteModeChange('imageOption', e.target.value)}
                                className="mr-3"
                              />
                              <div>
                                <div className="font-medium text-sm">No Images</div>
                                <div className="text-xs text-gray-500">Text-only emails</div>
                              </div>
                            </label>
                          </div>

                          {/* Image Upload Area */}
                          {emailWriteMode.imageOption === 'upload' && (
                            <div className="mt-4">
                              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center bg-gray-50">
                                <Upload className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                                <p className="text-sm text-gray-600 mb-2">Upload email images</p>
                                <p className="text-xs text-gray-500 mb-4">Recommended: High resolution images for best results</p>
                                <div className="flex justify-center">
                                  <label className="inline-flex items-center px-4 py-2 text-white rounded-md hover:opacity-90 transition-colors cursor-pointer" style={{ backgroundColor: 'var(--theme-primary)' }}>
                                    <Upload className="h-4 w-4 mr-2" />
                                    Upload Image
                                    <input
                                      type="file"
                                      multiple
                                      accept="image/*"
                                      onChange={(e) => handleImageUpload(e, 'emailWrite')}
                                      className="hidden"
                                    />
                                  </label>
                                </div>
                              </div>
                            </div>
                          )}
                          
                          {emailWriteMode.imageOption === 's3_browse' && (
                            <div className="mt-4">
                              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center bg-gray-50">
                                <ImageIcon className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                                <p className="text-sm text-gray-600 mb-2">Select existing images</p>
                                <p className="text-xs text-gray-500 mb-4">Choose images from your asset library</p>
                                <div className="flex justify-center">
                                  <button
                                    type="button"
                                    onClick={() => openAssetBrowser('emailWrite')}
                                    className="inline-flex items-center px-4 py-2 text-white rounded-md hover:opacity-90 transition-colors" style={{ backgroundColor: 'var(--theme-primary)' }}
                                  >
                                    <Folder className="h-4 w-4 mr-2" />
                                    Browse Images
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Uploaded Images Preview */}
                          {(emailWriteMode.imageOption === 'upload' || emailWriteMode.imageOption === 's3_browse') && emailWriteMode.uploadedImages && emailWriteMode.uploadedImages.length > 0 && (
                            <div className="mt-4">
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Selected Images ({emailWriteMode.imageOption === 's3_browse' ? 'S3 Browse' : 'Upload'})
                              </label>
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                {emailWriteMode.uploadedImages.map((image, index) => (
                                  <div key={index} className="relative">
                                    <img 
                                      src={image.url} 
                                      alt={image.name}
                                      className="w-full h-24 object-cover rounded-lg border"
                                    />
                                    <button
                                      onClick={() => removeUploadedImage(index, 'emailWrite')}
                                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                                    >
                                      <Trash2 className="h-3 w-3" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Select Image Format for Email */}
                        {(emailWriteMode.imageOption === 'ai_generate' || emailWriteMode.imageOption === 'upload' || emailWriteMode.imageOption === 's3_browse') && (
                          <div className="space-y-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                            <label className="block font-medium text-sm text-gray-700">
                              <ImageIcon className="h-4 w-4 inline mr-2" />
                              Select Image Format *
                            </label>
                            
                            <div className="flex flex-col">
                              <label className="font-medium text-sm text-gray-600 mb-2 text-left">
                                📧 Email
                              </label>
                              <select
                                value={emailWriteMode.imageFormat || ''}
                                onChange={(e) => handleEmailWriteModeChange('imageFormat', e.target.value)}
                                className="w-full px-3 py-2 font-medium text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                              >
                                <option value="">Select...</option>
                                <option value="portrait">Portrait</option>
                                <option value="landscape">Landscape</option>
                                <option value="square">Square</option>
                              </select>
                              
                              {/* Show format dimensions */}
                              {emailWriteMode.imageFormat && socialMediaImageSettings.email?.[emailWriteMode.imageFormat] && (
                                <div className="text-xs text-gray-600 mt-1 text-left">
                                  {socialMediaImageSettings.email[emailWriteMode.imageFormat].width} × {socialMediaImageSettings.email[emailWriteMode.imageFormat].height}
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Add Custom Text Banner to Image */}
                        {(emailWriteMode.imageOption === 'ai_generate' || emailWriteMode.imageOption === 'upload' || emailWriteMode.imageOption === 's3_browse') && (
                          <div className="p-4 bg-white border border-gray-200 rounded-lg">
                            <label className="flex items-start">
                              <input
                                type="checkbox"
                                checked={emailWriteMode.enableCustomImageText}
                                onChange={(e) => handleEmailWriteModeChange('enableCustomImageText', e.target.checked)}
                                className="h-4 w-4 text-theme-blue border-gray-300 rounded focus:ring-gray-500 mr-3"
                              />
                              <span className="block text-sm font-medium text-gray-700">
                                <FileText className="h-4 w-4 inline mr-2" />
                                Add Custom Text Banner to Image
                              </span>
                            </label>
                            <p className="text-xs text-gray-500 mt-2 ml-7">
                              Add custom text overlay on the generated image (e.g., your clinic name, promotional text)
                            </p>
                            
                            {emailWriteMode.enableCustomImageText && (
                              <div className="mt-3">
                                <div className="flex items-center justify-between mb-2">
                                  <label className="block text-sm font-medium text-gray-700">
                                    Custom Banner Text
                                  </label>
                                  <button
                                    type="button"
                                    onClick={() => openPlaceholderModal('customImageBannerText', setEmailWriteMode)}
                                    className="px-3 py-1 text-xs font-medium text-white rounded transition-colors"
                                    style={{ backgroundColor: 'var(--theme-primary)' }}
                                    onMouseEnter={(e) => e.target.style.backgroundColor = 'rgb(35, 147, 180)'}
                                    onMouseLeave={(e) => e.target.style.backgroundColor = 'var(--theme-primary)'}
                                  >
                                    Available Placeholders
                                  </button>
                                </div>
                                <input
                                  type="text"
                                  value={emailWriteMode.customImageBannerText || '[BUSINESS_NAME] [PHONE_NUMBER]'}
                                  onChange={(e) => handleEmailWriteModeChange('customImageBannerText', e.target.value)}
                                  className="w-full px-3 py-2 text-sm font-medium border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                                  placeholder="[BUSINESS_NAME] [PHONE_NUMBER]"
                                />
                              </div>
                            )}
                          </div>
                        )}

                        {/* Content Review Workflow - Styled like Image Options blocks */}
                        <div className="space-y-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Content Review Workflow
                          </label>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
                              <input
                                type="radio"
                                name="emailWritePostDestination"
                                value="in_review"
                                checked={emailWriteMode.postDestination === 'in_review'}
                                onChange={(e) => handleEmailWriteModeChange('postDestination', e.target.value)}
                                className="mr-3"
                              />
                              <div>
                                <div className="font-medium text-sm">In Review First</div>
                                <div className="text-xs text-gray-500">Generated emails go to "In Review" for manager approval</div>
                              </div>
                            </label>
                            <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
                              <input
                                type="radio"
                                name="emailWritePostDestination"
                                value="ready_to_publish"
                                checked={emailWriteMode.postDestination === 'ready_to_publish'}
                                onChange={(e) => handleEmailWriteModeChange('postDestination', e.target.value)}
                                className="mr-3"
                              />
                              <div>
                                <div className="font-medium text-sm">Ready to Publish</div>
                                <div className="text-xs text-gray-500">Generated emails go directly to "Ready to Publish"</div>
                              </div>
                            </label>
                            <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
                              <input
                                type="radio"
                                name="emailWritePostDestination"
                                value="auto_post"
                                checked={emailWriteMode.postDestination === 'auto_post'}
                                onChange={(e) => handleEmailWriteModeChange('postDestination', e.target.value)}
                                className="mr-3"
                              />
                              <div>
                                <div className="font-medium text-sm">Send Immediately</div>
                                <div className="text-xs text-gray-500">Emails are sent immediately without manual review</div>
                              </div>
                            </label>
                          </div>
                        </div>

                        {/* Upload images section moved to Image Options above */}

                        {/* Save/Run Button */}
                        <div className="flex justify-end space-x-3">
                          <button
                            onClick={isRunMode ? saveAndRunAgent : saveAgent}
                            disabled={loading || (!isEditMode && !isRunMode && (!emailWriteMode.agentName || !emailWriteMode.emailSubject || !emailWriteMode.emailContent))}
                            className="text-white px-6 py-2 rounded-lg transition-colors disabled:opacity-50 flex items-center font-medium"
                            style={{ 
                              backgroundColor: loading ? '#94a3b8' : 
                                              isRunMode ? '#10b981' : 'var(--theme-primary)'
                            }}
                            onMouseEnter={(e) => {
                              if (!loading && ((isEditMode || isRunMode) || (emailWriteMode.agentName && emailWriteMode.emailSubject && emailWriteMode.emailContent))) {
                                e.target.style.backgroundColor = isRunMode ? '#059669' : '#1e88e5';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!loading) {
                                e.target.style.backgroundColor = isRunMode ? '#10b981' : 'var(--theme-primary)';
                              }
                            }}
                          >
                            {isRunMode ? <Play className="h-4 w-4 mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                            {loading ? (isRunMode ? 'Updating & Running...' : 'Saving...') : 
                             isRunMode ? 'Update & Run Agent' : 
                             isEditMode ? 'Update Agent Configuration' : 'Save Agent'}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <h3 className="text-lg font-medium text-gray-900 mb-2">Social Media Write Mode</h3>
                        <p className="text-gray-600">Social Media Write Mode form will be available here.</p>
                        <p className="text-sm text-gray-500 mt-2">Currently under development.</p>
                      </div>
                    )}
                  </div>
                )}

                {/* SMS Scheduled Mode Tab */}
                {activeTab === 'sms-scheduled' && (
                  <div className="space-y-6">
                    {searchParams.get('agent_type') === 'sms_agent' ? (
                      // SMS Scheduled Mode Form
                      <div className="space-y-6">
                        {/* Agent Name & Send Time Section - Top Section */}
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Agent Name */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                <MessageSquare className="h-4 w-4 inline mr-2" />
                                Agent Name *
                              </label>
                              <input
                                type="text"
                                value={smsScheduledMode.agentName}
                                onChange={(e) => setSmsScheduledMode(prev => ({ ...prev, agentName: e.target.value }))}
                                placeholder="My Holiday SMS Agent"
                                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-theme-blue bg-white text-sm font-medium"
                              />
                            </div>

                            {/* Send Time */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                <Clock className="h-4 w-4 inline mr-2" />
                                Send Time *
                              </label>
                              <select
                                value={smsScheduledMode.postTime}
                                onChange={(e) => setSmsScheduledMode(prev => ({ ...prev, postTime: e.target.value }))}
                                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-theme-blue bg-white text-sm font-medium"
                              >
                                {timeOptions.map(option => (
                                  <option key={option.value} value={option.value} className="text-sm font-medium">
                                    {option.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                          <p className="text-xs text-gray-500 mt-4">
                            SMS will be sent automatically on each selected holiday at this time of day with personalized customer and pet names.
                          </p>
                        </div>

                        {/* Select Holidays Section */}
                        <div className="bg-white border border-gray-200 rounded-lg p-6">
                          <div className="flex justify-between items-center mb-4">
                            <label className="text-sm font-medium text-gray-700">
                              <Calendar className="h-4 w-4 inline mr-2" />
                              Select Holidays *
                            </label>
                            
                            {availableHolidays.length > 0 && (
                              <label className="flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={smsScheduledMode.selectedHolidays.length === availableHolidays.length && availableHolidays.length > 0}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      // Select all holidays
                                      const allHolidayIds = availableHolidays.map(holiday => holiday.id);
                                      setSmsScheduledMode(prev => ({ ...prev, selectedHolidays: allHolidayIds }));
                                    } else {
                                      // Deselect all holidays
                                      setSmsScheduledMode(prev => ({ ...prev, selectedHolidays: [] }));
                                    }
                                  }}
                                  className="h-4 w-4 text-theme-blue border-gray-300 rounded focus:ring-gray-500 mr-2"
                                />
                                <span className="text-sm font-medium text-gray-700">Select All</span>
                              </label>
                            )}
                          </div>
                          
                          {availableHolidays.length > 0 ? (
                            <>
                              <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-lg p-4 bg-gray-50">
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                {availableHolidays.map((holiday) => {
                                  const IconComponent = getCategoryIcon(holiday.category);
                                  return (
                                    <label key={holiday.id} className="flex items-center p-3 bg-white rounded-lg hover:bg-gray-100 cursor-pointer border">
                                      <input
                                        type="checkbox"
                                        checked={smsScheduledMode.selectedHolidays.includes(holiday.id)}
                                        onChange={() => {
                                          setSmsScheduledMode(prev => ({
                                            ...prev,
                                            selectedHolidays: prev.selectedHolidays.includes(holiday.id)
                                              ? prev.selectedHolidays.filter(id => id !== holiday.id)
                                              : [...prev.selectedHolidays, holiday.id]
                                          }));
                                        }}
                                        className="h-4 w-4 text-theme-blue border-gray-300 rounded focus:ring-gray-500 mr-3"
                                      />
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center mb-1">
                                          <IconComponent className="h-4 w-4 mr-2 text-gray-500 flex-shrink-0" />
                                          <span className="text-sm font-medium text-gray-900 truncate">{holiday.name}</span>
                                        </div>
                                        <span className="text-xs text-gray-500 block">
                                          {new Date(holiday.date).toLocaleDateString('en-US', { 
                                            weekday: 'short', 
                                            month: 'short', 
                                            day: 'numeric',
                                            year: 'numeric'
                                          })}
                                        </span>
                                      </div>
                                    </label>
                                  );
                                })}
                              </div>
                            </div>
                            </>
                          ) : (
                            <div className="text-center py-4 text-gray-500 bg-gray-50 rounded-lg">
                              <Calendar className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                              <p className="text-sm font-medium">Loading holidays...</p>
                            </div>
                          )}
                          <p className="text-xs text-gray-500">
                            Selected: {smsScheduledMode.selectedHolidays.length} holiday{smsScheduledMode.selectedHolidays.length !== 1 ? 's' : ''}
                          </p>
                        </div>

                        {/* Template Configuration Section - Select Template & SMS Content */}
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 space-y-4">
                          {/* Select Template */}
                          <div>
                            <TemplateDropdown
                              templates={smsTemplates}
                              selectedValue={smsScheduledMode.smsContentTemplate}
                              onSelect={(templateContent) => {
                                if (templateContent.length <= 160) {
                                  setSmsScheduledMode(prev => ({ ...prev, smsContentTemplate: templateContent }));
                                }
                              }}
                              placeholder="Select an SMS template..."
                              templateType="sms"
                              isPersonalized={true}
                              requireChatGPT={true}
                            />
                          </div>
                          
                          <div className="flex items-center justify-between mb-2">
                            <label className="block text-sm font-medium text-gray-700">
                              <MessageSquare className="h-4 w-4 inline mr-2" />
                              SMS Content * 
                              <span className="text-xs text-green-600 ml-2">
                                (ChatGPT will optimize length)
                              </span>
                            </label>
                            <button
                              type="button"
                              onClick={() => openPlaceholderModal('smsContentTemplate', setSmsScheduledMode)}
                              className="px-3 py-1 text-xs font-medium text-white rounded transition-colors"
                              style={{ backgroundColor: 'var(--theme-primary)' }}
                              onMouseEnter={(e) => e.target.style.backgroundColor = 'rgb(35, 147, 180)'}
                              onMouseLeave={(e) => e.target.style.backgroundColor = 'var(--theme-primary)'}
                            >
                              Available Placeholders
                            </button>
                          </div>
                          <textarea
                            value={smsScheduledMode.smsContentTemplate}
                            onChange={(e) => {
                              // SMS Scheduled mode always uses ChatGPT, so skip character limit validation
                              setSmsScheduledMode(prev => ({ ...prev, smsContentTemplate: e.target.value }));
                            }}
                            placeholder="Hi [CUSTOMER_NAME]! Hope [PET_NAME] is doing well. Special holiday offer - 20% off checkups this month. Call us at (555) 123-4567 to book!"
                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-theme-blue resize-none bg-white text-sm font-medium"
                            rows="3"
                          />
                          <p className="text-sm text-gray-600 mt-2 bg-blue-50 border border-blue-200 rounded-lg p-3">
                            <Info className="h-4 w-4 inline mr-2 text-theme-blue" />
                            Use [CUSTOMER_NAME] and [PET_NAME] placeholders for personalized SMS. CHATGTP_CONTENT placeholder is mandatory as ChatGPT replaces it with AI-rendered content.
                          </p>
                        </div>

                        {/* Image Options Section */}
                        <div className="space-y-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                          <label className="block text-sm font-medium text-gray-700">
                            <ImageIcon className="h-4 w-4 inline mr-2" />
                            Image Options
                          </label>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
                              <input
                                type="radio"
                                name="smsScheduledImageOption"
                                value="ai_generate"
                                checked={smsScheduledMode.imageOption === 'ai_generate'}
                                onChange={(e) => setSmsScheduledMode(prev => ({ 
                                  ...prev, 
                                  imageOption: e.target.value,
                                  enableCustomImageText: e.target.value === 'none' ? false : prev.enableCustomImageText
                                }))}
                                className="mr-3"
                              />
                              <div>
                                <div className="font-medium text-sm">AI Generate</div>
                                <div className="text-xs text-gray-500">Create unique images using AI</div>
                              </div>
                            </label>
                            <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
                              <input
                                type="radio"
                                name="smsScheduledImageOption"
                                value="upload"
                                checked={smsScheduledMode.imageOption === 'upload'}
                                onChange={(e) => setSmsScheduledMode(prev => ({ 
                                  ...prev, 
                                  imageOption: e.target.value,
                                  enableCustomImageText: e.target.value === 'none' ? false : prev.enableCustomImageText
                                }))}
                                className="mr-3"
                              />
                              <div>
                                <div className="font-medium text-sm">Upload Image</div>
                                <div className="text-xs text-gray-500">Use your own images</div>
                              </div>
                            </label>
                            <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
                              <input
                                type="radio"
                                name="smsScheduledImageOption"
                                value="s3_browse"
                                checked={smsScheduledMode.imageOption === 's3_browse'}
                                onChange={(e) => setSmsScheduledMode(prev => ({ 
                                  ...prev, 
                                  imageOption: e.target.value,
                                  enableCustomImageText: e.target.value === 'none' ? false : prev.enableCustomImageText
                                }))}
                                className="mr-3"
                              />
                              <div>
                                <div className="font-medium text-sm">Select Existing Image</div>
                                <div className="text-xs text-gray-500">Select from uploaded images</div>
                              </div>
                            </label>
                            <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
                              <input
                                type="radio"
                                name="smsScheduledImageOption"
                                value="none"
                                checked={smsScheduledMode.imageOption === 'none'}
                                onChange={(e) => setSmsScheduledMode(prev => ({ 
                                  ...prev, 
                                  imageOption: e.target.value,
                                  enableCustomImageText: e.target.value === 'none' ? false : prev.enableCustomImageText
                                }))}
                                className="mr-3"
                              />
                              <div>
                                <div className="font-medium text-sm">No Images</div>
                                <div className="text-xs text-gray-500">Text-only SMS</div>
                              </div>
                            </label>
                          </div>

                          {/* Image Upload Area */}
                          {smsScheduledMode.imageOption === 'upload' && (
                            <div className="mt-4">
                              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center bg-gray-50">
                                <Upload className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                                <p className="text-sm text-gray-600 mb-2">Upload SMS images</p>
                                <p className="text-xs text-gray-500 mb-4">Recommended: High resolution images for best results</p>
                                <div className="flex justify-center">
                                  <label className="inline-flex items-center px-4 py-2 text-white rounded-md hover:opacity-90 transition-colors cursor-pointer" style={{ backgroundColor: 'var(--theme-primary)' }}>
                                    <Upload className="h-4 w-4 mr-2" />
                                    Upload Image
                                    <input
                                      type="file"
                                      multiple
                                      accept="image/*"
                                      onChange={(e) => handleImageUpload(e, 'smsScheduled')}
                                      className="hidden"
                                    />
                                  </label>
                                </div>
                              </div>
                            </div>
                          )}
                          
                          {smsScheduledMode.imageOption === 's3_browse' && (
                            <div className="mt-4">
                              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center bg-gray-50">
                                <ImageIcon className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                                <p className="text-sm text-gray-600 mb-2">Select existing images</p>
                                <p className="text-xs text-gray-500 mb-4">Choose images from your asset library</p>
                                <div className="flex justify-center">
                                  <button
                                    type="button"
                                    onClick={() => openAssetBrowser('smsScheduled')}
                                    className="inline-flex items-center px-4 py-2 text-white rounded-md hover:opacity-90 transition-colors" style={{ backgroundColor: 'var(--theme-primary)' }}
                                  >
                                    <Folder className="h-4 w-4 mr-2" />
                                    Browse Images
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Custom Text Banner Option */}
                          {(smsScheduledMode.imageOption === 'ai_generate' || smsScheduledMode.imageOption === 'upload' || smsScheduledMode.imageOption === 's3_browse') && (
                            <div className="mt-4">
                              <label className="flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={smsScheduledMode.enableCustomImageText}
                                  onChange={(e) => setSmsScheduledMode(prev => ({ ...prev, enableCustomImageText: e.target.checked }))}
                                  className="h-4 w-4 text-gray-600 border-gray-300 rounded focus:ring-gray-500 mr-3"
                                />
                                <span className="text-sm font-medium text-gray-700">Add Custom Text Banner to Image</span>
                              </label>
                              
                              {smsScheduledMode.enableCustomImageText && (
                                <div className="mt-3 space-y-2">
                                  <div className="flex items-center justify-between">
                                    <label className="block text-sm font-medium text-gray-700">
                                      Custom Text Banner
                                    </label>
                                    <button
                                      type="button"
                                      onClick={() => openPlaceholderModal('customImageBannerText', setSmsScheduledMode)}
                                      className="px-3 py-1 text-xs font-medium text-white rounded transition-colors"
                                      style={{ backgroundColor: 'var(--theme-primary)' }}
                                      onMouseEnter={(e) => e.target.style.backgroundColor = 'rgb(35, 147, 180)'}
                                      onMouseLeave={(e) => e.target.style.backgroundColor = 'var(--theme-primary)'}
                                    >
                                      Available Placeholders
                                    </button>
                                  </div>
                                  <textarea
                                    value={smsScheduledMode.customImageBannerText || '[BUSINESS_NAME] [PHONE_NUMBER]'}
                                    onChange={(e) => setSmsScheduledMode(prev => ({ ...prev, customImageBannerText: e.target.value }))}
                                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-theme-blue resize-none bg-white text-sm font-medium"
                                    rows="2"
                                    placeholder="[BUSINESS_NAME] [PHONE_NUMBER]"
                                  />
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Select Image Format for SMS */}
                        {(smsScheduledMode.imageOption === 'ai_generate' || smsScheduledMode.imageOption === 'upload' || smsScheduledMode.imageOption === 's3_browse') && (
                          <div className="space-y-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                            <label className="block font-medium text-sm text-gray-700">
                              <ImageIcon className="h-4 w-4 inline mr-2" />
                              Select Image Format *
                            </label>
                            
                            <div className="flex flex-col">
                              <label className="font-medium text-sm text-gray-600 mb-2 text-left">
                                📱 SMS
                              </label>
                              <select
                                value={smsScheduledMode.imageFormat || ''}
                                onChange={(e) => setSmsScheduledMode(prev => ({ ...prev, imageFormat: e.target.value }))}
                                className="w-full px-3 py-2 font-medium text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                              >
                                <option value="">Select...</option>
                                <option value="portrait">Portrait</option>
                                <option value="landscape">Landscape</option>
                                <option value="square">Square</option>
                              </select>
                              
                              {/* Show format dimensions */}
                              {smsScheduledMode.imageFormat && socialMediaImageSettings.sms?.[smsScheduledMode.imageFormat] && (
                                <div className="text-xs text-gray-600 mt-1 text-left">
                                  {socialMediaImageSettings.sms[smsScheduledMode.imageFormat].width} × {socialMediaImageSettings.sms[smsScheduledMode.imageFormat].height}
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Add Link Section */}
                        <div className="space-y-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                          <label className="block text-sm font-medium text-gray-700">
                            <Globe className="h-4 w-4 inline mr-2" />
                            Add Link
                          </label>
                          
                          <div className="space-y-4">
                            <label className="flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={smsScheduledMode.enableSmsLink}
                                onChange={(e) => setSmsScheduledMode(prev => ({ ...prev, enableSmsLink: e.target.checked }))}
                                className="h-4 w-4 text-theme-blue border-gray-300 rounded focus:ring-gray-500 mr-3"
                              />
                              <span className="text-sm font-medium text-gray-700">Include SMS Link in message</span>
                            </label>
                            
                            {smsScheduledMode.enableSmsLink && (
                              <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700">
                                  SMS Link
                                </label>
                                <input
                                  type="url"
                                  value={smsScheduledMode.smsLink}
                                  onChange={(e) => setSmsScheduledMode(prev => ({ ...prev, smsLink: e.target.value }))}
                                  placeholder="https://example.com"
                                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-theme-blue bg-white text-sm font-medium"
                                />
                                <p className="text-xs text-gray-500">
                                  This link will be added at the bottom of the SMS message, before any image.
                                </p>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* SMS Recipients Section */}
                        <div className="bg-white border border-gray-200 rounded-lg p-6">
                          <label className="block text-sm font-medium text-gray-700 mb-4">
                            SMS Recipients
                          </label>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <label className="flex items-center p-4 bg-gray-50 rounded-lg border cursor-pointer hover:bg-gray-100">
                              <input
                                type="checkbox"
                                checked={smsScheduledMode.smsType === 'bulk'}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSmsScheduledMode(prev => ({ ...prev, smsType: 'bulk', selectedCustomer: null }));
                                  }
                                }}
                                className="h-4 w-4 text-theme-blue border-gray-300 rounded focus:ring-gray-500 mr-3"
                              />
                              <div>
                                <div className="text-sm font-medium text-gray-900">Bulk SMS (All Customers)</div>
                                <div className="text-xs text-gray-500">Send to all customers with phone numbers</div>
                              </div>
                            </label>
                            
                            <label className="flex items-center p-4 bg-gray-50 rounded-lg border cursor-pointer hover:bg-gray-100">
                              <input
                                type="checkbox"
                                checked={smsScheduledMode.smsType === 'single'}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSmsScheduledMode(prev => ({ ...prev, smsType: 'single' }));
                                  }
                                }}
                                className="h-4 w-4 text-theme-blue border-gray-300 rounded focus:ring-gray-500 mr-3"
                              />
                              <div>
                                <div className="text-sm font-medium text-gray-900">Single Customer</div>
                                <div className="text-xs text-gray-500">Send to one specific customer</div>
                              </div>
                            </label>
                          </div>
                          
                          {/* Descriptive labels */}
                          {smsScheduledMode.smsType === 'bulk' && (
                            <div className="p-3 bg-gray-50 rounded-lg border">
                              <div className="flex items-start">
                                <Users className="h-4 w-4 text-gray-600 mt-0.5 mr-2" />
                                <p className="text-xs text-gray-500">
                                  SMS will be sent to all customers in your customer management database who have opted in for SMS communications and have valid phone numbers.
                                </p>
                              </div>
                            </div>
                          )}
                          
                          {smsScheduledMode.smsType === 'single' && (
                            <div className="space-y-3">
                              <div className="p-3 bg-gray-50 rounded-lg border">
                                <div className="flex items-start">
                                  <User className="h-4 w-4 text-gray-600 mt-0.5 mr-2" />
                                  <p className="text-xs text-gray-500">
                                    SMS will be sent to the selected customer only.
                                  </p>
                                </div>
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  <User className="h-4 w-4 inline mr-2" />
                                  Select Customer *
                                </label>
                                
                                {/* Customer Search Input */}
                                <div className="relative">
                                  <input
                                    type="text"
                                    value={customerSearchTerm}
                                    onChange={(e) => setCustomerSearchTerm(e.target.value)}
                                    placeholder="Type at least 3 characters to search by customer name, email, or pet name..."
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500 pl-10 bg-white text-sm font-medium"
                                  />
                                  <Search className="h-5 w-5 text-gray-400 absolute left-3 top-3.5" />
                                  {isSearchingCustomers && (
                                    <div className="absolute right-3 top-3.5">
                                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-theme-blue"></div>
                                    </div>
                                  )}
                                </div>
                                
                                {/* Search Results */}
                                {customerSearchTerm.length >= 3 && !isSearchingCustomers && (
                                  <div className="border border-gray-300 rounded-lg max-h-40 overflow-y-auto mt-2 bg-gray-50">
                                    {searchedCustomers.length > 0 ? (
                                      searchedCustomers
                                        .filter(customer => customer.phone && customer.phone.trim())
                                        .map((customer) => (
                                          <div
                                            key={customer.id}
                                            onClick={() => {
                                              setSelectedCustomer(customer);
                                              setCustomerSearchTerm(''); // Clear search input for easy new search
                                              setSearchedCustomers([]);
                                              setSmsScheduledMode(prev => ({ 
                                                ...prev, 
                                                selectedCustomer: customer.id, 
                                                customerName: getCustomerFullName(customer) 
                                              }));
                                            }}
                                            className="p-3 hover:bg-gray-100 cursor-pointer border-b border-gray-200 last:border-b-0 bg-white mx-2 my-1 rounded"
                                          >
                                            <div className="text-sm font-medium text-gray-900">{getCustomerFullName(customer)}</div>
                                            <div className="text-xs text-gray-500">{customer.phone}</div>
                                            {customer.pets && customer.pets.length > 0 && (
                                              <div className="text-xs text-gray-400">
                                                Pets: {customer.pets.map(pet => pet.name).join(', ')}
                                              </div>
                                            )}
                                          </div>
                                        ))
                                    ) : (
                                      <div className="p-3 text-gray-500 text-center bg-white mx-2 my-1 rounded">
                                        <div className="text-sm font-medium">No customers with phone numbers found matching "{customerSearchTerm}"</div>
                                      </div>
                                    )}
                                  </div>
                                )}
                                
                                {/* Selected Customer Display */}
                                {selectedCustomer && (
                                  <div className="bg-gray-50 border border-gray-300 rounded-lg p-3 mt-2">
                                    <div className="flex items-center justify-between">
                                      <div>
                                        <div className="text-sm font-medium text-gray-700">Selected Customer:</div>
                                        <div className="text-sm font-medium text-gray-900">{getCustomerFullName(selectedCustomer)}</div>
                                        <div className="text-xs text-gray-500">{selectedCustomer.phone}</div>
                                      </div>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setSelectedCustomer(null);
                                          setCustomerSearchTerm('');
                                          setSmsScheduledMode(prev => ({ ...prev, selectedCustomer: null, customerName: '' }));
                                        }}
                                        className="text-gray-600 hover:text-gray-800"
                                      >
                                        <X className="h-5 w-5" />
                                      </button>
                                    </div>
                                  </div>
                                )}
                                {availableCustomers.length > 0 && availableCustomers.filter(c => c.phone && c.phone.trim()).length === 0 && (
                                  <p className="text-xs text-gray-500 mt-1">
                                    No customers with phone numbers found. Please add phone numbers to customers in Customer Management.
                                  </p>
                                )}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Content Review Workflow - Styled like Image Options blocks */}
                        <div className="space-y-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Content Review Workflow
                          </label>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
                              <input
                                type="radio"
                                name="smsScheduledPostDestination"
                                value="in_review"
                                checked={smsScheduledMode.postDestination === 'in_review'}
                                onChange={(e) => setSmsScheduledMode(prev => ({ ...prev, postDestination: e.target.value }))}
                                className="mr-3"
                              />
                              <div>
                                <div className="font-medium text-sm">In Review First</div>
                                <div className="text-xs text-gray-500">SMS goes to "In Review" → "Ready to Publish" → Sent</div>
                              </div>
                            </label>
                            <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
                              <input
                                type="radio"
                                name="smsScheduledPostDestination"
                                value="ready_to_publish"
                                checked={smsScheduledMode.postDestination === 'ready_to_publish'}
                                onChange={(e) => setSmsScheduledMode(prev => ({ ...prev, postDestination: e.target.value }))}
                                className="mr-3"
                              />
                              <div>
                                <div className="font-medium text-sm">Ready to Publish</div>
                                <div className="text-xs text-gray-500">SMS goes directly to "Ready to Publish" → Sent</div>
                              </div>
                            </label>
                            <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
                              <input
                                type="radio"
                                name="smsScheduledPostDestination"
                                value="auto_send"
                                checked={smsScheduledMode.postDestination === 'auto_send'}
                                onChange={(e) => setSmsScheduledMode(prev => ({ ...prev, postDestination: e.target.value }))}
                                className="mr-3"
                              />
                              <div>
                                <div className="font-medium text-sm">Send Immediately</div>
                                <div className="text-xs text-gray-500">SMS is sent immediately on holidays without manual review</div>
                              </div>
                            </label>
                          </div>
                        </div>

 

                        {/* Save/Run Button */}
                        <div className="flex justify-end space-x-3">
                          <button
                            onClick={isRunMode ? saveAndRunAgent : saveAgent}
                            disabled={loading || (!isEditMode && !isRunMode && (!smsScheduledMode.agentName || smsScheduledMode.selectedHolidays.length === 0 || !smsScheduledMode.smsContentTemplate || (smsScheduledMode.smsType === 'single' && !smsScheduledMode.selectedCustomer)))}
                            className="text-white px-6 py-2 rounded-lg transition-colors disabled:opacity-50 flex items-center font-medium"
                            style={{ 
                              backgroundColor: loading ? '#94a3b8' : 
                                            isRunMode ? '#10b981' : 'var(--theme-primary)'
                            }}
                            onMouseEnter={(e) => {
                              if (!loading && ((isEditMode || isRunMode) || (smsScheduledMode.agentName && smsScheduledMode.selectedHolidays.length > 0 && smsScheduledMode.smsContentTemplate && (smsScheduledMode.smsType === 'bulk' || smsScheduledMode.selectedCustomer)))) {
                                e.target.style.backgroundColor = isRunMode ? '#059669' : '#1e88e5';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!loading) {
                                e.target.style.backgroundColor = isRunMode ? '#10b981' : 'var(--theme-primary)';
                              }
                            }}
                          >
                            {isRunMode ? <Play className="h-4 w-4 mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                            {loading ? (isRunMode ? 'Updating & Running...' : 'Saving...') : 
                             isRunMode ? 'Update & Run Agent' : 
                             isEditMode ? 'Update Agent Configuration' : 'Save Agent'}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <h3 className="text-lg font-medium text-gray-900 mb-2">SMS Scheduled Mode</h3>
                        <p className="text-gray-600">SMS Scheduled Mode form will be available here.</p>
                        <p className="text-sm text-gray-500 mt-2">Currently under development.</p>
                      </div>
                    )}
                  </div>
                )}

                {/* SMS Write Mode Tab */}
                {activeTab === 'sms-write' && (
                  <div className="space-y-6">
                    {searchParams.get('agent_type') === 'sms_agent' ? (
                      // SMS Write Mode Form
                      <div className="space-y-6">
                        {/* Agent Name & Schedule Section - Top Section */}
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 space-y-4">
                          {/* Agent Name & Schedule checkbox in one row */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Agent Name */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                <MessageSquare className="h-4 w-4 inline mr-2" />
                                Agent Name *
                              </label>
                              <input
                                type="text"
                                value={smsWriteMode.agentName}
                                onChange={(e) => setSmsWriteMode(prev => ({ ...prev, agentName: e.target.value }))}
                                placeholder="My SMS Campaign"
                                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-theme-blue bg-white text-sm font-medium"
                              />
                            </div>

                            {/* Schedule SMS Option */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                <Clock className="h-4 w-4 inline mr-2" />
                                Schedule SMS
                              </label>
                              <div className="space-y-2">
                                <label className="flex items-start">
                                  <input
                                    type="checkbox"
                                    checked={smsWriteMode.enableScheduling || false}
                                    onChange={(e) => setSmsWriteMode(prev => ({ ...prev, enableScheduling: e.target.checked }))}
                                    className="h-4 w-4 text-theme-blue border-gray-300 rounded focus:ring-gray-500 mr-3 mt-1"
                                  />
                                  <span className="text-sm font-medium text-gray-700">Schedule this SMS for a specific date and time</span>
                                </label>
                                {!smsWriteMode.enableScheduling && (
                                  <p className="text-xs text-gray-500 mt-2 ml-7">
                                    If unchecked, you can manually run this agent anytime from the dashboard
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Schedule Details (only if scheduled) */}
                          {smsWriteMode.enableScheduling && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6 pt-6 border-t border-gray-200">
                              {/* Send Date */}
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  <Calendar className="h-4 w-4 inline mr-2" />
                                  Send Date *
                                </label>
                                <input
                                  type="date"
                                  value={smsWriteMode.postDate}
                                  onChange={(e) => setSmsWriteMode(prev => ({ ...prev, postDate: e.target.value }))}
                                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-theme-blue bg-white text-sm font-medium"
                                  required
                                />
                              </div>

                              {/* Send Time */}
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  <Clock className="h-4 w-4 inline mr-2" />
                                  Send Time *
                                </label>
                                <select
                                  value={smsWriteMode.postTime}
                                  onChange={(e) => setSmsWriteMode(prev => ({ ...prev, postTime: e.target.value }))}
                                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-theme-blue bg-white text-sm font-medium"
                                  required
                                >
                                  {timeOptions.map(option => (
                                    <option key={option.value} value={option.value} className="text-sm font-medium">{option.label}</option>
                                  ))}
                                </select>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* SMS Content, ChatGPT Formatting, Quick Template & SMS Content Section */}
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 space-y-4">
                          {/* SMS Content & ChatGPT Formatting in one row */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* SMS Content */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                <MessageSquare className="h-4 w-4 inline mr-2" />
                                SMS Content *
                              </label>
                              <textarea
                                value={smsWriteMode.smsSubject}
                                onChange={(e) => setSmsWriteMode(prev => ({ ...prev, smsSubject: e.target.value }))}
                                placeholder="Holiday special offer - 20% off checkups this month!"
                                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-theme-blue resize-none bg-white text-sm font-medium"
                                rows="3"
                              />
                              <p className="text-xs text-gray-500 mt-1">
                                💡 This text will replace the [CHATGPT_CONTENT] placeholder in your SMS content below
                              </p>
                            </div>

                            {/* ChatGPT SMS Formatting */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                SMS Formatting Options
                              </label>
                              <div className="p-3 bg-white rounded-lg border">
                                <label className="flex items-center cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={smsWriteMode.useSMSChatGPTFormatting}
                                    onChange={(e) => setSmsWriteMode(prev => ({ ...prev, useSMSChatGPTFormatting: e.target.checked }))}
                                    className="h-4 w-4 text-gray-600 border-gray-300 rounded focus:ring-gray-500 mr-3"
                                  />
                                  <span className="text-sm font-medium text-gray-700">Use ChatGPT for SMS Formatting</span>
                                </label>
                                <p className="text-xs text-gray-500 ml-7 mt-1">
                                  Enhance SMS content with AI-powered formatting and optimization
                                </p>
                              </div>
                            </div>
                          </div>

                          {/* Select Template */}
                          <div>
                            <TemplateDropdown
                              templates={smsTemplates}
                              selectedValue={smsWriteMode.smsTemplate}
                              onSelect={(templateContent) => setSmsWriteMode(prev => ({ ...prev, smsTemplate: templateContent }))}
                              placeholder="Select an SMS template..."
                              templateType="sms"
                              isPersonalized={true}
                            />
                          </div>
                          
                          {/* SMS Content */}
                          {smsWriteMode.smsTemplate && (
                            <div className="space-y-2">
                              <div className="flex items-center justify-between">
                                <label className="block text-sm font-medium text-gray-700">
                                  <MessageSquare className="h-4 w-4 inline mr-2" />
                                  SMS Content *
                                  {!smsWriteMode.useSMSChatGPTFormatting && (
                                    <span className="text-xs text-gray-500 ml-2">
                                      ({smsWriteMode.smsTemplate.length}/160 characters)
                                    </span>
                                  )}
                                  {smsWriteMode.useSMSChatGPTFormatting && (
                                    <span className="text-xs text-green-600 ml-2">
                                      (ChatGPT will optimize length)
                                    </span>
                                  )}
                                </label>
                                <button
                                  type="button"
                                  onClick={() => openPlaceholderModal('smsTemplate', setSmsWriteMode)}
                                  className="px-3 py-1 text-xs font-medium text-white rounded transition-colors"
                                  style={{ backgroundColor: 'var(--theme-primary)' }}
                                  onMouseEnter={(e) => e.target.style.backgroundColor = 'rgb(35, 147, 180)'}
                                  onMouseLeave={(e) => e.target.style.backgroundColor = 'var(--theme-primary)'}
                                >
                                  Available Placeholders
                                </button>
                              </div>
                              <textarea
                                value={smsWriteMode.smsTemplate}
                                onChange={(e) => {
                                  // Skip character limit validation if ChatGPT formatting is enabled
                                  if (smsWriteMode.useSMSChatGPTFormatting || e.target.value.length <= 160) {
                                    setSmsWriteMode(prev => ({ ...prev, smsTemplate: e.target.value }));
                                  }
                                }}
                                placeholder="Hi [CUSTOMER_NAME]! [CHATGPT_CONTENT] Call us at [PHONE_NUMBER]!"
                                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-theme-blue resize-none bg-white text-sm font-medium"
                                rows="3"
                              />
                              {/* Conditional messaging based on ChatGPT formatting */}
                              {smsWriteMode.useSMSChatGPTFormatting ? (
                                <p className="text-sm text-gray-600 mt-2 bg-blue-50 border border-blue-200 rounded-lg p-3">
                                  <Info className="h-4 w-4 inline mr-2 text-theme-blue" />
                                  Use [CUSTOMER_NAME] and [PET_NAME] placeholders for personalized SMS. CHATGTP_CONTENT placeholder is mandatory as ChatGPT replaces it with AI-rendered content.
                                </p>
                              ) : (
                                <p className="text-sm text-gray-600 mt-2 bg-blue-50 border border-blue-200 rounded-lg p-3">
                                  <Info className="h-4 w-4 inline mr-2 text-theme-blue" />
                                  Use [CUSTOMER_NAME] and [PET_NAME] placeholders for personalized SMS.
                                </p>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Image Options Section */}
                        <div className="space-y-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                          <label className="block text-sm font-medium text-gray-700">
                            <ImageIcon className="h-4 w-4 inline mr-2" />
                            Image Options
                          </label>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
                              <input
                                type="radio"
                                name="smsWriteImageOption"
                                value="ai_generate"
                                checked={smsWriteMode.imageOption === 'ai_generate'}
                                onChange={(e) => setSmsWriteMode(prev => ({ 
                                  ...prev, 
                                  imageOption: e.target.value,
                                  enableCustomImageText: e.target.value === 'none' ? false : prev.enableCustomImageText
                                }))}
                                className="mr-3"
                              />
                              <div>
                                <div className="font-medium text-sm">AI Generate</div>
                                <div className="text-xs text-gray-500">Create unique images using AI</div>
                              </div>
                            </label>
                            <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
                              <input
                                type="radio"
                                name="smsWriteImageOption"
                                value="upload"
                                checked={smsWriteMode.imageOption === 'upload'}
                                onChange={(e) => setSmsWriteMode(prev => ({ 
                                  ...prev, 
                                  imageOption: e.target.value,
                                  enableCustomImageText: e.target.value === 'none' ? false : prev.enableCustomImageText
                                }))}
                                className="mr-3"
                              />
                              <div>
                                <div className="font-medium text-sm">Upload Image</div>
                                <div className="text-xs text-gray-500">Use your own images</div>
                              </div>
                            </label>
                            <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
                              <input
                                type="radio"
                                name="smsWriteImageOption"
                                value="s3_browse"
                                checked={smsWriteMode.imageOption === 's3_browse'}
                                onChange={(e) => setSmsWriteMode(prev => ({ 
                                  ...prev, 
                                  imageOption: e.target.value,
                                  enableCustomImageText: e.target.value === 'none' ? false : prev.enableCustomImageText
                                }))}
                                className="mr-3"
                              />
                              <div>
                                <div className="font-medium text-sm">Select Existing Image</div>
                                <div className="text-xs text-gray-500">Select from uploaded images</div>
                              </div>
                            </label>
                            <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
                              <input
                                type="radio"
                                name="smsWriteImageOption"
                                value="none"
                                checked={smsWriteMode.imageOption === 'none'}
                                onChange={(e) => setSmsWriteMode(prev => ({ 
                                  ...prev, 
                                  imageOption: e.target.value,
                                  enableCustomImageText: e.target.value === 'none' ? false : prev.enableCustomImageText
                                }))}
                                className="mr-3"
                              />
                              <div>
                                <div className="font-medium text-sm">No Images</div>
                                <div className="text-xs text-gray-500">Text-only SMS</div>
                              </div>
                            </label>
                          </div>

                          {/* Image Upload Area */}
                          {smsWriteMode.imageOption === 'upload' && (
                            <div className="mt-4">
                              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center bg-gray-50">
                                <Upload className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                                <p className="text-sm text-gray-600 mb-2">Upload SMS images</p>
                                <p className="text-xs text-gray-500 mb-4">Recommended: High resolution images for best results</p>
                                <div className="flex justify-center">
                                  <label className="inline-flex items-center px-4 py-2 text-white rounded-md hover:opacity-90 transition-colors cursor-pointer" style={{ backgroundColor: 'var(--theme-primary)' }}>
                                    <Upload className="h-4 w-4 mr-2" />
                                    Upload Image
                                    <input
                                      type="file"
                                      multiple
                                      accept="image/*"
                                      onChange={(e) => handleImageUpload(e, 'smsWrite')}
                                      className="hidden"
                                    />
                                  </label>
                                </div>
                              </div>
                            </div>
                          )}
                          
                          {smsWriteMode.imageOption === 's3_browse' && (
                            <div className="mt-4">
                              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center bg-gray-50">
                                <ImageIcon className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                                <p className="text-sm text-gray-600 mb-2">Select existing images</p>
                                <p className="text-xs text-gray-500 mb-4">Choose images from your asset library</p>
                                <div className="flex justify-center">
                                  <button
                                    type="button"
                                    onClick={() => openAssetBrowser('smsWrite')}
                                    className="inline-flex items-center px-4 py-2 text-white rounded-md hover:opacity-90 transition-colors" style={{ backgroundColor: 'var(--theme-primary)' }}
                                  >
                                    <Folder className="h-4 w-4 mr-2" />
                                    Browse Images
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Custom Text Banner Option */}
                          {(smsWriteMode.imageOption === 'ai_generate' || smsWriteMode.imageOption === 'upload' || smsWriteMode.imageOption === 's3_browse') && (
                            <div className="mt-4">
                              <label className="flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={smsWriteMode.enableCustomImageText}
                                  onChange={(e) => setSmsWriteMode(prev => ({ ...prev, enableCustomImageText: e.target.checked }))}
                                  className="h-4 w-4 text-gray-600 border-gray-300 rounded focus:ring-gray-500 mr-3"
                                />
                                <span className="text-sm font-medium text-gray-700">Add Custom Text Banner to Image</span>
                              </label>
                              
                              {smsWriteMode.enableCustomImageText && (
                                <div className="mt-3 space-y-2">
                                  <div className="flex items-center justify-between">
                                    <label className="block text-sm font-medium text-gray-700">
                                      Custom Text Banner
                                    </label>
                                    <button
                                      type="button"
                                      onClick={() => openPlaceholderModal('customImageBannerText', setSmsWriteMode)}
                                      className="px-3 py-1 text-xs font-medium text-white rounded transition-colors"
                                      style={{ backgroundColor: 'var(--theme-primary)' }}
                                      onMouseEnter={(e) => e.target.style.backgroundColor = 'rgb(35, 147, 180)'}
                                      onMouseLeave={(e) => e.target.style.backgroundColor = 'var(--theme-primary)'}
                                    >
                                      Available Placeholders
                                    </button>
                                  </div>
                                  <textarea
                                    value={smsWriteMode.customImageBannerText || '[BUSINESS_NAME] [PHONE_NUMBER]'}
                                    onChange={(e) => setSmsWriteMode(prev => ({ ...prev, customImageBannerText: e.target.value }))}
                                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-theme-blue resize-none bg-white text-sm font-medium"
                                    rows="2"
                                    placeholder="[BUSINESS_NAME] [PHONE_NUMBER]"
                                  />
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Select Image Format for SMS */}
                        {(smsWriteMode.imageOption === 'ai_generate' || smsWriteMode.imageOption === 'upload' || smsWriteMode.imageOption === 's3_browse') && (
                          <div className="space-y-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                            <label className="block font-medium text-sm text-gray-700">
                              <ImageIcon className="h-4 w-4 inline mr-2" />
                              Select Image Format *
                            </label>
                            
                            <div className="flex flex-col">
                              <label className="font-medium text-sm text-gray-600 mb-2 text-left">
                                📱 SMS
                              </label>
                              <select
                                value={smsWriteMode.imageFormat || ''}
                                onChange={(e) => setSmsWriteMode(prev => ({ ...prev, imageFormat: e.target.value }))}
                                className="w-full px-3 py-2 font-medium text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                              >
                                <option value="">Select...</option>
                                <option value="portrait">Portrait</option>
                                <option value="landscape">Landscape</option>
                                <option value="square">Square</option>
                              </select>
                              
                              {/* Show format dimensions */}
                              {smsWriteMode.imageFormat && socialMediaImageSettings.sms?.[smsWriteMode.imageFormat] && (
                                <div className="text-xs text-gray-600 mt-1 text-left">
                                  {socialMediaImageSettings.sms[smsWriteMode.imageFormat].width} × {socialMediaImageSettings.sms[smsWriteMode.imageFormat].height}
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Add Link Section */}
                        <div className="space-y-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                          <label className="block text-sm font-medium text-gray-700">
                            <Globe className="h-4 w-4 inline mr-2" />
                            Add Link
                          </label>
                          
                          <div className="space-y-4">
                            <label className="flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={smsWriteMode.enableSmsLink}
                                onChange={(e) => setSmsWriteMode(prev => ({ ...prev, enableSmsLink: e.target.checked }))}
                                className="h-4 w-4 text-theme-blue border-gray-300 rounded focus:ring-gray-500 mr-3"
                              />
                              <span className="text-sm font-medium text-gray-700">Include SMS Link in message</span>
                            </label>
                            
                            {smsWriteMode.enableSmsLink && (
                              <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700">
                                  SMS Link
                                </label>
                                <input
                                  type="url"
                                  value={smsWriteMode.smsLink}
                                  onChange={(e) => setSmsWriteMode(prev => ({ ...prev, smsLink: e.target.value }))}
                                  placeholder="https://example.com"
                                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-theme-blue bg-white text-sm font-medium"
                                />
                                <p className="text-xs text-gray-500">
                                  This link will be added at the bottom of the SMS message, before any image.
                                </p>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* SMS Recipients Section */}
                        <div className="bg-white border border-gray-200 rounded-lg p-6">
                          <label className="block text-sm font-medium text-gray-700 mb-4">
                            SMS Recipients
                          </label>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <label className="flex items-center p-4 bg-gray-50 rounded-lg border cursor-pointer hover:bg-gray-100">
                              <input
                                type="checkbox"
                                checked={smsWriteMode.smsType === 'bulk'}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSmsWriteMode(prev => ({ ...prev, smsType: 'bulk', selectedCustomer: null }));
                                  }
                                }}
                                className="h-4 w-4 text-theme-blue border-gray-300 rounded focus:ring-gray-500 mr-3"
                              />
                              <div>
                                <div className="text-sm font-medium text-gray-900">Bulk SMS (All Customers)</div>
                                <div className="text-xs text-gray-500">Send to all customers with phone numbers</div>
                              </div>
                            </label>
                            
                            <label className="flex items-center p-4 bg-gray-50 rounded-lg border cursor-pointer hover:bg-gray-100">
                              <input
                                type="checkbox"
                                checked={smsWriteMode.smsType === 'single'}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSmsWriteMode(prev => ({ ...prev, smsType: 'single' }));
                                  }
                                }}
                                className="h-4 w-4 text-theme-blue border-gray-300 rounded focus:ring-gray-500 mr-3"
                              />
                              <div>
                                <div className="text-sm font-medium text-gray-900">Single Customer</div>
                                <div className="text-xs text-gray-500">Send to one specific customer</div>
                              </div>
                            </label>
                          </div>
                          
                          {/* Descriptive labels */}
                          {smsWriteMode.smsType === 'bulk' && (
                            <div className="p-3 bg-gray-50 rounded-lg border">
                              <div className="flex items-start">
                                <Users className="h-4 w-4 text-gray-600 mt-0.5 mr-2" />
                                <p className="text-xs text-gray-500">
                                  SMS will be sent to all customers in your customer management database who have opted in for SMS communications and have valid phone numbers.
                                </p>
                              </div>
                            </div>
                          )}
                          
                          {smsWriteMode.smsType === 'single' && (
                            <div className="space-y-3">
                              <div className="p-3 bg-gray-50 rounded-lg border">
                                <div className="flex items-start">
                                  <User className="h-4 w-4 text-gray-600 mt-0.5 mr-2" />
                                  <p className="text-xs text-gray-500">
                                    SMS will be sent to the selected customer only.
                                  </p>
                                </div>
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  <User className="h-4 w-4 inline mr-2" />
                                  Select Customer *
                                </label>
                                
                                {/* Customer Search Input */}
                                <div className="relative">
                                  <input
                                    type="text"
                                    value={customerSearchTerm}
                                    onChange={(e) => setCustomerSearchTerm(e.target.value)}
                                    placeholder="Type at least 3 characters to search by customer name, email, or pet name..."
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500 pl-10 bg-white text-sm font-medium"
                                  />
                                  <Search className="h-5 w-5 text-gray-400 absolute left-3 top-3.5" />
                                  {isSearchingCustomers && (
                                    <div className="absolute right-3 top-3.5">
                                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-theme-blue"></div>
                                    </div>
                                  )}
                                </div>
                                
                                {/* Search Results */}
                                {customerSearchTerm.length >= 3 && !isSearchingCustomers && (
                                  <div className="border border-gray-300 rounded-lg max-h-40 overflow-y-auto mt-2 bg-gray-50">
                                    {searchedCustomers.length > 0 ? (
                                      searchedCustomers
                                        .filter(customer => customer.phone && customer.phone.trim())
                                        .map((customer) => (
                                          <div
                                            key={customer.id}
                                            onClick={() => {
                                              setSelectedCustomer(customer);
                                              setCustomerSearchTerm(''); // Clear search input for easy new search
                                              setSearchedCustomers([]);
                                              setSmsWriteMode(prev => ({ ...prev, selectedCustomer: customer.id, customerName: getCustomerFullName(customer) }));
                                            }}
                                            className="p-3 hover:bg-gray-100 cursor-pointer border-b border-gray-200 last:border-b-0 bg-white mx-2 my-1 rounded"
                                          >
                                            <div className="text-sm font-medium text-gray-900">{getCustomerFullName(customer)}</div>
                                            <div className="text-xs text-gray-500">{customer.phone}</div>
                                            {customer.pets && customer.pets.length > 0 && (
                                              <div className="text-xs text-gray-400">
                                                Pets: {customer.pets.map(pet => pet.name).join(', ')}
                                              </div>
                                            )}
                                          </div>
                                        ))
                                    ) : (
                                      <div className="p-3 text-gray-500 text-center bg-white mx-2 my-1 rounded">
                                        <div className="text-sm font-medium">No customers with phone numbers found matching "{customerSearchTerm}"</div>
                                      </div>
                                    )}
                                  </div>
                                )}
                                
                                {/* Selected Customer Display */}
                                {selectedCustomer && (
                                  <div className="bg-gray-50 border border-gray-300 rounded-lg p-3 mt-2">
                                    <div className="flex items-center justify-between">
                                      <div>
                                        <div className="text-sm font-medium text-gray-700">Selected Customer:</div>
                                        <div className="text-sm font-medium text-gray-900">{getCustomerFullName(selectedCustomer)}</div>
                                        <div className="text-xs text-gray-500">{selectedCustomer.phone}</div>
                                      </div>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setSelectedCustomer(null);
                                          setCustomerSearchTerm('');
                                          setSmsWriteMode(prev => ({ ...prev, selectedCustomer: null, customerName: '' }));
                                        }}
                                        className="text-gray-600 hover:text-gray-800"
                                      >
                                        <X className="h-5 w-5" />
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Content Review Workflow - Styled like Image Options blocks */}
                        <div className="space-y-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Content Review Workflow
                          </label>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
                              <input
                                type="radio"
                                name="smsWritePostDestination"
                                value="in_review"
                                checked={smsWriteMode.postDestination === 'in_review'}
                                onChange={(e) => setSmsWriteMode(prev => ({ ...prev, postDestination: e.target.value }))}
                                className="mr-3"
                              />
                              <div>
                                <div className="font-medium text-sm">In Review First</div>
                                <div className="text-xs text-gray-500">SMS goes to "In Review" → "Ready to Publish" → Sent</div>
                              </div>
                            </label>
                            <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
                              <input
                                type="radio"
                                name="smsWritePostDestination"
                                value="ready_to_publish"
                                checked={smsWriteMode.postDestination === 'ready_to_publish'}
                                onChange={(e) => setSmsWriteMode(prev => ({ ...prev, postDestination: e.target.value }))}
                                className="mr-3"
                              />
                              <div>
                                <div className="font-medium text-sm">Ready to Publish</div>
                                <div className="text-xs text-gray-500">SMS goes directly to "Ready to Publish" → Sent</div>
                              </div>
                            </label>
                            <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
                              <input
                                type="radio"
                                name="smsWritePostDestination"
                                value="auto_post"
                                checked={smsWriteMode.postDestination === 'auto_post'}
                                onChange={(e) => setSmsWriteMode(prev => ({ ...prev, postDestination: e.target.value }))}
                                className="mr-3"
                              />
                              <div>
                                <div className="font-medium text-sm">Send Immediately</div>
                                <div className="text-xs text-gray-500">SMS is sent immediately without manual review</div>
                              </div>
                            </label>
                          </div>
                        </div>

 

                        {/* Save/Run Button */}
                        <div className="flex justify-end space-x-3">
                          <button
                            onClick={isRunMode ? saveAndRunAgent : saveAgent}
                            disabled={loading || (!isEditMode && !isRunMode && (!smsWriteMode.agentName || !smsWriteMode.smsContent || (smsWriteMode.smsType === 'single' && !smsWriteMode.selectedCustomer)))}
                            className="text-white px-6 py-2 rounded-lg transition-colors disabled:opacity-50 flex items-center font-medium"
                            style={{ 
                              backgroundColor: loading ? '#94a3b8' : 
                                            isRunMode ? '#10b981' : 'var(--theme-primary)'
                            }}
                            onMouseEnter={(e) => {
                              if (!loading && ((isEditMode || isRunMode) || (smsWriteMode.agentName && smsWriteMode.smsContent && (smsWriteMode.smsType === 'bulk' || smsWriteMode.selectedCustomer)))) {
                                e.target.style.backgroundColor = isRunMode ? '#059669' : '#1e88e5';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!loading) {
                                e.target.style.backgroundColor = isRunMode ? '#10b981' : 'var(--theme-primary)';
                              }
                            }}
                          >
                            {isRunMode ? <Play className="h-4 w-4 mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                            {loading ? (isRunMode ? 'Updating & Running...' : 'Saving...') : 
                             isRunMode ? 'Update & Run Agent' : 
                             isEditMode ? 'Update Agent Configuration' : 'Save Agent'}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <h3 className="text-lg font-medium text-gray-900 mb-2">SMS Write Mode</h3>
                        <p className="text-gray-600">SMS Write Mode form will be available here.</p>
                        <p className="text-sm text-gray-500 mt-2">Currently under development.</p>
                      </div>
                    )}
                  </div>
                )}

                {/* SMS Recurring Mode Tab */}
                {activeTab === 'sms-recurring' && (
                  <div className="space-y-6">
                    {searchParams.get('agent_type') === 'sms_agent' ? (
                      // SMS Recurring Mode Form
                      <div className="space-y-6">
                        {/* Agent Name, Send Time & Schedule Type Section - Top Section */}
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 space-y-6">
                          {/* Agent Name & Send Time in one row */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Agent Name */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                <MessageSquare className="h-4 w-4 inline mr-2" />
                                Agent Name *
                              </label>
                              <input
                                type="text"
                                value={smsRecurringMode.agentName}
                                onChange={(e) => setSmsRecurringMode(prev => ({ ...prev, agentName: e.target.value }))}
                                placeholder="My Recurring SMS Agent"
                                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-theme-blue bg-white text-sm font-medium"
                              />
                            </div>

                            {/* Send Time */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                <Clock className="h-4 w-4 inline mr-2" />
                                Send Time *
                              </label>
                              <select
                                value={smsRecurringMode.postTime}
                                onChange={(e) => setSmsRecurringMode(prev => ({ ...prev, postTime: e.target.value }))}
                                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-theme-blue bg-white text-sm font-medium"
                              >
                                {timeOptions.map(option => (
                                  <option key={option.value} value={option.value} className="text-sm font-medium">
                                    {option.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>

                          {/* Schedule Type - Tab style with boxes */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              <Calendar className="h-4 w-4 inline mr-2" />
                              Schedule Type *
                            </label>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
                                <input
                                  type="radio"
                                  name="smsRecurringScheduleType"
                                  value="weekly"
                                  checked={smsRecurringMode.scheduleType === 'weekly'}
                                  onChange={(e) => setSmsRecurringMode(prev => ({ ...prev, scheduleType: e.target.value }))}
                                  className="mr-3"
                                />
                                <div>
                                  <div className="font-medium text-sm">Weekly</div>
                                  <div className="text-xs text-gray-500">Send SMS on selected days of the week</div>
                                </div>
                              </label>
                              <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
                                <input
                                  type="radio"
                                  name="smsRecurringScheduleType"
                                  value="monthly"
                                  checked={smsRecurringMode.scheduleType === 'monthly'}
                                  onChange={(e) => setSmsRecurringMode(prev => ({ ...prev, scheduleType: e.target.value }))}
                                  className="mr-3"
                                />
                                <div>
                                  <div className="font-medium text-sm">Monthly</div>
                                  <div className="text-xs text-gray-500">Send SMS on the 1st of every month</div>
                                </div>
                              </label>
                            </div>

                            {/* Weekly Schedule Selection - Inside the same section */}
                            {smsRecurringMode.scheduleType === 'weekly' && (
                              <div className="mt-4">
                                <label className="block text-sm font-medium text-gray-700 mb-3">
                                  Select Days of Week *
                                </label>
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                  {Object.entries(smsRecurringMode.daysOfWeek).map(([day, checked]) => (
                                    <label key={day} className="flex items-center p-3 bg-white rounded-lg hover:bg-gray-100 cursor-pointer border">
                                      <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={(e) => setSmsRecurringMode(prev => ({
                                          ...prev,
                                          daysOfWeek: {
                                            ...prev.daysOfWeek,
                                            [day]: e.target.checked
                                          }
                                        }))}
                                        className="h-4 w-4 text-theme-blue border-gray-300 rounded focus:ring-gray-500 mr-3"
                                      />
                                      <span className="text-sm font-medium text-gray-900 capitalize">{day}</span>
                                    </label>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Topic Selection, Quick Template & SMS Content Section */}
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 space-y-4">
                          {/* Topic Selection & Select Template in one row */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Topic Selection */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                <MessageSquare className="h-4 w-4 inline mr-2" />
                                SMS Topic *
                              </label>
                              <select
                                value={smsRecurringMode.topic}
                                onChange={(e) => setSmsRecurringMode(prev => ({ ...prev, topic: e.target.value }))}
                                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-theme-blue bg-white text-sm font-medium"
                              >
                                <option value="" className="text-sm font-medium">Select a topic...</option>
                                <option value="Pet vaccination reminders" className="text-sm font-medium">Pet vaccination reminders</option>
                                <option value="Appointment reminders" className="text-sm font-medium">Appointment reminders</option>
                                <option value="Health checkup alerts" className="text-sm font-medium">Health checkup alerts</option>
                                <option value="Special offers" className="text-sm font-medium">Special offers</option>
                                <option value="Holiday greetings" className="text-sm font-medium">Holiday greetings</option>
                                <option value="Pet care tips" className="text-sm font-medium">Pet care tips</option>
                                <option value="Emergency updates" className="text-sm font-medium">Emergency updates</option>
                                <option value="Custom" className="text-sm font-medium">Custom</option>
                              </select>
                              
                              {smsRecurringMode.topic === 'Custom' && (
                                <div className="mt-3">
                                  <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Custom Topic *
                                  </label>
                                  <input
                                    type="text"
                                    value={smsRecurringMode.customTopic}
                                    onChange={(e) => setSmsRecurringMode(prev => ({ ...prev, customTopic: e.target.value }))}
                                    placeholder="Enter your custom SMS topic"
                                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-theme-blue bg-white text-sm font-medium"
                                  />
                                </div>
                              )}
                            </div>

                            {/* Select Template */}
                            <div>
                              <TemplateDropdown
                                templates={smsTemplates}
                                selectedValue={smsRecurringMode.smsContentTemplate}
                                onSelect={(templateContent) => {
                                  if (templateContent.length <= 160) {
                                    setSmsRecurringMode(prev => ({ ...prev, smsContentTemplate: templateContent }));
                                  }
                                }}
                                placeholder="Select an SMS template..."
                                templateType="sms"
                                isPersonalized={true}
                                requireChatGPT={true}
                              />
                            </div>
                          </div>

                          <div className="flex items-center justify-between mb-2">
                            <label className="block text-sm font-medium text-gray-700">
                              <MessageSquare className="h-4 w-4 inline mr-2" />
                              SMS Content *
                              <span className="text-xs text-green-600 ml-2">
                                (ChatGPT will optimize length)
                              </span>
                            </label>
                            <button
                              type="button"
                              onClick={() => openPlaceholderModal('smsContentTemplate', setSmsRecurringMode)}
                              className="px-3 py-1 text-xs font-medium text-white rounded transition-colors"
                              style={{ backgroundColor: 'var(--theme-primary)' }}
                              onMouseEnter={(e) => e.target.style.backgroundColor = 'rgb(35, 147, 180)'}
                              onMouseLeave={(e) => e.target.style.backgroundColor = 'var(--theme-primary)'}
                            >
                              Available Placeholders
                            </button>
                          </div>
                          <textarea
                            value={smsRecurringMode.smsContentTemplate}
                            onChange={(e) => {
                              // SMS Recurring mode always uses ChatGPT, so skip character limit validation
                              setSmsRecurringMode(prev => ({ ...prev, smsContentTemplate: e.target.value }));
                            }}
                            placeholder="Hi [CUSTOMER_NAME]! Regular reminder about [PET_NAME]'s care. Visit us for checkups, vaccines & more. Call (555) 123-4567"
                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-theme-blue resize-none bg-white text-sm font-medium"
                            rows="3"
                          />
                          <p className="text-sm text-gray-600 mt-2 bg-blue-50 border border-blue-200 rounded-lg p-3">
                            <Info className="h-4 w-4 inline mr-2 text-theme-blue" />
                            Use [CUSTOMER_NAME] and [PET_NAME] placeholders for personalized SMS. CHATGTP_CONTENT placeholder is mandatory as ChatGPT replaces it with AI-rendered content.
                          </p>
                        </div>

                        {/* Image Options Section */}
                        <div className="space-y-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                          <label className="block text-sm font-medium text-gray-700">
                            <ImageIcon className="h-4 w-4 inline mr-2" />
                            Image Options
                          </label>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
                              <input
                                type="radio"
                                name="smsRecurringImageOption"
                                value="ai_generate"
                                checked={smsRecurringMode.imageOption === 'ai_generate'}
                                onChange={(e) => setSmsRecurringMode(prev => ({ 
                                  ...prev, 
                                  imageOption: e.target.value,
                                  enableCustomImageText: e.target.value === 'none' ? false : prev.enableCustomImageText
                                }))}
                                className="mr-3"
                              />
                              <div>
                                <div className="font-medium text-sm">AI Generate</div>
                                <div className="text-xs text-gray-500">Create unique images using AI</div>
                              </div>
                            </label>
                            <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
                              <input
                                type="radio"
                                name="smsRecurringImageOption"
                                value="upload"
                                checked={smsRecurringMode.imageOption === 'upload'}
                                onChange={(e) => setSmsRecurringMode(prev => ({ 
                                  ...prev, 
                                  imageOption: e.target.value,
                                  enableCustomImageText: e.target.value === 'none' ? false : prev.enableCustomImageText
                                }))}
                                className="mr-3"
                              />
                              <div>
                                <div className="font-medium text-sm">Upload Image</div>
                                <div className="text-xs text-gray-500">Use your own images</div>
                              </div>
                            </label>
                            <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
                              <input
                                type="radio"
                                name="smsRecurringImageOption"
                                value="s3_browse"
                                checked={smsRecurringMode.imageOption === 's3_browse'}
                                onChange={(e) => setSmsRecurringMode(prev => ({ 
                                  ...prev, 
                                  imageOption: e.target.value,
                                  enableCustomImageText: e.target.value === 'none' ? false : prev.enableCustomImageText
                                }))}
                                className="mr-3"
                              />
                              <div>
                                <div className="font-medium text-sm">Select Existing Image</div>
                                <div className="text-xs text-gray-500">Select from uploaded images</div>
                              </div>
                            </label>
                            <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
                              <input
                                type="radio"
                                name="smsRecurringImageOption"
                                value="none"
                                checked={smsRecurringMode.imageOption === 'none'}
                                onChange={(e) => setSmsRecurringMode(prev => ({ 
                                  ...prev, 
                                  imageOption: e.target.value,
                                  enableCustomImageText: e.target.value === 'none' ? false : prev.enableCustomImageText
                                }))}
                                className="mr-3"
                              />
                              <div>
                                <div className="font-medium text-sm">No Images</div>
                                <div className="text-xs text-gray-500">Text-only SMS</div>
                              </div>
                            </label>
                          </div>

                          {/* Image Upload Area */}
                          {smsRecurringMode.imageOption === 'upload' && (
                            <div className="mt-4">
                              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center bg-gray-50">
                                <Upload className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                                <p className="text-sm text-gray-600 mb-2">Upload SMS images</p>
                                <p className="text-xs text-gray-500 mb-4">Recommended: High resolution images for best results</p>
                                <div className="flex justify-center">
                                  <label className="inline-flex items-center px-4 py-2 text-white rounded-md hover:opacity-90 transition-colors cursor-pointer" style={{ backgroundColor: 'var(--theme-primary)' }}>
                                    <Upload className="h-4 w-4 mr-2" />
                                    Upload Image
                                    <input
                                      type="file"
                                      multiple
                                      accept="image/*"
                                      onChange={(e) => handleImageUpload(e, 'smsRecurring')}
                                      className="hidden"
                                    />
                                  </label>
                                </div>
                              </div>
                            </div>
                          )}
                          
                          {smsRecurringMode.imageOption === 's3_browse' && (
                            <div className="mt-4">
                              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center bg-gray-50">
                                <ImageIcon className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                                <p className="text-sm text-gray-600 mb-2">Select existing images</p>
                                <p className="text-xs text-gray-500 mb-4">Choose images from your asset library</p>
                                <div className="flex justify-center">
                                  <button
                                    type="button"
                                    onClick={() => openAssetBrowser('smsRecurring')}
                                    className="inline-flex items-center px-4 py-2 text-white rounded-md hover:opacity-90 transition-colors" style={{ backgroundColor: 'var(--theme-primary)' }}
                                  >
                                    <Folder className="h-4 w-4 mr-2" />
                                    Browse Images
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Custom Text Banner Option */}
                          {(smsRecurringMode.imageOption === 'ai_generate' || smsRecurringMode.imageOption === 'upload' || smsRecurringMode.imageOption === 's3_browse') && (
                            <div className="mt-4">
                              <label className="flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={smsRecurringMode.enableCustomImageText}
                                  onChange={(e) => setSmsRecurringMode(prev => ({ ...prev, enableCustomImageText: e.target.checked }))}
                                  className="h-4 w-4 text-gray-600 border-gray-300 rounded focus:ring-gray-500 mr-3"
                                />
                                <span className="text-sm font-medium text-gray-700">Add Custom Text Banner to Image</span>
                              </label>
                              
                              {smsRecurringMode.enableCustomImageText && (
                                <div className="mt-3 space-y-2">
                                  <div className="flex items-center justify-between">
                                    <label className="block text-sm font-medium text-gray-700">
                                      Custom Text Banner
                                    </label>
                                    <button
                                      type="button"
                                      onClick={() => openPlaceholderModal('customImageBannerText', setSmsRecurringMode)}
                                      className="px-3 py-1 text-xs font-medium text-white rounded transition-colors"
                                      style={{ backgroundColor: 'var(--theme-primary)' }}
                                      onMouseEnter={(e) => e.target.style.backgroundColor = 'rgb(35, 147, 180)'}
                                      onMouseLeave={(e) => e.target.style.backgroundColor = 'var(--theme-primary)'}
                                    >
                                      Available Placeholders
                                    </button>
                                  </div>
                                  <textarea
                                    value={smsRecurringMode.customImageBannerText || '[BUSINESS_NAME] [PHONE_NUMBER]'}
                                    onChange={(e) => setSmsRecurringMode(prev => ({ ...prev, customImageBannerText: e.target.value }))}
                                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-theme-blue resize-none bg-white text-sm font-medium"
                                    rows="2"
                                    placeholder="[BUSINESS_NAME] [PHONE_NUMBER]"
                                  />
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Select Image Format for SMS */}
                        {(smsRecurringMode.imageOption === 'ai_generate' || smsRecurringMode.imageOption === 'upload' || smsRecurringMode.imageOption === 's3_browse') && (
                          <div className="space-y-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                            <label className="block font-medium text-sm text-gray-700">
                              <ImageIcon className="h-4 w-4 inline mr-2" />
                              Select Image Format *
                            </label>
                            
                            <div className="flex flex-col">
                              <label className="font-medium text-sm text-gray-600 mb-2 text-left">
                                📱 SMS
                              </label>
                              <select
                                value={smsRecurringMode.imageFormat || ''}
                                onChange={(e) => setSmsRecurringMode(prev => ({ ...prev, imageFormat: e.target.value }))}
                                className="w-full px-3 py-2 font-medium text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                              >
                                <option value="">Select...</option>
                                <option value="portrait">Portrait</option>
                                <option value="landscape">Landscape</option>
                                <option value="square">Square</option>
                              </select>
                              
                              {/* Show format dimensions */}
                              {smsRecurringMode.imageFormat && socialMediaImageSettings.sms?.[smsRecurringMode.imageFormat] && (
                                <div className="text-xs text-gray-600 mt-1 text-left">
                                  {socialMediaImageSettings.sms[smsRecurringMode.imageFormat].width} × {socialMediaImageSettings.sms[smsRecurringMode.imageFormat].height}
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Add Link Section */}
                        <div className="space-y-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                          <label className="block text-sm font-medium text-gray-700">
                            <Globe className="h-4 w-4 inline mr-2" />
                            Add Link
                          </label>
                          
                          <div className="space-y-4">
                            <label className="flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={smsRecurringMode.enableSmsLink}
                                onChange={(e) => setSmsRecurringMode(prev => ({ ...prev, enableSmsLink: e.target.checked }))}
                                className="h-4 w-4 text-theme-blue border-gray-300 rounded focus:ring-gray-500 mr-3"
                              />
                              <span className="text-sm font-medium text-gray-700">Include SMS Link in message</span>
                            </label>
                            
                            {smsRecurringMode.enableSmsLink && (
                              <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700">
                                  SMS Link
                                </label>
                                <input
                                  type="url"
                                  value={smsRecurringMode.smsLink}
                                  onChange={(e) => setSmsRecurringMode(prev => ({ ...prev, smsLink: e.target.value }))}
                                  placeholder="https://example.com"
                                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-theme-blue bg-white text-sm font-medium"
                                />
                                <p className="text-xs text-gray-500">
                                  This link will be added at the bottom of the SMS message, before any image.
                                </p>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* SMS Recipients Section */}
                        <div className="bg-white border border-gray-200 rounded-lg p-6">
                          <label className="block text-sm font-medium text-gray-700 mb-4">
                            SMS Recipients
                          </label>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <label className="flex items-center p-4 bg-gray-50 rounded-lg border cursor-pointer hover:bg-gray-100">
                              <input
                                type="checkbox"
                                checked={smsRecurringMode.smsType === 'bulk'}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSmsRecurringMode(prev => ({ ...prev, smsType: 'bulk', selectedCustomer: null }));
                                  }
                                }}
                                className="h-4 w-4 text-theme-blue border-gray-300 rounded focus:ring-gray-500 mr-3"
                              />
                              <div>
                                <div className="text-sm font-medium text-gray-900">Bulk SMS (All Customers)</div>
                                <div className="text-xs text-gray-500">Send to all customers with phone numbers</div>
                              </div>
                            </label>
                            
                            <label className="flex items-center p-4 bg-gray-50 rounded-lg border cursor-pointer hover:bg-gray-100">
                              <input
                                type="checkbox"
                                checked={smsRecurringMode.smsType === 'single'}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSmsRecurringMode(prev => ({ ...prev, smsType: 'single' }));
                                  }
                                }}
                                className="h-4 w-4 text-theme-blue border-gray-300 rounded focus:ring-gray-500 mr-3"
                              />
                              <div>
                                <div className="text-sm font-medium text-gray-900">Single Customer</div>
                                <div className="text-xs text-gray-500">Send to one specific customer</div>
                              </div>
                            </label>
                          </div>
                          
                          {/* Descriptive labels */}
                          {smsRecurringMode.smsType === 'bulk' && (
                            <div className="p-3 bg-gray-50 rounded-lg border">
                              <div className="flex items-start">
                                <Users className="h-4 w-4 text-gray-600 mt-0.5 mr-2" />
                                <p className="text-xs text-gray-500">
                                  SMS will be sent to all customers in your customer management database who have opted in for SMS communications and have valid phone numbers.
                                </p>
                              </div>
                            </div>
                          )}
                          
                          {smsRecurringMode.smsType === 'single' && (
                            <div className="space-y-3">
                              <div className="p-3 bg-gray-50 rounded-lg border">
                                <div className="flex items-start">
                                  <User className="h-4 w-4 text-gray-600 mt-0.5 mr-2" />
                                  <p className="text-xs text-gray-500">
                                    SMS will be sent to the selected customer only.
                                  </p>
                                </div>
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  <User className="h-4 w-4 inline mr-2" />
                                  Select Customer *
                                </label>
                                
                                {/* Customer Search Input */}
                                <div className="relative">
                                  <input
                                    type="text"
                                    value={customerSearchTerm}
                                    onChange={(e) => setCustomerSearchTerm(e.target.value)}
                                    placeholder="Type at least 3 characters to search by customer name, email, or pet name..."
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500 pl-10 bg-white text-sm font-medium"
                                  />
                                  <Search className="h-5 w-5 text-gray-400 absolute left-3 top-3.5" />
                                  {isSearchingCustomers && (
                                    <div className="absolute right-3 top-3.5">
                                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-theme-blue"></div>
                                    </div>
                                  )}
                                </div>
                                
                                {/* Search Results */}
                                {customerSearchTerm.length >= 3 && !isSearchingCustomers && (
                                  <div className="border border-gray-300 rounded-lg max-h-40 overflow-y-auto mt-2 bg-gray-50">
                                    {searchedCustomers.length > 0 ? (
                                      searchedCustomers
                                        .filter(customer => customer.phone && customer.phone.trim())
                                        .map((customer) => (
                                          <div
                                            key={customer.id}
                                            onClick={() => {
                                              setSelectedCustomer(customer);
                                              setCustomerSearchTerm(''); // Clear search input for easy new search
                                              setSearchedCustomers([]);
                                              setSmsRecurringMode(prev => ({ ...prev, selectedCustomer: customer.id, customerName: getCustomerFullName(customer) }));
                                            }}
                                            className="p-3 hover:bg-gray-100 cursor-pointer border-b border-gray-200 last:border-b-0 bg-white mx-2 my-1 rounded"
                                          >
                                            <div className="text-sm font-medium text-gray-900">{getCustomerFullName(customer)}</div>
                                            <div className="text-xs text-gray-500">{customer.phone}</div>
                                            {customer.pets && customer.pets.length > 0 && (
                                              <div className="text-xs text-gray-400">
                                                Pets: {customer.pets.map(pet => pet.name).join(', ')}
                                              </div>
                                            )}
                                          </div>
                                        ))
                                    ) : (
                                      <div className="p-3 text-gray-500 text-center bg-white mx-2 my-1 rounded">
                                        <div className="text-sm font-medium">No customers with phone numbers found matching "{customerSearchTerm}"</div>
                                      </div>
                                    )}
                                  </div>
                                )}
                                
                                {/* Selected Customer Display */}
                                {selectedCustomer && (
                                  <div className="bg-gray-50 border border-gray-300 rounded-lg p-3 mt-2">
                                    <div className="flex items-center justify-between">
                                      <div>
                                        <div className="text-sm font-medium text-gray-700">Selected Customer:</div>
                                        <div className="text-sm font-medium text-gray-900">{getCustomerFullName(selectedCustomer)}</div>
                                        <div className="text-xs text-gray-500">{selectedCustomer.phone}</div>
                                      </div>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setSelectedCustomer(null);
                                          setCustomerSearchTerm('');
                                          setSmsRecurringMode(prev => ({ ...prev, selectedCustomer: null, customerName: '' }));
                                        }}
                                        className="text-gray-600 hover:text-gray-800"
                                      >
                                        <X className="h-5 w-5" />
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Content Review Workflow - Styled like Image Options blocks */}
                        <div className="space-y-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Content Review Workflow
                          </label>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
                              <input
                                type="radio"
                                name="smsRecurringPostDestination"
                                value="in_review"
                                checked={smsRecurringMode.postDestination === 'in_review'}
                                onChange={(e) => setSmsRecurringMode(prev => ({ ...prev, postDestination: e.target.value }))}
                                className="mr-3"
                              />
                              <div>
                                <div className="font-medium text-sm">In Review First</div>
                                <div className="text-xs text-gray-500">SMS goes to "In Review" → "Ready to Publish" → Sent</div>
                              </div>
                            </label>
                            <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
                              <input
                                type="radio"
                                name="smsRecurringPostDestination"
                                value="ready_to_publish"
                                checked={smsRecurringMode.postDestination === 'ready_to_publish'}
                                onChange={(e) => setSmsRecurringMode(prev => ({ ...prev, postDestination: e.target.value }))}
                                className="mr-3"
                              />
                              <div>
                                <div className="font-medium text-sm">Ready to Publish</div>
                                <div className="text-xs text-gray-500">SMS goes directly to "Ready to Publish" → Sent</div>
                              </div>
                            </label>
                            <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
                              <input
                                type="radio"
                                name="smsRecurringPostDestination"
                                value="auto_send"
                                checked={smsRecurringMode.postDestination === 'auto_send'}
                                onChange={(e) => setSmsRecurringMode(prev => ({ ...prev, postDestination: e.target.value }))}
                                className="mr-3"
                              />
                              <div>
                                <div className="font-medium text-sm">Send Immediately</div>
                                <div className="text-xs text-gray-500">SMS is sent immediately on schedule without manual review</div>
                              </div>
                            </label>
                          </div>
                        </div>

 

                        {/* Save/Run Button */}
                        <div className="flex justify-end space-x-3">
                          <button
                            onClick={isRunMode ? saveAndRunAgent : saveAgent}
                            disabled={loading || (!isEditMode && !isRunMode && (!smsRecurringMode.agentName || !smsRecurringMode.topic || !smsRecurringMode.smsContentTemplate || (smsRecurringMode.scheduleType === 'weekly' && !Object.values(smsRecurringMode.daysOfWeek).some(day => day)) || (smsRecurringMode.smsType === 'single' && !smsRecurringMode.selectedCustomer)))}
                            className="text-white px-6 py-2 rounded-lg transition-colors disabled:opacity-50 flex items-center font-medium"
                            style={{ 
                              backgroundColor: loading ? '#94a3b8' : 
                                            isRunMode ? '#10b981' : 'var(--theme-primary)'
                            }}
                            onMouseEnter={(e) => {
                              if (!loading && ((isEditMode || isRunMode) || (smsRecurringMode.agentName && smsRecurringMode.topic && smsRecurringMode.smsContentTemplate && (smsRecurringMode.scheduleType !== 'weekly' || Object.values(smsRecurringMode.daysOfWeek).some(day => day)) && (smsRecurringMode.smsType === 'bulk' || smsRecurringMode.selectedCustomer)))) {
                                e.target.style.backgroundColor = isRunMode ? '#059669' : '#1e88e5';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!loading) {
                                e.target.style.backgroundColor = isRunMode ? '#10b981' : 'var(--theme-primary)';
                              }
                            }}
                          >
                            {isRunMode ? <Play className="h-4 w-4 mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                            {loading ? (isRunMode ? 'Updating & Running...' : 'Saving...') : 
                             isRunMode ? 'Update & Run Agent' : 
                             isEditMode ? 'Update Agent Configuration' : 'Save Agent'}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <h3 className="text-lg font-medium text-gray-900 mb-2">SMS Recurring Mode</h3>
                        <p className="text-gray-600">SMS Recurring Mode form will be available here.</p>
                        <p className="text-sm text-gray-500 mt-2">Currently under development.</p>
                      </div>
                    )}
                  </div>
                )}

                
              </div>
            </div>
            )}

            {/* Marketing Agent Form */}
            {searchParams.get('agent_type') === 'marketing_agent' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="p-6 space-y-6">
                {/* Agent Name & Schedule Campaign - Top Section */}
                <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-6">
                  {/* Agent Name - Full Row */}
                  <div>
                    <label className="block font-medium text-sm text-gray-700 mb-2">
                      <Bot className="h-4 w-4 inline mr-2" />
                      Agent Name *
                    </label>
                    <input
                      type="text"
                      value={marketingMode.agentName}
                      onChange={(e) => setMarketingMode(prev => ({ ...prev, agentName: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 font-medium text-sm"
                      placeholder="e.g., Holiday Promotion Campaign"
                      required
                    />
                  </div>

                  {/* Schedule Campaign - Separate Row */}
                  <div>
                    <label className="flex items-center cursor-pointer mb-2">
                      <input
                        type="checkbox"
                        checked={marketingMode.enableScheduling || false}
                        onChange={(e) => setMarketingMode(prev => ({
                          ...prev,
                          enableScheduling: e.target.checked,
                          // Reset date and time when unchecked
                          scheduledDate: e.target.checked ? prev.scheduledDate : '',
                          postTime: e.target.checked ? prev.postTime : '09:00'
                        }))}
                        className="h-4 w-4 text-theme-blue border-gray-300 rounded focus:ring-gray-500 mr-3"
                      />
                      <span className="block font-medium text-sm text-gray-700">
                        <Calendar className="h-4 w-4 inline mr-2" />
                        Schedule Campaign
                      </span>
                    </label>
                    {!marketingMode.enableScheduling && (
                      <p className="font-medium text-sm text-orange-600 mb-3">💡 If not scheduled, you'll need to run this campaign manually from the dashboard.</p>
                    )}
                    {marketingMode.enableScheduling && (
                      <p className="text-xs text-gray-500 mb-3">Schedule your campaign for a specific date and time</p>
                    )}
                      
                      {marketingMode.enableScheduling && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block font-medium text-sm text-gray-700 mb-2">
                              <Calendar className="h-4 w-4 inline mr-2" />
                              Post Date *
                            </label>
                            <input
                              type="date"
                              value={marketingMode.scheduledDate}
                              onChange={(e) => setMarketingMode(prev => ({ ...prev, scheduledDate: e.target.value }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 font-medium text-sm"
                              required
                            />
                          </div>
                          <div>
                            <label className="block font-medium text-sm text-gray-700 mb-2">
                              <Clock className="h-4 w-4 inline mr-2" />
                              Post Time *
                            </label>
                            <input
                              type="time"
                              value={marketingMode.postTime}
                              onChange={(e) => setMarketingMode(prev => ({ ...prev, postTime: e.target.value }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 font-medium text-sm"
                              required
                            />
                          </div>
                        </div>
                      )}
                  </div>
                </div>

                {/* Content Configuration */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 space-y-6">
                  {/* Select Content Type */}
                  <div className="space-y-4">
                    <label className="block font-medium text-sm text-gray-700">
                      <FileText className="h-4 w-4 inline mr-2" />
                      Select Content Type *
                    </label>
                    
                    {/* Tab Navigation */}
                    <div className="border-b border-gray-200">
                      <nav className="-mb-px flex space-x-8" aria-label="Content Type Tabs">
                        {/* Topic Tab */}
                        <button
                          type="button"
                          onClick={() => setMarketingMode(prev => ({ ...prev, contentType: 'topic' }))}
                          className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap flex items-center space-x-2 ${
                            marketingMode.contentType === 'topic'
                              ? 'border-gray-500 text-gray-700'
                              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                          }`}
                        >
                          <Hash className="h-4 w-4" />
                          <span>Topic</span>
                          <input
                            type="radio"
                            name="contentType"
                            value="topic"
                            checked={marketingMode.contentType === 'topic'}
                            onChange={(e) => {
                              e.stopPropagation(); // Prevent tab switching when clicking radio button
                              setMarketingMode(prev => ({ ...prev, contentType: e.target.value }));
                            }}
                            className="h-4 w-4 text-gray-600 border-gray-300 focus:ring-gray-500 ml-2"
                            title="Select Topic Content Type"
                          />
                        </button>

                        {/* Holidays Tab */}
                        <button
                          type="button"
                          onClick={() => setMarketingMode(prev => ({ ...prev, contentType: 'holidays' }))}
                          className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap flex items-center space-x-2 ${
                            marketingMode.contentType === 'holidays'
                              ? 'border-gray-500 text-gray-700'
                              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                          }`}
                        >
                          <CalendarDays className="h-4 w-4" />
                          <span>Holidays</span>
                          <input
                            type="radio"
                            name="contentType"
                            value="holidays"
                            checked={marketingMode.contentType === 'holidays'}
                            onChange={(e) => {
                              e.stopPropagation(); // Prevent tab switching when clicking radio button
                              setMarketingMode(prev => ({ ...prev, contentType: e.target.value }));
                            }}
                            className="h-4 w-4 text-gray-600 border-gray-300 focus:ring-gray-500 ml-2"
                            title="Select Holidays Content Type"
                          />
                        </button>

                        {/* Custom Campaign Tab */}
                        <button
                          type="button"
                          onClick={() => setMarketingMode(prev => ({ ...prev, contentType: 'custom_campaign' }))}
                          className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap flex items-center space-x-2 ${
                            marketingMode.contentType === 'custom_campaign'
                              ? 'border-gray-500 text-gray-700'
                              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                          }`}
                        >
                          <FileText className="h-4 w-4" />
                          <span>Custom Campaign</span>
                          <input
                            type="radio"
                            name="contentType"
                            value="custom_campaign"
                            checked={marketingMode.contentType === 'custom_campaign'}
                            onChange={(e) => {
                              e.stopPropagation(); // Prevent tab switching when clicking radio button
                              setMarketingMode(prev => ({ ...prev, contentType: e.target.value }));
                            }}
                            className="h-4 w-4 text-gray-600 border-gray-300 focus:ring-gray-500 ml-2"
                            title="Select Custom Campaign Content Type"
                          />
                        </button>
                      </nav>
                    </div>
                  </div>

                  {/* Tab Content */}
                  <div className="mt-4">
                    {/* Topic Tab Content */}
                    {marketingMode.contentType === 'topic' && (
                      <div className="p-4 border border-gray-200 bg-white rounded-lg">
                      <label className="block font-medium text-sm text-gray-700 mb-2">Select Topic *</label>
                      <select
                        value={marketingMode.selectedTopic}
                        onChange={(e) => setMarketingMode(prev => ({ ...prev, selectedTopic: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 font-medium text-sm"
                        required
                      >
                        <option value="">Choose a topic...</option>
                        <option value="Pet Health Tips">Pet Health Tips</option>
                        <option value="Vaccination Reminders">Vaccination Reminders</option>
                        <option value="Seasonal Pet Care">Seasonal Pet Care</option>
                        <option value="Pet Nutrition">Pet Nutrition</option>
                        <option value="Pet Dental Care">Pet Dental Care</option>
                        <option value="Emergency Pet Care">Emergency Pet Care</option>
                        <option value="Pet Grooming">Pet Grooming</option>
                        <option value="Senior Pet Care">Senior Pet Care</option>
                        <option value="Puppy/Kitten Care">Puppy/Kitten Care</option>
                        <option value="Custom">Custom Topic</option>
                      </select>
                      {marketingMode.selectedTopic === 'Custom' && (
                        <input
                          type="text"
                          placeholder="Enter your custom topic..."
                          className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 font-medium text-sm"
                          onChange={(e) => setMarketingMode(prev => ({ ...prev, customTopic: e.target.value }))}
                        />
                      )}
                    </div>
                  )}

                    {/* Holidays Tab Content */}
                    {marketingMode.contentType === 'holidays' && (
                      <div className="p-4 border border-gray-200 bg-gray-50 rounded-lg">
                      <div className="flex justify-between items-center mb-4">
                        <label className="text-sm font-medium text-gray-700">
                          <Calendar className="h-4 w-4 inline mr-2" />
                          Select Holidays *
                        </label>
                        
                        {availableHolidays.length > 0 && (
                          <label className="flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={marketingMode.selectedHolidays.length === availableHolidays.length && availableHolidays.length > 0}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  // Select all holidays
                                  const allHolidayIds = availableHolidays.map(holiday => holiday.id);
                                  setMarketingMode(prev => ({ ...prev, selectedHolidays: allHolidayIds }));
                                } else {
                                  // Deselect all holidays
                                  setMarketingMode(prev => ({ ...prev, selectedHolidays: [] }));
                                }
                              }}
                              className="h-4 w-4 text-green-600 border-gray-300 rounded focus:ring-green-500 mr-2"
                            />
                            <span className="text-sm font-medium text-gray-700">Select All</span>
                          </label>
                        )}
                      </div>
                      
                      {availableHolidays.length > 0 ? (
                        <>
                          <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-lg p-4">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            {availableHolidays.map((holiday) => {
                              const IconComponent = getCategoryIcon(holiday.category);
                              return (
                                <label key={holiday.id} className="flex items-center p-3 bg-white rounded-lg hover:bg-gray-100 cursor-pointer border">
                                  <input
                                    type="checkbox"
                                    checked={marketingMode.selectedHolidays.includes(holiday.id)}
                                    onChange={(e) => {
                                      const isChecked = e.target.checked;
                                      setMarketingMode(prev => ({
                                        ...prev,
                                        selectedHolidays: isChecked 
                                          ? [...prev.selectedHolidays, holiday.id]
                                          : prev.selectedHolidays.filter(id => id !== holiday.id)
                                      }));
                                    }}
                                    className="h-4 w-4 text-green-600 border-gray-300 rounded focus:ring-green-500 mr-3"
                                  />
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center mb-1">
                                      <IconComponent className="h-4 w-4 mr-2 text-gray-500 flex-shrink-0" />
                                      <span className="text-sm font-medium text-gray-900 truncate">{holiday.name}</span>
                                    </div>
                                    <span className="text-xs text-gray-500 block">
                                      {new Date(holiday.date).toLocaleDateString('en-US', { 
                                        weekday: 'short', 
                                        month: 'short', 
                                        day: 'numeric',
                                        year: 'numeric'
                                      })}
                                    </span>
                                  </div>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                        </>
                      ) : (
                        <div className="text-center py-4 text-gray-500">
                          <Calendar className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                          <p>Loading holidays...</p>
                        </div>
                      )}
                      <p className="text-xs text-gray-500 mt-2">
                        Selected: {marketingMode.selectedHolidays.length} holiday{marketingMode.selectedHolidays.length !== 1 ? 's' : ''}
                        <span className="ml-4">Missing a holiday? Configure additional holidays in the Holiday Management page.</span>
                      </p>
                      {/* Duplicate section removed */}
                    </div>
                  )}

                    {/* Custom Campaign Tab Content */}
                    {marketingMode.contentType === 'custom_campaign' && (
                      <div className="p-4 border border-gray-200 bg-white rounded-lg">
                      <label className="block font-medium text-sm text-gray-700 mb-2">Campaign Content *</label>
                      <textarea
                        value={marketingMode.customCampaign}
                        onChange={(e) => setMarketingMode(prev => ({ ...prev, customCampaign: e.target.value }))}
                        rows={8}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 font-medium text-sm"
                        placeholder="Write your campaign content here..."
                        required
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Use [CUSTOMER_NAME] and [PET_NAME] placeholders for personalized content.
                      </p>
                      
                      {/* Format using ChatGPT checkbox */}
                      <div className="mt-4">
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={marketingMode.useChatGPTFormatting}
                            onChange={(e) => setMarketingMode(prev => ({ ...prev, useChatGPTFormatting: e.target.checked }))}
                            className="h-4 w-4 text-theme-blue border-gray-300 rounded focus:ring-gray-500 mr-3"
                          />
                          <span className="text-sm font-medium text-gray-700">Format using ChatGPT</span>
                        </label>
                        <p className="text-xs text-gray-500 mt-1 ml-7">
                          When enabled, ChatGPT will format and enhance the campaign content. When disabled, content will be posted as-is with placeholder replacements.
                        </p>
                      </div>
                    </div>
                  )}
                  </div>
                </div>

                {/* Select Channels */}
                <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-6">
                  <label className="block font-medium text-sm text-gray-700">
                    <Share2 className="h-4 w-4 inline mr-2" />
                    Select Channels *
                  </label>
                  {/* Tab Navigation */}
                  <div className="border-b border-gray-200">
                    <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                      {/* Social Media Tab */}
                      <button
                        type="button"
                        onClick={() => setMarketingMode(prev => ({ ...prev, activeTab: 'social_media' }))}
                        className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap flex items-center space-x-2 ${
                          marketingMode.activeTab === 'social_media'
                            ? 'border-theme-blue text-theme-blue'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        <Globe className="h-4 w-4" />
                        <span>Social Media</span>
                        <input
                          type="checkbox"
                          checked={marketingMode.channels.includes('social_media')}
                          onChange={(e) => {
                            e.stopPropagation(); // Prevent tab switching when clicking checkbox
                            const isChecked = e.target.checked;
                            setMarketingMode(prev => ({
                              ...prev,
                              channels: isChecked 
                                ? [...prev.channels, 'social_media']
                                : prev.channels.filter(c => c !== 'social_media')
                            }));
                          }}
                          className="h-4 w-4 text-theme-blue border-gray-300 rounded focus:ring-gray-500 ml-2"
                          title="Enable Social Media Channel"
                        />
                      </button>

                      {/* Email Tab */}
                      <button
                        type="button"
                        onClick={() => setMarketingMode(prev => ({ ...prev, activeTab: 'email' }))}
                        className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap flex items-center space-x-2 ${
                          marketingMode.activeTab === 'email'
                            ? 'border-green-500 text-green-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        <Mail className="h-4 w-4" />
                        <span>Email</span>
                        <input
                          type="checkbox"
                          checked={marketingMode.channels.includes('email')}
                          onChange={(e) => {
                            e.stopPropagation(); // Prevent tab switching when clicking checkbox
                            const isChecked = e.target.checked;
                            setMarketingMode(prev => ({
                              ...prev,
                              channels: isChecked 
                                ? [...prev.channels, 'email']
                                : prev.channels.filter(c => c !== 'email')
                            }));
                          }}
                          className="h-4 w-4 text-green-600 border-gray-300 rounded focus:ring-green-500 ml-2"
                          title="Enable Email Channel"
                        />
                      </button>

                      {/* SMS Tab */}
                      <button
                        type="button"
                        onClick={() => setMarketingMode(prev => ({ ...prev, activeTab: 'sms' }))}
                        className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap flex items-center space-x-2 ${
                          marketingMode.activeTab === 'sms'
                            ? 'border-purple-500 text-purple-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        <MessageCircle className="h-4 w-4" />
                        <span>SMS</span>
                        <input
                          type="checkbox"
                          checked={marketingMode.channels.includes('sms')}
                          onChange={(e) => {
                            e.stopPropagation(); // Prevent tab switching when clicking checkbox
                            const isChecked = e.target.checked;
                            setMarketingMode(prev => ({
                              ...prev,
                              channels: isChecked 
                                ? [...prev.channels, 'sms']
                                : prev.channels.filter(c => c !== 'sms')
                            }));
                          }}
                          className="h-4 w-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500 ml-2"
                          title="Enable SMS Channel"
                        />
                      </button>
                    </nav>
                  </div>

                  {/* Tab Content */}
                  <div className="mt-4">
                    {/* Social Media Tab Content */}
                    {marketingMode.activeTab === 'social_media' && (
                    <div className="space-y-4">
                      {/* Social Media Platforms & Image Format Selection - Combined Section - MOVED TO TOP */}
                      <div className="space-y-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                        {/* Social Platforms */}
                        <div className="space-y-3">
                          <label className="block font-medium text-sm text-gray-700 mb-2">
                            <Share2 className="h-4 w-4 inline mr-2" />
                            Select Social Media Platforms *
                          </label>
                          {/* All 5 platforms in one row with checkboxes on the left */}
                          <div className="grid grid-cols-5 gap-2">
                            {Object.entries(marketingMode.socialPlatforms).map(([platform, selected]) => (
                              <label
                                key={platform}
                                className={`flex items-center p-2 rounded-lg border cursor-pointer transition-colors ${
                                  selected ? 'bg-gray-50 border-gray-300' : 'bg-white border-gray-200 hover:bg-gray-50'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={selected}
                                  onChange={(e) => setMarketingMode(prev => ({
                                    ...prev,
                                    socialPlatforms: {
                                      ...prev.socialPlatforms,
                                      [platform]: e.target.checked
                                    }
                                  }))}
                                  className="h-3 w-3 text-gray-600 border-gray-300 rounded focus:ring-gray-500 mr-2"
                                />
                                <span className="capitalize font-medium text-sm">{platform === 'twitter' ? 'X' : platform}</span>
                              </label>
                            ))}
                          </div>
                          {!Object.values(marketingMode.socialPlatforms).some(platform => platform) && (
                            <p className="font-medium text-sm text-red-600">Please select at least one platform.</p>
                          )}
                        </div>

                        {/* Image Format Selection moved to Image Options section below */}
                      </div>

                      {/* Image Options moved to separate section below */}
                    </div>
                  )}

                  {/* Image Format Selection moved to Social Media Platforms section */}

                  {marketingMode.activeTab === 'email' && (
                    <div className="mt-4 p-4 border border-gray-200 bg-gray-50 rounded-lg">
                      <h4 className="font-medium text-sm text-gray-900 mb-3">Email Configuration</h4>
                      
                      {/* Include Image Option */}
                      <label className="flex items-center space-x-2 mb-3">
                        <input
                          type="checkbox"
                          checked={marketingMode.emailIncludeImage}
                          onChange={(e) => {
                            const newValue = e.target.checked;
                            setMarketingMode(prev => ({
                              ...prev, 
                              emailIncludeImage: newValue,
                              // If enabling email images and current option is "none", switch to "ai_generate"
                              imageOption: (newValue && prev.imageOption === 'none') ? 'ai_generate' : prev.imageOption
                            }));
                          }}
                          className="h-4 w-4 text-gray-600 rounded border-gray-300 focus:ring-gray-500"
                        />
                        <span className="font-medium text-sm text-gray-700">Include Image</span>
                      </label>
                      <p className="text-xs text-gray-500 mb-3">📧 Images will be attached to emails when sent to customers</p>
                      
                      <div>
                        <TemplateDropdown
                          templates={emailTemplates}
                          selectedValue={marketingMode.emailTemplate}
                          onSelect={(templateContent) => setMarketingMode(prev => ({ ...prev, emailTemplate: templateContent }))}
                          placeholder="Select an email template..."
                          templateType="email"
                          isPersonalized={marketingMode.emailPersonalized}
                        />
                        <div className="flex items-center justify-between mb-2">
                          <label className="block font-medium text-sm text-gray-700">Email Content</label>
                          <div className="flex items-center space-x-2">
                            <button
                              type="button"
                              onClick={() => openPlaceholderModal('emailTemplate', setMarketingMode)}
                              className="px-3 py-1 text-xs font-medium text-white rounded transition-colors"
                              style={{ backgroundColor: 'var(--theme-primary)' }}
                              onMouseEnter={(e) => e.target.style.backgroundColor = 'rgb(35, 147, 180)'}
                              onMouseLeave={(e) => e.target.style.backgroundColor = 'var(--theme-primary)'}
                            >
                              Available Placeholders
                            </button>
                            <FullScreenEditorButton
                              content={marketingMode.emailTemplate}
                              title="Marketing Campaign - Email Content"
                              onSave={(content) => setMarketingMode(prev => ({ ...prev, emailTemplate: content }))}
                              className="text-xs"
                              isPersonalized={marketingMode.emailPersonalized}
                            />
                          </div>
                        </div>
                        <textarea
                          value={marketingMode.emailTemplate}
                          onChange={(e) => setMarketingMode(prev => ({ ...prev, emailTemplate: e.target.value }))}
                          rows={4}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 font-medium text-sm"
                          placeholder="Email template content..."
                        />
                        <p className="text-xs text-gray-500 mt-2">
                          Use [CUSTOMER_NAME] and [PET_NAME] placeholders for personalized emails.
                        </p>
                        
                        {/* Format using ChatGPT checkbox for Email (only for custom campaign) */}
                        {marketingMode.contentType === 'custom_campaign' && (
                          <div className="mt-4">
                            <label className="flex items-center">
                              <input
                                type="checkbox"
                                checked={marketingMode.emailUseChatGPT}
                                onChange={(e) => setMarketingMode(prev => ({ ...prev, emailUseChatGPT: e.target.checked }))}
                                className="h-4 w-4 text-theme-blue border-gray-300 rounded focus:ring-gray-500 mr-3"
                              />
                              <span className="text-sm font-medium text-gray-700">Format using ChatGPT</span>
                            </label>
                            <p className="text-xs text-gray-500 mt-1 ml-7">
                              {marketingMode.emailUseChatGPT 
                                ? "ChatGPT will format and enhance the email content. CHATGTP_CONTENT placeholder is mandatory as ChatGPT replaces it with AI-rendered content."
                                : "When enabled, ChatGPT will format and enhance the email content. When disabled, content will be posted as-is with placeholder replacements."
                              }
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {marketingMode.activeTab === 'sms' && (
                    <div className="mt-4 p-4 border border-gray-200 bg-white rounded-lg">
                      <h4 className="font-medium text-sm text-gray-900 mb-3">SMS Configuration</h4>
                      
                      {/* Include Image Option */}
                      <label className="flex items-center space-x-2 mb-3">
                        <input
                          type="checkbox"
                          checked={marketingMode.smsIncludeImage}
                          onChange={(e) => {
                            const newValue = e.target.checked;
                            setMarketingMode(prev => ({
                              ...prev, 
                              smsIncludeImage: newValue,
                              // If enabling SMS images and current option is "none", switch to "ai_generate"
                              imageOption: (newValue && prev.imageOption === 'none') ? 'ai_generate' : prev.imageOption
                            }));
                          }}
                          className="h-4 w-4 text-gray-600 rounded border-gray-300 focus:ring-gray-500"
                        />
                        <span className="font-medium text-sm text-gray-700">Include Image</span>
                      </label>
                      <p className="text-xs text-gray-500 mb-3">📱 Images will be attached to SMS messages when sent to customers</p>
                      
                      <div>
                        <TemplateDropdown
                          templates={smsTemplates}
                          selectedValue={marketingMode.smsTemplate}
                          onSelect={(templateContent) => setMarketingMode(prev => ({ ...prev, smsTemplate: templateContent }))}
                          placeholder="Select an SMS template..."
                          templateType="sms"
                          isPersonalized={marketingMode.smsPersonalized}
                        />
                        <div className="flex items-center justify-between mb-2">
                          <label className="block font-medium text-sm text-gray-700">SMS Content</label>
                          <button
                            type="button"
                            onClick={() => openPlaceholderModal('smsTemplate', setMarketingMode)}
                            className="px-3 py-1 text-xs font-medium text-white rounded transition-colors"
                            style={{ backgroundColor: 'var(--theme-primary)' }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = 'rgb(35, 147, 180)'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = 'var(--theme-primary)'}
                          >
                            Available Placeholders
                          </button>
                        </div>
                        <textarea
                          value={marketingMode.smsTemplate}
                          onChange={(e) => setMarketingMode(prev => ({ ...prev, smsTemplate: e.target.value }))}
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 font-medium text-sm"
                          placeholder="SMS template content..."
                        />
                        <p className="text-xs text-gray-500 mt-2">
                          Use [CUSTOMER_NAME] and [PET_NAME] placeholders for personalized SMS.
                        </p>
                        
                        {/* Format using ChatGPT checkbox for SMS (only for custom campaign) */}
                        {marketingMode.contentType === 'custom_campaign' && (
                          <div className="mt-4">
                            <label className="flex items-center">
                              <input
                                type="checkbox"
                                checked={marketingMode.smsUseChatGPT}
                                onChange={(e) => setMarketingMode(prev => ({ ...prev, smsUseChatGPT: e.target.checked }))}
                                className="h-4 w-4 text-theme-blue border-gray-300 rounded focus:ring-gray-500 mr-3"
                              />
                              <span className="text-sm font-medium text-gray-700">Format using ChatGPT</span>
                            </label>
                            <p className="text-xs text-gray-500 mt-1 ml-7">
                              {marketingMode.smsUseChatGPT 
                                ? "ChatGPT will format and enhance the SMS content. CHATGTP_CONTENT placeholder is mandatory as ChatGPT replaces it with AI-rendered content."
                                : "When enabled, ChatGPT will format and enhance the SMS content. When disabled, content will be posted as-is with placeholder replacements."
                              }
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  </div>
                </div>

                {/* Select Image Section */}
                <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-6">
                  <label className="block font-medium text-sm text-gray-700">
                    <ImageIcon className="h-4 w-4 inline mr-2" />
                    Select Image
                  </label>
                  
                  {/* Image Options Subsection */}
                  <div className="space-y-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                    <label className="block font-medium text-sm text-gray-700">
                      <ImageIcon className="h-4 w-4 inline mr-2" />
                      Image Options
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
                        <input
                          type="radio"
                          name="marketingImageOption"
                          value="ai_generate"
                          checked={marketingMode.imageOption === 'ai_generate'}
                          onChange={(e) => setMarketingMode(prev => ({ ...prev, imageOption: e.target.value }))}
                          className="mr-3"
                        />
                        <div>
                          <div className="font-medium text-sm">AI Generate</div>
                          <div className="text-xs text-gray-500">Create unique images using AI</div>
                        </div>
                      </label>
                      <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
                        <input
                          type="radio"
                          name="marketingImageOption"
                          value="upload"
                          checked={marketingMode.imageOption === 'upload'}
                          onChange={(e) => setMarketingMode(prev => ({ ...prev, imageOption: e.target.value }))}
                          className="mr-3"
                        />
                        <div>
                          <div className="font-medium text-sm">Upload Image</div>
                          <div className="text-xs text-gray-500">Use your own images</div>
                        </div>
                      </label>
                      <label className="flex items-start p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-100 border">
                        <input
                          type="radio"
                          name="marketingImageOption"
                          value="reference"
                          checked={marketingMode.imageOption === 'reference'}
                          onChange={(e) => setMarketingMode(prev => ({ ...prev, imageOption: e.target.value }))}
                          className="mr-3"
                        />
                        <div>
                          <div className="font-medium text-sm">Select Existing Image</div>
                          <div className="text-xs text-gray-500">Select from uploaded images</div>
                        </div>
                      </label>
                      <label className={`flex items-start p-3 bg-white rounded-lg border ${(marketingMode.emailIncludeImage || marketingMode.smsIncludeImage) ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:bg-gray-100'}`}>
                        <input
                          type="radio"
                          name="marketingImageOption"
                          value="none"
                          checked={marketingMode.imageOption === 'none'}
                          onChange={(e) => setMarketingMode(prev => ({ ...prev, imageOption: e.target.value }))}
                          disabled={marketingMode.emailIncludeImage || marketingMode.smsIncludeImage}
                          className="mr-3"
                        />
                        <div>
                          <div className="font-medium text-sm">No Images</div>
                          <div className="text-xs text-gray-500">
                            {(marketingMode.emailIncludeImage || marketingMode.smsIncludeImage) 
                              ? "Disabled (Email/SMS images required)" 
                              : "Text-only content"
                            }
                          </div>
                        </div>
                      </label>
                    </div>

                    {/* Image Upload Area */}
                    {marketingMode.imageOption === 'upload' && (
                      <div className="mt-4">
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center bg-gray-50">
                          <Upload className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                          <p className="font-medium text-sm text-gray-600 mb-2">Upload marketing images</p>
                          <p className="text-xs text-gray-500 mb-4">Recommended: High resolution images for best results</p>
                          <div className="flex justify-center">
                            <label className="inline-flex items-center px-4 py-2 text-white rounded-md hover:opacity-90 transition-colors cursor-pointer" style={{ backgroundColor: 'var(--theme-primary)' }}>
                              <Upload className="h-4 w-4 mr-2" />
                              Upload Image
                              <input
                                type="file"
                                multiple
                                accept="image/*"
                                onChange={(e) => handleImageUpload(e, 'marketing')}
                                className="hidden"
                              />
                            </label>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {marketingMode.imageOption === 'reference' && (
                      <div className="mt-4">
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center bg-gray-50">
                          <ImageIcon className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                          <p className="font-medium text-sm text-gray-600 mb-2">Select reference images</p>
                          <p className="text-xs text-gray-500 mb-4">Choose images from your asset library to use as style reference</p>
                          <div className="flex justify-center">
                            <button
                              type="button"
                              onClick={() => openAssetBrowser('marketing')}
                              className="inline-flex items-center px-4 py-2 text-white rounded-md hover:opacity-90 transition-colors" style={{ backgroundColor: 'var(--theme-primary)' }}
                            >
                              <Folder className="h-4 w-4 mr-2" />
                              Select Existing Image
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Uploaded Images Preview */}
                    {(marketingMode.imageOption === 'upload' || marketingMode.imageOption === 'reference') && marketingMode.uploadedImages.length > 0 && (
                      <div className="mt-4">
                        <label className="block font-medium text-sm text-gray-700 mb-2">
                          Selected Images ({marketingMode.imageOption === 'reference' ? 'Reference' : 'Upload'})
                        </label>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          {marketingMode.uploadedImages.map((image, index) => (
                            <div key={index} className="relative">
                              <img 
                                src={image.url} 
                                alt={image.name}
                                className="w-full h-24 object-cover rounded-lg border"
                              />
                              <button
                                onClick={() => removeUploadedImage(index, 'marketing')}
                                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                              >
                                <Trash2 className="h-3 w-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Select Image Format for Each Platform Subsection */}
                  {((Object.values(marketingMode.socialPlatforms).some(platform => platform)) || marketingMode.emailIncludeImage || marketingMode.smsIncludeImage) && marketingMode.imageOption !== 'none' && (
                    <div className="space-y-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                      <label className="block font-medium text-sm text-gray-700">
                        <ImageIcon className="h-4 w-4 inline mr-2" />
                        Select Image Format for Each Platform *
                      </label>
                      
                      {/* Format Selection for Each Enabled Platform - Dynamic grid */}
                      <div className={`grid gap-2 ${(() => {
                        const socialPlatforms = Object.entries(marketingMode.socialPlatforms).filter(([platform, enabled]) => enabled);
                        const emailSms = [marketingMode.emailIncludeImage && 'email', marketingMode.smsIncludeImage && 'sms'].filter(Boolean);
                        const totalPlatforms = socialPlatforms.length + emailSms.length;
                        return totalPlatforms === 1 ? 'grid-cols-1' : totalPlatforms === 2 ? 'grid-cols-2' : totalPlatforms === 3 ? 'grid-cols-3' : totalPlatforms === 4 ? 'grid-cols-4' : 'grid-cols-5';
                      })()}`}>
                        {/* Social Media Platforms */}
                        {Object.entries(marketingMode.socialPlatforms)
                          .filter(([platform, enabled]) => enabled)
                          .map(([platform, enabled]) => (
                            <div key={platform} className="flex flex-col">
                              <label className="font-medium text-sm text-gray-600 mb-2 text-left capitalize">
                                {platform === 'twitter' ? 'X' : platform}
                              </label>
                              <select
                                value={marketingMode.platformImageFormats[platform] || ''}
                                onChange={(e) => {
                                  const newFormats = { ...(marketingMode.platformImageFormats || {}) };
                                  if (e.target.value) {
                                    newFormats[platform] = e.target.value;
                                  } else {
                                    delete newFormats[platform];
                                  }
                                  setMarketingMode(prev => ({ ...prev, platformImageFormats: newFormats }));
                                }}
                                className="w-full px-2 py-2 font-medium text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                              >
                                <option value="">Select...</option>
                                <option value="none">None</option>
                                {Object.entries(socialMediaPlatforms[platform === 'twitter' ? 'twitter' : platform]?.formats || {}).map(([formatKey, formatName]) => (
                                  <option key={formatKey} value={formatKey}>{formatName}</option>
                                ))}
                              </select>
                              
                              {/* Show format dimensions */}
                              {marketingMode.platformImageFormats[platform] === 'none' && (
                                <div className="text-xs text-gray-500 mt-1 text-left">
                                  No image for {platform === 'twitter' ? 'X' : platform}
                                </div>
                              )}
                              {marketingMode.platformImageFormats[platform] && marketingMode.platformImageFormats[platform] !== 'none' && socialMediaImageSettings[platform === 'twitter' ? 'twitter' : platform]?.[marketingMode.platformImageFormats[platform]] && (
                                <div className="text-xs text-gray-600 mt-1 text-left">
                                  {socialMediaImageSettings[platform === 'twitter' ? 'twitter' : platform][marketingMode.platformImageFormats[platform]].width} × {socialMediaImageSettings[platform === 'twitter' ? 'twitter' : platform][marketingMode.platformImageFormats[platform]].height}
                                </div>
                              )}
                            </div>
                          ))
                        }
                        
                        {/* Email Format Selection */}
                        {marketingMode.emailIncludeImage && (
                          <div className="flex flex-col">
                            <label className="font-medium text-sm text-gray-600 mb-2 text-left">
                              📧 Email
                            </label>
                            <select
                              value={marketingMode.platformImageFormats.email || ''}
                              onChange={(e) => {
                                const newFormats = { ...(marketingMode.platformImageFormats || {}) };
                                if (e.target.value) {
                                  newFormats.email = e.target.value;
                                } else {
                                  delete newFormats.email;
                                }
                                setMarketingMode(prev => ({ ...prev, platformImageFormats: newFormats }));
                              }}
                              className="w-full px-2 py-2 font-medium text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                            >
                              <option value="">Select...</option>
                              <option value="none">None</option>
                              <option value="portrait">Portrait</option>
                              <option value="landscape">Landscape</option>
                              <option value="square">Square</option>
                            </select>
                            
                            {/* Show format dimensions */}
                            {marketingMode.platformImageFormats.email && marketingMode.platformImageFormats.email !== 'none' && (
                              <div className="text-xs text-gray-600 mt-1 text-left">
                                {marketingMode.platformImageFormats.email === 'portrait' && '1080 × 1920'}
                                {marketingMode.platformImageFormats.email === 'landscape' && '1280 × 720'}
                                {marketingMode.platformImageFormats.email === 'square' && '600 × 600'}
                              </div>
                            )}
                            {marketingMode.platformImageFormats.email === 'none' && (
                              <div className="text-xs text-gray-500 mt-1 text-left">
                                No image for email
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* SMS Format Selection */}
                        {marketingMode.smsIncludeImage && (
                          <div className="flex flex-col">
                            <label className="font-medium text-sm text-gray-600 mb-2 text-left">
                              📱 SMS
                            </label>
                            <select
                              value={marketingMode.platformImageFormats.sms || ''}
                              onChange={(e) => {
                                const newFormats = { ...(marketingMode.platformImageFormats || {}) };
                                if (e.target.value) {
                                  newFormats.sms = e.target.value;
                                } else {
                                  delete newFormats.sms;
                                }
                                setMarketingMode(prev => ({ ...prev, platformImageFormats: newFormats }));
                              }}
                              className="w-full px-2 py-2 font-medium text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                            >
                              <option value="">Select...</option>
                              <option value="none">None</option>
                              <option value="portrait">Portrait</option>
                              <option value="landscape">Landscape</option>
                              <option value="square">Square</option>
                            </select>
                            
                            {/* Show format dimensions */}
                            {marketingMode.platformImageFormats.sms && marketingMode.platformImageFormats.sms !== 'none' && (
                              <div className="text-xs text-gray-600 mt-1 text-left">
                                {marketingMode.platformImageFormats.sms === 'portrait' && '1080 × 1920'}
                                {marketingMode.platformImageFormats.sms === 'landscape' && '1280 × 720'}
                                {marketingMode.platformImageFormats.sms === 'square' && '600 × 600'}
                              </div>
                            )}
                            {marketingMode.platformImageFormats.sms === 'none' && (
                              <div className="text-xs text-gray-500 mt-1 text-left">
                                No image for SMS
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Add Custom Text Banner to Image Subsection */}
                  {(marketingMode.imageOption === 'ai_generate' || marketingMode.imageOption === 'upload' || marketingMode.imageOption === 'reference') && (
                    <div className="space-y-4 p-4 bg-white border border-gray-200 rounded-lg">
                      <label className="flex items-center mb-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={marketingMode.enableCustomImageText}
                          onChange={(e) => setMarketingMode(prev => ({ ...prev, enableCustomImageText: e.target.checked }))}
                          className="h-4 w-4 text-theme-blue border-gray-300 rounded focus:ring-gray-500 mr-3"
                        />
                        <span className="block font-medium text-sm text-gray-700">
                          <FileText className="h-4 w-4 inline mr-2" />
                          Add Custom Text Banner to Image
                        </span>
                      </label>
                      
                      <p className="text-xs text-gray-500 mb-3">
                        Add custom text overlay on the generated image (e.g., your clinic name, promotional text)
                      </p>
                      
                      {marketingMode.enableCustomImageText && (
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <label className="block font-medium text-sm text-gray-700">
                              Custom Banner Text
                            </label>
                            <button
                              type="button"
                              onClick={() => openPlaceholderModal('imageText', (field, value) => setMarketingMode(prev => ({ ...prev, [field]: value })))}
                              className="px-3 py-1 text-xs font-medium text-white rounded transition-colors"
                              style={{ backgroundColor: 'var(--theme-primary)' }}
                              onMouseEnter={(e) => e.target.style.backgroundColor = 'rgb(35, 147, 180)'}
                              onMouseLeave={(e) => e.target.style.backgroundColor = 'var(--theme-primary)'}
                            >
                              Available Placeholders
                            </button>
                          </div>
                          <input
                            type="text"
                            value={marketingMode.imageText || '[BUSINESS_NAME] [PHONE_NUMBER]'}
                            onChange={(e) => setMarketingMode(prev => ({ ...prev, imageText: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 font-medium text-sm"
                            placeholder="[BUSINESS_NAME] [PHONE_NUMBER]"
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Workflow Mode */}
                <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-6">
                  <label className="block font-medium text-sm text-gray-700">
                    <Bot className="h-4 w-4 inline mr-2" />
                    Workflow Mode *
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {[
                      { id: 'in_review', label: 'In Review', desc: 'Review before publishing' },
                      { id: 'ready_to_publish', label: 'Ready to Publish', desc: 'Ready for manual publish' },
                      { id: 'auto_publish', label: 'Auto Publish', desc: 'Publish automatically' }
                    ].map(({ id, label, desc }) => (
                      <label key={id} className="relative">
                        <div className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                          marketingMode.workflowMode === id 
                            ? 'border-gray-400 bg-gray-100' 
                            : 'border-gray-200 hover:border-gray-300 bg-white'
                        }`}>
                          <div className="flex items-center space-x-2 mb-2">
                            <input
                              type="radio"
                              name="workflowMode"
                              value={id}
                              checked={marketingMode.workflowMode === id}
                              onChange={(e) => setMarketingMode(prev => ({ ...prev, workflowMode: e.target.value }))}
                              className="h-4 w-4 text-gray-600 border-gray-300 focus:ring-gray-500"
                            />
                            <div className="font-medium text-sm">{label}</div>
                          </div>
                          <p className="font-medium text-sm text-gray-600">{desc}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Save Button */}
                <div className="flex justify-end pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => handleSave(false)}
                    disabled={loading || !marketingMode.agentName.trim() || marketingMode.channels.length === 0}
                    className="px-8 py-3 rounded-lg font-medium text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                    style={{ backgroundColor: 'var(--theme-primary)', ':hover': { backgroundColor: 'var(--theme-secondary)' } }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--theme-secondary)'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'rgb(41, 163, 201)'}
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {loading ? 'Saving...' : isEditMode ? 'Update Agent Configuration' : 'Save Agent'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        </> {/* End Content Area */}
      </div>
      
      {/* Loading Modal for Save & Redirect */}
      {showSaveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 shadow-xl max-w-sm mx-4">
            <div className="flex items-center space-x-3">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-theme-blue"></div>
              <div>
                <h3 className="text-lg font-medium text-gray-900">
                  {isEditMode ? 'Updating Agent' : 'Creating Agent'}
                </h3>
                <p className="text-sm text-gray-600">
                  {isEditMode ? 'Saving changes and redirecting...' : 'Setting up your agent and redirecting...'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Email Editor Modal Popup */}
      {showFullScreenEditor && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="px-6 py-4 rounded-t-xl flex-shrink-0" style={{ backgroundColor: 'var(--theme-primary)' }}>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Mail className="h-6 w-6 text-white" />
                  <h2 className="text-lg font-medium text-white">{fullScreenEditorTitle}</h2>
                </div>
                <div className="flex items-center space-x-4">
                  <button
                    type="button"
                    onClick={() => {
                      // Get the textarea element in the full-screen modal
                      const textarea = document.querySelector('.full-screen-editor-textarea');
                      // Create a setter function for the full-screen editor
                      const tempSetter = (updateFunction) => (newValue) => {
                        setFullScreenEditorContent(newValue);
                      };
                      openPlaceholderModal('fullScreenEditor', tempSetter, textarea);
                    }}
                    className="px-3 py-1 text-xs font-medium text-white bg-white bg-opacity-20 rounded transition-colors hover:bg-opacity-30"
                  >
                    Available Placeholders
                  </button>
                  <div className="hidden sm:flex items-center space-x-2 text-sm text-white text-opacity-90">
                    <kbd className="px-2 py-1 bg-white bg-opacity-20 rounded text-xs text-white">Ctrl+S</kbd>
                    <span>to save</span>
                    <kbd className="px-2 py-1 bg-white bg-opacity-20 rounded text-xs text-white">Esc</kbd>
                    <span>to close</span>
                  </div>
                  <button
                    onClick={closeFullScreenEditor}
                    className="text-white hover:text-gray-200 transition-colors"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>
              </div>
            </div>

            {/* Scrollable Content Area */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Email Content
                  </label>
                  <div className="relative">
                    <textarea
                      value={fullScreenEditorContent}
                      onChange={(e) => setFullScreenEditorContent(e.target.value)}
                      className="w-full h-80 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent text-sm font-mono resize-none full-screen-editor-textarea"
                      style={{ '--tw-ring-color': 'var(--theme-primary)' }}
                      placeholder="Enter your email content here...

You can use placeholders like:
[CUSTOMER_NAME] - Customer's name
[PET_NAME] - Pet's name  
[PET_NAMES] - All pet names
[WEBSITE_LINK] - Website URL
[BOOK_NOW_LINK] - Booking URL
[PHONE_NUMBER] - Business phone
[BUSINESS_NAME] - Business name
[BUSINESS_ADDRESS] - Business address"
                      autoFocus
                    />
                    <div className="absolute bottom-3 right-3 text-xs text-gray-400 bg-white px-2 py-1 rounded">
                      {fullScreenEditorContent.length} characters
                    </div>
                  </div>
                </div>

                {/* Email content editing area only - tips and placeholders removed */}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 rounded-b-xl flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-500">
                  {fullScreenEditorContent.length} characters
                </div>
                <div className="flex items-center space-x-3">
                  <button
                    type="button"
                    onClick={closeFullScreenEditor}
                    className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={saveFullScreenEditor}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white transition-colors"
                    style={{ backgroundColor: 'var(--theme-primary)' }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = 'rgb(35, 147, 180)'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'var(--theme-primary)'}
                  >
                    <Save className="h-4 w-4 mr-2" />
                    Save Changes
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* S3 Asset Browser Modal */}
      {showAssetBrowser && (
        <S3AssetBrowser
          isOpen={showAssetBrowser}
          onClose={() => {
            setShowAssetBrowser(false);
            setCurrentImageMode('');
          }}
          onSelectAsset={handleAssetSelect}
          allowedTypes={['images']}
          title="Browse Agent Images"
          showUpload={true}
          initialPath="AI Agent Uploads"
        />
      )}

      {/* Placeholder Modal */}
      <PlaceholderModal
        isOpen={placeholderModal.isOpen}
        onClose={closePlaceholderModal}
        onInsertPlaceholder={insertPlaceholder}
        placeholderType="sms"
      />

      {/* Validation Modal */}
      {validationModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <div className="flex items-center mb-4">
              <div className="bg-red-100 rounded-full p-2 mr-3">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900">{validationModal.title}</h3>
            </div>
            <p className="text-sm text-gray-600 mb-6">{validationModal.message}</p>
            <div className="flex justify-end">
              <button
                onClick={() => setValidationModal({ isOpen: false, title: '', message: '' })}
                className="px-4 py-2 bg-theme-blue text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </div>
    </div>
  );
};

export default AIAgentConfig;